import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { createPortal } from "react-dom";
import { loadPrefs, savePrefs, loadTravelPrefs, saveTravelPrefs, onSaveStatus } from './lib/userPrefs';
import { generateBridgeSlots, detectCountry } from './lib/holidays';
import { useT, LANG_STORAGE_KEY } from './lib/i18n';
import MapScreen from './MapScreen';
import { supabase } from './lib/supabase';

const LANG_LOCALE = { fr:'fr-FR', en:'en-GB', es:'es-ES', de:'de-DE', zh:'zh-CN' };
const langToLocale = lang => LANG_LOCALE[lang] || 'fr-FR';


// Formate un slot à l'affichage depuis ses ISO dates (ignore les labels pré-stockés en FR)
function fmtSlotDisplay(slot, locale) {
  if (!slot?.departIso || !slot?.returnIso) return slot?.label || '';
  const fmt = iso => {
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString(locale, { weekday:'short' }) + ' ' + d.toLocaleDateString(locale, { day:'numeric', month:'long' });
  };
  return `${fmt(slot.departIso)} — ${fmt(slot.returnIso)}`;
}


const AIRPORT_CODES = {
  // DESTS (English)
  "Prague":"PRG","Barcelona":"BCN","Vilnius":"VNO","Cinque Terre":"GNO",
  "Reykjavik":"KEF","Istanbul":"IST","Marrakech":"RAK","Matera":"BRI",
  "Rome":"FCO","Athens":"ATH","Lisbon":"LIS","Dubrovnik":"DBV",
  "Edinburgh":"EDI","Bruges":"CRL","Valletta":"MLA","Avignon":"MPL",
  "Stockholm":"ARN","Venezia":"VCE",
  // French aliases for DESTS
  "Barcelone":"BCN","Venise":"VCE","Athènes":"ATH","Lisbonne":"LIS",
  "Édimbourg":"EDI","La Valette":"MLA","Reykjavik":"KEF",
  // Europe
  "Madrid":"MAD","Séville":"SVQ","Seville":"SVQ","Valence":"VLC","Valencia":"VLC",
  "Malaga":"AGP","Ibiza":"IBZ","Palma":"PMI","Gran Canaria":"LPA","Fuerteventura":"FUE","Tenerife":"TFN",
  "Lisbonne":"LIS","Porto":"OPO",
  "Amsterdam":"AMS","Bruxelles":"BRU","Bruges":"CRL",
  "Berlin":"BER","Munich":"MUC","Francfort":"FRA","Frankfurt":"FRA",
  "Vienne":"VIE","Vienna":"VIE",
  "Copenhague":"CPH","Copenhagen":"CPH","Oslo":"OSL","Bergen":"BGO","Helsinki":"HEL",
  "Budapest":"BUD","Varsovie":"WAW","Warsaw":"WAW","Cracovie":"KRK","Krakow":"KRK",
  "Bucarest":"OTP","Bucharest":"OTP","Belgrade":"BEG","Sofia":"SOF","Zagreb":"ZAG",
  "Tallinn":"TLL","Riga":"RIX",
  "Santorin":"JTR","Santorini":"JTR","Mykonos":"JMK","Rhodes":"RHO","Crète":"HER","Crete":"HER",
  "Malte":"MLA","Malta":"MLA","Chypre":"LCA","Cyprus":"LCA",
  "Londres":"LHR","London":"LHR",
  "Naples":"NAP","Milan":"MXP","Florence":"FLR","Bari":"BRI",
  "Nice":"NCE","Lyon":"LYS","Bordeaux":"BOD","Toulouse":"TLS","Marseille":"MRS",
  // Monaco (uses Nice)
  "Monaco":"NCE","Grand Prix de Monaco":"NCE","Formule 1 Monaco":"NCE",
  // Afrique
  "Le Cap":"CPT","Cape Town":"CPT","Johannesburg":"JNB","Durban":"DUR",
  "Nairobi":"NBO","Rwanda":"KGL","Kigali":"KGL","Zanzibar":"ZNZ",
  "Casablanca":"CMN","Agadir":"AGA","Fès":"FEZ","Fez":"FEZ","Tunis":"TUN","Alger":"ALG","Dakar":"DSS",
  "Addis-Abeba":"ADD","Addis Ababa":"ADD","Sainte-Hélène":"HLE","Saint Helena":"HLE",
  "Chutes Victoria":"VFA","Victoria Falls":"VFA",
  "Antananarivo":"TNR","Maurice":"MRU","Mauritius":"MRU","Réunion":"RUN","Maldives":"MLE",
  // Moyen-Orient
  "Dubaï":"DXB","Dubai":"DXB","Abu Dhabi":"AUH","Doha":"DOH","Riyad":"RUH",
  "Amman":"AMM","Beyrouth":"BEY","Beirut":"BEY","Tel Aviv":"TLV",
  // Asie
  "Tokyo":"NRT","Osaka":"KIX","Kyoto":"ITM",
  "Pékin":"PEK","Beijing":"PEK","Shanghai":"PVG","Hong Kong":"HKG",
  "Séoul":"ICN","Seoul":"ICN",
  "Bangkok":"BKK","Chiang Mai":"CNX","Phuket":"HKT",
  "Singapour":"SIN","Singapore":"SIN","Kuala Lumpur":"KUL",
  "Bali":"DPS","Jakarta":"CGK","Manila":"MNL",
  "Ho Chi Minh":"SGN","Hanoi":"HAN","Phnom Penh":"PNH",
  "Mumbai":"BOM","Delhi":"DEL","Goa":"GOI","Colombo":"CMB",
  "Katmandou":"KTM","Kathmandu":"KTM",
  "Tbilissi":"TBS","Tbilisi":"TBS","Bakou":"GYD","Baku":"GYD",
  // Amériques
  "New York":"JFK","Los Angeles":"LAX","Miami":"MIA","Chicago":"ORD",
  "Montréal":"YUL","Montreal":"YUL","Toronto":"YYZ","Vancouver":"YVR",
  "Mexico":"MEX","Cancún":"CUN","Cancun":"CUN","La Havane":"HAV","Havana":"HAV","Punta Cana":"PUJ",
  "Buenos Aires":"EZE","Iguazu":"IGR","Iguaçu":"IGR","Rio de Janeiro":"GIG","Rio":"GIG",
  "Lima":"LIM","Bogota":"BOG","Medellín":"MDE","Medellin":"MDE","Santiago":"SCL",
  // Océanie
  "Sydney":"SYD","Melbourne":"MEL","Auckland":"AKL","Bora Bora":"BOB",
};

// Airport coordinates [lat, lng] — used to find nearest airport to event destinations
const AIRPORT_LATLNG = {
  // France
  CDG:[49.009,2.548],ORY:[48.726,2.365],LYS:[45.726,5.081],NCE:[43.658,7.216],MRS:[43.437,5.221],
  TLS:[43.629,1.364],BOD:[44.828,-0.716],NTE:[47.153,-1.611],MPL:[43.576,3.963],
  // Benelux / UK
  AMS:[52.309,4.764],BRU:[50.901,4.484],CRL:[50.459,4.453],LHR:[51.477,-0.461],LGW:[51.148,-0.190],STN:[51.885,0.235],EDI:[55.950,-3.373],
  // Italy
  FCO:[41.800,12.239],MXP:[45.631,8.728],LIN:[45.445,9.277],VCE:[45.505,12.352],VRN:[45.396,10.889],
  BGY:[45.674,9.704],BLQ:[44.535,11.289],FLR:[43.810,11.205],NAP:[40.886,14.291],BRI:[41.138,16.760],
  CTA:[37.467,15.066],PMO:[38.176,13.091],GOA:[44.413,8.838],TSF:[45.648,12.194],
  // Germany / Austria / CH
  BER:[52.367,13.503],MUC:[48.354,11.786],FRA:[50.033,8.571],HAM:[53.630,9.988],DUS:[51.290,6.767],
  VIE:[48.110,16.570],ZRH:[47.465,8.549],GVA:[46.238,6.109],
  // Iberia
  BCN:[41.297,2.079],MAD:[40.498,-3.568],SVQ:[37.418,-5.893],VLC:[39.489,-0.481],PMI:[39.552,2.739],LIS:[38.776,-9.135],OPO:[41.248,-8.681],
  // Nordic
  CPH:[55.618,12.656],OSL:[60.194,11.100],ARN:[59.652,17.919],BGO:[60.293,5.218],HEL:[60.317,24.963],
  // Eastern Europe
  PRG:[50.101,14.260],BUD:[47.430,19.261],WAW:[52.166,20.967],KRK:[49.997,19.785],
  DBV:[42.561,18.268],ZAG:[45.743,16.069],BEG:[44.818,20.309],SOF:[42.697,23.411],
  OTP:[44.571,26.085],TLL:[59.413,24.833],RIX:[56.924,23.971],VNO:[54.634,25.286],
  // Med / islands
  ATH:[37.937,23.945],JTR:[36.399,25.480],MLA:[35.857,14.478],LCA:[34.875,33.625],
  IST:[40.977,28.815],TLV:[32.011,34.887],AMM:[31.722,35.993],BEY:[33.821,35.488],
  // Africa / Middle East
  CMN:[33.368,-7.590],RAK:[31.607,-8.036],TUN:[36.851,10.227],ALG:[36.691,3.215],
  DXB:[25.253,55.364],DOH:[25.261,51.614],AUH:[24.433,54.651],RUH:[24.958,46.699],
  NBO:[1.319,36.925],CPT:[-33.965,18.602],JNB:[-26.134,28.242],
  // Americas
  JFK:[40.641,-73.778],MIA:[25.796,-80.287],LAX:[33.943,-118.408],YUL:[45.471,-73.741],
  // Asia / Oceania
  NRT:[35.772,140.393],SIN:[1.364,103.992],SYD:[-33.940,151.175],
  // Misc
  KEF:[63.985,-22.606],
};

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371, toRad = x => x * Math.PI / 180;
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

// Nominatim geocoding with localStorage cache
const _geoCache = (() => {
  try { return new Map(JSON.parse(localStorage.getItem('_plotted_geo') || '[]')); } catch { return new Map(); }
})();
function _saveGeoCache() {
  try { localStorage.setItem('_plotted_geo', JSON.stringify([..._geoCache])); } catch {}
}

async function geocodeCity(name) {
  if (_geoCache.has(name)) return _geoCache.get(name);
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(name)}&format=json&limit=1`, { headers: {'Accept-Language':'en'} });
    const d = await r.json();
    const coords = d?.[0] ? [parseFloat(d[0].lat), parseFloat(d[0].lon)] : null;
    _geoCache.set(name, coords); _saveGeoCache();
    return coords;
  } catch { _geoCache.set(name, null); return null; }
}

// Returns IATA codes of airports within maxKm of a city, sorted by distance
async function airportsNearCity(cityName, maxKm = 60) {
  const coords = await geocodeCity(cityName);
  if (!coords) return [];
  const [lat, lng] = coords;
  return Object.entries(AIRPORT_LATLNG)
    .map(([iata, [alat, alng]]) => ({ iata, km: haversineKm(lat, lng, alat, alng) }))
    .filter(x => x.km <= maxKm)
    .sort((a, b) => a.km - b.km)
    .map(x => x.iata);
}

// Approximate flight hours from Paris CDG/ORY — used to generate realistic mock durations
// and to filter slot types (WE/longWE/short/long) by actual travel time
const FLIGHT_HOURS_FROM_CDG = {
  // ~1-2h (domestic / very short Europe)
  NCE:1.5, LYS:1, MRS:1.2, TLS:1.2, BOD:1.2, MPL:1, BRU:1, LHR:1.2, LGW:1.2, CRL:1.2, AMS:1.2,
  // ~2-3h (Europe)
  BCN:2, MAD:2.5, SVQ:2.5, VLC:2.2, LIS:2.5, OPO:2.5, FCO:2.5, MXP:1.5, NAP:2.5, BRI:2.5, FLR:2,
  PRG:1.5, BER:2, MUC:1.8, FRA:1.5, VIE:2.2, BUD:2.5, WAW:2.5, KRK:2.5,
  AMS:1.2, CPH:2, ARN:2.5, OSL:2.5, BGO:2.5, HEL:3, EDI:2, STN:1.2,
  ATH:3.5, DBV:2.5, BEG:2.2, SOF:3, ZAG:2, OTP:3, TLL:3, RIX:3, SJJ:2.5, TIV:2.5,
  JTR:3.5, JMK:3.5, RHO:3.5, HER:3.5, MLA:2.2, LCA:4, VNO:2.5,
  GNO:2, MPL:1, VCE:2, VRN:2, BGY:1.8, BLQ:2, GOA:2, TSF:2, KEF:3,
  // ~3-5h (medium haul)
  IST:3.5, TLV:4, AMM:4.5, BEY:4.5, TUN:2.5, ALG:2, CMN:2.5, AGA:3, FEZ:2.5,
  RAK:3, DSS:5, ADD:7, RUH:6, DXB:6.5, AUH:6.5, DOH:6, TNG:2.5, IBZ:2, PMI:2,
  // ~8-12h (long haul Africa)
  CPT:11, JNB:11, DUR:11, NBO:9, KGL:9, ZNZ:10, MRU:11, RUN:11, HLE:10, VFA:10, TNR:10,
  // ~5-7h (Canaries / Atlantic)
  LPA:4, FUE:4, TFN:4, PUJ:9, HAV:9, CUN:10,
  // ~8-13h (Americas)
  JFK:8, MIA:9, ORD:9, LAX:11, YUL:8, YYZ:9, YVR:10,
  MEX:11, EZE:14, SCL:14, LIM:12, BOG:11, GIG:11, IGR:14,
  // ~9-13h (Asia)
  DXB:6.5, NRT:12, KIX:12, PEK:10, PVG:11, HKG:12, ICN:11,
  BKK:11, HKT:11, CNX:11, SIN:13, KUL:13, DPS:15,
  SGN:11, HAN:11, BOM:9, DEL:8, GOI:9, CMB:10, MLE:9,
  TBS:4, GYD:4.5,
  SYD:22, MEL:22, AKL:24, BOB:20,
};

// Returns compatible slot types for a destination based on flight duration
function compatibleSlotTypes(iata) {
  const h = FLIGHT_HOURS_FROM_CDG[iata];
  if (!h) return ['WE','longWE','short','long']; // unknown → allow all
  if (h <= 2.5) return ['WE','longWE','short','long'];
  if (h <= 4)   return ['longWE','short','long'];
  if (h <= 7)   return ['short','long'];
  return ['long'];
}

const DEPARTURE_AIRPORTS = [
  { iata:'ORY', city:'Paris',      name:'Orly',                    lat:48.7233, lng:2.3794 },
  { iata:'CDG', city:'Paris',      name:'Charles de Gaulle',       lat:49.0097, lng:2.5479 },
  { iata:'BVA', city:'Paris',      name:'Beauvais-Tillé',          lat:49.4544, lng:2.1128 },
  { iata:'LYS', city:'Lyon',       name:'Saint-Exupéry',           lat:45.7256, lng:5.0811 },
  { iata:'MRS', city:'Marseille',  name:'Provence',                lat:43.4393, lng:5.2215 },
  { iata:'NCE', city:'Nice',       name:'Côte d\'Azur',            lat:43.6584, lng:7.2159 },
  { iata:'TLS', city:'Toulouse',   name:'Blagnac',                 lat:43.6291, lng:1.3638 },
  { iata:'BOD', city:'Bordeaux',   name:'Mérignac',                lat:44.8283, lng:-0.7153 },
  { iata:'NTE', city:'Nantes',     name:'Atlantique',              lat:47.1532, lng:-1.6108 },
  { iata:'SXB', city:'Strasbourg', name:'Entzheim',                lat:48.5383, lng:7.6281 },
  { iata:'LIL', city:'Lille',      name:'Lesquin',                 lat:50.5619, lng:3.0886 },
  { iata:'MPL', city:'Montpellier',name:'Méditerranée',            lat:43.5763, lng:3.9630 },
  { iata:'RNS', city:'Rennes',     name:'Saint-Jacques',           lat:48.0695, lng:-1.7348 },
  { iata:'BES', city:'Brest',      name:'Bretagne',                lat:48.4478, lng:-4.4186 },
  { iata:'BIQ', city:'Biarritz',   name:'Pays Basque',             lat:43.4683, lng:-1.5231 },
  { iata:'LHR', city:'Londres',    name:'Heathrow',                lat:51.4775, lng:-0.4614 },
  { iata:'LGW', city:'Londres',    name:'Gatwick',                 lat:51.1481, lng:-0.1903 },
  { iata:'STN', city:'Londres',    name:'Stansted',                lat:51.8850, lng:0.2350 },
  { iata:'AMS', city:'Amsterdam',  name:'Schiphol',                lat:52.3086, lng:4.7639 },
  { iata:'BRU', city:'Bruxelles',  name:'Zaventem',                lat:50.9010, lng:4.4844 },
  { iata:'FRA', city:'Francfort',  name:'Frankfurt',               lat:50.0379, lng:8.5622 },
  { iata:'MUC', city:'Munich',     name:'Munich',                  lat:48.3537, lng:11.7750 },
  { iata:'MAD', city:'Madrid',     name:'Barajas',                 lat:40.4936, lng:-3.5668 },
  { iata:'BCN', city:'Barcelone',  name:'El Prat',                 lat:41.2971, lng:2.0785 },
  { iata:'FCO', city:'Rome',       name:'Fiumicino',               lat:41.8003, lng:12.2389 },
  { iata:'MXP', city:'Milan',      name:'Malpensa',                lat:45.6306, lng:8.7281 },
  { iata:'ZRH', city:'Zurich',     name:'Kloten',                  lat:47.4647, lng:8.5492 },
  { iata:'GVA', city:'Genève',     name:'Cointrin',                lat:46.2381, lng:6.1089 },
  { iata:'LIS', city:'Lisbonne',   name:'Humberto Delgado',        lat:38.7756, lng:-9.1354 },
  { iata:'DUB', city:'Dublin',     name:'Dublin',                  lat:53.4213, lng:-6.2701 },
  { iata:'CPH', city:'Copenhague', name:'Kastrup',                 lat:55.6181, lng:12.6561 },
  { iata:'ARN', city:'Stockholm',  name:'Arlanda',                 lat:59.6519, lng:17.9186 },
  { iata:'OSL', city:'Oslo',       name:'Gardermoen',              lat:60.1939, lng:11.1004 },
  { iata:'HEL', city:'Helsinki',   name:'Vantaa',                  lat:60.3172, lng:24.9633 },
  { iata:'VIE', city:'Vienne',     name:'Schwechat',               lat:48.1103, lng:16.5697 },
  { iata:'WAW', city:'Varsovie',   name:'Chopin',                  lat:52.1657, lng:20.9671 },
  { iata:'PRG', city:'Prague',     name:'Václav Havel',            lat:50.1008, lng:14.2600 },
  { iata:'BUD', city:'Budapest',   name:'Liszt Ferenc',            lat:47.4369, lng:19.2556 },
  { iata:'ATH', city:'Athènes',    name:'Elefthérios-Vénizélos',   lat:37.9364, lng:23.9445 },
  { iata:'IST', city:'Istanbul',   name:'Istanbul',                lat:41.2753, lng:28.7519 },
  { iata:'DXB', city:'Dubaï',      name:'International',           lat:25.2532, lng:55.3657 },
  { iata:'JFK', city:'New York',   name:'JFK',                     lat:40.6413, lng:-73.7781 },
  { iata:'EWR', city:'New York',   name:'Newark',                  lat:40.6895, lng:-74.1745 },
  { iata:'YUL', city:'Montréal',   name:'Trudeau',                 lat:45.4706, lng:-73.7408 },
];

const HOTEL_CHAINS = [
  { brand:"Marriott",   tier:"luxury",   logo:"🏨", bonus:"Marriott Bonvoy points" },
  { brand:"Accor",      tier:"upscale",  logo:"🏩", bonus:"ALL loyalty points" },
  { brand:"IHG",        tier:"upscale",  logo:"🏪", bonus:"IHG One Rewards" },
  { brand:"Hilton",     tier:"luxury",   logo:"🏫", bonus:"Hilton Honors points" },
  { brand:"B&B Hotels", tier:"budget",   logo:"🏬", bonus:"B&B loyalty" },
  { brand:"Ibis",       tier:"budget",   logo:"🛏️",  bonus:"ALL loyalty points" },
  { brand:"NH Hotels",  tier:"upscale",  logo:"🏦", bonus:"NH Rewards" },
  { brand:"Independent",tier:"boutique", logo:"🏡", bonus:"Direct booking discount" },
];

const HOTEL_AMENITIES = [
  ["🛁 Breakfast","🌐 WiFi","🅿️ Parking"],
  ["🌐 WiFi","🏊 Pool","🍽️ Restaurant"],
  ["🛁 Breakfast","🌐 WiFi","💆 Spa"],
  ["🌐 WiFi","🚗 Airport transfer","🍽️ Restaurant"],
  ["🛁 Breakfast","🌐 WiFi","💪 Gym"],
];

const ROOM_TYPES = [
  "Deluxe Double, City View",
  "Superior Twin, River View",
  "Standard Double Room",
  "Junior Suite, Canal View",
  "Classic Room, Garden View",
  "Executive Double, Top Floor",
];

const CANCELLATION = [
  { label: "Free cancellation", detail: "until 48h before check-in", color: "#27C97A", icon: "✓" },
  { label: "Free cancellation", detail: "until 7 days before check-in", color: "#27C97A", icon: "✓" },
  { label: "Non-refundable",    detail: "Lowest price guaranteed",    color: "#F4A928", icon: "⚠️" },
  { label: "Partially refundable", detail: "50% refund if cancelled 14 days before", color: "#8AD9F2", icon: "◎" },
];

// FNV-1a hash → stable 32-bit integer from any string
function fnv1a(s) {
  let h = 2166136261;
  for (const c of String(s)) { h ^= c.charCodeAt(0); h = Math.imul(h, 16777619) >>> 0; }
  return h;
}
function slotNum(slot) { return fnv1a(slot.departIso || slot.id || ''); }
function destNum(dest) { return fnv1a(dest.id ?? dest.city ?? ''); }

// seeded deterministic RNG
function rng(seed) {
  let s = ((seed * 1664525) + 1013904223) & 0xffffffff;
  const next = () => { s = (s * 1664525 + 1013904223) & 0xffffffff; return (s >>> 0) / 0xffffffff; };
  return next;
}
function pick(arr, r) { return arr[Math.floor(r() * arr.length)]; }


// ── MOCK HOTEL API (Booking.com style) ────────────────────────────────────

function fetchHotels(dest, slot, pax, seed) {
  const r = rng(seed * 7 + destNum(dest) * 23 + slotNum(slot) * 41);
  const results = [];
  const count = 5 + Math.floor(r() * 4);

  const cityHotelNames = {
    Prague:       ["Hotel Pod Věží","MOODs Charles Bridge","Smetana Hotel","Charles Bridge Palace","Aria Hotel","Grand Mark Prague","Hotel Josef","Mosaic House"],
    Barcelona:    ["W Barcelona","Hotel Arts","Mandarin Oriental","El Palace","Casa Camper","Praktik Rambla","Hotel 1898","Ohla Barcelona"],
    Athens:       ["Hotel Grande Bretagne","King George","Electra Metropolis","New Hotel","O&B Athens","Periscope","Herodion","Divani Caravel"],
    Rome:         ["Hotel de Russie","Hassler Roma","St. Regis Rome","Eden Roma","Baglioni Regina","Hotel Indigo","Portrait Roma","Fifty House"],
    Lisbon:       ["Bairro Alto Hotel","Memmo Alfama","Palácio Belmonte","Verride Palácio","Valverde Hotel","The Lumiares","Alma Lisboa","Dear Lisbon"],
    Edinburgh:    ["The Balmoral","Scotsman Hotel","G&V Royal Mile","Hotel Missoni","Prestonfield","21212","The Witchery","Fingal Hotel"],
    default:      ["Grand Hotel","Palace Hotel","City Boutique","Hotel Central","Le Méridien","Radisson Blu","Sofitel","Novotel"],
  };

  const names = cityHotelNames[dest.city] || cityHotelNames.default;

  for (let i = 0; i < count; i++) {
    const chain = pick(HOTEL_CHAINS, r);
    const stars = 3 + Math.floor(r() * 3); // 3-5
    const rating = +(7.2 + r() * 2.7).toFixed(1);
    const reviewCount = Math.round(200 + r() * 4800);
    const pricePerNight = Math.round(65 + r() * 220);
    const totalPrice = pricePerNight * slot.nights;
    const name = names[i % names.length];
    const room = pick(ROOM_TYPES, r);
    const amenities = pick(HOTEL_AMENITIES, r);
    const cancel = pick(CANCELLATION, r);
    const distCenter = +(0.1 + r() * 2.5).toFixed(1);
    const breakfast = amenities[0].includes("Breakfast");
    const loyaltyBadge = chain.tier !== "budget" && r() > 0.4;
    const discount = r() > 0.65 ? Math.floor(10 + r() * 30) : 0;
    const originalPrice = discount ? Math.round(totalPrice / (1 - discount / 100)) : null;
    const imgHue = Math.floor(r() * 360);
    const photoUrl = `https://picsum.photos/seed/${(destNum(dest) + i) % 1000}/400/220`;

    // Booking.com style review breakdown
    const reviewBreakdown = {
      location:     +(7 + r() * 3).toFixed(1),
      cleanliness:  +(7 + r() * 3).toFixed(1),
      comfort:      +(7 + r() * 3).toFixed(1),
      valueForMoney:+(6.5 + r() * 3).toFixed(1),
      staff:        +(7.5 + r() * 2.5).toFixed(1),
    };

    const guestQuote = pick([
      `"Spectacular views and impeccable service. Will definitely return!"`,
      `"Central location, spotless rooms. Breakfast was outstanding."`,
      `"Cozy boutique feel with all modern amenities. Loved it."`,
      `"The staff went above and beyond. Room was exactly as shown."`,
      `"Perfect base for exploring the city. Highly recommend."`,
    ], r);

    results.push({
      id: i, name, chain, stars, rating, reviewCount, reviewBreakdown, guestQuote,
      pricePerNight, totalPrice, originalPrice, discount,
      room, amenities, cancel, distCenter, breakfast, loyaltyBadge,
      photoUrl, imgHue,
      nights: slot.nights,
      score: Math.round(rating * 10 + (breakfast ? 8 : 0) + (cancel.icon === "✓" ? 6 : 0) - pricePerNight / 20 + (loyaltyBadge ? 4 : 0)),
    });
  }
  results.sort((a, b) => b.score - a.score);
  return results;
}

// Noms FR→EN pour Wikidata quand name_en est absent en DB et que le nom FR est ambigu
const WIKIDATA_EN_OVERRIDES = {
  'Grenade': 'Granada',
};

const DEST_PHOTOS = {
  "Prague":       "https://images.unsplash.com/photo-1592906209472-a36b1f3782ef?w=600&q=80",
  "Barcelona":    "https://images.unsplash.com/photo-1523531294919-4bcd7c65e216?w=600&q=80",
  "Vilnius":      "https://images.unsplash.com/photo-1587974928442-77dc3e0dba72?w=600&q=80",
  "Cinque Terre": "https://images.unsplash.com/photo-1534430480872-3498386e7856?w=600&q=80",
  "Reykjavik":    "https://images.unsplash.com/photo-1476610182048-b716b8518aae?w=600&q=80",
  "Istanbul":     "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=600&q=80",
  "Marrakech":    "https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=600&q=80",
  "Matera":       "https://images.unsplash.com/photo-1518730518541-d0843268c287?w=600&q=80",
  "Rome":         "https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=600&q=80",
  "Dubrovnik":    "https://images.unsplash.com/photo-1555990793-da11153b2473?w=600&q=80",
  "Édimbourg":    "https://images.unsplash.com/photo-1506377585622-bedcbb027afc?w=600&q=80",
  "Bruges":       "https://images.unsplash.com/photo-1491557345352-5929e343eb89?w=600&q=80",
  "La Valette":   "https://images.unsplash.com/photo-1586500036706-41963de24d8b?w=600&q=80",
  "Avignon":      "https://images.unsplash.com/photo-1560969184-10fe8719e047?w=600&q=80",
  "Stockholm":    "https://images.unsplash.com/photo-1509356843151-3e7d96241e11?w=600&q=80",
  // Îles grecques
  "Mykonos":      "https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?w=600&q=80",
  "Santorin":     "https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=600&q=80",
  // Rhodes — laissé à Wikidata (URL Unsplash incorrecte)
  "Crète":        "https://images.unsplash.com/photo-1533104816931-20fa691ff6ca?w=600&q=80",
  // Espagne / Îles
  "Ibiza":        "https://images.unsplash.com/photo-1563492065599-3520f775eeed?w=600&q=80",
  "Majorque":     "https://images.unsplash.com/photo-1564984719606-ed5c1f5f8b6b?w=600&q=80",
  "Tenerife":     "https://images.unsplash.com/photo-1588668214407-6ea9a6d8c272?w=600&q=80",
  // Italie — Naples laissé à Wikidata (photo dupliquée)
  "Florence":     "https://images.unsplash.com/photo-1543429258-54e2c11bd827?w=600&q=80",
  "Venise":       "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=600&q=80",
  // Balkans / Est
  "Tbilissi":     "https://images.unsplash.com/photo-1565008576549-57569a49371d?w=600&q=80",
  "Sarajevo":     "https://images.unsplash.com/photo-1585732374948-a56c5e50b77e?w=600&q=80",
  // Afrique du Nord
  "Fès":          "https://images.unsplash.com/photo-1549880338-65ddcdfd017b?w=600&q=80",
  "Agadir":       "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80",
  // Long courrier
  "Dubai":        "https://images.unsplash.com/photo-1518684079-3c830dcef090?w=600&q=80",
  "Bangkok":      "https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=600&q=80",
  "Bali":         "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=600&q=80",
  "Tokyo":        "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=600&q=80",
  "New York":     "https://images.unsplash.com/photo-1485871981521-5b1fd3805eee?w=600&q=80",
  // Nature / destinations iconiques
  // Uluru — laissé à Wikidata (URL Unsplash incorrecte)
  "Patagonie":    "https://images.unsplash.com/photo-1501854140801-50d01698950b?w=600&q=80",
  "Fiordland":    "https://images.unsplash.com/photo-1507699622108-4be3abd695ad?w=600&q=80",
  "Serengeti":    "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=600&q=80",
  "Maldives":     "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=600&q=80",
  "Cappadoce":    "https://images.unsplash.com/photo-1641128324972-af3212f0f6bd?w=600&q=80",
  "Luang Prabang":"https://images.unsplash.com/photo-1528181304800-259b08848526?w=600&q=80",
  "Petra":        "https://images.unsplash.com/photo-1579606032821-4e6161c81bd3?w=600&q=80",
  "Machu Picchu": "https://images.unsplash.com/photo-1526392060635-9d6019884377?w=600&q=80",
  "Kyoto":        "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=600&q=80",
  "Sydney":       "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=600&q=80",
  "Le Cap":       "https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=600&q=80",
  "Nairobi":      "https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=600&q=80",
  // Noms français
  "Vienne":       "https://images.unsplash.com/photo-1516550893923-42d28e5677af?w=600&q=80",
  "Lisbonne":     "https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=600&q=80",
  "Athènes":      "https://images.unsplash.com/photo-1555993539-1732b0258235?w=600&q=80",
  "Copenhague":   "https://images.unsplash.com/photo-1513622470522-26c3c8a854bc?w=600&q=80",
  "Budapest":     "https://images.unsplash.com/photo-1541343672885-9be56236302a?w=600&q=80",
  "Amsterdam":    "https://images.unsplash.com/photo-1512470876302-972faa2aa9a4?w=600&q=80",
  "Séville":      "https://images.unsplash.com/photo-1559060017-445fb9722f2a?w=600&q=80",
  "Valence":      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80",
  "Madrid":       "https://images.unsplash.com/photo-1543785734-4b6e564642f8?w=600&q=80",
};

// Cache photo partagé — évite les doubles fetches Wikidata entre PolaCard et DestPhotoBanner
const _photoCache = new Map(Object.entries(DEST_PHOTOS));
const _BAD_PHOTO = /flag|coat|logo|blason|emblem|icon|seal|arms|wappen|shield|banner|locator|map|carte|plan\b/i;

async function resolveDestPhoto(city, nameEn, type) {
  const key = city;
  if (_photoCache.has(key)) return _photoCache.get(key);
  const fallback = TYPE_PHOTOS[type] || TYPE_PHOTOS.ville;
  const tryWikidata = async (lang, title) => {
    try {
      const r = await fetch(`https://www.wikidata.org/w/api.php?action=wbgetentities&sites=${lang}wiki&titles=${encodeURIComponent(title)}&props=claims&format=json&origin=*`);
      const data = await r.json();
      const entity = Object.values(data.entities || {}).find(e => e.id && e.id !== '-1');
      if (!entity) return null;
      const p18 = entity.claims?.P18?.[0]?.mainsnak?.datavalue?.value;
      if (!p18 || _BAD_PHOTO.test(p18)) return null;
      return `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(p18.replace(/ /g,'_'))}?width=800`;
    } catch { return null; }
  };
  const en = WIKIDATA_EN_OVERRIDES[city] || nameEn || city;
  const url = await tryWikidata('en', en) || await tryWikidata('fr', city) || fallback;
  _photoCache.set(key, url);
  return url;
}




// ── Claude destination guide ───────────────────────────────────────────────────
const _guideCache   = new Map(); // `${destName}|${appLang}` → guide data
const _guidePending = new Map(); // same key → in-flight Promise (dedup concurrent calls)
async function fetchDestGuide(destName, country, appLang, fromCountry = '', tripFrom = '', tripTo = '') {
  const key = `${destName}|${appLang}|${fromCountry}|${tripFrom}|${tripTo}`;
  if (_guideCache.has(key)) return _guideCache.get(key);
  if (_guidePending.has(key)) return _guidePending.get(key);
  const doFetch = async () => {
    const body = JSON.stringify({ destName, country, appLang, fromCountry, tripFrom, tripTo });
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 800));
        const r = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/destination-guide`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        });
        if (!r.ok) continue;
        const data = await r.json();
        if (!data || (!data.highlights && !data.hidden_gems && !data.bars && !data.restaurants)) continue;
        _guideCache.set(key, data);
        return data;
      } catch { /* retry */ }
    }
    return null;
  };
  const p = doFetch().finally(() => _guidePending.delete(key));
  _guidePending.set(key, p);
  return p;
}

// ── Claude destination itinerary ───────────────────────────────────────────────
const _itinCache   = new Map();
const _itinPending = new Map();
async function fetchDestItinerary(destName, country, nights, appLang) {
  const key = `${destName}|${nights}|${appLang}`;
  if (_itinCache.has(key)) return _itinCache.get(key);
  if (_itinPending.has(key)) return _itinPending.get(key);
  const doFetch = async () => {
    const body = JSON.stringify({ destName, country, nights, appLang });
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 800));
        const r = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/destination-itinerary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        });
        if (!r.ok) continue;
        const data = await r.json();
        if (!data?.days?.length) continue;
        _itinCache.set(key, data);
        return data;
      } catch { /* retry */ }
    }
    return null;
  };
  const p = doFetch().finally(() => _itinPending.delete(key));
  _itinPending.set(key, p);
  return p;
}

// ── Duffel real-price search ───────────────────────────────────────────────────
const _duffelCache   = new Map(); // cacheKey → result | null (null = genuine no-offer)
const _duffelPending = new Map(); // cacheKey → Promise  (dedup in-flight requests)

// Rate-limited queue: max MAX concurrent, DELAY_MS between starts (avoid Duffel 429)
const _duffelSemaphore = (() => {
  let active = 0;
  const queue = [];
  const MAX = 4;
  const DELAY_MS = 100;
  const dequeue = () => { if (queue.length && active < MAX) { active++; queue.shift()(); } };
  return (fn) => new Promise((res, rej) => {
    const run = () => {
      Promise.resolve(fn()).then(res).catch(rej).finally(() => {
        active--;
        setTimeout(dequeue, DELAY_MS);
      });
    };
    queue.push(run);
    if (active < MAX) { active++; queue.shift()(); }
  });
})();

function parseDuffelDuration(iso) {
  if (!iso) return 120;
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  return (parseInt(m?.[1] || 0) * 60) + parseInt(m?.[2] || 0);
}

function fetchDuffelFlights(dest, slot, pax, origin) {
  const iata = dest.airport_iata || AIRPORT_CODES[dest.city];
  if (!origin || !slot.departIso) return Promise.resolve(null);

  // No known IATA → geocode the event location and try nearby airports (within 60 km)
  if (!iata) {
    return airportsNearCity(dest.city, 80).then(iatas => {
      if (!iatas.length) return null;
      return Promise.all(iatas.map(i => _fetchDuffelForIata(i, slot, pax, origin)))
        .then(results => {
          const valid = results.filter(Boolean);
          return valid.length ? valid.sort((a, b) => a[0].totalPrice - b[0].totalPrice)[0] : null;
        });
    });
  }
  return _fetchDuffelForIata(iata, slot, pax, origin);
}

// Core single-IATA Duffel call — shared by direct and geo-fallback paths
function _fetchDuffelForIata(iata, slot, pax, origin) {
  const cacheKey = `${origin}|${iata}|${slot.departIso}|${slot.returnIso || ''}|${pax}`;
  if (_duffelCache.has(cacheKey)) return Promise.resolve(_duffelCache.get(cacheKey));
  if (_duffelPending.has(cacheKey)) return _duffelPending.get(cacheKey);

  const promise = _duffelSemaphore(async () => {
    try {
      await supabase.auth.getSession();
      const body = { origin, destination: iata, departure_date: slot.departIso, passengers: pax, cabin_class: 'economy' };
      if (slot.returnIso) body.return_date = slot.returnIso;
      const { data, error } = await supabase.functions.invoke('duffel-search', { body });
      if (error) return null; // transport failure — don't cache, allow retry
      // The Edge Function answers 200 even when Duffel fails, so `retryable`
      // is what separates an upstream error from a route with no offers.
      // Caching the former turned a transient 429 into "no flights" for the
      // rest of the session.
      if (!data?.found) {
        if (!data?.retryable) _duffelCache.set(cacheKey, null);
        return null;
      }
      const durationMins = parseDuffelDuration(data.slices?.[0]?.duration);
      const result = [{
        airline:       { name: data.airline || 'Vol direct', iata: data.airline_iata || '' },
        totalPrice:    Math.round(data.total_amount),
        pricePerPax:   Math.round(data.total_amount / pax),
        durationMins,
        co2:           +(durationMins * 0.009).toFixed(2),
        departureTime: data.departure_time || '',
        duffelOfferId: data.offer_id,
        isReal:        true,
      }];
      _duffelCache.set(cacheKey, result);
      return result;
    } catch { return null; }
    finally { _duffelPending.delete(cacheKey); }
  });
  _duffelPending.set(cacheKey, promise);
  return promise;
}

// Build 3 date-variation slots for an event destination
// (arrive -3j, -2j, -1j before the event date; return day+1)
function eventSlots(ev) {
  const base = ev.event_date;
  if (!base) return [];
  const isoBase = nextOccurrence(base, ev.recurrent, ev.event_end);
  if (!isoBase) return [];
  const fmt = iso => { const d = new Date(iso + 'T12:00:00'); return d.toLocaleDateString('fr-FR', { weekday:'short' }) + ' ' + d.toLocaleDateString('fr-FR', { day:'numeric', month:'long' }); }; // labels stockés en FR, reformatés à l'affichage via fmtSlotDisplay
  const addD = (iso, n) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return d.toISOString().split('T')[0]; };
  return [3, 2, 1].map(offset => {
    const dep = addD(isoBase, -offset);
    const ret = addD(isoBase, 1);
    return {
      id: `event_${ev.id}_${offset}`,
      label: `${ev.name} · ${fmt(dep)} — ${fmt(ret)}`,
      from: fmt(dep), to: fmt(ret),
      departIso: dep, returnIso: ret,
      type: offset >= 2 ? 'short' : 'longWE',
      nights: offset + 1,
      source: 'event',
      eventId: ev.id,
      eventName: ev.name,
      locked: true,
    };
  });
}

// Build the canonical "reference" event slot (arrive -2j, return +1j) used in Calendar
function canonicalEventSlot(ev) {
  return eventSlots(ev).find(s => s.id.endsWith('_2')) || eventSlots(ev)[0] || null;
}

// Module-level cache — avoids re-fetching destinations on every ranking compute
const _destsCache = new Map();

async function computeRealRanking(planIds, pax, slots, origin, prefs, flightOnly = false) {
  if (!slots?.length || !planIds?.length) return { trips: [], nextDates: [] };

  // Fetch destination data — use cache to avoid repeated Supabase reads
  const cacheKey = [...planIds].sort().join(',');
  let supabaseDests = _destsCache.get(cacheKey);
  if (!supabaseDests) {
    const { data } = await supabase
      .from('destinations')
      .select('id, name, name_en, name_es, name_zh, type, event_date, event_end, recurrent, airport_iata, lat, lng')
      .in('id', planIds);
    supabaseDests = data || [];
    _destsCache.set(cacheKey, supabaseDests);
  }

  const regularDests = (supabaseDests || [])
    .filter(d => d.type !== 'event')
    .map(d => ({
      id: d.id, city: d.name, name_en: d.name_en, name_es: d.name_es, name_zh: d.name_zh,
      type: d.type || 'ville',
      lat: d.lat, lng: d.lng,
      types: compatibleSlotTypes(d.airport_iata || AIRPORT_CODES[d.name]),
      airport_iata: d.airport_iata,
      isEvent: false,
    }));

  const eventDests = (supabaseDests || [])
    .filter(d => d.type === 'event' && d.event_date)
    .map(d => ({
      id: d.id, city: d.name, name_en: d.name_en, name_es: d.name_es, name_zh: d.name_zh, type: d.type,
      types: ['WE','longWE','short','long'],
      isEvent: true, eventDate: d.event_date, eventEnd: d.event_end, recurrent: d.recurrent,
      _evSlots: eventSlots(d),
    }));

  const dests = [...regularDests, ...eventDests];
  if (!dests.length) return { trips: [], nextDates: [] };

  // Effective min nights for a dest: user range if set, else recommended
  const effectiveMin = dest => {
    const nr = prefs?.destNights?.[dest.id];
    if (nr) return typeof nr === 'object' ? nr.min : nr;
    return getRecommendedNights(dest, getOriginCoords(prefs)).min;
  };

  // Build dest×slot pairs:
  // - regular dests → user slots (excluding event slots), filtered by compatible types
  //   then by slot.nights >= effectiveMin (slot long enough for this dest).
  //   Fallback: if NO dest passes the nights check for a given slot, include all
  //   type-compatible dests (don't leave the slot empty).
  // - event dests → their own date-variation slots only
  const userSlots = (slots || []).filter(s => s.source !== 'event');
  const pairs = [];
  userSlots.forEach(slot => {
    const typeDests = regularDests.filter(d => d.types.includes(slot.type));
    const nightsOk  = typeDests.filter(d => slot.nights >= effectiveMin(d));
    (nightsOk.length > 0 ? nightsOk : typeDests).forEach(dest => pairs.push({ dest, slot }));
  });
  eventDests.forEach(dest => dest._evSlots.forEach(slot => pairs.push({ dest, slot })));

  // Option B: dests with NO compatible slot (ex: long-haul with only WE slots)
  // → add them with a synthetic long slot so they still appear in ranking, flagged
  const pairedDestIds = new Set(pairs.map(p => p.dest.id));
  const incompatDests = regularDests.filter(d => !pairedDestIds.has(d.id));
  if (incompatDests.length) {
    const today = new Date();
    const depDate = new Date(today); depDate.setMonth(depDate.getMonth() + 3);
    const retDate = new Date(depDate); retDate.setDate(retDate.getDate() + 10);
    const toIso = d => d.toISOString().split('T')[0];
    const syntheticSlot = {
      id: 'synthetic_long', type: 'long', nights: 10, source: 'synthetic',
      departIso: toIso(depDate), returnIso: toIso(retDate),
      from: toIso(depDate), to: toIso(retDate), label: '',
    };
    incompatDests.forEach(dest => pairs.push({ dest, slot: syntheticSlot, noCompatibleSlot: true }));
  }

  // Ranking uses estimated prices only — Duffel is called on-demand when user opens a dest
  const results = await Promise.all(pairs.map(async ({ dest, slot, noCompatibleSlot }) => {
    const iata = dest.airport_iata || AIRPORT_CODES[dest.city];
    const hours = FLIGHT_HOURS_FROM_CDG[iata] || 2.5;
    const r2 = rng(destNum(dest) * 97 + slotNum(slot) * 53);
    const basePrice = Math.round((50 + hours * 35 + r2() * 80) * pax);
    const durationMins = Math.round(hours * 60 + r2() * 20);
    const flights = [{ airline: { name: '', iata: '' }, totalPrice: basePrice, pricePerPax: Math.round(basePrice/pax), durationMins, co2: +(durationMins*0.009).toFixed(2), duffelOfferId: null, isReal: false }];
    const hotels = flightOnly ? [] : fetchHotels(dest, slot, pax, destNum(dest) * 200 + slotNum(slot));
    if (!flightOnly && !hotels?.length) return null;
    const bestFlight = flights[0];
    const bestHotel  = flightOnly ? null : hotels[0];
    const total      = flightOnly ? bestFlight.totalPrice : bestFlight.totalPrice + bestHotel.totalPrice;
    const r          = rng(destNum(dest) * 137 + slotNum(slot) * 31 + pax * 7);
    const histAvg    = Math.round(total * (1.12 + r() * 0.3));
    const saving     = histAvg - total;
    const savePct    = Math.round((saving / histAvg) * 100);
    const co2Cost    = +(bestFlight.co2 * 4.5).toFixed(2);
    const score      = Math.round(130 - total/40 - bestFlight.durationMins*0.07 - bestFlight.co2*9 + Math.max(0,savePct)*0.6);
    return { dest, slot, pax, bestFlight, bestHotel, flights, hotels, total, co2Cost, histAvg, saving, savePct, score, nights: slot.nights, isRealPrice: bestFlight.isReal !== false, noCompatibleSlot: !!noCompatibleSlot };
  }));

  const valid = results.filter(Boolean);
  valid.sort((a, b) => b.score - a.score);

  const trips = valid;
  // nextDates: best trip per user slot (event slots are inline in trips)
  const nextDates = userSlots.map(slot => {
    const opts = valid.filter(t => t.slot.id === slot.id);
    return opts[0] || null;
  }).filter(Boolean);

  return { trips, nextDates };
}

// Try all departure airports, return cheapest Duffel result + which origin was used
async function fetchDuffelFlightsBestOrigin(dest, slot, pax, origins) {
  const arr = Array.isArray(origins) ? origins.filter(Boolean) : (origins ? [origins] : []);
  if (!arr.length) return null;
  if (arr.length === 1) {
    const flights = await fetchDuffelFlights(dest, slot, pax, arr[0]);
    return flights ? { flights, origin: arr[0] } : null;
  }
  const results = await Promise.all(
    arr.map(o => fetchDuffelFlights(dest, slot, pax, o).then(f => f ? { flights: f, origin: o } : null))
  );
  const valid = results.filter(Boolean);
  if (!valid.length) return null;
  valid.sort((a, b) => a.flights[0].totalPrice - b.flights[0].totalPrice);
  return valid[0];
}

// Compute all slot options for a single destination — called on-demand when user picks a dest
async function computeDestSlots(dest, slots, pax, origins, prefs, flightOnly = false) {
  if (!dest) return [];
  // Events: use their date-variation synthetic slots (not user's calendar slots)
  if (dest.isEvent) {
    const evSlotList = dest._evSlots || eventSlots({ id: dest.id, name: dest.city, event_date: dest.eventDate, event_end: dest.eventEnd, recurrent: dest.recurrent });
    if (!evSlotList.length) return [];
    const morningDepEv = prefs?.departureTimePref === 'Matin';
    const results = await Promise.all(evSlotList.map(async slot => {
      const duffelSlotEv = morningDepEv && slot.departIso ? (() => {
        const d = new Date(slot.departIso + 'T12:00:00');
        d.setDate(d.getDate() + 1);
        return { ...slot, departIso: d.toISOString().split('T')[0] };
      })() : slot;
      const fr = await fetchDuffelFlightsBestOrigin(dest, duffelSlotEv, pax, origins);
      if (!fr) return null;
      const { flights, origin } = fr;
      const hotels = flightOnly ? [] : fetchHotels(dest, slot, pax, destNum(dest) * 200 + slotNum(slot));
      if (!flights?.length || (!flightOnly && !hotels?.length)) return null;
      const bestFlight = flights[0];
      const bestHotel = flightOnly ? null : hotels[0];
      const total = flightOnly ? bestFlight.totalPrice : bestFlight.totalPrice + bestHotel.totalPrice;
      const r = rng(destNum(dest) * 137 + slotNum(slot) * 31 + pax * 7);
      const histAvg = Math.round(total * (1.12 + r() * 0.3));
      const saving = histAvg - total; const savePct = Math.round((saving / histAvg) * 100);
      const co2Cost = +(bestFlight.co2 * 4.5).toFixed(2);
      const score = Math.round(130 - total/40 - bestFlight.durationMins*0.07 - bestFlight.co2*9 + Math.max(0,savePct)*0.6);
      return { dest, slot, pax, origin, bestFlight, bestHotel, flights, hotels, total, co2Cost, histAvg, saving, savePct, score, nights: slot.nights };
    }));
    return results.filter(Boolean).sort((a, b) => a.total - b.total);
  }
  if (!slots?.length) return [];
  const compatTypes = compatibleSlotTypes(dest.airport_iata || AIRPORT_CODES[dest.city]);
  const nr2 = prefs?.destNights?.[dest.id];
  // Effective min: user-defined or recommended
  const userRange2 = nr2 ? (typeof nr2 === 'object' ? nr2 : { min: nr2, max: nr2 }) : null;
  const recMin2 = userRange2 ? userRange2.min : getRecommendedNights(dest, getOriginCoords(prefs)).min;
  const recMax2 = userRange2 ? userRange2.max : null; // no hard max cap unless user set it
  // Filter by type first, then by nights floor; if nothing passes, fall back to type-only
  const typeSlots = slots.filter(s => s.source !== 'event' && compatTypes.includes(s.type));
  const nightsOk2 = typeSlots.filter(s => s.nights >= recMin2 && (recMax2 === null || s.nights <= recMax2));
  const compatSlots = nightsOk2.length > 0 ? nightsOk2 : typeSlots;
  if (!compatSlots.length) return [];
  const morningDep = prefs?.departureTimePref === 'Matin';
  const results = await Promise.all(compatSlots.map(async slot => {
    const duffelSlot = morningDep && slot.departIso ? (() => {
      const d = new Date(slot.departIso + 'T12:00:00');
      d.setDate(d.getDate() + 1);
      return { ...slot, departIso: d.toISOString().split('T')[0] };
    })() : slot;
    const fr = await fetchDuffelFlightsBestOrigin(dest, duffelSlot, pax, origins);
    if (!fr) return null;
    const { flights, origin } = fr;
    const hotels = flightOnly ? [] : fetchHotels(dest, slot, pax, destNum(dest) * 200 + slotNum(slot));
    if (!flights?.length || (!flightOnly && !hotels?.length)) return null;
    const bestFlight = flights[0];
    const bestHotel  = flightOnly ? null : hotels[0];
    const total      = flightOnly ? bestFlight.totalPrice : bestFlight.totalPrice + bestHotel.totalPrice;
    const r          = rng(destNum(dest) * 137 + slotNum(slot) * 31 + pax * 7);
    const histAvg    = Math.round(total * (1.12 + r() * 0.3));
    const saving     = histAvg - total;
    const savePct    = Math.round((saving / histAvg) * 100);
    const co2Cost    = +(bestFlight.co2 * 4.5).toFixed(2);
    const score      = Math.round(130 - total/40 - bestFlight.durationMins*0.07 - bestFlight.co2*9 + Math.max(0,savePct)*0.6);
    return { dest, slot, pax, origin, bestFlight, bestHotel, flights, hotels, total, co2Cost, histAvg, saving, savePct, score, nights: slot.nights };
  }));
  return results.filter(Boolean).sort((a, b) => a.total - b.total);
}

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────

const C = {
  bg:"#07101a", card:"#0d1a27", cardB:"#122234", border:"#1e3347",
  accent:"#009EC2", accentHi:"#4DD4F0", accentDim:"rgba(0,158,194,0.12)", accentGlow:"rgba(0,158,194,0.30)",
  gold:"#F4A928", goldDim:"rgba(244,169,40,0.15)",
  green:"#27C97A", greenDim:"rgba(39,201,122,0.14)",
  rose:"#F06292", roseDim:"rgba(240,98,146,0.14)",
  blue:"#4DD4F0", blueDim:"rgba(77,212,240,0.12)",
  text:"#E6FBFF", sub:"#7ab8c8", muted:"#3d6b7a", white:"#ffffff",
  tabBg:"rgba(7,16,26,0.97)",
};

// ─── MICRO COMPONENTS ─────────────────────────────────────────────────────────

// The app is mobile-first, so the header signature only appears where there is
// room for it and cannot crowd the wordmark.
function useIsDesktop(minWidth = 768) {
  const [wide, setWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(min-width:${minWidth}px)`).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(`(min-width:${minWidth}px)`);
    const on = e => setWide(e.matches);
    mq.addEventListener('change', on);
    setWide(mq.matches);
    return () => mq.removeEventListener('change', on);
  }, [minWidth]);
  return wide;
}

function Chip({ label, color=C.accentHi, bg=C.accentDim, size=9 }) {
  return <span style={{ background:bg, color, fontSize:size, fontWeight:800, padding:"2px 8px", borderRadius:99, letterSpacing:0.5, textTransform:"uppercase", flexShrink:0 }}>{label}</span>;
}
const SLOT_TYPE_LABELS = {
  fr: { WE:'WE', longWE:'LONG WE', short:'COURT', long:'LONG' },
  en: { WE:'WE', longWE:'LONG WE', short:'SHORT', long:'LONG' },
  es: { WE:'WE', longWE:'WE LARGO', short:'CORTO', long:'LARGO' },
};
function slotTypeLabel(type, lang) {
  const l = SLOT_TYPE_LABELS[lang] || SLOT_TYPE_LABELS.fr;
  return l[type] || type;
}
function TypeChip({ type, lang = 'fr' }) {
  const colors = { WE:[C.accentHi,C.accentDim], longWE:[C.gold,C.goldDim], short:[C.green,C.greenDim], long:[C.rose,C.roseDim] };
  const [color, bg] = colors[type] || [C.sub, C.border];
  return <Chip label={slotTypeLabel(type, lang)} color={color} bg={bg}/>;
}
function StarRow({ n }) {
  return <span style={{color:C.gold,fontSize:11}}>{"★".repeat(n)}{"☆".repeat(5-n)}</span>;
}
function ScoreBar({ label, val }) {
  return (
    <div style={{marginBottom:5}}>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:C.sub,marginBottom:2}}>
        <span>{label}</span><span style={{color:C.text,fontWeight:700}}>{val}</span>
      </div>
      <div style={{background:C.border,borderRadius:3,height:4,overflow:"hidden"}}>
        <div style={{width:`${(val/10)*100}%`,height:"100%",background:`linear-gradient(90deg,${C.accent},${C.accentHi})`,borderRadius:3}}/>
      </div>
    </div>
  );
}
function useCountdown(sec=600){
  const [t,setT]=useState(sec);
  useEffect(()=>{const id=setInterval(()=>setT(p=>Math.max(0,p-1)),1000);return()=>clearInterval(id);},[]);
  return `${String(Math.floor(t/60)).padStart(2,"0")}:${String(t%60).padStart(2,"0")}`;
}
function fmtMins(m){return `${Math.floor(m/60)}h${String(m%60).padStart(2,"0")}`;}
// ─── LOADING ANIMATION (card shuffle) ─────────────────────────────────────────
// Pseudo-random pile rotations/offsets — seeded by index, no repeating pattern
const _prand = (seed) => { let x = Math.sin(seed * 9301 + 49297) * 233280; return x - Math.floor(x); };
const PILE_ROT  = (i) => (_prand(i * 3 + 1) - 0.5) * 44;   // ±22°
const PILE_DX   = (i) => (_prand(i * 7 + 2) - 0.5) * 36;   // ±18px
const SHUFFLE_STYLE_ID = 'plotted-shuffle-kf';

// Card dimensions — polaroid format identical to the validated Home screen
const PW = 150; // polaroid width
const PH = Math.round(PW * 0.75 + 28); // photo 4:3 + name band ≈ 140px
const CW = 144; // calendar width (similar scale)
const CH = 158; // calendar height (monthly grid taller than wide)

function PolaCard({ city, nameEn, type }) {
  const fallback = _photoCache.get(city) || TYPE_PHOTOS[type] || TYPE_PHOTOS.ville;
  const [photo, setPhoto] = useState(fallback);
  useEffect(() => {
    let cancelled = false;
    resolveDestPhoto(city, nameEn, type).then(url => { if (!cancelled) setPhoto(url); });
    return () => { cancelled = true; };
  }, [city, nameEn, type]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ width:'100%', height:'100%', background:'white', padding:'4px 4px 0', borderRadius:3, boxShadow:'4px 10px 28px rgba(0,0,0,0.58)' }}>
      <div style={{ width:'100%', aspectRatio:'4/3', overflow:'hidden' }}>
        <img src={photo} alt={city}
          style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
          onError={e => { e.target.src = fallback; }}/>
      </div>
      <div style={{ textAlign:'center', padding:'4px 3px 5px', fontFamily:"'Caveat','Comic Sans MS',cursive", fontSize:22, fontWeight:700, color:'#1a1a1a', lineHeight:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{city}</div>
    </div>
  );
}

function CalCard({ departIso, returnIso }) {
  const d0 = departIso ? new Date(departIso + 'T12:00:00') : new Date('2026-04-11T12:00:00');
  const d1 = returnIso ? new Date(returnIso  + 'T12:00:00') : new Date(d0.getTime() + 3 * 86400000);
  const year     = d0.getFullYear();
  const month    = d0.getMonth();
  const mthName  = d0.toLocaleDateString('fr-FR', { month:'long' });
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0
  const lastDate = new Date(year, month + 1, 0).getDate();
  const depDay   = d0.getDate();
  const retDay   = d0.getMonth() === d1.getMonth() ? d1.getDate() : lastDate;
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= lastDate; d++) cells.push(d);
  return (
    <div style={{ width:'100%', height:'100%', background:'white', borderRadius:3, boxShadow:'4px 10px 28px rgba(0,0,0,0.58)', overflow:'hidden', display:'flex', flexDirection:'column' }}>
      <div style={{ background:'#E53935', padding:'5px 8px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontSize:13, color:'#fff', fontWeight:700, textTransform:'capitalize' }}>{mthName}</span>
        <span style={{ fontSize:11, color:'rgba(255,255,255,0.85)' }}>{year}</span>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', padding:'4px 4px 2px' }}>
        {['L','M','M','J','V','S','D'].map((d, i) => (
          <div key={i} style={{ textAlign:'center', fontSize:9, color:'#bbb', fontWeight:700 }}>{d}</div>
        ))}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', padding:'1px 4px 4px', flex:1, alignContent:'start', gap:'2px 0' }}>
        {cells.map((day, i) => {
          const inRange = day && day >= depDay && day <= retDay;
          return (
            <div key={i} style={{
              textAlign:'center', fontSize:11, fontWeight: inRange ? 800 : 400,
              color: inRange ? '#C62828' : (day ? '#444' : 'transparent'),
              background: inRange ? 'rgba(229,57,53,0.13)' : 'transparent',
              borderRadius:3, lineHeight:'17px',
            }}>{day || ''}</div>
          );
        })}
      </div>
    </div>
  );
}

// Pile centers — outer quarter of each toggle half (pola=left, cal=right)
const PLX = -108, PRX = 108;

function Loading({ label, total = 0, nOrigins = 1, cities = [], slotRanges = [] }) {
  useEffect(() => {
    if (document.getElementById('caveat-font')) return;
    const link = document.createElement('link');
    link.id = 'caveat-font'; link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap';
    document.head.appendChild(link);
  }, []);

  const seedRef = useRef(Math.floor(Date.now() / 1000));
  const seededShuffle = (arr, seed) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(_prand(seed + i * 31 + 7) * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const cityItems  = seededShuffle(cities,     seedRef.current      ).slice(0, 5);
  const rangeItems = seededShuffle(slotRanges, seedRef.current + 999).slice(0, 5);
  const nDest  = cityItems.length;
  const nSlot  = rangeItems.length;
  const nTotal = nDest + nSlot;

  const [dealtCount, setDealtCount] = useState(nTotal);
  const [shuffleKey, setShuffleKey] = useState(0);
  const [countdown,  setCountdown]  = useState(0);

  // Timing constants (ms from Loading mount)
  // max card duration = animDelay(max=40) + animDur(max=220) = 260ms
  // WAVE_DUR = 300ms → 40ms gap between waves
  const SHUFFLE_START    = 350;
  const WAVE_DUR         = 300;
  const NUM_WAVES        = 34;   // 34 × 300ms = 10 200ms ≈ 10s total
  const MAX_CARD_DUR     = 480; // delay(80) + dur(400)
  const SHUFFLE_DURATION = NUM_WAVES * WAVE_DUR;
  const SHUFFLE_END      = SHUFFLE_START + SHUFFLE_DURATION;

  // Derived display values
  // ~18 tarifs par route (cabines + compagnies), ×nOrigins aéroports
  const comboStart = Math.max(total, nDest * nSlot) * Math.max(1, nOrigins) * 18 + 157;
  const carriers   = Math.max(8, Math.min(32, nDest * 2 + nSlot * nOrigins + 3));

  useEffect(() => {
    setDealtCount(nTotal);
    setShuffleKey(0);
    setCountdown(comboStart);
    const timers = [];
    const T = (fn, ms) => timers.push(setTimeout(fn, ms));

    // Each wave reshuffles all cards to new random positions — keeps moves fast & chaotic
    // prev[i] tracks where card i landed after each wave (start of next wave)
    let prev = Array.from({ length: nTotal }, (_, i) => {
      const isDest = i < nDest;
      const g = isDest ? i : i - nDest;
      return { x: isDest ? PLX + PILE_DX(g) : PRX + PILE_DX(nDest + g), y: g * 6, r: PILE_ROT(isDest ? g : nDest + g) };
    });

    for (let wave = 0; wave < NUM_WAVES; wave++) {
      const waveOffset = SHUFFLE_START + wave * WAVE_DUR;
      const wavePrev   = prev.slice(); // capture for closure
      T(() => {
        const key = Date.now() + wave * 1000; // unique key per wave
        const finals = Array.from({ length: nTotal }, (_, i) => ({
          x: (_prand(key + i * 13) - 0.5) * 400,
          y: (_prand(key + i * 17) - 0.5) * 340,
          r: (_prand(key + i * 11) - 0.5) * 120,
        }));
        const css = Array.from({ length: nTotal }, (_, i) => {
          const s = wavePrev[i], f = finals[i];
          const dX = f.x - s.x, dY = f.y - s.y;
          const len = Math.sqrt(dX*dX + dY*dY) || 1;
          // Arc 1 : déviation principale (30–50% du chemin)
          const arcSign1 = (_prand(key + i * 5 + 3) > 0.5 ? 1 : -1);
          const arcMag1  = 120 + _prand(key + i * 19 + 7) * 180;
          const t1 = 0.3 + _prand(key + i * 41 + 2) * 0.2; // 30–50%
          const m1X = s.x + dX * t1 + (-dY / len) * arcSign1 * arcMag1;
          const m1Y = s.y + dY * t1 + ( dX / len) * arcSign1 * arcMag1;
          // Arc 2 : contre-courbe (65–75%) pour trajectoire en S
          const arcMag2  = 60 + _prand(key + i * 23 + 11) * 100;
          const t2 = 0.65 + _prand(key + i * 29 + 5) * 0.1;
          const m2X = s.x + dX * t2 + (-dY / len) * (-arcSign1) * arcMag2;
          const m2Y = s.y + dY * t2 + ( dX / len) * (-arcSign1) * arcMag2;
          const p1 = Math.round(t1 * 100), p2 = Math.round(t2 * 100);
          const midR1 = (s.r * (1-t1) + f.r * t1).toFixed(1);
          const midR2 = (s.r * (1-t2) + f.r * t2).toFixed(1);
          return `@keyframes ks${key}i${i}{` +
            `0%{transform:translate(${s.x}px,${s.y}px) rotate(${s.r}deg)}` +
            `${p1}%{transform:translate(${m1X.toFixed(1)}px,${m1Y.toFixed(1)}px) rotate(${midR1}deg)}` +
            `${p2}%{transform:translate(${m2X.toFixed(1)}px,${m2Y.toFixed(1)}px) rotate(${midR2}deg)}` +
            `100%{transform:translate(${f.x}px,${f.y}px) rotate(${f.r}deg)}}`;
        }).join('');
        let el = document.getElementById(SHUFFLE_STYLE_ID);
        if (!el) { el = document.createElement('style'); el.id = SHUFFLE_STYLE_ID; document.head.appendChild(el); }
        el.textContent = css;
        setShuffleKey(key);
        prev = finals; // next wave starts from where this one ends
      }, waveOffset);
    }

    // Ease-out countdown: fast at first, decelerates toward 0
    // value(t) = comboStart * (1 - easeOut(t))  where easeOut = 1-(1-t)^2.5
    const intervals = [];
    T(() => {
      const startTime = Date.now();
      const id = setInterval(() => {
        const progress = Math.min((Date.now() - startTime) / SHUFFLE_DURATION, 1);
        const eased = 1 - Math.pow(1 - progress, 2.5); // quadratic-ish ease-out
        const value = Math.max(0, Math.round(comboStart * (1 - eased)));
        setCountdown(value);
        if (progress >= 1) clearInterval(id);
      }, 50);
      intervals.push(id);
    }, SHUFFLE_START);
    // Guarantee exact 0 after last wave's cards stop
    T(() => setCountdown(0), SHUFFLE_END - WAVE_DUR + MAX_CARD_DUR + 60);

    return () => { timers.forEach(clearTimeout); intervals.forEach(clearInterval); };
  }, [nTotal]); // eslint-disable-line

  // Fluid saccades: smooth easing, varied per card
  const EASINGS = [
    'cubic-bezier(0.22,1,0.36,1)',      // snappy ease-out
    'cubic-bezier(0.34,1.25,0.64,1)',   // léger overshoot / spring
    'cubic-bezier(0.55,0,0.1,1)',       // sharp ease-in-out
  ];
  const animEase   = (i) => EASINGS[Math.floor(_prand(i * 43 + 17) * EASINGS.length)];
  const animDelay  = (i) => Math.round(_prand(i * 37 + 11) * 80);    // 0–80ms
  const animDur    = (i) => 150 + Math.round(_prand(i * 97 + 13) * 250); // 150–400ms

  const cardEl = (cardKey, rot, dx, pileCX, gIdx, dealt, cardW, cardH, children) => {
    const style = shuffleKey > 0 && dealt ? {
      animation: `ks${shuffleKey}i${cardKey} ${animDur(cardKey)}ms ${animEase(cardKey)} ${animDelay(cardKey)}ms both`,
      zIndex: Math.round(_prand(shuffleKey % 9999 + cardKey * 31 + 7) * 50) + 1,
    } : dealt ? {
      transform:  `translate(${pileCX + dx}px,${gIdx * 6}px) rotate(${rot}deg)`,
      transition: 'transform 0.45s cubic-bezier(0.34,1.4,0.64,1)',
      zIndex: gIdx + 1,
    } : {
      transform:  `translate(${pileCX}px,-340px) rotate(${rot}deg)`,
      transition: 'none', zIndex: 0,
    };
    return (
      <div key={`c${cardKey}`} style={{ position:'absolute', top:'50%', left:'50%', marginTop:-(cardH/2), marginLeft:-(cardW/2), width:cardW, height:cardH, ...style }}>
        {children}
      </div>
    );
  };

  // Scale down on small screens — natural height ≈ 500px (cards + label + padding)
  // window.innerHeight sur iPhone 12 = 844 → scale=1 ; iPhone SE = 667 → scale=0.78
  const scale = typeof window !== 'undefined'
    ? Math.min(1, Math.max(0.55, (window.innerHeight - 150) / 650))
    : 1;

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'20px 0', transform:`scale(${scale})`, transformOrigin:'center center' }}>
      {label && (
        <div style={{ color:C.sub, fontSize:15, fontWeight:600, letterSpacing:'-0.2px', marginBottom:24, textAlign:'center', opacity:0.85 }}>
          {label}
        </div>
      )}
      <div style={{ position:'relative', width:320, height:320, overflow:'visible' }}>
        {cityItems.map((item, gIdx) => {
          const city = typeof item === 'string' ? item : item.city;
          const nameEn = typeof item === 'string' ? undefined : item.nameEn;
          const type   = typeof item === 'string' ? undefined : item.type;
          return cardEl(gIdx, PILE_ROT(gIdx), PILE_DX(gIdx),
            PLX, gIdx, gIdx < dealtCount, PW, PH, <PolaCard city={city} nameEn={nameEn} type={type}/>);
        })}
        {rangeItems.map((range, gIdx) => cardEl(
          nDest + gIdx, PILE_ROT(nDest + gIdx), PILE_DX(nDest + gIdx),
          PRX, gIdx, (nDest + gIdx) < dealtCount, CW, CH, <CalCard departIso={range.departIso} returnIso={range.returnIso}/>
        ))}
      </div>
      <div style={{ textAlign:'center', marginTop:70 }}>
        <div style={{ color:C.sub, fontSize:12 }}>{carriers} transporteurs considérés</div>
        <div style={{ color:C.accentHi, fontSize:32, fontWeight:800, letterSpacing:'-1px', fontVariantNumeric:'tabular-nums', lineHeight:1.1, marginTop:10 }}>
          {countdown.toLocaleString('fr-FR')}
        </div>
        <div style={{ color:C.muted, fontSize:11, marginTop:4 }}>combinaisons à analyser</div>
      </div>
    </div>
  );
}

const card = (ex={}) => ({background:C.card,border:`1px solid ${C.border}`,borderRadius:18,padding:"14px 16px",...ex});
const btnPrimary = { width:"100%",padding:"16px",background:`linear-gradient(135deg,#006F87,${C.accent})`,color:C.white,border:"none",borderRadius:16,fontSize:15,fontWeight:700,cursor:"pointer",boxShadow:`0 6px 24px ${C.accentGlow}` };
const btnSec = { flex:1,padding:"13px",background:C.card,color:C.text,border:`1px solid ${C.border}`,borderRadius:16,fontSize:13,fontWeight:600,cursor:"pointer" };
const titleStyle = { color:C.text,fontSize:22,fontWeight:800,margin:0,letterSpacing:"-0.5px" };

function Scroll({children}){
  return (
    <div style={{height:"100%",overflowY:"auto",overflowX:"hidden",scrollbarWidth:"none",background:C.bg}}>
      <div style={{maxWidth:600,margin:"0 auto"}}>
        {children}
        <div style={{height:'calc(80px + env(safe-area-inset-bottom, 0px))'}}/>
      </div>
    </div>
  );
}
function Avatar(){
  return <div style={{width:38,height:38,borderRadius:19,background:`linear-gradient(135deg,#006F87,${C.accent})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:C.white}}>AD</div>;
}

// ─── AIRLINE BADGE ────────────────────────────────────────────────────────────

function AirlineBadge({ airline, size = 'md' }) {
  const dim = size === 'xs' ? 20 : size === 'sm' ? 26 : size === 'lg' ? 48 : 36;
  const fs  = size === 'xs' ? 7  : size === 'sm' ? 8  : size === 'lg' ? 13 : 11;
  const [err, setErr] = useState(false);
  const iata = (airline?.iata || airline?.code || '').toUpperCase();
  if (iata && !err) {
    return (
      <img
        src={`https://pics.avs.io/${dim * 2}/${dim * 2}/${iata}.png`}
        alt={iata}
        onError={() => setErr(true)}
        style={{ width: dim, height: dim, borderRadius: 6, flexShrink: 0, objectFit: 'contain' }}
      />
    );
  }
  return (
    <div style={{
      width: dim, height: dim, borderRadius: 8, flexShrink: 0,
      background: airline?.color || '#1e2133',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', position: 'relative',
    }}>
      {airline?.accent && <div style={{ position:'absolute', bottom:0, left:0, right:0, height:4, background:airline.accent, opacity:0.85 }}/>}
      <div style={{ color: airline?.textColor || '#fff', fontSize: fs, fontWeight: 800, letterSpacing: '-0.3px', lineHeight:1 }}>
        {iata.slice(0, 2) || '?'}
      </div>
    </div>
  );
}

// ─── FLIGHT RESULT CARD ───────────────────────────────────────────────────────

function FlightCard({ f, selected, onSelect, pax, appLang }) {
  const tr = useT(appLang);
  const isSel = selected?.id === f.id;
  return (
    <button onClick={()=>onSelect(f)} style={{...card({marginBottom:10,width:"100%",textAlign:"left",cursor:"pointer",padding:"14px",border:`1.5px solid ${isSel?C.accent:C.border}`,background:isSel?C.accentDim:C.card}),position:'relative'}} >
      {/* airline + price row */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <div style={{position:'relative',flexShrink:0}}>
            <AirlineBadge airline={f.airline} size="md"/>
            {isSel && <div style={{position:'absolute',bottom:-3,right:-3,width:16,height:16,borderRadius:8,background:C.accent,border:`2px solid ${C.card}`,display:'flex',alignItems:'center',justifyContent:'center'}}>
              <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1.5,5 4,7.5 8.5,2"/></svg>
            </div>}
          </div>
          <div>
            <div style={{color:C.text,fontSize:13,fontWeight:700}}>{f.airline.name}</div>
            <div style={{color:C.muted,fontSize:10}}>{f.flightNum} · {f.cabin}</div>
          </div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{color:C.text,fontSize:17,fontWeight:800}}>€{f.totalPrice}</div>
          <div style={{color:C.muted,fontSize:10}}>€{f.pricePerPax}/pax</div>
        </div>
      </div>

      {/* flight timeline */}
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <div style={{textAlign:"center"}}>
          <div style={{color:C.text,fontSize:15,fontWeight:800}}>{f.depart}</div>
          <div style={{color:C.muted,fontSize:10}}>ORY</div>
        </div>
        <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
          <div style={{color:C.muted,fontSize:10}}>{fmtMins(f.durationMins)}</div>
          <div style={{display:"flex",alignItems:"center",width:"100%",gap:4}}>
            <div style={{flex:1,height:1,background:C.borderL??C.border}}/>
            {f.isDirect
              ? <div style={{fontSize:10,color:C.green,fontWeight:700}}>{tr('flight.direct')}</div>
              : <div style={{background:C.goldDim,color:C.gold,fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:99}}>1 stop {f.stopCity} +{f.stopTime}m</div>}
            <div style={{flex:1,height:1,background:C.border}}/>
          </div>
        </div>
        <div style={{textAlign:"center"}}>
          <div style={{color:C.text,fontSize:15,fontWeight:800}}>{f.arrive}</div>
          <div style={{color:C.muted,fontSize:10}}>{f.dest}</div>
        </div>
      </div>

      {/* return */}
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,opacity:0.7}}>
        <div style={{textAlign:"center"}}>
          <div style={{color:C.sub,fontSize:12,fontWeight:700}}>{f.returnDepart}</div>
          <div style={{color:C.muted,fontSize:9}}>{f.dest}</div>
        </div>
        <div style={{flex:1,height:1,background:C.border,borderTop:"1px dashed #333"}}/>
        <div style={{color:C.muted,fontSize:10}}>↩ {f.returnFlight}</div>
        <div style={{flex:1,height:1,background:C.border}}/>
        <div style={{textAlign:"center"}}>
          <div style={{color:C.sub,fontSize:12,fontWeight:700}}>{f.returnArrive}</div>
          <div style={{color:C.muted,fontSize:9}}>ORY</div>
        </div>
      </div>

      {/* badges */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
        {f.luggageIncl && <Chip label={tr('flight.bagIncl')} color={C.green} bg={C.greenDim} size={9}/>}
        {f.flexTicket && <Chip label={tr('flight.flexible')} color={C.accentHi} bg={C.accentDim} size={9}/>}
        {f.seatsLeft <= 3 && <Chip label={tr('flight.seatsLeft').replace('{n}',f.seatsLeft)} color={C.rose} bg={C.roseDim} size={9}/>}
        <Chip label={`${f.co2}t CO₂`} color={f.co2vsAvg<0?C.green:C.gold} bg={f.co2vsAvg<0?C.greenDim:C.goldDim} size={9}/>
        {f.co2vsAvg < 0 && <Chip label={tr('flight.vsAvg').replace('{n}',f.co2vsAvg)} color={C.green} bg={C.greenDim} size={9}/>}
      </div>

      {/* alliance + miles */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{color:C.muted,fontSize:10}}>{f.airline.alliance !== "none" ? `${f.airline.alliance} ${tr('flight.alliance')}` : tr('flight.lowcost')}</span>
        <span style={{color:C.accentHi,fontSize:10}}>+{(f.loyaltyMiles||0).toLocaleString()} miles</span>
      </div>

    </button>
  );
}

// ─── HOTEL RESULT CARD ────────────────────────────────────────────────────────

function HotelCard({ h, selected, onSelect, appLang }) {
  const tr = useT(appLang);
  const isSel = selected?.id === h.id;
  const [imgErr, setImgErr] = useState(false);
  return (
    <button onClick={()=>onSelect(h)} style={{...card({marginBottom:12,width:"100%",textAlign:"left",cursor:"pointer",padding:0,overflow:"hidden",border:`1.5px solid ${isSel?C.accent:C.border}`,background:isSel?C.accentDim:C.card})}} >
      {/* photo */}
      <div style={{height:150,overflow:"hidden",position:"relative",background:`hsl(${h.imgHue},20%,15%)`}}>
        {!imgErr
          ? <img src={h.photoUrl} alt={h.name} onError={()=>setImgErr(true)} style={{width:"100%",height:"100%",objectFit:"cover",opacity:0.9}}/>
          : <img src={`https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80`} alt={h.name} style={{width:"100%",height:"100%",objectFit:"cover",opacity:0.7}}/>
        }
        {h.discount > 0 && (
          <div style={{position:"absolute",top:10,left:10,background:C.rose,color:C.white,fontSize:11,fontWeight:800,padding:"3px 10px",borderRadius:99}}>−{h.discount}%</div>
        )}
        <div style={{position:"absolute",bottom:10,right:10,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(4px)",borderRadius:10,padding:"4px 10px"}}>
          <span style={{color:C.white,fontSize:13,fontWeight:800}}>€{h.pricePerNight}</span>
          <span style={{color:"rgba(255,255,255,0.6)",fontSize:10}}>{tr('hotel.perNight')}</span>
        </div>
      </div>

      <div style={{padding:"12px 14px"}}>
        {/* name + stars */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
          <div style={{flex:1,marginRight:8}}>
            <div style={{color:C.text,fontSize:14,fontWeight:700,lineHeight:1.2}}>{h.name}</div>
            <div style={{marginTop:2}}><StarRow n={h.stars}/></div>
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{background:C.accent,color:C.white,fontSize:13,fontWeight:800,padding:"3px 9px",borderRadius:"10px 10px 2px 10px",display:"inline-block"}}>{h.rating}</div>
            <div style={{color:C.muted,fontSize:9,marginTop:2}}>{h.reviewCount.toLocaleString()} {tr('hotel.reviews')}</div>
          </div>
        </div>

        {/* location + chain */}
        <div style={{display:"flex",gap:8,marginBottom:8,alignItems:"center"}}>
          <span style={{color:C.sub,fontSize:11}}>📍 {h.distCenter}{tr('hotel.fromCentre')}</span>
          <span style={{color:C.muted,fontSize:10}}>{h.chain.logo} {h.chain.brand}</span>
        </div>

        {/* room type */}
        <div style={{color:C.sub,fontSize:11,marginBottom:8}}>🛏 {h.room}</div>

        {/* amenities */}
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
          {h.amenities.map((a,i)=><Chip key={i} label={a} color={C.sub} bg="rgba(255,255,255,0.04)" size={9}/>)}
        </div>

        {/* cancellation */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <span style={{fontSize:11,color:h.cancel.color,fontWeight:600}}>{h.cancel.icon} {h.cancel.label}</span>
          <span style={{color:C.muted,fontSize:10}}>{h.cancel.detail}</span>
        </div>

        {/* price total */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",borderTop:`1px solid ${C.border}`,paddingTop:10,marginTop:4}}>
          <div>
            {h.originalPrice && <div style={{color:C.muted,fontSize:11,textDecoration:"line-through"}}>€{h.originalPrice}</div>}
            <div style={{color:C.text,fontSize:17,fontWeight:800}}>€{h.totalPrice} <span style={{color:C.muted,fontSize:11,fontWeight:400}}>total · {h.nights}n</span></div>
          </div>
          {h.loyaltyBadge && <Chip label={`🏆 ${h.chain.bonus}`} color={C.gold} bg={C.goldDim} size={8}/>}
        </div>

        {/* guest quote */}
        <div style={{marginTop:8,padding:"8px 10px",background:"rgba(255,255,255,0.03)",borderRadius:10,borderLeft:`2px solid ${C.accent}`}}>
          <div style={{color:C.sub,fontSize:10,fontStyle:"italic",lineHeight:1.5}}>{h.guestQuote}</div>
        </div>

        {isSel && <div style={{marginTop:10,padding:"8px 12px",background:`linear-gradient(135deg,#006F87,${C.accent})`,borderRadius:10,color:C.white,fontSize:12,fontWeight:700,textAlign:"center"}}>{tr('hotel.selected')}</div>}
      </div>
    </button>
  );
}

// ─── TRIP DETAIL WITH REAL COMPARATORS ───────────────────────────────────────

function TripDetail({ t, onBook, onBack, travelPrefs, onShare }) {
  const tr = useT(travelPrefs?.appLang);
  const [tab, setTab] = useState("flights");
  const [selFlight, setSelFlight] = useState(t.bestFlight);
  const [selHotel, setSelHotel]   = useState(t.bestHotel);
  const [flightSort, setFlightSort] = useState("recommended");
  const [hotelSort, setHotelSort]   = useState("recommended");

  const co2Cost = +(selFlight.co2 * 4.5).toFixed(2);
  const grandTotal = selFlight.totalPrice + (selHotel?.totalPrice || 0) + co2Cost;

  const sortFlights = (arr, key) => {
    const s = [...arr];
    if (key==="price") s.sort((a,b)=>a.totalPrice-b.totalPrice);
    else if (key==="duration") s.sort((a,b)=>a.durationMins-b.durationMins);
    else if (key==="co2") s.sort((a,b)=>a.co2-b.co2);
    else s.sort((a,b)=>b.score-a.score);
    return s;
  };
  const sortHotels = (arr, key) => {
    const s = [...arr];
    if (key==="price") s.sort((a,b)=>a.totalPrice-b.totalPrice);
    else if (key==="rating") s.sort((a,b)=>b.rating-a.rating);
    else if (key==="distance") s.sort((a,b)=>a.distCenter-b.distCenter);
    else s.sort((a,b)=>b.score-a.score);
    return s;
  };

  // Flights and hotels are already in memory here — the previous 900 ms
  // spinner was purely decorative, and its labels were hardcoded English.
  const switchTab = (newTab) => setTab(newTab);

  const heroPhoto = DEST_PHOTOS[t.dest.city] || DEST_PHOTOS[t.dest.name_en] || TYPE_PHOTOS[t.dest.type] || TYPE_PHOTOS.ville;
  const travelerNames = [
    travelPrefs?.firstName,
    ...(travelPrefs?.coTravelers?.length
      ? travelPrefs.coTravelers.map(c => c.firstName)
      : travelPrefs?.coFirstName ? [travelPrefs.coFirstName] : []),
  ].filter(Boolean);

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
    {/* Header buttons — in normal flow so iOS scroll doesn't block taps */}
    <div style={{flexShrink:0, display:'flex', alignItems:'center', justifyContent:'space-between',
      padding:`calc(env(safe-area-inset-top, 0px) + 10px) 16px 8px`,
      background:'transparent', position:'relative', zIndex:10}}>
      <button onClick={onBack} style={{
        width:40, height:40, borderRadius:20, border:`1px solid rgba(255,255,255,0.18)`,
        background:"rgba(0,0,0,0.5)", backdropFilter:"blur(10px)",
        display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0}}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
      <div style={{width:40, height:40, flexShrink:0}}/>
    </div>
    <Scroll>
      {/* hero — polaroid gauche + info droite */}
      <div style={{display:"flex", alignItems:"flex-start", background:C.card, borderBottom:`1px solid ${C.border}`, padding:"14px 16px 14px 16px", gap:14}}>
        {/* Polaroid */}
        <div style={{flexShrink:0, width:140}}>
          <div style={{
            background:"white", padding:"5px 5px 0", borderRadius:3,
            boxShadow:"4px 8px 22px rgba(0,0,0,0.6)", transform:"rotate(-2.5deg)",
          }}>
            <div style={{width:"100%", aspectRatio:"4/3", overflow:"hidden"}}>
              <img src={heroPhoto} alt={t.dest.city}
                style={{width:"100%", height:"100%", objectFit:"cover", display:"block"}}/>
            </div>
            <div style={{
              textAlign:"center", padding:"5px 4px 7px",
              fontFamily:"'Caveat','Comic Sans MS',cursive",
              fontSize:15, fontWeight:700, color:"#1a1a1a", lineHeight:1,
            }}>{localDestName(t.dest, travelPrefs?.appLang)}</div>
          </div>
        </div>
        {/* Info droite */}
        <div style={{flex:1, display:"flex", flexDirection:"column", justifyContent:"flex-start", gap:6, paddingTop:4}}>
          {/* Destination */}
          <div style={{fontSize:17, fontWeight:800, color:C.text, letterSpacing:"-0.3px", lineHeight:1.1}}>
            {localDestName(t.dest, travelPrefs?.appLang)}
          </div>
          {/* Dates + nuits */}
          <div style={{color:C.sub, fontSize:12}}>{t.slot.from} – {t.slot.to}</div>
          <div style={{display:"flex", gap:6, alignItems:"center"}}>
            <TypeChip type={t.slot.type} lang={travelPrefs?.appLang}/>
            <span style={{color:C.muted, fontSize:11}}>{t.nights}n</span>
          </div>
          {/* Voyageurs */}
          {travelerNames.length > 0 && (
            <div style={{marginTop:2}}>
              <div style={{color:C.muted, fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.6px", marginBottom:2}}>
                {travelerNames.length > 1 ? "Voyageurs" : "Voyageur"}
              </div>
              <div style={{color:C.sub, fontSize:12}}>{travelerNames.join(" · ")}</div>
            </div>
          )}
          {/* Bouton partager */}
          {onShare && (
            <button onClick={() => onShare({...t, bestFlight: selFlight, bestHotel: selHotel})}
              style={{ alignSelf:'flex-start', marginTop:4, display:'flex', alignItems:'center', gap:6,
                background:'none', border:`1px solid ${C.border}`, borderRadius:10,
                padding:'5px 10px', cursor:'pointer', color:C.sub, fontSize:11, fontWeight:600 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                <polyline points="16 6 12 2 8 6"/>
                <line x1="12" y1="2" x2="12" y2="15"/>
              </svg>
              Partager ce projet
            </button>
          )}
        </div>
      </div>
      <div style={{padding:"0 20px 18px"}}>

        {/* live price summary */}
        <div style={{marginTop:14,...card({background:"rgba(0,158,194,0.08)",borderColor:C.accent,padding:"12px 14px"})}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{color:C.sub,fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>{tr('trip.yourSelection')}</span>
            <span style={{color:C.accentHi,fontSize:18,fontWeight:800}}>€{grandTotal}</span>
          </div>
          <div style={{display:"flex",gap:0}}>
            <div style={{flex:1,borderRight:`1px solid ${C.border}`,paddingRight:12}}>
              <div style={{color:C.muted,fontSize:9,textTransform:"uppercase",letterSpacing:0.5}}>{tr('trip.flight')}</div>
              <div style={{color:C.text,fontSize:12,fontWeight:700,marginTop:2}}>{selFlight.airline.name}</div>
              <div style={{color:C.sub,fontSize:11}}>€{selFlight.totalPrice}</div>
            </div>
            {selHotel && (
            <div style={{flex:1,paddingLeft:12,borderRight:`1px solid ${C.border}`,paddingRight:12}}>
              <div style={{color:C.muted,fontSize:9,textTransform:"uppercase",letterSpacing:0.5}}>{tr('trip.hotel')}</div>
              <div style={{color:C.text,fontSize:12,fontWeight:700,marginTop:2}}>{selHotel.name.split(" ").slice(0,2).join(" ")}</div>
              <div style={{color:C.sub,fontSize:11}}>€{selHotel.totalPrice}</div>
            </div>
            )}
            <div style={{flex:1,paddingLeft:12}}>
              <div style={{color:C.muted,fontSize:9,textTransform:"uppercase",letterSpacing:0.5}}>{tr('trip.co2offset')}</div>
              <div style={{color:C.green,fontSize:12,fontWeight:700,marginTop:2}}>{selFlight.co2}t CO₂</div>
              <div style={{color:C.green,fontSize:11}}>+€{co2Cost}</div>
            </div>
          </div>
          <button style={{...btnPrimary,marginTop:12,padding:"13px"}} onClick={()=>onBook(t,selFlight,selHotel)}>
            {tr('trip.bookNow').replace('{total}', grandTotal)}
          </button>
        </div>
      </div>

      {/* tabs — Flights est le défaut (pas de tab), Hotels + Stats seulement */}
      <div style={{display:"flex",margin:"0 16px 2px",background:C.card,borderRadius:13,padding:3,alignItems:"center"}}>
        {t.hotels?.length > 0 && (
          <button onClick={()=>switchTab(tab==="hotels"?"flights":"hotels")}
            style={{flex:1,padding:"8px 0",fontSize:10,fontWeight:700,borderRadius:11,
              background:tab==="hotels"?C.accent:"transparent",
              color:tab==="hotels"?C.white:C.sub,border:"none",cursor:"pointer"}}>
            Hotels ({t.hotels.length})
          </button>
        )}
        <button onClick={()=>switchTab(tab==="detail"?"flights":"detail")}
          style={{padding:"7px 10px",borderRadius:10,
            background:tab==="detail"?C.accent:"transparent",
            color:tab==="detail"?C.white:C.muted,border:"none",cursor:"pointer",flexShrink:0,
            display:'flex',alignItems:'center',justifyContent:'center'}}
          title="Analysis">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="8" width="3" height="7" rx="1"/><rect x="6.5" y="4" width="3" height="11" rx="1"/><rect x="12" y="1" width="3" height="14" rx="1"/>
          </svg>
        </button>
      </div>

      {/* sort bar */}
      {tab === "hotels" && (
        <div style={{display:"flex",gap:6,padding:"8px 16px",overflowX:"auto"}}>
          <span style={{color:C.muted,fontSize:10,alignSelf:"center",whiteSpace:"nowrap"}}>{tr('trip.sort')}</span>
          {[["recommended",tr('trip.sortBest')],["price",tr('trip.sortPrice')],["rating",tr('trip.sortRating')],["distance",tr('trip.sortDistance')]].map(([k,l])=>(
            <button key={k} onClick={()=>setHotelSort(k)} style={{padding:"5px 12px",borderRadius:20,fontSize:11,fontWeight:600,background:hotelSort===k?C.accent:C.card,color:hotelSort===k?C.white:C.sub,border:`1px solid ${hotelSort===k?C.accent:C.border}`,cursor:"pointer",whiteSpace:"nowrap"}}>{l}</button>
          ))}
        </div>
      )}

      {/* flight results */}
      {tab==="flights" && (
        <div style={{padding:"4px 16px"}}>
          <div style={{color:C.muted,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>
            {tr('trip.flightsFound').replace('{n}',t.flights.length).replace('{origin}',t.origin||travelPrefs?.departureAirports?.[0]||travelPrefs?.departureAirport||'?').replace('{city}',t.dest.city).replace('{date}',t.slot.from)}
          </div>
          {sortFlights(t.flights, flightSort).map(f=>(
            <FlightCard key={f.id} f={f} selected={selFlight} onSelect={setSelFlight} pax={t.pax} appLang={travelPrefs?.appLang}/>
          ))}
        </div>
      )}

      {/* hotel results */}
      {tab==="hotels" && (
        <div style={{padding:"4px 16px"}}>
          <div style={{color:C.muted,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>
            {tr('trip.hotelsFound').replace('{n}',t.hotels.length).replace('{city}',t.dest.city).replace('{from}',t.slot.from).replace('{to}',t.slot.to).replace('{nights}',t.nights)}
          </div>
          {sortHotels(t.hotels, hotelSort).map(h=>(
            <HotelCard key={h.id} h={h} selected={selHotel} onSelect={setSelHotel} appLang={travelPrefs?.appLang}/>
          ))}
        </div>
      )}

      {/* analysis tab */}
      {tab==="detail" && (
        <div style={{padding:"8px 16px"}}>
          {/* price vs history */}
          {t.savePct>0&&<div style={{...card({background:C.greenDim,borderColor:C.green,marginBottom:12})}}>
            <div style={{color:C.green,fontSize:14,fontWeight:800}}>{tr('trip.belowAvg').replace('{pct}',t.savePct)}</div>
            <div style={{color:C.sub,fontSize:12,marginTop:4}}>{tr('trip.historicAvg')} <strong style={{color:C.text}}>€{t.histAvg}</strong></div>
            <div style={{color:C.sub,fontSize:12}}>{tr('trip.yourSaving')} <strong style={{color:C.green}}>€{t.saving}</strong></div>
          </div>}

          {/* flight review breakdown */}
          <div style={{...card({marginBottom:12})}}>
            <div style={{color:C.muted,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:12}}>{tr('trip.flightAnalysis')}</div>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
              <span style={{color:C.text,fontSize:13,fontWeight:700}}>{selFlight.airline.name} {selFlight.flightNum}</span>
              {selFlight.isDirect?<Chip label={tr('flight.direct')} color={C.green} bg={C.greenDim}/>:<Chip label={tr('flight.stop')} color={C.gold} bg={C.goldDim}/>}
            </div>
            {[[tr('trip.scorePrice'),selFlight.totalPrice<=200?9.2:selFlight.totalPrice<=350?7.5:6.0],
              [tr('trip.scoreDuration'),selFlight.durationMins<=90?9.5:selFlight.durationMins<=150?8.0:6.5],
              [tr('trip.scoreCo2'),selFlight.co2vsAvg<-10?9.0:selFlight.co2vsAvg<0?7.5:5.5],
              [tr('trip.scoreComfort'),selFlight.cabin==="Business"?9.5:selFlight.luggageIncl?8.0:6.5],
            ].map(([l,v])=><ScoreBar key={l} label={l} val={v}/>)}
          </div>

          {/* hotel review breakdown */}
          {selHotel && (
          <div style={{...card({marginBottom:12})}}>
            <div style={{color:C.muted,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:12}}>{tr('trip.hotelReview')}</div>
            <div style={{color:C.text,fontSize:13,fontWeight:700,marginBottom:10}}>{selHotel.name}</div>
            {Object.entries(selHotel.reviewBreakdown).map(([k,v])=>(
              <ScoreBar key={k} label={k.replace(/([A-Z])/g," $1").trim()} val={v}/>
            ))}
            <div style={{marginTop:10,padding:"8px 10px",background:"rgba(255,255,255,0.03)",borderRadius:10,borderLeft:`2px solid ${C.accent}`}}>
              <div style={{color:C.sub,fontSize:10,fontStyle:"italic"}}>{selHotel.guestQuote}</div>
            </div>
          </div>
          )}

          {/* CO2 */}
          <div style={{...card({background:C.greenDim,borderColor:C.green})}}>
            <div style={{color:C.green,fontSize:13,fontWeight:700,marginBottom:6}}>{tr('trip.co2footprint')}</div>
            <div style={{color:C.sub,fontSize:12}}>{tr('trip.co2roundtrip').replace('{co2}',selFlight.co2)}</div>
            <div style={{color:C.sub,fontSize:12,marginTop:2}}>{tr('trip.co2offsetCost').replace('{cost}',co2Cost)}</div>
            {selFlight.co2vsAvg<0&&<div style={{color:C.green,fontSize:12,marginTop:2,fontWeight:600}}>{tr('trip.greener').replace('{pct}',Math.abs(selFlight.co2vsAvg))}</div>}
          </div>
        </div>
      )}
    </Scroll>
    </div>
  );
}

// ─── HOME ─────────────────────────────────────────────────────────────────────

// Chaque pattern : [ghost1, ghost2] — transform + transformOrigin libres pour l'effet pile naturelle
const STACK_PATTERNS = [
  [{t:'rotate(8deg)',             o:'bottom left'},  {t:'rotate(-5deg) translate(3px,2px)', o:'top right'}],
  [{t:'rotate(-7deg)',            o:'top right'},    {t:'rotate(5deg)  translate(-2px,3px)',o:'bottom left'}],
  [{t:'rotate(9deg)  translate(2px,1px)', o:'center'},{t:'rotate(-4deg)',                  o:'top left'}],
  [{t:'rotate(-6deg) translate(1px,2px)', o:'bottom right'},{t:'rotate(6deg)',              o:'center'}],
];

function DestPhotoBanner({ name, nameEn, type, travelPrefs, destDropdown, onShareDest }) {
  const t = useT(travelPrefs?.appLang);
  const [photo, setPhoto] = useState(_photoCache.get(name) || TYPE_PHOTOS[type] || TYPE_PHOTOS.ville);
  const [imgReady, setImgReady] = useState(false);
  const [patternIdx, setPatternIdx] = useState(0);
  const imgRef = useRef(null);
  const polaRef = useRef(null);
  const fetchingRef = useRef(false); // true pendant un fetch Wikidata en cours

  useEffect(() => {
    setImgReady(false);
    setPatternIdx(p => (p + 1) % STACK_PATTERNS.length);
    if (polaRef.current) polaRef.current.style.animation = 'none';
    // Si déjà en cache (Unsplash statique ou Wikidata précédemment résolu) → immédiat
    if (_photoCache.has(name)) { fetchingRef.current = false; setPhoto(_photoCache.get(name)); return; }
    fetchingRef.current = true;
    let cancelled = false;
    resolveDestPhoto(name, nameEn, type).then(url => {
      if (!cancelled) { fetchingRef.current = false; setPhoto(url); }
    });
    return () => { cancelled = true; fetchingRef.current = false; };
  }, [name, nameEn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si l'image est déjà en cache, onLoad ne se déclenche pas → vérifier .complete
  // Mais pas si on attend encore Wikidata (évite d'afficher la photo générique en avance)
  useEffect(() => {
    if (fetchingRef.current) return;
    if (imgRef.current?.complete) setImgReady(true);
  }, [photo]);

  // Déclencher l'animation via DOM quand l'image est prête (évite le faux onLoad sur cache)
  useEffect(() => {
    if (!imgReady || !polaRef.current) return;
    void polaRef.current.offsetHeight; // force reflow
    polaRef.current.style.animation = 'polaroidDrop 0.4s cubic-bezier(0.34,1.4,0.64,1) forwards';
  }, [imgReady]);

  const travelerNames = travelPrefs ? [
    travelPrefs.firstName,
    ...(travelPrefs.coTravelers?.length
      ? travelPrefs.coTravelers.map(c => c.firstName)
      : travelPrefs.coFirstName ? [travelPrefs.coFirstName] : []),
  ].filter(Boolean) : [];
  const pax = travelerNames.length || travelPrefs?.pax || 1;

  // Injecter keyframes une seule fois
  useEffect(() => {
    if (document.getElementById('pola-drop-style')) return;
    const s = document.createElement('style');
    s.id = 'pola-drop-style';
    s.textContent = `@keyframes polaroidDrop { from { transform: translateY(-28px) rotate(-6deg); opacity:0; } to { transform: translateY(0) rotate(-2deg); opacity:1; } }`;
    document.head.appendChild(s);
  }, []);

  return (
    <div style={{ display:'flex', gap:20, alignItems:'flex-start', marginBottom:14 }}>
      {/* Pile de polaroids */}
      <div style={{ position:'relative', width:'calc(50% - 3px)', flexShrink:0 }}>
        {/* Cartes fantômes — pattern change à chaque destination */}
        <div style={{ position:'absolute', inset:0, background:'white', borderRadius:3,
          boxShadow:'2px 4px 10px rgba(0,0,0,0.18)',
          transform:STACK_PATTERNS[patternIdx][0].t, transformOrigin:STACK_PATTERNS[patternIdx][0].o, zIndex:1 }}/>
        <div style={{ position:'absolute', inset:0, background:'white', borderRadius:3,
          boxShadow:'2px 4px 12px rgba(0,0,0,0.2)',
          transform:STACK_PATTERNS[patternIdx][1].t, transformOrigin:STACK_PATTERNS[patternIdx][1].o, zIndex:2 }}/>
        {/* Polaroid actif — animation déclenchée via DOM quand l'image est prête */}
        <div ref={polaRef} style={{ position:'relative', zIndex:3,
          background:'white', padding:'5px 5px 0', borderRadius:3,
          boxShadow:'4px 10px 24px rgba(0,0,0,0.55)', transformOrigin:'top left',
          opacity: imgReady ? 1 : 0 }}>
          <div style={{ width:'100%', aspectRatio:'4/3', overflow:'hidden' }}>
            <img ref={imgRef} src={photo} alt={name} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
              onLoad={() => setImgReady(true)}
              onError={e => { e.target.src = TYPE_PHOTOS[type] || TYPE_PHOTOS.ville; setImgReady(true); }}/>
          </div>
          <div style={{ textAlign:'center', padding:'4px 3px 5px', fontFamily:"'Caveat','Comic Sans MS',cursive", fontSize:26, fontWeight:700, color:'#1a1a1a', lineHeight:1 }}>{name}</div>
        </div>
      </div>
      {/* Dropdown + voyageurs + share — colonne droite, sans cadre */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', gap:8 }}>
        {destDropdown && <div>{destDropdown}</div>}
        <div style={{ paddingLeft:14 }}>
          <div style={{ color:C.muted, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:4 }}>
            {travelerNames.length > 1 ? 'Voyageurs' : 'Voyageur'}
          </div>
          {travelerNames.length > 0
            ? travelerNames.length <= 3
              ? travelerNames.map((n, i) => <div key={i} style={{ color:C.text, fontSize:14, fontWeight:700 }}>{n}</div>)
              : <div style={{ color:C.text, fontSize:13, fontWeight:700, lineHeight:1.5 }}>{travelerNames.join(', ')}</div>
            : <div style={{ color:C.muted, fontSize:12, fontStyle:'italic' }}>Compléter le profil →</div>
          }
        </div>
        {onShareDest && (
          <button onClick={onShareDest} style={{
            marginLeft:14, marginTop:4,
            display:'flex', alignItems:'center', gap:6,
            background:'none', border:`1px solid ${C.border}`, borderRadius:10,
            padding:'7px 12px', cursor:'pointer', color:C.sub, fontSize:12, fontWeight:600,
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
              <polyline points="16 6 12 2 8 6"/>
              <line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
            {t('home.share')}
          </button>
        )}
      </div>
    </div>
  );
}

function SlotCalendarBanner({ slot, travelPrefs, slotDropdown, onShare, onDismiss }) {
  const t = useT(travelPrefs?.appLang);
  const locale = langToLocale(travelPrefs?.appLang);
  const polaRef = useRef(null);
  const [patternIdx] = useState(() => Math.floor(Math.random() * STACK_PATTERNS.length));

  // Déclencher l'animation polaroid au montage
  useEffect(() => {
    if (!polaRef.current) return;
    if (!document.getElementById('pola-drop-style')) {
      const s = document.createElement('style');
      s.id = 'pola-drop-style';
      s.textContent = `@keyframes polaroidDrop { from { transform: translateY(-28px) rotate(-6deg); opacity:0; } to { transform: translateY(0) rotate(-2deg); opacity:1; } }`;
      document.head.appendChild(s);
    }
    void polaRef.current.offsetHeight;
    polaRef.current.style.animation = 'polaroidDrop 0.4s cubic-bezier(0.34,1.4,0.64,1) forwards';
  }, [slot?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const travelerNames = travelPrefs ? [
    travelPrefs.firstName,
    ...(travelPrefs.coTravelers?.length
      ? travelPrefs.coTravelers.map(c => c.firstName)
      : travelPrefs.coFirstName ? [travelPrefs.coFirstName] : []),
  ].filter(Boolean) : [];

  // Mini calendar grid data
  const depart = slot ? new Date(slot.departIso + 'T12:00:00') : null;
  const ret    = slot ? new Date(slot.returnIso  + 'T12:00:00') : null;
  const monthLabel = depart ? depart.toLocaleDateString(locale, { month: 'long', year: 'numeric' }) : '';
  const slotLabel  = slot ? `${depart.toLocaleDateString(locale, { day:'numeric', month:'short' })} — ${ret.toLocaleDateString(locale, { day:'numeric', month:'short' })}` : '';

  // Build mini grid for depart month
  const gridCells = (() => {
    if (!depart) return [];
    const m = new Date(depart.getFullYear(), depart.getMonth(), 1);
    const mDays = new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate();
    const firstDow = (m.getDay() + 6) % 7; // lundi=0
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let n = 1; n <= mDays; n++) {
      const iso = `${m.getFullYear()}-${String(m.getMonth()+1).padStart(2,'0')}-${String(n).padStart(2,'0')}`;
      cells.push({ n, inRange: iso >= slot.departIso && iso <= slot.returnIso, isDepart: iso === slot.departIso, isReturn: iso === slot.returnIso });
    }
    return cells;
  })();

  return (
    <div style={{ display:'flex', gap:20, alignItems:'flex-start', marginBottom:14 }}>
      {/* Left: dropdown + voyageurs + share */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', gap:8 }}>
        {slotDropdown && <div>{slotDropdown}</div>}
        <div style={{ paddingLeft:14 }}>
          <div style={{ color:C.muted, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:4 }}>
            {travelerNames.length > 1 ? 'Voyageurs' : 'Voyageur'}
          </div>
          {travelerNames.length > 0
            ? travelerNames.length <= 3
              ? travelerNames.map((n, i) => <div key={i} style={{ color:C.text, fontSize:14, fontWeight:700 }}>{n}</div>)
              : <div style={{ color:C.text, fontSize:13, fontWeight:700, lineHeight:1.5 }}>{travelerNames.join(', ')}</div>
            : <div style={{ color:C.muted, fontSize:12, fontStyle:'italic' }}>Compléter le profil →</div>
          }
        </div>
        {onShare && (
          <button onClick={onShare} style={{
            marginLeft:14, marginTop:4,
            display:'flex', alignItems:'center', gap:6,
            background:'none', border:`1px solid ${C.border}`, borderRadius:10,
            padding:'7px 12px', cursor:'pointer', color:C.sub, fontSize:12, fontWeight:600,
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
              <polyline points="16 6 12 2 8 6"/>
              <line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
            {t('home.share')}
          </button>
        )}
      </div>
      {/* Right: polaroid calendar — même format que DestPhotoBanner */}
      <div style={{ position:'relative', width:'calc(50% - 3px)', flexShrink:0 }}>
        {onDismiss && (
          <button onClick={onDismiss} style={{
            position:'absolute', top:-4, right:-4, zIndex:10,
            background:C.card, border:`1px solid ${C.border}`, borderRadius:'50%',
            width:20, height:20, display:'flex', alignItems:'center', justifyContent:'center',
            cursor:'pointer', color:C.muted, fontSize:10, fontWeight:800, lineHeight:1,
          }}>✕</button>
        )}
        {/* Ghost cards — même pattern que polaroid */}
        <div style={{ position:'absolute', inset:0, background:'white', borderRadius:3,
          boxShadow:'2px 4px 10px rgba(0,0,0,0.18)',
          transform:STACK_PATTERNS[patternIdx][0].t, transformOrigin:STACK_PATTERNS[patternIdx][0].o, zIndex:1 }}/>
        <div style={{ position:'absolute', inset:0, background:'white', borderRadius:3,
          boxShadow:'2px 4px 12px rgba(0,0,0,0.2)',
          transform:STACK_PATTERNS[patternIdx][1].t, transformOrigin:STACK_PATTERNS[patternIdx][1].o, zIndex:2 }}/>
        {/* Polaroid actif — même rendu exact que CalCard du shuffle */}
        <div ref={polaRef} style={{ position:'relative', zIndex:3,
          background:'white', borderRadius:3,
          boxShadow:'4px 10px 24px rgba(0,0,0,0.55)', transformOrigin:'top left', opacity:1,
          overflow:'hidden', display:'flex', flexDirection:'column' }}>
          {/* Bandeau rouge — identique CalCard */}
          <div style={{ background:'#E53935', padding:'5px 8px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:13, color:'#fff', fontWeight:700, textTransform:'capitalize' }}>{depart ? depart.toLocaleDateString('fr-FR', { month:'long' }) : ''}</span>
            <span style={{ fontSize:11, color:'rgba(255,255,255,0.85)' }}>{depart ? depart.getFullYear() : ''}</span>
          </div>
          {/* Jours de semaine */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', padding:'4px 4px 2px' }}>
            {['L','M','M','J','V','S','D'].map((d, i) => (
              <div key={i} style={{ textAlign:'center', fontSize:9, color:'#bbb', fontWeight:700 }}>{d}</div>
            ))}
          </div>
          {/* Grille jours — même logique CalCard */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', padding:'1px 4px 4px', flex:1, alignContent:'start', gap:'2px 0' }}>
            {gridCells.map((cell, i) => {
              const inRange = cell && cell.inRange;
              return (
                <div key={i} style={{
                  textAlign:'center', fontSize:11, fontWeight: inRange ? 800 : 400,
                  color: inRange ? '#C62828' : (cell ? '#444' : 'transparent'),
                  background: inRange ? 'rgba(229,57,53,0.13)' : 'transparent',
                  borderRadius:3, lineHeight:'17px',
                }}>{cell ? cell.n : ''}</div>
              );
            })}
          </div>
          {/* Label manuscrit sous la grille */}
          <div style={{ textAlign:'center', padding:'3px 3px 5px',
            fontFamily:"'Caveat','Comic Sans MS',cursive", fontSize:16, fontWeight:700,
            color:'#1a1a1a', lineHeight:1 }}>{slotLabel}</div>
        </div>
      </div>
    </div>
  );
}

function ShareTopNModal({ modal, onClose, travelPrefs }) {
  const t = useT(travelPrefs?.appLang);
  const [copied, setCopied] = useState(false);
  const url = modal?.url || '';
  const payload = modal?.payload || {};
  const ownerName = payload.ownerName || '';
  const mode = payload.mode || 'list';

  const planDests = (payload.dests || []).filter(d => d.inPlan).slice(0, 15);
  const totalDests = (payload.dests || []).length;
  const planCount = payload.planCount || planDests.length;

  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const share = () => {
    const name = ownerName || 'Quelqu\u2019un';
    const text = mode === 'topn'
      ? `${name} te partage sa bucket list de voyages 🌍`
      : mode === 'trip'
      ? `${name} te partage un voyage 🌍`
      : `${name} te partage ses options de vol ✈️`;
    if (navigator.share) navigator.share({ text: `${text}\n${url}` });
    else copy();
  };

  const POLA_ROTS_MODAL = [-8, 4, -5, 7, -3, 6, -7, 3, -9, 5, -4, 8, -6, 2, -5];
  const POLA_MTS_MODAL  = [0, 12, -4, 16, 2, -8, 8, -3, 13, -6, 4, 18, -2, 10, 0];

  return createPortal(
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:9100,
      display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background:C.card, border:`1px solid ${C.border}`,
        borderRadius:'24px 24px 0 0', width:'100%', maxWidth:480,
        maxHeight:'88vh', overflowY:'auto' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'16px 20px 4px' }}>
          <div style={{ fontSize:16, fontWeight:800, color:C.text }}>
            {t('profile.shareModalTitle')}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer',
            color:C.muted, display:'flex', alignItems:'center', justifyContent:'center',
            width:28, height:28, borderRadius:'50%', fontSize:18, lineHeight:1 }}>×</button>
        </div>

        {modal === 'creating' ? (
          <div style={{ textAlign:'center', padding:'40px 20px' }}>
            <div style={{ width:20, height:20, borderRadius:'50%', border:`2px solid ${C.border}`,
              borderTopColor:C.accent, animation:'spin 0.8s linear infinite', margin:'0 auto 16px' }}/>
            <div style={{ color:C.sub, fontSize:14 }}>{t('profile.shareTopNCreating')}</div>
          </div>
        ) : (
          <div style={{ padding:'8px 24px 40px' }}>

            {/* Mini polaroid preview — mode topn */}
            {mode === 'topn' && planDests.length > 0 && (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)',
                gap:'4px 10px', margin:'12px 0 20px', padding:'4px 0 8px' }}>
                {planDests.map((dest, i) => {
                  const rot = POLA_ROTS_MODAL[i];
                  const mt  = POLA_MTS_MODAL[i];
                  return (
                    <div key={i} style={{ marginTop: mt, display:'flex', flexDirection:'column', alignItems:'center' }}>
                      <div style={{ width:'100%',
                        background:'white', padding:'3px 3px 0', borderRadius:2,
                        boxShadow:'2px 4px 14px rgba(0,0,0,0.5)',
                        transform:`rotate(${rot}deg)`, transformOrigin:'top center' }}>
                        <div style={{ width:'100%', aspectRatio:'4/3', overflow:'hidden' }}>
                          <img src={dest.photoUrl} alt={dest.name}
                            style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                            onError={e => { e.target.style.display='none'; }}/>
                        </div>
                        <div style={{ textAlign:'center', padding:'2px 2px 3px',
                          fontFamily:"'Caveat','Comic Sans MS',cursive",
                          fontSize:12, fontWeight:700, color:'#1a1a1a', lineHeight:1 }}>
                          {dest.name}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Title */}
            <div style={{ textAlign:'center', marginBottom:20 }}>
              <div style={{ fontSize:20, fontWeight:900, color:C.text, letterSpacing:'-0.5px', marginBottom:4 }}>
                {mode === 'topn' ? t('profile.shareModalReady') : t('profile.shareModalReadyTrip')}
              </div>
              {mode === 'topn' && (
                <div style={{ fontSize:13, color:C.sub }}>
                  {t('profile.shareModalCount')(planCount)}{t('profile.shareModalTotal')(totalDests)}
                </div>
              )}
            </div>

            {/* Share button — primary */}
            <button onClick={share} style={{ width:'100%', padding:'15px',
              background:`linear-gradient(135deg,#006F87,${C.accent})`, border:'none',
              borderRadius:16, color:'#fff', fontSize:15, fontWeight:800, cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', gap:8,
              boxShadow:`0 6px 24px rgba(0,158,194,0.35)`, marginBottom:10 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                <polyline points="16 6 12 2 8 6"/>
                <line x1="12" y1="2" x2="12" y2="15"/>
              </svg>
              {t('profile.shareModalShare')}
            </button>

            {/* Copy link — secondary */}
            <button onClick={copy} style={{ width:'100%', padding:'13px',
              background: copied ? 'rgba(39,201,122,0.08)' : 'transparent',
              border:`1px solid ${copied ? C.green : C.border}`,
              borderRadius:14, color: copied ? C.green : C.sub,
              fontSize:13, fontWeight:700, cursor:'pointer', marginBottom:6 }}>
              {copied ? t('profile.shareModalCopied') : t('profile.shareModalCopyLink')}
            </button>

          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function GeoBanner({ onDismiss, onGoToProfile, travelPrefs }) {
  const t = useT(travelPrefs?.appLang);
  return (
    <div style={{
      margin:'12px 16px 0', padding:'12px 14px',
      background:'#FFF8E7', border:'1px solid #F5C842',
      borderRadius:12, display:'flex', gap:10, alignItems:'flex-start',
    }}>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#7A5800', marginBottom:3 }}>
          {t('home.geoBlockedTitle')}
        </div>
        <div style={{ fontSize:12, color:'#7A5800', lineHeight:1.5 }}>
          {t('home.geoBlockedMsg')}
        </div>
        <button onClick={onGoToProfile} style={{
          marginTop:8, padding:'6px 12px', borderRadius:8,
          background:'#F5C842', border:'none', cursor:'pointer',
          fontSize:12, fontWeight:700, color:'#3D2C00',
        }}>
          {t('home.geoBlockedCta')}
        </button>
      </div>
      <button onClick={onDismiss} style={{
        background:'none', border:'none', cursor:'pointer',
        color:'#7A5800', fontSize:16, lineHeight:1, padding:'0 2px', flexShrink:0,
      }}>✕</button>
    </div>
  );
}

function Home({ allTrips, planIds, planLimit, slots, planSlotIds, pax, onTrip, loading, travelPrefs, saveTravelPrefs, user, onRefresh, autoHelp, onHelpSeen, onShare, onShareDest, onGoToDest, onGoToCalendar, onGoToMap, geoBlocked, onGeoDismiss, onGoToProfile, selCount, planDestNames, onAutoGenSlots, lastRankingTime }) {
  const tr = useT(travelPrefs?.appLang);
  const isDesktop = useIsDesktop();
  const locale = langToLocale(travelPrefs?.appLang);
  const todayIso = new Date().toISOString().split('T')[0];
  const activePlanSlots = planSlotIds?.length ? slots?.filter(s => planSlotIds.includes(s.id)) : [];

  const fmtRankingAge = (ts) => {
    if (!ts) return null;
    const diffMs = Date.now() - ts;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 2) return tr('home.updatedNow');
    if (mins < 60) return tr('home.updatedMins')(mins);
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return tr('home.updatedHours')(hrs);
    return tr('home.updatedDays')(Math.floor(hrs / 24));
  };
  const rankingAge = !loading ? fmtRankingAge(lastRankingTime) : null;
  const [fetchingTripId, setFetchingTripId] = useState(null);

  const handleTripClick = async (t) => {
    const isEstimated = t.bestFlight?.isReal === false && !t.bestFlight?.depart;
    if (!isEstimated) { onTrip(t); return; }
    const tripId = `${t.dest.id}-${t.slot.id}`;
    setFetchingTripId(tripId);
    const origins = travelPrefs?.departureAirports?.filter(Boolean) || (travelPrefs?.departureAirport ? [travelPrefs.departureAirport] : []);
    try {
      const realTrips = await computeDestSlots(t.dest, [t.slot], pax, origins, travelPrefs, travelPrefs?.flightOnly !== false);
      onTrip(realTrips.length ? realTrips[0] : t);
    } catch (_) { onTrip(t); }
    setFetchingTripId(null);
  };

  // Ranked destinations: for each dest, keep best-score trip across all slots
  const rankedDests = useMemo(() => {
    const byDest = {};
    (allTrips || []).forEach(t => {
      if (!byDest[t.dest.id] || t.score > byDest[t.dest.id].score) byDest[t.dest.id] = t;
    });
    return Object.values(byDest).sort((a, b) => b.score - a.score);
  }, [allTrips]);

  // Slots du plan uniquement (si planSlotIds défini), triés par date de départ
  const sortedSlots = useMemo(() => {
    const base = planSlotIds?.length
      ? (slots || []).filter(s => planSlotIds.includes(s.id))
      : [];
    return [...base].sort((a, b) => a.departIso.localeCompare(b.departIso));
  }, [slots, planSlotIds]);

  // Keep last known cities for spinner (rankedDests is empty during loading)
  const lastCitiesRef = useRef([]);
  const prevPlanIdsRef = useRef(planIds);
  const spinnerCities = useMemo(() => {
    // Reset when plan changes so stale cities never show
    if (prevPlanIdsRef.current !== planIds) {
      prevPlanIdsRef.current = planIds;
      lastCitiesRef.current = [];
    }
    // planDestNames is always in sync with current plan — use as primary source
    if (planDestNames?.length) {
      lastCitiesRef.current = planDestNames;
    } else {
      // Fallback: ranked results filtered to current plan (before planDestNames fetch completes)
      const cs = rankedDests
        .filter(rd => planIds?.includes(rd.dest.id))
        .map(rd => ({ city: rd.dest.city, nameEn: rd.dest.name_en, type: rd.dest.type }));
      if (cs.length) lastCitiesRef.current = cs;
    }
    return lastCitiesRef.current;
  }, [rankedDests, planDestNames, planIds]);

  // Default slot = 3rd future slot
  const defaultSlotId = useMemo(() => {
    const future = sortedSlots.filter(s => s.departIso >= todayIso);
    return (future[2] || future[1] || future[0] || sortedSlots[0])?.id ?? null;
  }, [sortedSlots, todayIso]);

  const saved = travelPrefs?.homeView || {};
  const [mode, setMode] = useState('dest');
  const [selectedDestId, setSelectedDestId] = useState(saved.destId ?? null);
  const [selectedSlotId, setSelectedSlotId] = useState(saved.slotId ?? null);

  // Validated active selection — fall back to first ranked dest if saved ID is stale
  // Use string comparison to handle both UUID strings and integer IDs from Supabase
  const activeDestId = useMemo(() => {
    const match = rankedDests.find(t => String(t.dest.id) === String(selectedDestId));
    return match ? match.dest.id : (rankedDests[0]?.dest?.id ?? null);
  }, [selectedDestId, rankedDests]);

  const activeSlotId = selectedSlotId ?? defaultSlotId;

  // On-demand dest trips (recomputed when activeDestId changes)
  const [destTrips, setDestTrips] = useState([]);
  const [destLoading, setDestLoading] = useState(false);

  useEffect(() => {
    if (mode !== 'dest' || activeDestId == null || !rankedDests.length) { setDestTrips([]); return; }
    const destObj = rankedDests.find(t => String(t.dest.id) === String(activeDestId))?.dest;
    if (!destObj) return;
    let cancelled = false;
    // Affichage immédiat avec les prix estimés déjà calculés (évite l'écran blanc)
    const estimated = (allTrips || [])
      .filter(t => String(t.dest.id) === String(activeDestId))
      .sort((a, b) => a.total - b.total);
    setDestTrips(estimated);
    setDestLoading(true);
    const origins = travelPrefs?.departureAirports?.filter(Boolean) || (travelPrefs?.departureAirport ? [travelPrefs.departureAirport] : []);
    const planSlots = planSlotIds?.length ? slots.filter(s => planSlotIds.includes(s.id)) : slots;
    computeDestSlots(destObj, planSlots, pax, origins, travelPrefs, travelPrefs?.flightOnly !== false).then(trips => {
      if (!cancelled) { setDestTrips(trips.length ? trips : estimated); setDestLoading(false); }
    });
    return () => { cancelled = true; };
  }, [activeDestId, mode, slots, planSlotIds, pax, travelPrefs?.departureAirports?.[0], travelPrefs?.departureAirport]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync selectedDestId once rankedDests first load
  useEffect(() => {
    if (selectedDestId == null && rankedDests.length) setSelectedDestId(rankedDests[0].dest.id);
  }, [rankedDests]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedSlotId == null && defaultSlotId) setSelectedSlotId(defaultSlotId);
  }, [defaultSlotId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first slot with results when current slot has none
  useEffect(() => {
    if (!allTrips?.length) return;
    const hasResults = allTrips.some(t => t.slot.id === activeSlotId);
    if (!hasResults) {
      const firstSlotId = allTrips[0]?.slot?.id;
      if (firstSlotId) setSelectedSlotId(firstSlotId);
    }
  }, [allTrips]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = (m, dId, sId) => {
    if (!saveTravelPrefs) return;
    const np = { ...travelPrefs, homeView: { mode: m, destId: dId, slotId: sId } };
    saveTravelPrefs(np, user?.id);
  };

  // Par slot: filter allTrips by activeSlotId, sorted by price
  const slotResults = useMemo(() => {
    const filtered = (allTrips || []).filter(t => t.slot.id === activeSlotId);
    return filtered.sort((a, b) => a.total - b.total);
  }, [allTrips, activeSlotId]);

  // Active slot object + event-slot detection
  const activeSlot = useMemo(() =>
    sortedSlots.find(s => s.id === activeSlotId) ?? null,
  [sortedSlots, activeSlotId]);

  const isEventSlot = activeSlot?.source === 'event';

  // On-demand cost computation for event slots whose dest is not in planIds
  const [eventSlotTrips, setEventSlotTrips] = useState([]);
  const [eventSlotLoading, setEventSlotLoading] = useState(false);

  useEffect(() => {
    if (mode !== 'slot' || !isEventSlot || slotResults.length) {
      setEventSlotTrips([]);
      return;
    }
    const eventId = activeSlot?.eventId;
    if (!eventId) return;
    let cancelled = false;
    setEventSlotLoading(true);
    setEventSlotTrips([]);

    supabase.from('destinations')
      .select('id, name, type, event_date, event_end, recurrent')
      .eq('id', eventId)
      .single()
      .then(({ data }) => {
        if (cancelled || !data) { setEventSlotLoading(false); return; }
        const dest = {
          id: data.id, city: data.name, type: data.type,
          types: ['WE','longWE','short','long'],
          isEvent: true, eventDate: data.event_date, recurrent: data.recurrent,
          _evSlots: eventSlots({ id: data.id, name: data.name, event_date: data.event_date, event_end: data.event_end, recurrent: data.recurrent }),
        };
        const origins = travelPrefs?.departureAirports?.filter(Boolean) || (travelPrefs?.departureAirport ? [travelPrefs.departureAirport] : []);
        computeDestSlots(dest, slots, pax, origins, travelPrefs, travelPrefs?.flightOnly !== false).then(trips => {
          if (!cancelled) {
            const exact = trips.filter(t => t.slot.id === activeSlotId);
            setEventSlotTrips(exact.length ? exact : trips);
            setEventSlotLoading(false);
          }
        });
      });

    return () => { cancelled = true; };
  }, [mode, isEventSlot, activeSlotId, slotResults.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const selStyle = {
    width: '100%', background: C.card, border: `1.5px solid ${C.border}`,
    borderRadius: 12, padding: '11px 14px', color: C.text, fontSize: 13,
    fontWeight: 600, outline: 'none', WebkitAppearance: 'none', appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='7' viewBox='0 0 12 7'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%234DD4F0' stroke-width='1.8' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center', paddingRight: 38,
  };
  const compactSelStyle = { ...selStyle, padding: '6px 12px', paddingRight: 34, fontSize: 12, borderRadius: 10 };

  const [showHelp, setShowHelp] = useState(!!autoHelp);
  const dismissHelp = () => { setShowHelp(false); if (onHelpSeen) onHelpSeen(); };

  // Slot calendar banner — reversible, memorized in travelPrefs
  const [slotBannerHidden, setSlotBannerHidden] = useState(travelPrefs?.slotBannerHidden ?? false);
  useEffect(() => { setSlotBannerHidden(travelPrefs?.slotBannerHidden ?? false); }, [travelPrefs?.slotBannerHidden]);
  const setSlotBanner = (hidden) => {
    setSlotBannerHidden(hidden);
    const np = { ...travelPrefs, slotBannerHidden: hidden };
    saveTravelPrefs(np, user?.id);
  };

  const helpModal = showHelp && createPortal(
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onClick={dismissHelp}>
      <div onClick={e => e.stopPropagation()} style={{
        background:C.card, borderRadius:'20px 20px 0 0', padding:'24px 24px 40px',
        width:'100%', maxWidth:600, borderTop:`1px solid ${C.border}`,
      }}>
        <div style={{ width:36, height:4, borderRadius:2, background:C.border, margin:'0 auto 20px' }}/>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
          <div style={{ width:32, height:32, borderRadius:16, background:C.accentDim,
            display:'flex', alignItems:'center', justifyContent:'center',
            color:C.accentHi, fontSize:16, fontWeight:800 }}>?</div>
          <span style={{ color:C.text, fontSize:16, fontWeight:800 }}>{tr('common.howItWorks')}</span>
        </div>
        <p style={{ color:C.sub, fontSize:14, lineHeight:1.7, margin:0 }}>
          {tr('home.helpText')}
        </p>
        <button onClick={dismissHelp} style={{
          marginTop:20, width:'100%', padding:'13px', borderRadius:14,
          background:C.accent, border:'none', color:C.white, fontSize:14, fontWeight:700, cursor:'pointer',
        }}>{tr('common.understood')}</button>
      </div>
    </div>,
    document.body
  );

  const Spinner = () => (
    <Loading
      label={tr('home.loadingText')}
      total={Math.max(rankedDests.length, planLimit) * sortedSlots.length}
      nOrigins={Math.max(1, (travelPrefs?.departureAirports?.filter(Boolean).length || (travelPrefs?.departureAirport ? 1 : 0)) || 1)}
      cities={spinnerCities}
      slotRanges={sortedSlots.map(s => ({ departIso: s.departIso, returnIso: s.returnIso }))}
    />
  );

  const Empty = ({ msg }) => (
    <div style={{ textAlign:'center', padding:'40px 20px', color:C.muted, fontSize:13 }}>{msg}</div>
  );

  const EmptyCTA = ({ msg, btnLabel, onBtn }) => (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', minHeight:'50vh', gap:16, padding:'40px 20px', textAlign:'center' }}>
      <div style={{ color:C.sub, fontSize:15, fontWeight:600 }}>{msg}</div>
      <button onClick={onBtn} style={{ ...btnPrimary, width:'auto', padding:'12px 24px', fontSize:14 }}>{btnLabel}</button>
    </div>
  );

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', background:C.bg, overflow:'hidden', maxWidth:600, margin:'0 auto', width:'100%' }}>
      {helpModal}
      {geoBlocked && <GeoBanner onDismiss={onGeoDismiss} onGoToProfile={onGoToProfile} travelPrefs={travelPrefs}/>}
      <div style={{ flexShrink:0 }}>
      {/* Header */}
      <div style={{ padding:'calc(env(safe-area-inset-top, 0px) + 16px) 20px 12px', display:'flex', justifyContent:'space-between', alignItems:'flex-end' }}>
        <div>
          <div style={{ color:C.text, fontSize:23, fontWeight:800, letterSpacing:'-0.6px', display:'flex', alignItems:'center', gap:8 }}>
            plotted<span style={{ color:C.accentHi }}>.</span>
            <button onClick={() => setShowHelp(true)} style={{
              width:20, height:20, borderRadius:'50%', border:`1.5px solid ${C.border}`,
              background:'transparent', color:C.muted, fontSize:11, fontWeight:800,
              cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
              flexShrink:0, lineHeight:1,
            }}>?</button>
          </div>
          {isDesktop && (
            <div style={{ color:C.sub, fontSize:12, marginTop:3, letterSpacing:'0.2px' }}>
              {tr('brand.tagline')}
            </div>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <button onClick={onRefresh} disabled={loading}
            style={{ background:'none', border:`1px solid ${C.border}`, borderRadius:8,
              padding:'4px 8px', color: loading ? C.muted : C.accentHi, fontSize:16,
              cursor: loading ? 'default' : 'pointer', lineHeight:1,
              opacity: loading ? 0.4 : 1, transition:'opacity .2s' }}
            title={tr('home.recalculate')}>↻</button>
          {onShare && <button onClick={() => onShare && onShare(rankedDests)} disabled={loading}
            style={{ background:'none', border:`1px solid ${C.border}`, borderRadius:8,
              padding:'4px 8px', color: loading ? C.muted : C.sub, fontSize:13,
              cursor: loading ? 'default' : 'pointer', lineHeight:1,
              opacity: loading ? 0.4 : 1, transition:'opacity .2s' }}
            title={tr('profile.shareTopN')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
          </button>}
        </div>
      </div>

      {/* Mode toggle */}
      <div style={{ display:'flex', margin:'0 16px', background:C.card, borderRadius:13, padding:3 }}>
        {[['dest', tr('home.myDests')(planIds?.length ?? 0)],['slot', tr('home.mySlots')(planSlotIds?.length ?? 0)]].map(([k,l]) => (
          <button key={k} onClick={() => { setMode(k); persist(k, activeDestId, activeSlotId); }}
            style={{ flex:1, padding:'9px 0', fontSize:11, fontWeight:700, borderRadius:11,
              background:mode===k?C.accent:'transparent', color:mode===k?C.white:C.sub,
              border:'none', cursor:'pointer' }}>{l}</button>
        ))}
      </div>

      {/* Dropdown slots — standalone quand bannière masquée ou pas de slot actif */}
      <div style={{ margin:'10px 16px 0' }}>
        {mode === 'slot' && sortedSlots.length > 0 && (slotBannerHidden || !activeSlot) && (
          <>
            <select
              value={activeSlotId ?? ''}
              onChange={e => { setSelectedSlotId(e.target.value); persist(mode, activeDestId, e.target.value); }}
              style={selStyle}
            >
              {sortedSlots.map(s => (
                <option key={s.id} value={s.id}>
                  {s.source === 'event' ? `★ ${s.eventName} · ${s.from}` : `${fmtSlotDisplay(s, locale)} · ${s.nights}n`}
                </option>
              ))}
            </select>
            {slotBannerHidden && (
              <button onClick={() => setSlotBanner(false)} style={{
                marginTop:6, background:'none', border:'none', cursor:'pointer',
                color:C.muted, fontSize:11, padding:'2px 0', textDecoration:'underline',
              }}>{tr('home.showSlotBanner')}</button>
            )}
          </>
        )}
      </div>

      {/* Bannière slot — miroir de DestPhotoBanner, calendrier à droite */}
      {mode === 'slot' && sortedSlots.length > 0 && !slotBannerHidden && activeSlot && (
        <div style={{ margin:'8px 16px 0' }}>
          <SlotCalendarBanner
            slot={activeSlot}
            travelPrefs={travelPrefs}
            slotDropdown={sortedSlots.length > 1 && (
              <select value={activeSlotId ?? ''} onChange={e => { setSelectedSlotId(e.target.value); persist(mode, activeDestId, e.target.value); }} style={selStyle}>
                {sortedSlots.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.source === 'event' ? `★ ${s.eventName} · ${s.from}` : `${fmtSlotDisplay(s, locale)} · ${s.nights}n`}
                  </option>
                ))}
              </select>
            )}
            onShare={onShare ? () => onShare(rankedDests) : null}
            onDismiss={() => setSlotBanner(true)}
          />
        </div>
      )}

      {/* Bannière dest — hors du results div pour s'aligner avec le toggle (même margin:'0 16px') */}
      {!loading && mode === 'dest' && destTrips.length > 0 && (
        <div style={{ margin:'8px 16px 0' }}>
          <DestPhotoBanner name={destTrips[0].dest.city} nameEn={destTrips[0].dest.name_en} type={destTrips[0].dest.type} travelPrefs={travelPrefs}
            destDropdown={rankedDests.length > 1 && (
              <select value={activeDestId ?? ''} onChange={e => { setSelectedDestId(e.target.value); persist(mode, e.target.value, activeSlotId); }} style={selStyle}>
                {rankedDests.map((t) => {
                  const isActive = String(t.dest.id) === String(activeDestId);
                  const tripsForMin = isActive && destTrips.length
                    ? destTrips
                    : (allTrips||[]).filter(x => x.dest.id === t.dest.id);
                  const prices = tripsForMin.map(x => x.total).filter(Boolean);
                  const minP = prices.length ? Math.min(...prices) : t.total;
                  return <option key={t.dest.id} value={t.dest.id}>{localDestName(t.dest, travelPrefs?.appLang)} · {tr('home.from')} €{minP}</option>;
                })}
              </select>
            )}
            onShareDest={onShareDest ? () => onShareDest({ dest: rankedDests.find(t => String(t.dest.id) === String(activeDestId))?.dest || destTrips[0]?.dest, destTrips }) : null}
          />
        </div>
      )}

      </div>{/* end sticky header */}
      <div style={{ flex:1, overflowY:'auto', overflowX:'hidden', scrollbarWidth:'none', position:'relative' }}>
        {loading && planIds?.length > 0 && activePlanSlots?.length > 0 && (
          <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
            <Spinner/>
          </div>
        )}
      {/* Results */}
      <div style={{ padding:'10px 16px' }}>

        {!loading && !(allTrips?.length) && !selCount && (
          <EmptyCTA msg={tr('home.emptyNoDest')} btnLabel={tr('home.emptyCtaMap')} onBtn={onGoToMap}/>
        )}
        {!loading && !(allTrips?.length) && selCount > 0 && !planIds?.length && (
          <EmptyCTA msg={tr('home.emptyNoPlan')} btnLabel={tr('home.emptyCtaPlan')} onBtn={onGoToDest}/>
        )}
        {!loading && !(allTrips?.length) && planIds?.length > 0 && !activePlanSlots?.length && (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'50vh', gap:16, padding:'40px 20px', textAlign:'center' }}>
            <div style={{ color:C.sub, fontSize:15, fontWeight:600, lineHeight:1.5, maxWidth:300 }}>{tr('home.emptyNoSlot')}</div>
            <div style={{ display:'flex', flexDirection:'column', gap:10, width:'100%', maxWidth:260 }}>
              <button onClick={onAutoGenSlots} style={{ ...btnPrimary, width:'auto', padding:'12px 24px', fontSize:14 }}>{tr('home.emptyCtaAutoSlot')}</button>
              <button onClick={onGoToCalendar} style={{ ...btnSec, flex:'unset', width:'auto', padding:'12px 24px', fontSize:14 }}>{tr('home.emptyCtaSlot')}</button>
            </div>
          </div>
        )}
        {(() => {
          if (loading || allTrips?.length || !planIds?.length || !activePlanSlots?.length) return null;
          const maxNights = Math.max(...sortedSlots.map(s => {
            const d = (new Date(s.returnIso) - new Date(s.departIso)) / 86400000;
            return Math.round(d);
          }));
          return (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'50vh', gap:16, padding:'40px 20px', textAlign:'center' }}>
              <div style={{ color:C.sub, fontSize:15, fontWeight:600, lineHeight:1.5, maxWidth:300 }}>
                {typeof tr('home.emptyNoCompat') === 'function' ? tr('home.emptyNoCompat')(maxNights) : tr('home.emptyNoCompat')}
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:10, width:'100%', maxWidth:260 }}>
                <button onClick={onGoToCalendar} style={{ ...btnPrimary, width:'auto', padding:'12px 24px', fontSize:14 }}>{tr('home.emptyCtaLonger')}</button>
                <button onClick={onGoToDest} style={{ ...btnSec, flex:'unset', width:'auto', padding:'12px 24px', fontSize:14 }}>{tr('home.emptyCtaCloser')}</button>
              </div>
            </div>
          );
        })()}
        {!loading && mode === 'dest' && allTrips?.length > 0 && !destTrips.length && !destLoading && (
          <Empty msg={tr('home.noSlotForDest')}/>
        )}
        {!loading && mode === 'slot' && allTrips?.length > 0 && !slotResults.length && !isEventSlot && (
          <Empty msg={tr('home.noDestForSlot')}/>
        )}
        {!loading && mode === 'slot' && isEventSlot && !eventSlotLoading && !slotResults.length && !eventSlotTrips.length && (
          <Empty msg={tr('home.noFlightEvent')}/>
        )}
        {/* Event slot on-demand — bannière photo */}
        {!loading && mode === 'slot' && isEventSlot && !slotResults.length && eventSlotTrips.length > 0 && (
          <DestPhotoBanner name={eventSlotTrips[0].dest.city} nameEn={eventSlotTrips[0].dest.name_en} type={eventSlotTrips[0].dest.type} travelPrefs={travelPrefs}/>
        )}

        {!loading && (mode === 'dest'
          ? destTrips.filter((t, _, arr) => t.total <= arr[0].total * 2)
          : (slotResults.length ? slotResults : (isEventSlot ? eventSlotTrips : []))
        ).map((t, i, arr) => {
          const ppn = x => Math.round(x.total / x.nights);
          const minPPN = Math.min(...arr.map(ppn));
          const isBestPN = ppn(t) === minPPN && arr.length > 1;
          const tripId = `${t.dest.id}-${t.slot.id}`;
          const isFetching = fetchingTripId === tripId;
          return <button key={tripId} onClick={() => !isFetching && handleTripClick(t)}
            style={{ ...card({ display:'flex', alignItems:'center', gap:10, marginBottom:8,
              width:'100%', textAlign:'left', cursor: isFetching ? 'wait' : 'pointer', padding:'12px 14px',
              opacity: isFetching ? 0.7 : 1 }) }}>

            {/* Rank badge — les deux modes */}
            <div style={{ width:28, height:28, borderRadius:14, flexShrink:0,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:10, fontWeight:800,
              background: i===0 ? 'linear-gradient(135deg,#F4A928,#fb923c)' : C.accentDim,
              color: i===0 ? C.white : C.accentHi }}>
              {isFetching ? '⏳' : `#${i+1}`}
            </div>

            {/* City photo — seulement en mode par slot */}
            {mode === 'slot' && (
              <div style={{ width:42, height:42, borderRadius:10, overflow:'hidden', flexShrink:0, border:`1px solid ${C.border}` }}>
                <img src={DEST_PHOTOS[t.dest.city] || TYPE_PHOTOS[t.dest.type] || TYPE_PHOTOS.ville} alt={t.dest.city} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
              </div>
            )}

            {/* Main info */}
            <div style={{ flex:1, minWidth:0 }}>
              {mode === 'slot' ? (
                <>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ color:C.text, fontSize:14, fontWeight:700 }}>{localDestName(t.dest, travelPrefs?.appLang)}</span>
                    <TypeChip type={t.slot.type} lang={travelPrefs?.appLang}/>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:4, marginTop:3 }}>
                    {t.bestFlight?.isReal === false ? (
                      <span style={{ color:C.muted, fontSize:11, fontStyle:'italic' }}>{tr('home.estimated')}</span>
                    ) : (
                      <span style={{ color:C.accentHi, fontSize:11, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:100 }}>
                        {t.bestFlight?.airline?.name}
                      </span>
                    )}
                    {t.bestFlight?.departureTime && <span style={{ color:C.sub, fontSize:11 }}>· {t.bestFlight.departureTime.substring(11,16)}</span>}
                    <span style={{ color:C.muted, fontSize:11 }}>· {fmtMins(t.bestFlight?.durationMins)}</span>
                    {t.savePct > 10 && <span style={{ color:C.green, fontSize:11 }}>↓{t.savePct}%</span>}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <TypeChip type={t.slot.type} lang={travelPrefs?.appLang}/>
                    <span style={{ color:C.text, fontSize:13, fontWeight:700 }}>
                      {t.slot.source === 'event' ? `${tr('home.arrival')} ${t.slot.from}` : fmtSlotDisplay(t.slot, locale)}
                    </span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:4, marginTop:3 }}>
                    {t.bestFlight?.isReal === false ? (
                      <span style={{ color:C.muted, fontSize:11, fontStyle:'italic' }}>{tr('home.estimated')}</span>
                    ) : (
                      <span style={{ color:C.accentHi, fontSize:11, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:100 }}>
                        {t.bestFlight?.airline?.name}
                      </span>
                    )}
                    {t.bestFlight?.departureTime && <span style={{ color:C.sub, fontSize:11 }}>· {t.bestFlight.departureTime.substring(11,16)}</span>}
                    <span style={{ color:C.muted, fontSize:11 }}>· {fmtMins(t.bestFlight?.durationMins)}{t.bestHotel ? ` · ${t.bestHotel.rating}★` : ''}</span>
                    {t.savePct > 10 && <span style={{ color:C.green, fontSize:11 }}>↓{t.savePct}%</span>}
                  </div>
                </>
              )}
            </div>

            {/* Price */}
            <div style={{ flexShrink:0, textAlign:'right' }}>
              {t.noCompatibleSlot ? (
                <div style={{ textAlign:'right', maxWidth:110 }}>
                  <div style={{ fontSize:10, color:'#F4A928', fontWeight:700,
                    background:'rgba(244,169,40,0.12)', border:'1px solid rgba(244,169,40,0.3)',
                    borderRadius:6, padding:'3px 7px', lineHeight:1.4 }}>
                    ⚠️ {tr('home.needsLongSlot')}
                  </div>
                  <div style={{ fontSize:9, color:C.muted, marginTop:3, lineHeight:1.4, whiteSpace:'normal' }}>
                    {tr('home.needsLongSlotSub')}
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ color:C.text, fontSize:18, fontWeight:800 }}>{t.isRealPrice===false?'~':''}€{t.total}</div>
                  {t.bestHotel ? (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:4 }}>
                      {isBestPN && <span style={{ background:C.green, color:C.white, fontSize:9, fontWeight:800,
                        padding:'1px 5px', borderRadius:4, letterSpacing:0.3 }}>€/n min</span>}
                      <span style={{ color: isBestPN ? C.green : C.muted, fontSize:10, fontWeight: isBestPN ? 700 : 400 }}>
                        {t.nights}n · {t.isRealPrice===false?'~':''}€{Math.round(t.total/t.nights)}/n
                      </span>
                    </div>
                  ) : (
                    <div style={{ color:C.muted, fontSize:10 }}>{t.nights}n</div>
                  )}
                </>
              )}
            </div>
          </button>;
        })}

        {/* Prominent share CTA — appears after results load */}
        {!loading && allTrips?.length > 0 && onShare && (
          <button onClick={() => onShare && onShare(rankedDests)} style={{
            width:'100%', marginTop:6, padding:'14px 16px',
            background:`linear-gradient(135deg, rgba(0,111,135,0.25), rgba(0,158,194,0.12))`,
            border:`1px solid rgba(0,158,194,0.35)`, borderRadius:16,
            display:'flex', alignItems:'center', justifyContent:'space-between',
            cursor:'pointer', textAlign:'left',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ width:36, height:36, borderRadius:10,
                background:`linear-gradient(135deg,#006F87,${C.accent})`,
                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                  <polyline points="16 6 12 2 8 6"/>
                  <line x1="12" y1="2" x2="12" y2="15"/>
                </svg>
              </div>
              <div>
                <div style={{ color:C.text, fontSize:13, fontWeight:800 }}>{tr('home.shareCta')(planIds?.length || 0)}</div>
                <div style={{ color:C.sub, fontSize:11, marginTop:2 }}>{tr('home.shareCtaSub')}</div>
              </div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        )}
      </div>
      <div style={{height:'calc(80px + env(safe-area-inset-bottom, 0px))'}}/>
      </div>{/* end scroll */}
    </div>
  );
}

// ─── SUBSCRIPTION MODAL ───────────────────────────────────────────────────────

function CrownIcon({ id, color1, color2, size = 40 }) {
  const gid = `cg-${id}`;
  return (
    <svg width={size} height={Math.round(size * 0.76)} viewBox="0 0 50 38" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="50" y2="38" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={color1}/>
          <stop offset="100%" stopColor={color2}/>
        </linearGradient>
      </defs>
      {/* Base band */}
      <rect x="5" y="24" width="40" height="11" rx="3" fill={`url(#${gid})`}/>
      {/* Crown body — 5-point silhouette */}
      <polygon points="5,24 5,10 16,18 25,2 34,18 45,10 45,24" fill={`url(#${gid})`}/>
      {/* Tip gems */}
      <circle cx="5"  cy="10" r="3.2" fill={`url(#${gid})`}/>
      <circle cx="45" cy="10" r="3.2" fill={`url(#${gid})`}/>
      <circle cx="25" cy="2"  r="3.2" fill={`url(#${gid})`}/>
    </svg>
  );
}

function SubscriptionModal({ onClose, appLang, onUpgrade, currentPlan = 'free', prefillEmail = '' }) {
  const t = useT(appLang);
  const [billing, setBilling] = useState('monthly');
  const [checkout, setCheckout] = useState(null); // null | { plan }
  const [cardNum, setCardNum]   = useState('');
  const [expiry, setExpiry]     = useState('');
  const [cvc, setCvc]           = useState('');
  const [holder, setHolder]     = useState('');
  const [email, setEmail]       = useState(prefillEmail);
  const [paying, setPaying]     = useState(false);
  const [paid, setPaid]         = useState(false);

  const fmtCard = v => v.replace(/\D/g,'').slice(0,16).replace(/(.{4})/g,'$1 ').trim();
  const fmtExp  = v => { const d = v.replace(/\D/g,'').slice(0,4); return d.length>2 ? d.slice(0,2)+'/'+d.slice(2) : d; };

  const handlePay = () => {
    setPaying(true);
    setTimeout(() => {
      setPaid(true);
      setTimeout(() => {
        onUpgrade?.(checkout.plan.id, billing);
        onClose();
      }, 1200);
    }, 1800);
  };

  const plans = [
    {
      id: 'silver',
      color1: '#c8d6dc', color2: '#78909c',
      name: t('sub.planSilver'),
      color: '#b0bec5',
      border: '#78909c',
      features: t('sub.silverFeats'),
      monthly: '1,95€',
      annual:  '19€',
      annualSub: '1,58€ / mois',
    },
    {
      id: 'gold',
      color1: '#F4A928', color2: '#fb923c',
      name: t('sub.planGold'),
      color: '#F4A928',
      border: '#F4A92899',
      features: t('sub.goldFeats'),
      monthly: '4,95€',
      annual:  '49€',
      annualSub: '4,08€ / mois',
    },
  ];

  const inp = { width:'100%', padding:'11px 14px', borderRadius:10, border:`1.5px solid #2A2A2A`,
    background:'#0D0D0D', color:'#E6FBFF', fontSize:14, boxSizing:'border-box', outline:'none',
    fontFamily:"'DM Sans',sans-serif" };

  if (checkout) return createPortal(
    <div style={{ position:'fixed', inset:0, zIndex:199999, background:'rgba(0,0,0,0.82)',
      display:'flex', alignItems:'flex-end', justifyContent:'center', padding:'0' }}>
      <div style={{ background:'#0A0A0A', border:'1px solid #1A1A1A', borderRadius:'20px 20px 0 0',
        width:'100%', maxWidth:480, padding:'24px 20px 40px', overflowY:'auto', maxHeight:'92vh' }}>

        {/* Header */}
        {!paid && !paying && <button onClick={() => setCheckout(null)} style={{ background:'none', border:'none',
          color:'#555', fontSize:13, cursor:'pointer', marginBottom:16, padding:0 }}>{t('sub.back')}</button>}

        {/* Confirmation animée */}
        {paid && (
          <div style={{ textAlign:'center', padding:'40px 0' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>✓</div>
            <div style={{ fontSize:18, fontWeight:800, color:'#27C97A' }}>{t('sub.activated')}</div>
            <div style={{ color:'#555', fontSize:13, marginTop:8 }}>Plotted {checkout.plan.name}</div>
          </div>
        )}

        {!paid && <>
          {/* Résumé commande */}
          <div style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 16px',
            background:'#111', borderRadius:14, marginBottom:20, border:'1px solid #1A1A1A' }}>
            <CrownIcon id={`co-${checkout.plan.id}`} color1={checkout.plan.color1} color2={checkout.plan.color2} size={36}/>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:800, color:checkout.plan.color }}>Plotted {checkout.plan.name}</div>
              <div style={{ fontSize:11, color:'#555', marginTop:2 }}>
                {billing === 'annual' ? checkout.plan.annual+' '+t('sub.perYear') : checkout.plan.monthly+' '+t('sub.perMonth')}
              </div>
            </div>
            <div style={{ fontSize:18, fontWeight:800, color:'#E6FBFF' }}>
              {billing === 'annual' ? checkout.plan.annual : checkout.plan.monthly}
            </div>
          </div>

          {/* No payment processor is wired up yet: handlePay only waits and
              flips the plan. The notice must therefore always render — it used
              to sit behind REACT_APP_DEV_PAYMENTS, which is unset in
              production, so the live site showed a card form with no warning. */}
          <div style={{ textAlign:'center', marginBottom:16, padding:'10px 12px', borderRadius:10,
            background:'#F5A62311', border:'1px solid #F5A62344' }}>
            <span style={{ color:'#F5A623', fontSize:11, fontWeight:600 }}>{t('sub.mockNotice')}</span>
          </div>

          {/* Formulaire */}
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div style={{ fontSize:10, fontWeight:700, color:'#444', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:2 }}>{t('sub.contactDetails')}</div>
            <input style={inp} type="email" placeholder={t('sub.email')} value={email} onChange={e => setEmail(e.target.value)} disabled={paying}/>
            <div style={{ fontSize:10, fontWeight:700, color:'#444', textTransform:'uppercase', letterSpacing:'0.08em', margin:'6px 0 2px' }}>{t('sub.paymentInfo')}</div>
            <input style={{ ...inp, letterSpacing: cardNum ? '0.12em' : 0 }} type="text" inputMode="numeric"
              placeholder="1234 1234 1234 1234" value={cardNum}
              onChange={e => setCardNum(fmtCard(e.target.value))} disabled={paying}/>
            <div style={{ display:'flex', gap:10 }}>
              <input style={{ ...inp, flex:1 }} type="text" inputMode="numeric"
                placeholder={t('sub.expiry')} value={expiry}
                onChange={e => setExpiry(fmtExp(e.target.value))} disabled={paying}/>
              <input style={{ ...inp, width:80, flex:'none' }} type="text" inputMode="numeric"
                placeholder="CVC" maxLength={4} value={cvc}
                onChange={e => setCvc(e.target.value.replace(/\D/g,'').slice(0,4))} disabled={paying}/>
            </div>
            <input style={inp} type="text" placeholder={t('sub.cardHolder')} value={holder}
              onChange={e => setHolder(e.target.value)} disabled={paying}/>
          </div>

          {/* Bouton Pay */}
          <button onClick={handlePay} disabled={paying} style={{
            width:'100%', marginTop:20, padding:'14px', borderRadius:12,
            background: paying ? '#1A2A1A' : `linear-gradient(135deg,#006F87,${C.accent})`,
            border:'none', color: paying ? '#27C97A' : '#fff',
            fontSize:14, fontWeight:800, cursor: paying ? 'default' : 'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap:10, transition:'all 0.2s',
          }}>
            {paying ? (
              <>
                <div style={{ width:16, height:16, borderRadius:'50%', border:'2px solid #27C97A33',
                  borderTopColor:'#27C97A', animation:'spin 0.8s linear infinite' }}/>
                {t('sub.processing')}
              </>
            ) : t('sub.payAmount').replace('{amount}', billing === 'annual' ? checkout.plan.annual : checkout.plan.monthly)}
          </button>

          {/* The "Paiement sécurisé · Powered by Stripe" footer that used to sit
              here claimed a processor that is not integrated. Restore it with
              the real Stripe checkout, not before. */}
        </>}
      </div>
    </div>,
    document.body
  );

  return createPortal(
    <div style={{
      position:'fixed', inset:0, zIndex:199999,
      background:'rgba(0,0,0,0.72)',
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:'20px 16px',
    }}>
      <div style={{
        background:C.card, border:`1px solid ${C.border}`,
        borderRadius:20, padding:'24px 20px 20px',
        width:'100%', maxWidth:420, position:'relative',
      }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:18 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:800, color:C.text, marginBottom:5 }}
              dangerouslySetInnerHTML={{ __html: t('sub.headline').replace('<b>','<span style="color:#F4A928">').replace('</b>','</span>') }}
            />
            <div style={{ fontSize:11, color:C.sub }}>
              {t('sub.tagline')}
            </div>
          </div>
          <button onClick={onClose} style={{
            flexShrink:0,
            background:C.cardB, border:`1px solid ${C.border}`,
            borderRadius:'50%', width:28, height:28,
            color:C.text, fontSize:16, lineHeight:'28px', textAlign:'center',
            cursor:'pointer', padding:0,
          }}>×</button>
        </div>

        {/* Billing toggle */}
        <div style={{ display:'flex', justifyContent:'center', marginBottom:16 }}>
          <div style={{
            display:'flex', background:C.bg, borderRadius:10,
            border:`1px solid ${C.border}`, padding:3, gap:3,
          }}>
            {[['monthly', t('sub.monthly')],['annual', t('sub.annual')]].map(([key, label]) => (
              <button key={key} onClick={() => setBilling(key)} style={{
                padding:'5px 18px', borderRadius:7, border:'none', cursor:'pointer',
                fontSize:12, fontWeight:700,
                background: billing === key ? C.accent : 'transparent',
                color: billing === key ? '#fff' : C.sub,
                transition:'all 0.15s',
              }}>
                {label}
                {key === 'annual' && <span style={{
                  marginLeft:5, fontSize:9, background:'#27C97A22',
                  color:'#27C97A', borderRadius:4, padding:'1px 5px',
                }}>−20%</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Plans */}
        <div style={{ display:'flex', gap:12 }}>
          {plans.map(plan => {
            const price     = billing === 'monthly' ? plan.monthly : plan.annual;
            const priceSub  = billing === 'monthly' ? t('sub.perMonth') : `${t('sub.perYear')}${plan.annualSub}`;
            return (
              <div key={plan.id} style={{
                flex:1, border:`1px solid ${plan.border}`,
                borderRadius:14, padding:'16px 12px 14px',
                display:'flex', flexDirection:'column', gap:10,
              }}>
                <div style={{ textAlign:'center' }}>
                  <div style={{ display:'flex', justifyContent:'center', marginBottom:4 }}>
                    <CrownIcon id={plan.id} color1={plan.color1} color2={plan.color2} size={44}/>
                  </div>
                  <div style={{ fontSize:13, fontWeight:800, color:plan.color, marginTop:2 }}>
                    Plotted {plan.name}
                  </div>
                  <div style={{ fontSize:9, borderRadius:6, padding:'2px 8px', marginTop:4, display:'inline-block', fontWeight:700,
                    color: plan.id === currentPlan ? '#27C97A' : 'transparent',
                    background: plan.id === currentPlan ? '#27C97A18' : 'transparent' }}>
                    {plan.id === currentPlan ? <>{t('sub.currentPlan')} · <em>{plan.name}</em></> : '·'}
                  </div>
                </div>
                <ul style={{ listStyle:'none', margin:0, padding:0, display:'flex', flexDirection:'column', gap:5 }}>
                  {plan.features.map((f, i) => (
                    <li key={i} style={{ fontSize:10, color:C.sub, display:'flex', alignItems:'center', gap:5, minHeight:22 }}>
                      <span style={{ color:plan.color, flexShrink:0 }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
                <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:8 }}>
                  <div style={{ fontSize:18, fontWeight:800, color:C.text }}>{price}</div>
                  <div style={{ fontSize:10, color:C.sub, marginTop:2 }}>{priceSub}</div>
                </div>
                <button
                  onClick={() => plan.id !== currentPlan && setCheckout({ plan })}
                  disabled={plan.id === currentPlan}
                  style={{
                    width:'100%', padding:'9px 0', borderRadius:10, marginTop:'auto',
                    background: plan.id === currentPlan ? C.border : `linear-gradient(135deg,#006F87,${C.accent})`,
                    border:'none', color: plan.id === currentPlan ? C.muted : '#fff', fontSize:11, fontWeight:800,
                    cursor: plan.id === currentPlan ? 'default' : 'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                  }}>
                  {plan.id === currentPlan ? <>{t('sub.activePlan')} · <em>{plan.name}</em></> : t('sub.subscribe')}
                  {process.env.REACT_APP_DEV_PAYMENTS === 'true' && plan.id !== currentPlan &&
                    <span style={{ fontSize:8, fontWeight:800, letterSpacing:'0.1em', background:'#ffffff22', borderRadius:4, padding:'2px 5px' }}>DEV</span>}
                </button>
              </div>
            );
          })}
        </div>
        {currentPlan !== 'free' && (
          <div style={{ textAlign:'center', marginTop:14 }}>
            <button onClick={() => { onUpgrade?.('free', null); onClose(); }} style={{
              background:'none', border:'none', color:C.muted, fontSize:11, cursor:'pointer',
              textDecoration:'underline', padding:4,
            }} dangerouslySetInnerHTML={{ __html: t('sub.downgradeLink') }}/>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── ITINERARY MODAL ──────────────────────────────────────────────────────────

function ItineraryModal({ dest, prefs, photo, nights, onClose }) {
  const t = useT(prefs?.appLang);
  const [text,         setText]         = useState('');
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [dayPhotoUrls, setDayPhotoUrls] = useState([]);

  useEffect(() => {
    setLoading(true); setError(''); setText(''); setDayPhotoUrls([]);
    fetch('/api/itinerary', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        destination: dest.name,
        country:     dest.country,
        region:      dest.region || '',
        type:        dest.type,
        nights,
        prefs,
      }),
    })
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setText(d.itinerary); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [dest.name]); // eslint-disable-line

  // Fetch per-day photos after itinerary text is loaded
  useEffect(() => {
    if (!text) return;
    const dayBlocks = [];
    let cur = null;
    text.split('\n').forEach(line => {
      if (line.startsWith('## ')) {
        if (cur) dayBlocks.push(cur);
        cur = { lines: [] };
      } else if (cur) {
        cur.lines.push(line);
      }
    });
    if (cur) dayBlocks.push(cur);

    const CONNECTORS = new Set(['de', 'du', 'des', 'le', 'la', 'les', 'd', 'au', 'aux', 'of', 'the', 'el', 'los']);
    const SKIP_VERBS = new Set(['visite', 'visitez', 'explorez', 'découvrez', 'promenez', 'profitez',
      'rendez', 'commencez', 'déjeunez', 'dînez', 'rejoignez', 'partez', 'flânez', 'admirez', 'assistez']);

    const extractKeyword = (lines) => {
      for (const line of lines) {
        const m = line.match(/\*\*(?:Matin|Après-midi|Soir)\*\*\s*:\s*(.+)/);
        if (!m) continue;
        const content = m[1].replace(/[*_`]/g, '').trim();
        const words = content.split(/\s+/);
        // Look for proper noun sequences (capitalized, not first word which is usually a verb)
        const startIdx = SKIP_VERBS.has(words[0]?.toLowerCase()) ? 1 : 0;
        const proper = [];
        let inProper = false;
        for (let i = startIdx; i < words.length; i++) {
          const w = words[i].replace(/[,—.:!?«»"']/g, '');
          if (/^[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜ]/.test(w) && w.length > 1) {
            inProper = true;
            proper.push(w);
            if (proper.length >= 4) break;
          } else if (inProper && CONNECTORS.has(w.toLowerCase())) {
            proper.push(w);
          } else if (inProper) {
            break;
          }
        }
        if (proper.length > 0) return proper.join(' ');
        // Fallback: skip verb and take next 3 words
        return words.slice(startIdx, startIdx + 3).join(' ') || null;
      }
      return null;
    };

    Promise.all(
      dayBlocks.map(block => {
        const kw = extractKeyword(block.lines);
        if (!kw) return Promise.resolve(null);
        return fetch(`/api/day-photo?query=${encodeURIComponent(kw)}&dest=${encodeURIComponent(dest.name)}`)
          .then(r => r.json())
          .then(d => d.url || null)
          .catch(() => null);
      })
    ).then(urls => setDayPhotoUrls(urls));
  }, [text, dest.name]);

  const downloadPdf = async () => { try {
    const { jsPDF } = await import('jspdf');
    const doc    = new jsPDF({ unit:'mm', format:'a4' });
    const pageW  = doc.internal.pageSize.getWidth();
    const pageH  = doc.internal.pageSize.getHeight();
    const margin = 14;
    const cW     = pageW - margin * 2;   // 182mm
    const photoW = Math.round(cW / 3);   // ~61mm
    const gap    = 5;
    const tW2    = cW - photoW - gap;    // ~116mm (colonne texte 2-col)
    const photoH = Math.round(photoW * 0.67); // ~41mm

    // Conversion via canvas (évite CORS car le browser a déjà chargé l'image)
    const toBase64 = (url) => new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = img.naturalWidth; c.height = img.naturalHeight;
          c.getContext('2d').drawImage(img, 0, 0);
          resolve(c.toDataURL('image/jpeg', 0.75));
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });

    const mainImgData = photo ? await toBase64(photo) : null;

    // ── Header ────────────────────────────────────────────────────────
    doc.setFillColor(10, 11, 15);
    doc.rect(0, 0, pageW, 36, 'F');
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.setTextColor(212, 175, 122);
    doc.text(dest.name, margin, 14);
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.setTextColor(160, 160, 160);
    doc.text(`${dest.country}${dest.region ? ' · ' + dest.region : ''}  ·  ${nights} nuit${nights > 1 ? 's' : ''}`, margin, 23);
    doc.setTextColor(100, 100, 100);
    doc.text('Proposition d\'itinéraire  —  Plotted', margin, 31);

    let y = 44;

    // ── Parser les jours ──────────────────────────────────────────────
    const dayBlocks = [];
    let cur = null;
    text.split('\n').forEach(line => {
      if (line.startsWith('## ')) {
        if (cur) dayBlocks.push(cur);
        cur = { title: line.replace('## ', ''), lines: [] };
      } else if (cur) {
        cur.lines.push(line);
      }
    });
    if (cur) dayBlocks.push(cur);

    // ── Helper rendu ligne ─────────────────────────────────────────────
    const renderLine = (line, x, w, curY) => {
      const clean = line.replace(/[*#`]/g, '').trim();
      if (!clean) return curY + 2;
      if (line.match(/^\*\*(Matin|Après-midi|Soir)/)) {
        doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(80, 85, 200);
      } else if (line.startsWith('💡')) {
        doc.setFontSize(7.5); doc.setFont('helvetica', 'italic'); doc.setTextColor(160, 130, 80);
      } else {
        doc.setFontSize(8.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(50, 50, 50);
      }
      const wrapped = doc.splitTextToSize(clean, w);
      if (curY + wrapped.length * 4.5 > pageH - 14) { doc.addPage(); curY = 14; }
      doc.text(wrapped, x, curY);
      return curY + wrapped.length * 4.5 + 1;
    };

    // Per-day photos (fetched from Wikipedia via /api/day-photo), fallback to main photo
    const dayImgDatas = await Promise.all(
      dayBlocks.map(async (_, idx) => {
        const url = dayPhotoUrls[idx];
        if (url) {
          const data = await toBase64(url);
          if (data) return data;
        }
        return mainImgData;
      })
    );

    // ── Rendu par jour ─────────────────────────────────────────────────
    dayBlocks.forEach((block, idx) => {
      if (y > pageH - 70) { doc.addPage(); y = 14; }
      const isLeft = idx % 2 === 0;
      const photoX = isLeft ? margin : margin + tW2 + gap;
      const textX  = isLeft ? margin + photoW + gap : margin;

      // Titre du jour
      doc.setFillColor(22, 24, 40);
      doc.rect(margin - 2, y - 5, cW + 4, 11, 'F');
      doc.setFontSize(11); doc.setFont('helvetica', 'bold');
      doc.setTextColor(212, 175, 122);
      doc.text(block.title, margin, y + 2);
      y += 12;

      // Photo du jour
      const dayImg = dayImgDatas[idx];
      if (dayImg) doc.addImage(dayImg, 'JPEG', photoX, y, photoW, photoH);
      const photoEndY = y + photoH;
      let textY = y;

      // Texte : 2 colonnes tant que dans la zone photo, pleine largeur ensuite
      block.lines.forEach(line => {
        if (!line.trim()) { textY += 2; return; }
        if (textY < photoEndY) {
          textY = renderLine(line, textX, tW2, textY);
        } else {
          textY = renderLine(line, margin, cW, textY);
        }
      });

      y = Math.max(photoEndY, textY) + 8;
    });

    doc.save(`itineraire-${dest.name.toLowerCase().replace(/\s+/g, '-')}.pdf`);
  } catch(e) { console.error('PDF error:', e); alert('Erreur PDF : ' + e.message); } };

  return createPortal(
    <div style={{
      position:'fixed', inset:0, zIndex:99999,
      background:C.bg,
      display:'flex', flexDirection:'column',
    }}>
      {/* Header fixe */}
      <div style={{
        display:'flex', alignItems:'center', gap:10,
        padding:'max(14px, env(safe-area-inset-top, 14px)) 16px 12px',
        background:C.bg, borderBottom:`1px solid ${C.border}`,
        flexShrink:0,
      }}>
        <button onClick={e => { e.stopPropagation(); onClose(); }} style={{
          background:'transparent', border:`1px solid ${C.border}`, borderRadius:20,
          color:C.accentHi, fontSize:13, fontWeight:700, padding:'7px 14px', cursor:'pointer',
        }}>{t('common.back')}</button>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:'0.15em' }}>{t('itin.header')} · {nights} {nights>1?t('common.nights'):t('common.night')}</div>
          <div style={{ fontSize:16, fontWeight:800, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{dest.name}</div>
        </div>
        {!loading && !error && text && (
          <button onClick={e => { e.stopPropagation(); downloadPdf(); }} style={{
            background:'#D4AF7A', border:'none', borderRadius:20,
            color:'#0a0b0f', fontSize:11, fontWeight:800,
            padding:'7px 13px', cursor:'pointer', whiteSpace:'nowrap',
          }}>⬇ PDF</button>
        )}
      </div>

      {/* Contenu scrollable */}
      <div style={{ flex:1, overflowY:'auto', padding:'12px 16px 40px' }}>
        {loading && (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:14, padding:'80px 0' }}>
            <div style={{ width:36, height:36, borderRadius:'50%', border:`3px solid ${C.border}`, borderTopColor:C.accentHi, animation:'spin 0.8s linear infinite' }}/>
            <div style={{ color:C.sub, fontSize:14 }}>{t('itin.loading')}</div>
            <div style={{ color:C.muted, fontSize:12 }}>{t('itin.loadingHint')}</div>
          </div>
        )}
        {error && <SubscriptionModal onClose={onClose}/>}
        {!loading && !error && <ItineraryText text={text}/>}
      </div>
    </div>,
    document.body
  );
}

const SLOT_STYLES = {
  'Matin':       { icon:'🌅', color:'#F4A928', bg:'rgba(244,169,40,0.08)' },
  'Après-midi':  { icon:'☀️',  color:'#7B9FD4', bg:'rgba(123,159,212,0.08)' },
  'Soir':        { icon:'🌙', color:'#8AD9F2', bg:'rgba(138,217,242,0.08)' },
};

function ItineraryText({ text }) {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('## ')) {
      elements.push(
        <div key={i} style={{
          display:'flex', alignItems:'center', gap:10,
          background:`linear-gradient(135deg, ${C.accent}22, ${C.accentHi}11)`,
          border:`1px solid ${C.accent}44`,
          borderRadius:14, padding:'10px 14px',
          margin:'18px 0 10px', fontWeight:900, fontSize:15, color:C.accentHi,
        }}>
          <span style={{ fontSize:18 }}>📅</span>
          {line.replace('## ', '')}
        </div>
      );
      i++; continue;
    }

    const slotMatch = Object.keys(SLOT_STYLES).find(k => line.startsWith(`**${k}`));
    if (slotMatch) {
      const st = SLOT_STYLES[slotMatch];
      const content = line.replace(/\*\*(.*?)\*\*\s*:?\s*/, '').trim();
      elements.push(
        <div key={i} style={{
          background: st.bg, border:`1px solid ${st.color}33`,
          borderLeft:`3px solid ${st.color}`,
          borderRadius:10, padding:'9px 12px', marginBottom:6,
        }}>
          <div style={{ fontSize:11, fontWeight:800, color:st.color, marginBottom:4, display:'flex', alignItems:'center', gap:5 }}>
            <span>{st.icon}</span>{slotMatch.toUpperCase()}
          </div>
          <div style={{ fontSize:13, color:C.text, lineHeight:1.55 }}
            dangerouslySetInnerHTML={{ __html: content.replace(/\*\*(.*?)\*\*/g,'<b>$1</b>').replace(/\*(.*?)\*/g,'<i>$1</i>') }}/>
        </div>
      );
      i++; continue;
    }

    if (line.startsWith('💡')) {
      const content = line.replace('💡', '').replace(/\*(.*?)\*/g,'<i>$1</i>').trim();
      elements.push(
        <div key={i} style={{
          background:'rgba(212,175,122,0.08)', border:'1px solid rgba(212,175,122,0.25)',
          borderRadius:10, padding:'8px 12px', margin:'8px 0 4px',
          fontSize:12, color:'#D4AF7A', fontStyle:'italic', lineHeight:1.5,
        }}
          dangerouslySetInnerHTML={{ __html: `💡 ${content}` }}/>
      );
      i++; continue;
    }

    if (line.trim()) {
      const bold = line.replace(/\*\*(.*?)\*\*/g,'<b>$1</b>').replace(/\*(.*?)\*/g,'<i>$1</i>');
      elements.push(<div key={i} style={{ fontSize:13, color:C.sub, lineHeight:1.6, marginBottom:2 }} dangerouslySetInnerHTML={{ __html: bold }}/>);
    }
    i++;
  }

  return <div>{elements}</div>;
}

// ─── DESTINATIONS ─────────────────────────────────────────────────────────────

const TYPE_PHOTOS = {
  ville:  'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=300&q=70',  // generic cityscape
  nature: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=300&q=70',  // mountain landscape
  ile:    'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=300&q=70',  // tropical island / beach
  cote:   'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=300&q=70',  // coastline
  event:  'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=300&q=70',  // crowd/event
};
const TC_DEST = { ville:'#7B9FD4', nature:'#6DBF8A', event:'#F4A928' };
const TE_DEST = { ville:'🏙', nature:'🌿', event:'🎭' };
const TL_DEST = { ville:'Cité', nature:'Évasion', event:'À l\'affiche' };
const EV_ICONS  = { sport:'🏆', music:'🎵', culture:'🎭', nature_event:'🌸' };
const EV_LABELS = { sport:'Sport', music:'Musique', culture:'Culture', nature_event:'Nature' };

const DEFAULT_NIGHTS = {
  ville:  { min:2, max:6 },
  nature: { min:3, max:7 },
  event:  { min:1, max:3 },
};


const PARIS = { lat: 48.8566, lng: 2.3522 };

// Retourne les coords {lat,lng} de l'aéroport de départ du user
// Priorité : departureAirports[0] > departureAirport > geoOrigin > Paris
function getOriginCoords(travelPrefs) {
  const iata = travelPrefs?.departureAirports?.[0] || travelPrefs?.departureAirport;
  if (iata) {
    const ap = DEPARTURE_AIRPORTS.find(a => a.iata === iata);
    if (ap) return { lat: ap.lat, lng: ap.lng };
  }
  if (travelPrefs?.geoOrigin?.lat) return travelPrefs.geoOrigin;
  return PARIS;
}

function getRecommendedNights(d, originCoords) {
  const base = DEFAULT_NIGHTS[d.type] || { min:2, max:5 };
  if (!d.lat || !d.lng) return base;
  const origin = originCoords || PARIS;
  const km = haversineKm(origin.lat, origin.lng, d.lat, d.lng);
  // Pente standard jusqu'à 9 000 km, puis plus agressive (+1 nuit /600 km)
  const extra = km >= 9000
    ? 5 + Math.floor((km - 9000) / 600)
    : Math.floor(Math.max(0, km - 1500) / 1500);
  return { min: base.min + extra, max: base.max + extra };
}

function localDestName(d, lang) {
  if (lang === 'en' && d.name_en) return d.name_en;
  if (lang === 'es' && d.name_es) return d.name_es;
  if (lang === 'zh' && d.name_zh) return d.name_zh;
  return d.name || d.city || '';
}
function localCountryName(d, lang) {
  if (lang === 'en' && d.country_en) return d.country_en;
  if (lang === 'es' && d.country_es) return d.country_es;
  if (lang === 'zh' && d.country_zh) return d.country_zh;
  return d.country;
}

// Mirrors the accent stripping applied to destinations.search_text in Postgres,
// so a query typed without accents ("geneve") matches "Genève".
function stripAccents(str) {
  return (str || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function AddDestModal({ sel, onAdd, onClose, appLang }) {
  const t = useT(appLang);
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [fuzzy,    setFuzzy]    = useState(false);
  const [adding,   setAdding]   = useState(null); // id being adopted
  const [addError, setAddError] = useState('');
  // Rows added during this modal session. An adopted airport city can come
  // back under a different id, so track both the row's id and the returned one.
  const [justAdded, setJustAdded] = useState([]);
  const [addedCount, setAddedCount] = useState(0);

  const isAdded = r => sel.includes(r.id) || justAdded.includes(r.id);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = stripAccents(query.trim());
    setAddError('');
    if (q.length < 2) { setResults([]); setFuzzy(false); setSearching(false); return; }

    let stale = false;
    setSearching(true);

    // Debounced: each tier is a round trip and there are up to four of them,
    // so firing on every keystroke made the list stutter while typing.
    const timer = setTimeout(async () => {
      // Tier 1 curated catalogue, tier 2 the wide airport-city list, then fuzzy
      // on each in turn. A tier runs only when the previous one found nothing,
      // so curated destinations (descriptions, photos, translations) always win.
      const curated = await supabase
        .from('destinations')
        .select('id, name, name_en, name_es, name_zh, country, country_en, country_es, country_zh, type')
        .ilike('search_text', `%${q}%`)
        .order('name')
        .limit(20);
      if (stale) return;
      if (curated.data && curated.data.length) {
        setResults(curated.data); setFuzzy(false); setSearching(false); return;
      }

      const air = await supabase.rpc('search_airport_cities', { q, lim: 12 });
      if (stale) return;
      if (air.data && air.data.length) {
        setResults(air.data.map(r => ({ ...r, _airport: true })));
        setFuzzy(false); setSearching(false); return;
      }

      const nearCurated = await supabase.rpc('search_destinations_fuzzy', { q, lim: 8 });
      if (stale) return;
      if (nearCurated.data && nearCurated.data.length) {
        setResults(nearCurated.data); setFuzzy(true); setSearching(false); return;
      }

      const nearAir = await supabase.rpc('search_airport_cities_fuzzy', { q, lim: 8 });
      if (stale) return;
      setResults((nearAir.data || []).map(r => ({ ...r, _airport: true })));
      setFuzzy(!!(nearAir.data && nearAir.data.length));
      setSearching(false);
    }, 250);

    return () => { stale = true; clearTimeout(timer); };
  }, [query]);

  // Tapping a result adds it straight away and the modal stays open, so a
  // bucket list can be built in one pass instead of reopening per city.
  // An airport city lives outside `destinations`; promote it on first use so
  // the ranking and Duffel pipeline keep reading a single table.
  const addResult = async (r) => {
    if (adding || isAdded(r)) return;
    setAddError('');
    let id = r.id;
    if (r._airport) {
      setAdding(r.id);
      const { data, error } = await supabase.rpc('adopt_airport_city', { p_id: r.id });
      setAdding(null);
      if (error || !data) { setAddError(t('dest.addModalAddFailed')); return; }
      id = data;
    }
    setJustAdded(prev => [...prev, r.id, id]);
    setAddedCount(n => n + 1);
    onAdd(id);
  };

  return createPortal(
    <div style={{
      position:'fixed', inset:0, zIndex:99999,
      display:'flex', alignItems:'center', justifyContent:'center',
      background:'rgba(0,0,0,0.7)', backdropFilter:'blur(4px)',
      padding:'20px',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width:'100%', maxWidth:340,
        background:C.bg, borderRadius:24,
        border:`1px solid ${C.border}`,
        padding:'24px 20px',
        boxShadow:'0 8px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)',
        display:'flex', flexDirection:'column', gap:14,
        maxHeight:'75vh',
      }}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:16, fontWeight:800, color:C.text }}>{t('dest.addModalTitle')}</div>
          <button onClick={onClose} style={{
            background:'none', border:'none', color:C.muted, fontSize:22,
            cursor:'pointer', lineHeight:1, padding:'0 4px',
          }}>×</button>
        </div>

        {/* Input */}
        <input
          autoFocus
          value={query}
          onChange={e => { setQuery(e.target.value); setSelected(null); }}
          placeholder={t('dest.addModalSearch')}
          style={{
            background:C.card, border:`1px solid ${C.border}`,
            borderRadius:12, padding:'12px 14px',
            color:C.text, fontSize:14, outline:'none', width:'100%',
            boxSizing:'border-box',
          }}
        />

        {/* Hint */}
        {stripAccents(query.trim()).length < 2 && (
          <div style={{ color:C.muted, fontSize:12, textAlign:'center' }}>
            {t('dest.addModalHint')}
          </div>
        )}

        {/* Résultats — "aucun résultat" only once the search has actually
            settled, otherwise it flashed on every keystroke. */}
        {searching && results.length === 0 && (
          <div style={{ color:C.muted, fontSize:13, textAlign:'center' }}>{t('dest.addModalSearching')}</div>
        )}
        {!searching && stripAccents(query.trim()).length >= 2 && results.length === 0 && (
          <div style={{ color:C.muted, fontSize:13, textAlign:'center' }}>{t('dest.addModalEmpty')}</div>
        )}
        {fuzzy && results.length > 0 && (
          <div style={{ color:C.muted, fontSize:12 }}>{t('dest.addModalDidYouMean')}</div>
        )}
        {results.length > 0 && (
          <div style={{ overflowY:'auto', display:'flex', flexDirection:'column', gap:4 }}>
            {results.map(r => {
              const added   = isAdded(r);
              const pending = adding === r.id;
              return (
                <div key={r.id} onClick={() => addResult(r)} style={{
                  padding:'10px 12px', borderRadius:10,
                  background: added ? C.accentDim : C.card,
                  border:`1px solid ${added ? C.accent : C.border}`,
                  cursor: added || pending ? 'default' : 'pointer',
                  opacity: pending ? 0.6 : 1,
                  minHeight:44, boxSizing:'border-box',
                  display:'flex', justifyContent:'space-between', alignItems:'center', gap:10,
                }}>
                  <div>
                    <div style={{ color:C.text, fontSize:13, fontWeight:600 }}>{localDestName(r, appLang)}</div>
                    <div style={{ color:C.muted, fontSize:11 }}>
                      {localCountryName(r, appLang)}
                      {r._airport && r.airport_iata ? ` · ${r.airport_iata}` : ''}
                    </div>
                  </div>
                  <div style={{ color: added ? C.accentHi : C.muted, fontSize: added ? 10 : 18, fontWeight:700, flexShrink:0 }}>
                    {pending ? '…' : added ? t('dest.addModalAdded') : '+'}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {addError && (
          <div style={{ fontSize:12, color:C.rose, textAlign:'center' }}>{addError}</div>
        )}
        <button onClick={onClose} style={{
          padding:'14px 0', borderRadius:14, fontSize:15, fontWeight:800,
          background: addedCount ? `linear-gradient(135deg,#006F87,${C.accent})` : C.card,
          border: addedCount ? 'none' : `1px solid ${C.border}`,
          color: addedCount ? '#fff' : C.muted, cursor:'pointer',
        }}>
          {addedCount
            ? t('dest.addModalDoneN').replace('{n}', addedCount)
            : t('dest.addModalClose')}
        </button>
      </div>
    </div>,
    document.body
  );
}

function NightPicker({ dest, defNights, current, onConfirm, onCancel, appLang }) {
  const t = useT(appLang);
  const initMin = current ? (typeof current === 'object' ? current.min : current) : defNights.min;
  const initMax = current ? (typeof current === 'object' ? current.max : current) : defNights.max;
  const [minN, setMinN] = useState(initMin);
  const [maxN, setMaxN] = useState(initMax);

  const Stepper = ({ label, value, onDec, onInc, atMin, atMax }) => (
    <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
      <div style={{ fontSize:10, color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.15em' }}>{label}</div>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <button onClick={onDec} style={{
          width:38, height:38, borderRadius:19, fontSize:20, lineHeight:1,
          background:C.card, border:`2px solid ${atMin ? C.border : C.accent}`,
          color: atMin ? C.muted : C.text, cursor: atMin ? 'default' : 'pointer',
        }}>−</button>
        <div style={{ textAlign:'center', minWidth:48 }}>
          <div style={{ fontSize:44, fontWeight:900, color:C.accentHi, lineHeight:1 }}>{value}</div>
          <div style={{ fontSize:11, color:C.sub, marginTop:2 }}>{t('common.nights')}</div>
        </div>
        <button onClick={onInc} style={{
          width:38, height:38, borderRadius:19, fontSize:20, lineHeight:1,
          background:C.card, border:`2px solid ${atMax ? C.border : C.accent}`,
          color: atMax ? C.muted : C.text, cursor: atMax ? 'default' : 'pointer',
        }}>+</button>
      </div>
    </div>
  );

  return createPortal(
    <div style={{
      position:'fixed', inset:0, zIndex:99999,
      display:'flex', alignItems:'center', justifyContent:'center',
      background:'rgba(0,0,0,0.7)', backdropFilter:'blur(4px)',
      padding:'20px',
    }} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={{
        width:'100%', maxWidth:340,
        background:C.bg, borderRadius:24,
        border:`1px solid ${C.border}`,
        padding:'28px 24px',
        boxShadow:'0 8px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)',
      }}>
        <div style={{ fontSize:11, color:C.accentHi, fontWeight:700, letterSpacing:'0.2em', textTransform:'uppercase', marginBottom:4 }}>{t('nightpicker.title')}</div>
        <div style={{ fontSize:20, fontWeight:800, color:C.text, marginBottom:4 }}>{dest.name}</div>
        <div style={{ fontSize:12, color:C.muted, marginBottom:24 }}>
          {t('nightpicker.recommended').replace('{min}', defNights.min).replace('{max}', defNights.max)}
        </div>

        {/* Deux steppers côte à côte */}
        <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:28 }}>
          <Stepper
            label="Min"
            value={minN}
            onDec={() => setMinN(n => Math.max(1, n - 1))}
            onInc={() => setMinN(n => { const next = n + 1; if (next > maxN) setMaxN(next); return next; })}
            atMin={minN <= 1}
            atMax={false}
          />
          <div style={{ width:1, background:C.border, alignSelf:'stretch', margin:'24px 0 0' }}/>
          <Stepper
            label="Max"
            value={maxN}
            onDec={() => setMaxN(n => { const next = n - 1; if (next < minN) setMinN(next); return Math.max(1, next); })}
            onInc={() => setMaxN(n => Math.min(30, n + 1))}
            atMin={maxN <= minN}
            atMax={maxN >= 30}
          />
        </div>

        {/* Boutons */}
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onCancel} style={{
            flex:1, padding:'14px 0', borderRadius:14, fontSize:14, fontWeight:700,
            background:C.card, border:`1px solid ${C.border}`, color:C.muted, cursor:'pointer',
          }}>{t('common.cancel')}</button>
          <button onClick={() => onConfirm({ min: minN, max: maxN })} style={{
            flex:2, padding:'14px 0', borderRadius:14, fontSize:15, fontWeight:800,
            background:`linear-gradient(135deg,#006F87,${C.accent})`, border:'none', color:'#fff', cursor:'pointer',
          }}>{t('nightpicker.generate')}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const CARD_ROTATIONS = [-2.5, 1.5, -1, 2, -1.8, 1, -2, 1.5, -0.5, 2.5, -1.5, 1.2];

function useDestPhoto(d) {
  const [photo, setPhoto] = useState(d.photo_url || DEST_PHOTOS[d.name] || TYPE_PHOTOS[d.type] || TYPE_PHOTOS.ville);
  useEffect(() => {
    if (d.photo_url || DEST_PHOTOS[d.name]) return;
    const BAD = /flag|coat|logo|blason|emblem|icon|seal|arms|wappen|shield|banner|locator|map|carte|plan\b/i;
    const enName = d.name_en || d.name;
    const tryWikidata = async (siteLang, title) => {
      try {
        const r = await fetch(
          `https://www.wikidata.org/w/api.php?action=wbgetentities&sites=${siteLang}wiki` +
          `&titles=${encodeURIComponent(title)}&props=claims&format=json&origin=*`
        );
        const data = await r.json();
        const entity = Object.values(data.entities || {}).find(e => e.id && e.id !== '-1');
        if (!entity) return false;
        const p18 = entity.claims?.P18?.[0]?.mainsnak?.datavalue?.value;
        if (!p18 || BAD.test(p18)) return false;
        const filename = encodeURIComponent(p18.replace(/ /g, '_'));
        setPhoto(`https://commons.wikimedia.org/wiki/Special:FilePath/${filename}?width=600`);
        return true;
      } catch { return false; }
    };
    const tryMediaList = async (lang, title) => {
      try {
        const r = await fetch(`https://${lang}.wikipedia.org/api/rest_v1/page/media-list/${encodeURIComponent(title)}`);
        if (!r.ok) return false;
        const data = await r.json();
        const item = (data.items || []).find(it =>
          it.type === 'image' && it.showInGallery && !BAD.test(it.title || '')
        );
        if (!item) return false;
        const srcset = item.srcset || [];
        const best = srcset[srcset.length - 1]?.src || srcset[0]?.src;
        if (!best) return false;
        setPhoto('https:' + best);
        return true;
      } catch { return false; }
    };
    tryWikidata('en', enName)
      .then(ok => ok || tryWikidata('fr', d.name))
      .then(ok => ok || tryMediaList('en', enName))
      .then(ok => ok || tryMediaList('fr', d.name));
  }, [d.name]);
  return photo;
}

function DestCard({ d, remove, userNights, defNights, onEditNights, onOpenItinerary, inPlan, planFull, onTogglePlan, appLang, rotation = 0, highlighted = false }) {
  const t = useT(appLang);
  const isEv = d.type === 'event';
  const col  = TC_DEST[d.type] || C.accentHi;
  const photo = useDestPhoto(d);

  return (
    <div style={{
      background: 'white',
      padding: '7px 7px 0',
      borderRadius: 3,
      boxShadow: highlighted
        ? '0 0 0 3px #F4A928, 6px 10px 24px rgba(0,0,0,0.55)'
        : '4px 8px 22px rgba(0,0,0,0.42)',
      transform: `rotate(${rotation}deg) scale(${highlighted ? 1.05 : 1})`,
      transition: 'transform 0.35s ease, box-shadow 0.35s ease',
      position: 'relative',
    }}>
      {/* Photo 4:3 */}
      <div style={{ width: '100%', aspectRatio: '4/3', overflow: 'hidden', position: 'relative' }}>
        <img src={photo} alt={d.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={e => { e.target.src = TYPE_PHOTOS[d.type] || TYPE_PHOTOS.ville; }}
        />
        {isEv && nextOccurrence(d.event_date, d.recurrent, d.event_end) && (
          <div style={{
            position: 'absolute', bottom: 4, left: 4,
            background: 'rgba(0,0,0,0.68)', borderRadius: 5,
            padding: '2px 5px', fontSize: 8, color: '#F4A928', fontWeight: 700,
          }}>{fmtDateDest(nextOccurrence(d.event_date, d.recurrent, d.event_end))}</div>
        )}
        <button onClick={e => { e.stopPropagation(); remove(d.id); }} style={{
          position: 'absolute', top: 5, right: 5, width: 20, height: 20, borderRadius: 10,
          background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.2)',
          color: '#ddd', fontSize: 12, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, padding: 0,
        }}>×</button>
        {inPlan && (
          <div style={{
            position: 'absolute', top: 5, left: 5,
            background: C.accent, borderRadius: 6,
            padding: '2px 5px', fontSize: 9, color: 'white', fontWeight: 800,
          }}>★</div>
        )}
      </div>
      {/* Caption — style Caveat / polaroid */}
      <div style={{ textAlign: 'center', padding: '6px 4px 2px', lineHeight: 1.15 }}>
        <div style={{
          fontFamily: "'Caveat', 'Comic Sans MS', cursive",
          fontSize: 15, fontWeight: 700, color: '#1a1a1a',
        }}>{localDestName(d, appLang)}</div>
      </div>
      {/* Boutons action */}
      <div style={{ display: 'flex', gap: 3, padding: '4px 4px 7px' }}>
        <button onClick={() => onEditNights(d, photo, false)} style={{
          flex: 1, background: 'transparent',
          border: `1px solid ${userNights ? col + '80' : '#e0e0e0'}`,
          borderRadius: 6, padding: '3px 0',
          color: userNights ? col : '#bbb',
          fontSize: 8, fontWeight: 700, cursor: 'pointer',
        }}>
          🌙 {userNights
            ? (typeof userNights === 'object'
                ? (userNights.min === userNights.max ? `${userNights.min}n` : `${userNights.min}–${userNights.max}n`)
                : `${userNights}n`)
            : `${defNights.min}–${defNights.max}n`}
        </button>
        {onTogglePlan && (
          <button
            onClick={e => { e.stopPropagation(); if (!planFull) onTogglePlan(inPlan ? d.id : d); }}
            style={{
              flex: 1, borderRadius: 6, padding: '3px 0',
              fontSize: 8, fontWeight: 800, cursor: planFull ? 'not-allowed' : 'pointer',
              border: `1px solid ${inPlan ? col + '80' : '#e0e0e0'}`,
              background: inPlan ? col + '18' : 'transparent',
              color: inPlan ? col : planFull ? '#ccc' : '#999',
            }}
          >
            {inPlan ? t('dest.planIn') : planFull ? t('dest.planFull') : t('dest.planAdd')}
          </button>
        )}
      </div>
    </div>
  );
}

function FeaturedCard({ dest, badge, color, onPress, appLang, rotation = 0 }) {
  const photo = DEST_PHOTOS[dest.name] || TYPE_PHOTOS[dest.type] || TYPE_PHOTOS.ville;
  return (
    <button onClick={onPress} style={{
      flexShrink: 0, width: 120,
      background: 'white', padding: '5px 5px 0',
      borderRadius: 3, border: 'none', cursor: 'pointer',
      boxShadow: '4px 6px 18px rgba(0,0,0,0.40)',
      transform: `rotate(${rotation}deg)`,
    }}>
      <div style={{ width: '100%', aspectRatio: '4/3', overflow: 'hidden', position: 'relative' }}>
        <img src={photo} alt={dest.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '3px 5px 4px',
          background: color + 'dd',
          fontSize: 8, fontWeight: 700, color: 'white', lineHeight: 1.3,
        }}>{badge}</div>
      </div>
      <div style={{ textAlign: 'center', padding: '5px 3px 6px' }}>
        <div style={{
          fontFamily: "'Caveat', 'Comic Sans MS', cursive",
          fontSize: 13, fontWeight: 700, color: '#1a1a1a', lineHeight: 1,
        }}>{localDestName(dest, appLang)}</div>
      </div>
    </button>
  );
}

// Next date an event happens, or null if it is a one-off that is already over.
// endIso (event_end) keeps multi-day events visible until their last day.
function nextOccurrence(iso, recurrent, endIso) {
  if (!iso) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  if (!recurrent) {
    const last = new Date((endIso || iso) + 'T12:00:00');
    return last < today ? null : iso;
  }
  const d = new Date(iso + 'T12:00:00');
  while (d < today) d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().split('T')[0];
}

function fmtDateDest(iso) {
  if (!iso) return '';
  return new Date(iso + 'T12:00:00').toLocaleDateString('fr-FR', { day:'numeric', month:'short', year:'numeric' });
}

function DestListAccordion({ sorted, planIds, planLimit, travelPrefs, lang, onTogglePlan, onAddModal, t, cols = 1 }) {
  const tr = useT(travelPrefs?.appLang);
  const originCoords = getOriginCoords(travelPrefs);
  const GROUPS = [
    { key:'weekend',   label: tr('home.groupWeekend'),   test: min => min < 4 },
    { key:'citybreak', label: tr('home.groupCitybreak'),  test: min => min >= 4 && min < 8 },
    { key:'voyage',    label: tr('home.groupVoyage'),     test: min => min >= 8 },
  ];
  const [open, setOpen] = useState({ weekend:true, citybreak:true, voyage:true });

  const grouped = GROUPS.map(g => ({
    ...g,
    dests: sorted.filter(d => {
      const min = getRecommendedNights(d, originCoords).min;
      return g.test(min);
    }).sort((a, b) => (planIds?.includes(b.id) ? 1 : 0) - (planIds?.includes(a.id) ? 1 : 0)),
  })).filter(g => g.dests.length > 0);

  let rowIdx = 0;
  return (
    <div style={{ padding:'0 16px 100px' }}>
      {grouped.map(g => (
        <div key={g.key} style={{ marginBottom:4 }}>
          {/* Header accordéon */}
          <button onClick={() => setOpen(o => ({ ...o, [g.key]: !o[g.key] }))} style={{
            width:'100%', display:'flex', alignItems:'center', justifyContent:'center',
            padding:'10px 0 8px', background:'none', border:'none', borderBottom:`1px solid ${C.border}`,
            cursor:'pointer', marginBottom: open[g.key] ? 0 : 4, position:'relative',
          }}>
            <span style={{ fontSize:12, fontWeight:700, color:C.muted, letterSpacing:'0.5px', textTransform:'uppercase' }}>
              {g.key === 'voyage' ? <em style={{ fontStyle:'italic', textTransform:'none' }}>{g.label}</em> : g.label}
              {' '}<span style={{ fontWeight:400, opacity:0.7 }}>({g.dests.length})</span>
            </span>
            <span style={{ color:C.muted, fontSize:12, position:'absolute', right:0, transition:'transform 0.2s', display:'inline-block', transform: open[g.key] ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▾</span>
          </button>
          {/* Contenu */}
          {open[g.key] && (cols === 2 ? (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 8px', padding:'4px 0' }}>
              {g.dests.map(d => {
                const idx = rowIdx++;
                return <DestListRow key={d.id} d={d} idx={idx} planIds={planIds} planLimit={planLimit}
                  travelPrefs={travelPrefs} lang={lang} onTogglePlan={onTogglePlan} compact/>;
              })}
            </div>
          ) : g.dests.map(d => {
            const idx = rowIdx++;
            return <DestListRow key={d.id} d={d} idx={idx} planIds={planIds} planLimit={planLimit}
              travelPrefs={travelPrefs} lang={lang} onTogglePlan={onTogglePlan}/>;
          }))}
        </div>
      ))}
      <button onClick={onAddModal} style={{
        marginTop:12, width:'100%', padding:'12px', borderRadius:12,
        border:`2px dashed ${C.border}`, background:'transparent',
        color:C.muted, fontSize:13, fontWeight:600, cursor:'pointer',
      }}>+ {t('dest.add')}</button>
    </div>
  );
}

function DestListRow({ d, idx, planIds, planLimit, travelPrefs, lang, onTogglePlan, compact = false }) {
  const photo   = useDestPhoto(d);
  const rec     = getRecommendedNights(d, getOriginCoords(travelPrefs));
  const userN   = travelPrefs?.destNights?.[d.id];
  const nights  = userN != null ? `${userN}n` : `${rec.min}–${rec.max}n`;
  const inPlan  = planIds?.includes(d.id) ?? false;
  const planFull = !inPlan && (planIds?.length ?? 0) >= planLimit;
  const rot     = CARD_ROTATIONS[idx % CARD_ROTATIONS.length];
  const dname   = lang === 'en' ? (d.name_en || d.name) : lang === 'es' ? (d.name_es || d.name) : lang === 'zh' ? (d.name_zh || d.name) : d.name;

  if (compact) {
    return (
      <div style={{
        display:'flex', alignItems:'center', gap:10, padding:'10px 0',
        borderBottom:`1px solid ${C.border}`,
        animation:'destCardIn 0.3s ease both', animationDelay:`${idx*0.04}s`,
      }}>
        <div style={{ flexShrink:0, background:'#fff', padding:'2px 2px 7px', borderRadius:2, transform:`rotate(${rot}deg)`, boxShadow:'2px 3px 8px rgba(0,0,0,0.35)', width:52 }}>
          <img src={photo} alt={dname} style={{ width:48, height:36, objectFit:'cover', display:'block' }}/>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:12, fontWeight:700, color:C.text, lineHeight:1.3 }}>{dname}</div>
          <div style={{ fontSize:11, color:C.muted, display:'flex', gap:5, alignItems:'center', marginTop:2, flexWrap:'wrap' }}>
            <span>{nights}</span>
            {inPlan && <span style={{ color:C.accentHi, fontSize:9, fontWeight:700, background:C.accentDim, borderRadius:5, padding:'1px 5px' }}>Plan</span>}
          </div>
        </div>
        <button onClick={() => onTogglePlan(inPlan ? d.id : d)} style={{
          flexShrink:0, width:24, height:24, borderRadius:12,
          border:`1.5px solid ${inPlan ? C.accent : C.border}`,
          background: inPlan ? C.accentDim : 'transparent',
          color: inPlan ? C.accentHi : C.muted,
          cursor: 'pointer',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:12,
        }}>{inPlan ? '✓' : planFull ? '↑' : '+'}</button>
      </div>
    );
  }

  return (
    <div style={{
      display:'flex', alignItems:'center', gap:12, padding:'10px 0',
      borderBottom:`1px solid ${C.border}`,
      animation:'destCardIn 0.3s ease both', animationDelay:`${idx*0.04}s`,
    }}>
      <div style={{ flexShrink:0, background:'#fff', padding:'2px 2px 7px', borderRadius:2, transform:`rotate(${rot}deg)`, boxShadow:'2px 3px 8px rgba(0,0,0,0.35)', width:52 }}>
        <img src={photo} alt={dname} style={{ width:48, height:36, objectFit:'cover', display:'block' }}/>
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{dname}</div>
        <div style={{ fontSize:12, color:C.muted, display:'flex', gap:6, alignItems:'center', marginTop:2 }}>
          <span>{nights}</span>
          {inPlan && <span style={{ color:C.accentHi, fontSize:10, fontWeight:700, background:C.accentDim, borderRadius:6, padding:'1px 6px' }}>Plan</span>}
        </div>
      </div>
      <button onClick={() => onTogglePlan(inPlan ? d.id : d)} style={{
        flexShrink:0, width:28, height:28, borderRadius:14,
        border:`1.5px solid ${inPlan ? C.accent : C.border}`,
        background: inPlan ? C.accentDim : 'transparent',
        color: inPlan ? C.accentHi : C.muted,
        cursor: 'pointer',
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:14,
      }}>{inPlan ? '✓' : '+'}</button>
    </div>
  );
}

function Destinations({ sel, setSel, onExplore, onGoToMap, travelPrefs, onEditNights, onOpenItinerary, planIds, planLimit, onTogglePlan, autoHelp, onHelpSeen, onUpgrade }) {
  const t = useT(travelPrefs?.appLang);
  const lang = travelPrefs?.appLang || 'fr';
  const originCoords = getOriginCoords(travelPrefs);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showHelp,    setShowHelp]    = useState(!!autoHelp);
  const [viewMode,    setViewMode]    = useState('grid'); // 'grid' | 'list'
  const [gridOpen,    setGridOpen]    = useState({ weekend:true, citybreak:true, voyage:true });
  const dismissHelp = () => { setShowHelp(false); if (onHelpSeen) onHelpSeen(); };
  const [dests,      setDests]      = useState([]);
  const [filter,     setFilter]     = useState('all');
  const [addModal,   setAddModal]   = useState(false);
  const [rouletteId, setRouletteId] = useState(null);
  const rouletteTimer = useRef(null);

  useEffect(() => {
    if (!sel.length) { setDests([]); return; }
    supabase
      .from('destinations')
      .select('*')
      .in('id', sel)
      .then(({ data }) => { setDests(data || []); });
  }, [sel]);

  useEffect(() => () => clearTimeout(rouletteTimer.current), []);

  const spinRoulette = () => {
    if (!dests.length) return;
    const pick = dests[Math.floor(Math.random() * dests.length)];
    setRouletteId(pick.id);
    clearTimeout(rouletteTimer.current);
    rouletteTimer.current = setTimeout(() => setRouletteId(null), 3500);
  };

  const remove = (id) => {
    setSel(p => p.filter(x => x !== id));
    if (planIds?.includes(id)) onTogglePlan(id);
  };

  const destMin = d => getRecommendedNights(d, originCoords).min;
  const FILTERS = [
    ['all',       t('dest.filterAll'),           () => true],
    ['weekend',   t('home.groupWeekend'),         d => destMin(d) < 4],
    ['citybreak', t('home.groupCitybreak'),       d => destMin(d) >= 4 && destMin(d) < 8],
    ['voyage',    t('home.groupVoyage'),          d => destMin(d) >= 8],
  ];

  const filtered = dests.filter(d => {
    const f = FILTERS.find(([k]) => k === filter);
    return f ? f[2](d) : true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (a.type === 'event' && b.type === 'event') {
      const da = nextOccurrence(a.event_date, a.recurrent, a.event_end) || '9999';
      const db = nextOccurrence(b.event_date, b.recurrent, b.event_end) || '9999';
      return new Date(da) - new Date(db);
    }
    return a.name.localeCompare(b.name);
  });

  // Stats bar
  const countries  = new Set(dests.map(d => d.country)).size;
  const eventCount = dests.filter(d => d.type === 'event').length;
  const cityCount  = dests.filter(d => d.type !== 'event').length;

  // Featured strip
  const PARIS_LAT = 48.8566, PARIS_LNG = 2.3522;
  const farthest = dests.length > 0
    ? dests.reduce((best, d) =>
        haversineKm(PARIS_LAT, PARIS_LNG, d.lat, d.lng) > haversineKm(PARIS_LAT, PARIS_LNG, best.lat, best.lng) ? d : best,
        dests[0])
    : null;
  const nextEvent = [...dests]
    .filter(d => d.type === 'event' && nextOccurrence(d.event_date, d.recurrent, d.event_end))
    .sort((a, b) => (nextOccurrence(a.event_date, a.recurrent, a.event_end) || '').localeCompare(nextOccurrence(b.event_date, b.recurrent, b.event_end) || ''))[0] || null;
  const lastAdded = dests.find(d => d.id === sel[sel.length - 1]) || null;

  const FEAT_LABELS = {
    far:     { fr: '🌏 La plus lointaine', en: '🌏 Farthest away', es: '🌏 La más lejana', zh: '🌏 最远目的地' },
    nextEv:  { fr: '📅 Prochain événement', en: '📅 Next event', es: '📅 Próximo evento', zh: '📅 下个活动' },
    lastAdd: { fr: '✨ Dernière ajoutée', en: '✨ Recently added', es: '✨ Añadida', zh: '✨ 最近添加' },
  };
  const featured = [
    farthest && { dest: farthest, badge: (FEAT_LABELS.far[lang]    || FEAT_LABELS.far.fr),    color: '#5B8CDE', rotation: -2   },
    nextEvent && { dest: nextEvent, badge: (FEAT_LABELS.nextEv[lang] || FEAT_LABELS.nextEv.fr), color: '#F4A928', rotation:  1.5 },
    lastAdded && lastAdded.id !== farthest?.id && lastAdded.id !== nextEvent?.id
      && { dest: lastAdded, badge: (FEAT_LABELS.lastAdd[lang] || FEAT_LABELS.lastAdd.fr), color: '#6DBF8A', rotation: -1 },
  ].filter(Boolean);

  const ViewToggle = () => (
    <div style={{ display:'flex', gap:4 }}>
      {[
        ['grid', <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="0" width="6" height="6" rx="1"/><rect x="8" y="0" width="6" height="6" rx="1"/><rect x="0" y="8" width="6" height="6" rx="1"/><rect x="8" y="8" width="6" height="6" rx="1"/></svg>],
        ['list',  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="0" width="3" height="5" rx="1"/><rect x="4" y="0" width="3" height="5" rx="1"/><rect x="0" y="7" width="3" height="5" rx="1"/><rect x="4" y="7" width="3" height="5" rx="1"/><rect x="8" y="1" width="6" height="1.5" rx="0.5"/><rect x="8" y="3" width="4" height="1.5" rx="0.5"/><rect x="8" y="8" width="6" height="1.5" rx="0.5"/><rect x="8" y="10" width="4" height="1.5" rx="0.5"/></svg>],
        ['map',   <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"><polygon points="1,2 5,4 9,2 13,4 13,12 9,10 5,12 1,10"/><line x1="5" y1="4" x2="5" y2="12"/><line x1="9" y1="2" x2="9" y2="10"/></svg>],
      ].map(([mode, icon]) => (
        <button key={mode} onClick={() => mode === 'map' ? onGoToMap?.() : setViewMode(mode)} style={{
          width:30, height:30, borderRadius:8, border:`1.5px solid ${viewMode===mode&&mode!=='map' ? C.accent : C.border}`,
          background: viewMode===mode&&mode!=='map' ? C.accentDim : 'transparent',
          color: viewMode===mode&&mode!=='map' ? C.accentHi : C.muted,
          cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
        }}>{icon}</button>
      ))}
    </div>
  );

  const hero = (
    <div style={{ padding: 'calc(env(safe-area-inset-top, 0px) + 16px) 20px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h2 style={{ ...titleStyle, margin: 0 }}>{t('dest.title')}</h2>
        <button onClick={() => setShowHelp(true)} style={{
          width: 20, height: 20, borderRadius: '50%', border: `1.5px solid ${C.border}`,
          background: 'transparent', color: C.muted, fontSize: 11, fontWeight: 800,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, lineHeight: 1,
        }}>?</button>
      </div>
      {dests.length > 0 && <ViewToggle />}
    </div>
  );

  const filterCounts = Object.fromEntries(
    FILTERS.map(([k, , test]) => [k, k === 'all' ? dests.length : dests.filter(test).length])
  );

  const helpModal = showHelp && createPortal(
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onClick={dismissHelp}>
      <div onClick={e => e.stopPropagation()} style={{
        background:C.card, borderRadius:'20px 20px 0 0', padding:'24px 24px 40px',
        width:'100%', maxWidth:600, borderTop:`1px solid ${C.border}`,
      }}>
        <div style={{ width:36, height:4, borderRadius:2, background:C.border, margin:'0 auto 20px' }}/>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
          <div style={{ width:32, height:32, borderRadius:16, background:C.accentDim,
            display:'flex', alignItems:'center', justifyContent:'center',
            color:C.accentHi, fontSize:16, fontWeight:800 }}>?</div>
          <span style={{ color:C.text, fontSize:16, fontWeight:800 }}>{t('common.howItWorks')}</span>
        </div>
        <p style={{ color:C.sub, fontSize:14, lineHeight:1.7, margin:0 }}>
          {t('dest.helpText').replace('{n}', planLimit)}
        </p>
        <button onClick={dismissHelp} style={{
          marginTop:20, width:'100%', padding:'13px', borderRadius:14,
          background:C.accent, border:'none', color:C.white, fontSize:14, fontWeight:700, cursor:'pointer',
        }}>{t('common.understood')}</button>
      </div>
    </div>,
    document.body
  );

  if (!sel.length) {
    return (
      <Scroll>
        {hero}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'60px 32px', gap:20, textAlign:'center' }}>
          <div style={{ fontSize:52 }}>🌍</div>
          <div style={{ color:C.text, fontSize:20, fontWeight:800, letterSpacing:'-0.5px' }}>{t('dest.emptyTitle')}</div>
          <div style={{ color:C.sub, fontSize:13, lineHeight:1.6 }}>
            {t('dest.emptySub')}
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => setAddModal(true)} style={{ ...btnPrimary, width:'auto', padding:'14px 28px' }}>
              + {t('dest.add')}
            </button>
            <button onClick={onExplore} style={{ ...btnSec, width:'auto', padding:'14px 28px' }}>
              {t('dest.explore')}
            </button>
          </div>
        </div>
        {addModal && <AddDestModal sel={sel} onAdd={id => setSel(p => [...p, id])} onClose={() => setAddModal(false)} appLang={travelPrefs?.appLang}/>}
        {helpModal}
      </Scroll>
    );
  }

  return (
    <Scroll>
      <style>{`
        @keyframes destCardIn {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      {hero}


      {/* Filtres par durée — mêmes groupes que l'accordéon */}
      <div style={{ display:'flex', gap:6, padding:'0 16px 10px', overflowX:'auto', scrollbarWidth:'none' }}>
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} style={{
            padding:'7px 14px', borderRadius:20, fontSize:12, fontWeight:600,
            background: filter===k ? C.accent : C.card,
            color: filter===k ? C.white : C.sub,
            border: `1px solid ${filter===k ? C.accent : C.border}`,
            cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
          }}>
            {k === 'voyage' ? <em style={{ fontStyle:'italic' }}>{l}</em> : l}
            {' '}<span style={{ fontWeight:400, opacity:0.8 }}>({filterCounts[k] ?? 0})</span>
          </button>
        ))}
      </div>

      {/* Plan actif */}
      {planIds != null && (
        <div style={{ margin:'0 16px 14px', ...card({ padding:'12px 16px' }) }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <span style={{ color:C.text, fontSize:13, fontWeight:700 }}>
              {t('dest.activePlan')} · <em style={{ fontWeight:400, color:C.accentHi }}>{travelPrefs?.plan === 'gold' ? t('sub.planGold') : travelPrefs?.plan === 'silver' ? t('sub.planSilver') : t('sub.planFree')}</em>
            </span>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ color: planIds.length >= planLimit ? C.gold : C.accentHi, fontSize:12, fontWeight:700 }}>
                {planIds.length} / {planLimit}
              </span>
              <button onClick={() => setShowUpgrade(true)} style={{
                background:`linear-gradient(135deg,${C.gold},#fb923c)`,
                border:'none', borderRadius:20, padding:'4px 10px',
                color:'#fff', fontSize:11, fontWeight:800, cursor:'pointer',
              }}>⬆ Upgrade</button>
            </div>
          </div>
          <div style={{ height:5, background:C.border, borderRadius:3, overflow:'hidden' }}>
            <div style={{ height:'100%', borderRadius:3, transition:'width .3s ease',
              width:`${Math.min(100, planIds.length / planLimit * 100)}%`,
              background: planIds.length >= planLimit
                ? `linear-gradient(90deg,${C.gold},#fb923c)`
                : `linear-gradient(90deg,${C.accent},${C.accentHi})`,
            }}/>
          </div>
          {planIds.length === 0 && (
            <div style={{ color:C.muted, fontSize:11, marginTop:6 }}>
              {t('dest.planEmpty').replace('{n}', planLimit)}
            </div>
          )}
        </div>
      )}

      {/* Grille 2 colonnes — style polaroid, groupée par durée */}
      {viewMode === 'grid' && (() => {
        const GROUPS = [
          { key:'weekend',   label: t('home.groupWeekend'),   test: min => min < 4 },
          { key:'citybreak', label: t('home.groupCitybreak'),  test: min => min >= 4 && min < 8 },
          { key:'voyage',    label: t('home.groupVoyage'),     test: min => min >= 8 },
        ];
        const grouped = GROUPS.map(g => ({
          ...g,
          dests: sorted.filter(d => g.test(getRecommendedNights(d, originCoords).min))
            .sort((a, b) => (planIds?.includes(b.id) ? 1 : 0) - (planIds?.includes(a.id) ? 1 : 0)),
        })).filter(g => g.dests.length > 0);
        let cardIdx = 0;
        return (
          <div style={{ padding:'0 20px 100px' }}>
            {grouped.map(g => (
              <div key={g.key} style={{ marginBottom:4 }}>
                <button onClick={() => setGridOpen(o => ({ ...o, [g.key]: !o[g.key] }))} style={{
                  width:'100%', display:'flex', alignItems:'center', justifyContent:'center',
                  padding:'10px 0 8px', background:'none', border:'none', borderBottom:`1px solid ${C.border}`,
                  cursor:'pointer', marginBottom: gridOpen[g.key] ? 0 : 4, position:'relative',
                }}>
                  <span style={{ fontSize:12, fontWeight:700, color:C.muted, letterSpacing:'0.5px', textTransform:'uppercase' }}>
                    {g.key === 'voyage' ? <em style={{ fontStyle:'italic', textTransform:'none' }}>{g.label}</em> : g.label}
                    {' '}<span style={{ fontWeight:400, opacity:0.7 }}>({g.dests.length})</span>
                  </span>
                  <span style={{ color:C.muted, fontSize:12, position:'absolute', right:0, transition:'transform 0.2s', display:'inline-block', transform: gridOpen[g.key] ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▾</span>
                </button>
                {gridOpen[g.key] && (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:22, padding:'12px 0 4px' }}>
                    {g.dests.map(d => {
                      const idx        = cardIdx++;
                      const defNights  = getRecommendedNights(d, originCoords);
                      const userNights = travelPrefs?.destNights?.[d.id] ?? null;
                      const inPlan     = planIds?.includes(d.id) ?? false;
                      const planFull   = !inPlan && (planIds?.length ?? 0) >= planLimit;
                      return (
                        <div key={d.id} style={{ animation:'destCardIn 0.4s ease both', animationDelay:`${idx * 0.06}s` }}>
                          <DestCard d={d} remove={remove}
                            userNights={userNights} defNights={defNights}
                            onEditNights={onEditNights}
                            onOpenItinerary={onOpenItinerary}
                            inPlan={inPlan} planFull={planFull}
                            onTogglePlan={onTogglePlan}
                            appLang={travelPrefs?.appLang}
                            rotation={CARD_ROTATIONS[idx % CARD_ROTATIONS.length]}
                            highlighted={rouletteId === d.id}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
            {/* Bouton ajouter */}
            <button onClick={() => setAddModal(true)} style={{
              marginTop:12, width:'100%', padding:'12px', borderRadius:12,
              border:`2px dashed ${C.border}`,
              background:'transparent', cursor:'pointer',
              display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6,
            }}>
              <div style={{ fontSize:22, color:C.muted }}>+</div>
              <div style={{ fontSize:10, color:C.muted, fontWeight:600 }}>{t('dest.add')}</div>
            </button>
          </div>
        );
      })()}

      {/* Vue liste — accordéon par durée */}
      {viewMode === 'list' && (
        <DestListAccordion
          sorted={sorted} planIds={planIds} planLimit={planLimit}
          travelPrefs={travelPrefs} lang={lang} onTogglePlan={onTogglePlan}
          onAddModal={() => setAddModal(true)} t={t} cols={2}
        />
      )}

      {addModal && <AddDestModal sel={sel} onAdd={id => setSel(p => [...p, id])} onClose={() => setAddModal(false)} appLang={travelPrefs?.appLang}/>}
      {showUpgrade && <SubscriptionModal appLang={travelPrefs?.appLang} currentPlan={travelPrefs?.plan||'free'} onUpgrade={onUpgrade} prefillEmail={travelPrefs?.email||''} onClose={() => setShowUpgrade(false)}/>}
      {helpModal}
    </Scroll>
  );
}


// ─── CALENDAR ─────────────────────────────────────────────────────────────────

function AddSlotButton({ onAdd, slotFull, t, locale = 'fr-FR' }) {
  const [open, setOpen]       = useState(false);
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');
  const [hovered, setHovered] = useState('');
  const [monthOffset, setMonthOffset] = useState(0);

  const today = new Date();
  today.setHours(0,0,0,0);

  const fmtDate = iso => iso ? new Date(iso + 'T12:00:00').toLocaleDateString(locale, { weekday:'short', day:'numeric', month:'short' }) : '';

  const displayMonth = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const mDays  = new Date(displayMonth.getFullYear(), displayMonth.getMonth()+1, 0).getDate();
  const mFirst = (displayMonth.getDay() + 6) % 7;
  const mName  = displayMonth.toLocaleDateString(locale, { month:'long', year:'numeric' });

  const toIso = d => d.toISOString().split('T')[0];

  const handleDay = (iso) => {
    if (!from || (from && to)) {
      setFrom(iso); setTo('');
    } else {
      if (iso < from) { setTo(from); setFrom(iso); }
      else { setTo(iso); }
    }
  };

  const inRange = (iso) => {
    const end = to || hovered;
    if (!from || !end) return false;
    const a = from < end ? from : end;
    const b = from < end ? end : from;
    return iso > a && iso < b;
  };

  const handleAdd = () => {
    if (!from || !to) return;
    const dep = new Date(from + 'T12:00:00');
    const ret = new Date(to + 'T12:00:00');
    if (ret <= dep) return;
    const nights = Math.round((ret - dep) / 86400000);
    const depDay = dep.getDay();
    const retDay = ret.getDay();
    const hasWeekend = depDay===6||depDay===0||retDay===6||retDay===0||nights<4;
    const type = nights<=2&&hasWeekend ? 'WE' : nights===3&&hasWeekend ? 'longWE' : nights<=7 ? 'short' : 'long';
    onAdd({
      id:        `manual_${from}`,
      label:     dep.toLocaleDateString(locale, {day:'numeric', month:'long'}),
      from:      dep.toLocaleDateString(locale, {weekday:'short'}) + ' ' + dep.toLocaleDateString(locale, {day:'numeric', month:'long'}),
      to:        ret.toLocaleDateString(locale, {weekday:'short'}) + ' ' + ret.toLocaleDateString(locale, {day:'numeric', month:'long'}),
      departIso: from,
      returnIso: to,
      type,
      nights,
      source: 'manual',
    });
    setFrom(''); setTo(''); setOpen(false);
  };

  return (
    <>
      <button onClick={()=>{ if (!slotFull) setOpen(true); }} disabled={slotFull} style={{...btnSec,width:"100%",marginTop:4,opacity:slotFull?0.5:1}}>{slotFull ? t('cal.slotLimitReached') : t('cal.addSlot')}</button>
      {open && createPortal(
        <div style={{position:'fixed',inset:0,zIndex:99999,display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
          <div onClick={()=>{setOpen(false);setFrom('');setTo('');}} style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.5)'}}/>
          <div style={{position:'relative',background:C.card,borderRadius:'20px 20px 0 0',padding:'20px 20px 36px',maxHeight:'85vh',overflowY:'auto'}}>
            {/* Handle */}
            <div style={{width:40,height:4,borderRadius:2,background:C.border,margin:'0 auto 16px'}}/>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
              <div style={{color:C.text,fontSize:15,fontWeight:700}}>
                {!from ? t('cal.chooseDep') : !to ? t('cal.chooseRet') : `${fmtDate(from)} → ${fmtDate(to)}`}
              </div>
              <div onClick={()=>{setOpen(false);setFrom('');setTo('');}} style={{color:C.muted,cursor:"pointer",fontSize:20,lineHeight:1}}>✕</div>
            </div>

            {/* Month navigation */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <div onClick={()=>setMonthOffset(m=>m-1)} style={{width:36,height:36,borderRadius:18,background:C.border,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:C.text,fontSize:18}}>‹</div>
              <div style={{color:C.text,fontSize:14,fontWeight:600,textTransform:"capitalize"}}>{mName}</div>
              <div onClick={()=>setMonthOffset(m=>m+1)} style={{width:36,height:36,borderRadius:18,background:C.border,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:C.text,fontSize:18}}>›</div>
            </div>

            {/* Calendar grid */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:16}}>
              {["L","M","M","J","V","S","D"].map((d,i)=>(
                <div key={i} style={{color:C.muted,fontSize:10,textAlign:"center",paddingBottom:4}}>{d}</div>
              ))}
              {Array(mFirst).fill(null).map((_,i)=><div key={`e${i}`}/>)}
              {Array.from({length:mDays},(_,i)=>i+1).map(n=>{
                const d = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), n);
                const iso = toIso(d);
                const isPast = d < today;
                const isFrom = iso === from;
                const isTo   = iso === to;
                const isIn   = inRange(iso);
                const bg = isFrom||isTo ? C.accent : isIn ? C.accentDim : 'transparent';
                const color = isFrom||isTo ? C.white : isIn ? C.accentHi : isPast ? C.muted : C.text;
                return (
                  <div key={n}
                    onClick={()=>!isPast && handleDay(iso)}
                    onMouseEnter={()=>setHovered(iso)}
                    onMouseLeave={()=>setHovered('')}
                    style={{height:36,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:8,background:bg,color,fontSize:13,fontWeight:isFrom||isTo?700:400,cursor:isPast?"default":"pointer",opacity:isPast?0.3:1}}>
                    {n}
                  </div>
                );
              })}
            </div>

            {from && to && (
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{setFrom('');setTo('');}} style={{...btnSec,flex:1}}>{t('cal.reset')}</button>
                <button onClick={handleAdd} style={{...btnPrimary,flex:1}}>{t('cal.addThisSlot')}</button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

function EditSlotModal({ slot, onSave, onDelete, onClose, t, locale = 'fr-FR' }) {
  const today = new Date(); today.setHours(0,0,0,0);
  const toIso = d => d.toISOString().split('T')[0];
  const fmtDate = iso => iso ? new Date(iso + 'T12:00:00').toLocaleDateString(locale, { weekday:'short', day:'numeric', month:'short' }) : '';

  const initOffset = () => {
    const dep = new Date(slot.departIso + 'T12:00:00');
    return (dep.getFullYear() - today.getFullYear()) * 12 + (dep.getMonth() - today.getMonth());
  };

  const [from,         setFrom]         = useState(slot.departIso);
  const [to,           setTo]           = useState(slot.returnIso);
  const [hovered,      setHovered]      = useState('');
  const [monthOffset,  setMonthOffset]  = useState(initOffset);

  const displayMonth = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const mDays  = new Date(displayMonth.getFullYear(), displayMonth.getMonth()+1, 0).getDate();
  const mFirst = (displayMonth.getDay() + 6) % 7;
  const mName  = displayMonth.toLocaleDateString(locale, { month:'long', year:'numeric' });

  const handleDay = (iso) => {
    if (!from || (from && to)) { setFrom(iso); setTo(''); }
    else { if (iso < from) { setTo(from); setFrom(iso); } else setTo(iso); }
  };
  const inRange = (iso) => {
    const end = to || hovered;
    if (!from || !end) return false;
    const a = from < end ? from : end;
    const b = from < end ? end : from;
    return iso > a && iso < b;
  };

  const handleSave = () => {
    if (!from || !to) return;
    const dep = new Date(from + 'T12:00:00');
    const ret = new Date(to + 'T12:00:00');
    if (ret <= dep) return;
    const nights = Math.round((ret - dep) / 86400000);
    const depDay = dep.getDay(), retDay = ret.getDay();
    const hasWeekend = depDay===6||depDay===0||retDay===6||retDay===0||nights<4;
    const type = nights<=2&&hasWeekend ? 'WE' : nights===3&&hasWeekend ? 'longWE' : nights<=7 ? 'short' : 'long';
    onSave({
      id:        `manual_${from}`,
      label:     dep.toLocaleDateString(locale, {day:'numeric', month:'long'}),
      from:      dep.toLocaleDateString(locale, {weekday:'short'}) + ' ' + dep.toLocaleDateString(locale, {day:'numeric', month:'long'}),
      to:        ret.toLocaleDateString(locale, {weekday:'short'}) + ' ' + ret.toLocaleDateString(locale, {day:'numeric', month:'long'}),
      departIso: from, returnIso: to, type, nights,
      source: 'manual',
      holiday: slot.holiday,
    });
  };

  return createPortal(
    <div style={{ position:'fixed', inset:0, zIndex:99999,
      display:'flex', alignItems:'center', justifyContent:'center',
      background:'rgba(0,0,0,0.75)', backdropFilter:'blur(4px)', padding:'20px',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width:'100%', maxWidth:340, background:C.bg, borderRadius:24,
        border:`1px solid ${C.border}`, padding:'24px 20px',
        boxShadow:'0 8px 60px rgba(0,0,0,0.6)',
      }}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:11, color:C.accentHi, fontWeight:700, letterSpacing:'0.2em', textTransform:'uppercase' }}>
              {slot.source === 'manual' ? t('cal.editSlot') : t('cal.customSlot')}
            </div>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, marginTop:2 }}>
              {slot.holiday ? slot.holiday : slot.label}
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:C.muted, fontSize:22, cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        {/* Date sélectionnée */}
        <div style={{ color:C.sub, fontSize:12, marginBottom:10, minHeight:18 }}>
          {!from ? t('cal.selectDep') : !to ? t('cal.selectRet') : `${fmtDate(from)} → ${fmtDate(to)}`}
        </div>

        {/* Navigation mois */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div onClick={() => setMonthOffset(m=>m-1)} style={{ width:32, height:32, borderRadius:16, background:C.border, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:C.text }}>‹</div>
          <div style={{ color:C.text, fontSize:13, fontWeight:600, textTransform:'capitalize' }}>{mName}</div>
          <div onClick={() => setMonthOffset(m=>m+1)} style={{ width:32, height:32, borderRadius:16, background:C.border, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:C.text }}>›</div>
        </div>

        {/* Grille calendrier */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, marginBottom:16 }}>
          {['L','M','M','J','V','S','D'].map((d,i) => (
            <div key={i} style={{ color:C.muted, fontSize:9, textAlign:'center', paddingBottom:4 }}>{d}</div>
          ))}
          {Array(mFirst).fill(null).map((_,i) => <div key={`e${i}`}/>)}
          {Array.from({length:mDays}, (_,i) => i+1).map(n => {
            const d   = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), n);
            const iso = toIso(d);
            const isPast  = d < today;
            const isFrom  = iso === from;
            const isTo    = iso === to;
            const isIn    = inRange(iso);
            const bg    = isFrom||isTo ? C.accent : isIn ? C.accentDim : 'transparent';
            const color = isFrom||isTo ? C.white : isIn ? C.accentHi : isPast ? C.muted : C.text;
            return (
              <div key={n}
                onClick={() => !isPast && handleDay(iso)}
                onMouseEnter={() => setHovered(iso)}
                onMouseLeave={() => setHovered('')}
                style={{ height:32, display:'flex', alignItems:'center', justifyContent:'center',
                  borderRadius:8, background:bg, color, fontSize:12,
                  fontWeight:isFrom||isTo?700:400, cursor:isPast?'default':'pointer', opacity:isPast?0.3:1 }}>
                {n}
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={onDelete} style={{
            flex:1, padding:'12px 0', borderRadius:14, fontSize:13, fontWeight:700,
            background:'rgba(248,113,113,0.12)', border:'1px solid rgba(248,113,113,0.3)',
            color:'#F87171', cursor:'pointer',
          }}>{t('cal.delete')}</button>
          <button onClick={handleSave} disabled={!from||!to} style={{
            flex:2, padding:'12px 0', borderRadius:14, fontSize:14, fontWeight:800,
            background: from&&to ? `linear-gradient(135deg,#006F87,${C.accent})` : C.card,
            border: from&&to ? 'none' : `1px solid ${C.border}`,
            color: from&&to ? '#fff' : C.muted, cursor: from&&to ? 'pointer' : 'default',
          }}>{t('cal.save')}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Calendar({ pax, setPax, slots, setSlots, slotLimit = 5, planSlotIds = [], onTogglePlanSlot, onSlotAdded, onRegenAuto, onAutoSlotDeleted, autoHelp, onHelpSeen, appLang, plan, onUpgrade }) {
  const t = useT(appLang);
  const [view,         setView]         = useState('list');
  const [monthOffset,  setMonthOffset]  = useState(0);
  const [editingSlot,  setEditingSlot]  = useState(null);
  const [showHelp,     setShowHelp]     = useState(!!autoHelp);
  const dismissHelp = () => { setShowHelp(false); if (onHelpSeen) onHelpSeen(); };

  const today = new Date();
  const locale = langToLocale(appLang);
  const displayMonth = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const monthName = displayMonth.toLocaleDateString(locale, { month:'long', year:'numeric' });
  const daysInMonth = new Date(displayMonth.getFullYear(), displayMonth.getMonth()+1, 0).getDate();
  const firstDow = (displayMonth.getDay() + 6) % 7; // lundi=0

  const safeSlots = [...(slots || [])].sort((a, b) => a.departIso.localeCompare(b.departIso));

  const handleDelete = (id) => {
    const deleted = (slots || []).find(s => s.id === id);
    setSlots(prev => (prev||[]).filter(s => s.id !== id));
    setEditingSlot(null);
    if (deleted?.source === 'auto') onAutoSlotDeleted?.(id);
  };

  const handleSaveEdit = (newSlot) => {
    setSlots(prev => (prev||[]).map(s => s.id === editingSlot.id ? newSlot : s));
    setEditingSlot(null);
  };

  // Jours highlightés dans le mois affiché
  const highlightedDays = new Set();
  safeSlots.forEach(s => {
    const dep = new Date(s.departIso + 'T12:00:00');
    const ret = new Date(s.returnIso + 'T12:00:00');
    for (let d = new Date(dep); d <= ret; d.setDate(d.getDate()+1)) {
      if (d.getFullYear() === displayMonth.getFullYear() && d.getMonth() === displayMonth.getMonth()) {
        highlightedDays.add(d.getDate());
      }
    }
  });

  const autoCount = safeSlots.filter(s => s.source === 'auto').length;

  const helpModal = showHelp && createPortal(
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onClick={dismissHelp}>
      <div onClick={e => e.stopPropagation()} style={{
        background:C.card, borderRadius:'20px 20px 0 0', padding:'24px 24px 40px',
        width:'100%', maxWidth:600, borderTop:`1px solid ${C.border}`,
      }}>
        <div style={{ width:36, height:4, borderRadius:2, background:C.border, margin:'0 auto 20px' }}/>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
          <div style={{ width:32, height:32, borderRadius:16, background:C.accentDim,
            display:'flex', alignItems:'center', justifyContent:'center',
            color:C.accentHi, fontSize:16, fontWeight:800 }}>?</div>
          <span style={{ color:C.text, fontSize:16, fontWeight:800 }}>{t('common.howItWorks')}</span>
        </div>
        <p style={{ color:C.sub, fontSize:14, lineHeight:1.7, margin:0 }}>
          {t('cal.helpText')}
        </p>
        <button onClick={dismissHelp} style={{
          marginTop:20, width:'100%', padding:'13px', borderRadius:14,
          background:C.accent, border:'none', color:C.white, fontSize:14, fontWeight:700, cursor:'pointer',
        }}>{t('common.understood')}</button>
      </div>
    </div>,
    document.body
  );

  return (
    <Scroll>
      {/* Header */}
      <div style={{padding:"calc(env(safe-area-inset-top, 0px) + 16px) 20px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <h2 style={{...titleStyle, margin:0}}>{t('cal.title')}</h2>
          <button onClick={() => setShowHelp(true)} style={{
            width:20, height:20, borderRadius:'50%', border:`1.5px solid ${C.border}`,
            background:'transparent', color:C.muted, fontSize:11, fontWeight:800,
            cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
            flexShrink:0, lineHeight:1,
          }}>?</button>
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          {onRegenAuto && (
            <button onClick={onRegenAuto} style={{
              padding:'5px 11px', borderRadius:20, fontSize:11, fontWeight:700,
              background:C.card, border:`1px solid ${C.border}`,
              color:C.sub, cursor:'pointer', whiteSpace:'nowrap',
            }}>{t('cal.autoSlots')}{autoCount > 0 ? ` (${autoCount})` : ''}</button>
          )}
          <div style={{display:"flex",background:C.card,borderRadius:10,border:`1px solid ${C.border}`,overflow:"hidden"}}>
            {[['list','≡'],['cal','▦']].map(([v,icon])=>(
              <div key={v} onClick={()=>setView(v)} style={{padding:"5px 12px",cursor:"pointer",background:view===v?C.accent:"transparent",color:view===v?C.white:C.sub,fontSize:14,transition:"all 0.2s"}}>
                {icon}
              </div>
            ))}
          </div>
        </div>
      </div>
      {helpModal}

      {/* ── Plan actif ── */}
      <div style={{ margin:'0 16px 14px', ...card({ padding:'12px 16px' }) }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <span style={{ color:C.text, fontSize:13, fontWeight:700 }}>
            {t('dest.activePlan')} · <em style={{ fontWeight:400, color:C.accentHi }}>{plan === 'gold' ? t('sub.planGold') : plan === 'silver' ? t('sub.planSilver') : t('sub.planFree')}</em>
          </span>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ color: planSlotIds.length >= slotLimit ? C.gold : C.accentHi, fontSize:12, fontWeight:700 }}>
              {planSlotIds.length} / {slotLimit}
            </span>
            <button onClick={onUpgrade} style={{
              background:`linear-gradient(135deg,${C.gold},#fb923c)`,
              border:'none', borderRadius:20, padding:'4px 10px',
              color:'#fff', fontSize:11, fontWeight:800, cursor:'pointer',
            }}>⬆ Upgrade</button>
          </div>
        </div>
        <div style={{ height:5, background:C.border, borderRadius:3, overflow:'hidden' }}>
          <div style={{ height:'100%', borderRadius:3, transition:'width .3s ease',
            width:`${Math.min(100, planSlotIds.length / slotLimit * 100)}%`,
            background: planSlotIds.length >= slotLimit
              ? `linear-gradient(90deg,${C.gold},#fb923c)`
              : `linear-gradient(90deg,${C.accent},${C.accentHi})`,
          }}/>
        </div>
        {planSlotIds.length === 0 && (
          <div style={{ color:C.muted, fontSize:11, marginTop:6 }}>
            {t('dest.planEmpty').replace('{n}', slotLimit)}
          </div>
        )}
      </div>

      {view === 'cal' && <>
  {/* Month navigation */}
  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 16px 12px"}}>
    <div onClick={()=>setMonthOffset(m=>m-1)} style={{width:36,height:36,borderRadius:18,background:C.card,border:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:C.text,fontSize:18}}>‹</div>
    <div style={{color:C.text,fontSize:15,fontWeight:700,textTransform:"capitalize"}}>{monthName}</div>
    <div onClick={()=>setMonthOffset(m=>m+1)} style={{width:36,height:36,borderRadius:18,background:C.card,border:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:C.text,fontSize:18}}>›</div>
  </div>

  {/* 3 months grid */}
  {[0,1,2].map(offset => {
    const m = new Date(today.getFullYear(), today.getMonth() + monthOffset + offset, 1);
    const mName = m.toLocaleDateString(locale, { month:'long', year:'numeric' });
    const mDays = new Date(m.getFullYear(), m.getMonth()+1, 0).getDate();
    const mFirst = (m.getDay() + 6) % 7;
    // Map jour → slot (pour pouvoir ouvrir le bon slot au clic)
    const daySlot = new Map();
    const firstDay = new Set(); // jours de début de slot
    safeSlots.forEach(s => {
      const dep = new Date(s.departIso + 'T12:00:00');
      const ret = new Date(s.returnIso + 'T12:00:00');
      for (let d = new Date(dep); d <= ret; d.setDate(d.getDate()+1)) {
        if (d.getFullYear()===m.getFullYear() && d.getMonth()===m.getMonth()) {
          daySlot.set(d.getDate(), s);
          if (d.toISOString().split('T')[0] === s.departIso) firstDay.add(d.getDate());
        }
      }
    });
    return (
      <div key={offset} style={{margin:"0 16px 14px",...card({padding:"14px 12px 10px"})}}>
        <div style={{color:C.text,fontSize:13,fontWeight:700,marginBottom:8,textTransform:"capitalize"}}>{mName}</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
          {["L","M","M","J","V","S","D"].map((d,i)=>(
            <div key={i} style={{color:C.muted,fontSize:9,textAlign:"center",paddingBottom:3}}>{d}</div>
          ))}
          {Array(mFirst).fill(null).map((_,i)=><div key={`e${i}`}/>)}
          {Array.from({length:mDays},(_,i)=>i+1).map(n=>{
            const s        = daySlot.get(n);
            const hi       = !!s;
            const isManual = s?.source === 'manual';
            const isEvent  = s?.source === 'event';
            const isFirst  = firstDay.has(n);
            const slotName = isFirst && !isManual
              ? (isEvent ? s.eventName : s?.holiday || null)
              : null;
            const isToday  = today.getDate()===n && today.getMonth()===m.getMonth() && today.getFullYear()===m.getFullYear();
            const color    = isManual ? C.gold : isEvent ? C.gold : hi ? C.text : isToday ? C.white : C.muted;
            return (
              <div key={n}
                onClick={() => s && setEditingSlot(s)}
                style={{minHeight:30,height:slotName?'auto':30,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",borderRadius:8,padding:slotName?'3px 1px':'0',
                  background:hi?C.accentDim:isToday?C.border:"transparent",
                  color, fontSize:11, fontWeight: isManual||isEvent ? 800 : hi||isToday ? 600 : 400,
                  border:isToday?`1px solid ${C.accent}`:"none",
                  cursor: hi ? 'pointer' : 'default'}}>
                {n}
                {slotName && (
                  <div style={{fontSize:6,fontWeight:600,color:isEvent?C.gold:C.accentHi,lineHeight:1.1,textAlign:'center',maxWidth:'100%',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',paddingTop:1}}>
                    {slotName}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  })}
</>}

      {/* Slots list */}
      <div style={{padding:"0 16px"}}>
        {view === 'list' && safeSlots.map((s,i)=>{
          const isManual = s.source === 'manual';
          const isEvent  = s.source === 'event';
          return (
            <div key={i} style={{...card({display:"flex",alignItems:"center",gap:12,marginBottom:8,padding:"12px 14px",
              borderLeft: isEvent ? `3px solid ${C.gold}` : undefined})}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                  <span style={{color: isEvent ? C.gold : isManual ? C.accentHi : C.text, fontSize:13, fontWeight:700}}>
                    {isEvent ? s.eventName : new Date(s.departIso + 'T12:00:00').toLocaleDateString(locale, {weekday:'short', day:'numeric', month:'long'})}
                  </span>
                  <TypeChip type={s.type} lang={appLang}/>
                  {isEvent && <Chip label={t('cal.event')} color={C.gold} bg="rgba(244,169,40,0.12)"/>}
                  {s.source==='auto' && <Chip label={s.holiday||t('cal.bridge')} color={C.text} bg={C.border}/>}
                </div>
                <div style={{color:C.sub,fontSize:11,marginTop:2}}>
                  {isEvent ? `${s.from} → ${s.to}` : `→ ${new Date(s.returnIso + 'T12:00:00').toLocaleDateString(locale, {weekday:'short', day:'numeric', month:'long'})}`} · {s.nights} {t('cal.nights')}
                </div>
              </div>
              <div style={{display:'flex', gap:6, alignItems:'center'}}>
                {/* Plan toggle */}
                {(() => {
                  const inPlan  = planSlotIds.includes(s.id);
                  const full    = !inPlan && planSlotIds.length >= slotLimit;
                  return (
                    <button onClick={() => onTogglePlanSlot && onTogglePlanSlot(s.id)}
                      disabled={full}
                      style={{
                        width:30, height:30, borderRadius:8, border:'none', cursor: full ? 'default' : 'pointer',
                        background: inPlan ? C.accentDim : C.border,
                        color: inPlan ? C.accentHi : C.muted,
                        fontSize:14, display:'flex', alignItems:'center', justifyContent:'center',
                        opacity: full ? 0.35 : 1,
                      }}>★</button>
                  );
                })()}
                {!isEvent && <>
                  <button onClick={() => setEditingSlot(s)} style={{
                    width:30, height:30, borderRadius:8, background:C.border, border:'none',
                    color:C.sub, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                  }}>✎</button>
                  <button onClick={() => handleDelete(s.id)} style={{
                    width:30, height:30, borderRadius:8, background:'rgba(248,113,113,0.1)', border:'none',
                    color:'#F87171', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                  }}>🗑</button>
                </>}
              </div>
            </div>
          );
        })}
        {editingSlot && (
          <EditSlotModal
            slot={editingSlot}
            onSave={handleSaveEdit}
            onDelete={() => handleDelete(editingSlot.id)}
            onClose={() => setEditingSlot(null)}
            t={t} locale={locale}
          />
        )}
        {safeSlots.length === 0 && (
          <div style={{textAlign:"center",color:C.muted,fontSize:13,padding:"40px 0"}}>
            {t('cal.loading')}
          </div>
        )}
        <AddSlotButton t={t} locale={locale} slotFull={false} onAdd={s => { setSlots(prev => [...(prev||[]), s]); onSlotAdded?.(s.id); }} />
      </div>

    </Scroll>
  );
}

// ─── BOOKING ──────────────────────────────────────────────────────────────────

function Booking({ t, selFlight, selHotel, mode, onBack, onDone, travelPrefs, onGoToProfile }) {
  const totalSteps = mode==="both"?3:2;
  const [step, setStep] = useState(0);
  const [showPgmModal, setShowPgmModal] = useState(false);
  const timer = useCountdown(600);
  const co2Cost = +(selFlight.co2*4.5).toFixed(2);
  const hotelCost = mode==="both"?selHotel.totalPrice:0;
  const grandTotal = selFlight.totalPrice + hotelCost + co2Cost;
  const stepLabels = mode==="both"
    ? ["Book your trip – Flight","Book your trip – Hotel","Payment"]
    : ["Book your flight","Payment"];

  const isTravelerComplete = (idx) => {
    if (idx === 0) return !!(travelPrefs?.firstName && travelPrefs?.lastName && travelPrefs?.passportNumber && travelPrefs?.passportExpiry);
    const co = travelPrefs?.coTravelers?.[idx - 1];
    if (co) return !!(co.firstName && co.lastName && co.passport && co.passportExpiry);
    return !!(travelPrefs?.coFirstName && travelPrefs?.coLastName && travelPrefs?.coPassport);
  };
  const travelerName = (idx) => {
    if (idx === 0) return [travelPrefs?.firstName, travelPrefs?.lastName].filter(Boolean).join(' ') || 'Voyageur 1';
    const co = travelPrefs?.coTravelers?.[idx - 1];
    if (co) return [co.firstName, co.lastName].filter(Boolean).join(' ') || `Voyageur ${idx + 1}`;
    return [travelPrefs?.coFirstName, travelPrefs?.coLastName].filter(Boolean).join(' ') || `Voyageur ${idx + 1}`;
  };
  const bookingHeroPhoto = DEST_PHOTOS[t.dest.city] || DEST_PHOTOS[t.dest.name_en] || TYPE_PHOTOS[t.dest.type] || TYPE_PHOTOS.ville;

  if(step>=totalSteps) return (
    <Scroll>
      <div style={{padding:"36px 24px 0",textAlign:"center"}}>
        <div style={{fontSize:58,marginBottom:14}}>🎉</div>
        <h2 style={{color:C.text,fontSize:26,fontWeight:800,margin:"0 0 8px",letterSpacing:"-0.6px"}}>Congratulations!</h2>
        <p style={{color:C.sub,fontSize:14,lineHeight:1.65,margin:"0 0 24px"}}>
          Your trip to <strong style={{color:C.text}}>{t.dest.city}</strong><br/>on {t.slot.label} is confirmed.
        </p>
      </div>
      <div style={{margin:"0 16px",...card({padding:0,overflow:"hidden"})}}>
        <div style={{padding:"16px",display:"flex",gap:12,alignItems:"center",borderBottom:`1px solid ${C.border}`}}>
          <div style={{width:48,height:48,borderRadius:12,overflow:"hidden",flexShrink:0}}>
            <img src={DEST_PHOTOS[t.dest.city]} alt={t.dest.city} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          </div>
          <div><div style={{color:C.text,fontSize:16,fontWeight:800}}>{t.dest.city}</div><div style={{color:C.sub,fontSize:12}}>{t.slot.from} – {t.slot.to}</div></div>
        </div>
        {[
          [`✈️ ${selFlight.airline.name}`,`€${selFlight.totalPrice}`,false],
          ...(mode==="both"?[[`🏨 ${selHotel.name.split(" ").slice(0,3).join(" ")}`,`€${hotelCost}`,false]]:[]),
          ["🌿 CO₂ offset",`€${co2Cost}`,true],
        ].map(([n,p,g],i)=>(
          <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"12px 16px",borderBottom:`1px solid ${C.border}`}}>
            <span style={{color:C.sub,fontSize:13}}>{n}</span>
            <span style={{color:g?C.green:C.text,fontSize:13,fontWeight:700}}>{p}</span>
          </div>
        ))}
        <div style={{display:"flex",justifyContent:"space-between",padding:"14px 16px",background:C.accentDim}}>
          <span style={{color:C.text,fontSize:15,fontWeight:800}}>Total paid</span>
          <span style={{color:C.accentHi,fontSize:15,fontWeight:800}}>€{grandTotal}</span>
        </div>
      </div>
      <div style={{margin:"14px 16px 0",...card({background:C.greenDim,borderColor:C.green})}}>
        <div style={{color:C.green,fontSize:13,fontWeight:700}}>📧 Confirmation sent</div>
        <div style={{color:C.sub,fontSize:12,marginTop:3}}>to alexandra.davidneel@gmail.com · added to calendar</div>
      </div>
      <div style={{padding:"16px"}}><button style={btnPrimary} onClick={onDone}>← Back to My Top 5</button></div>
    </Scroll>
  );

  return (
    <Scroll>
      {/* ── Navigation top ── */}
      <div style={{padding:"calc(env(safe-area-inset-top, 0px) + 12px) 16px 0", display:"flex", alignItems:"center", justifyContent:"space-between"}}>
        <button style={{background:"none",border:"none",color:C.accentHi,fontSize:14,cursor:"pointer",padding:0}} onClick={step===0?onBack:()=>setStep(step-1)}>← Back</button>
        <Chip label={`Step ${step+1}/${totalSteps}`} color={C.gold} bg={C.goldDim}/>
      </div>

      {/* ── Polaroid + voyageurs ── */}
      <div style={{display:"flex", height:148, margin:"10px 16px 0", borderRadius:16, overflow:"hidden", background:C.card, border:`1px solid ${C.border}`}}>
        {/* Polaroid gauche */}
        <div style={{width:120, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", padding:"10px 6px 10px 14px"}}>
          <div style={{background:"white", padding:"5px 5px 0", borderRadius:3, boxShadow:"4px 8px 22px rgba(0,0,0,0.6)", transform:"rotate(-2deg)", width:"100%"}}>
            <div style={{width:"100%", height:80, overflow:"hidden"}}>
              <img src={bookingHeroPhoto} alt={t.dest.city} style={{width:"100%", height:"100%", objectFit:"cover", display:"block"}}/>
            </div>
            <div style={{textAlign:"center", padding:"4px 4px 5px", fontFamily:"'Caveat','Comic Sans MS',cursive", fontSize:13, fontWeight:700, color:"#1a1a1a", lineHeight:1}}>{t.dest.city}</div>
          </div>
        </div>
        {/* Voyageurs droite */}
        <div style={{flex:1, display:"flex", flexDirection:"column", justifyContent:"center", padding:"12px 12px 12px 10px", gap:5}}>
          {Array.from({length: t.pax}).map((_, i) => {
            const complete = isTravelerComplete(i);
            return (
              <button key={i} onClick={() => onGoToProfile?.('passport')}
                style={{display:"flex", alignItems:"center", gap:8, background:"none", border:"none", padding:"2px 0", cursor:"pointer", textAlign:"left"}}>
                <div style={{width:8, height:8, borderRadius:4, flexShrink:0, background: complete ? C.green : '#F4A928'}}/>
                <span style={{color:C.text, fontSize:13, fontWeight:600, flex:1}}>{travelerName(i)}</span>
                {!complete && <span style={{color:'#F4A928', fontSize:10}}>⚠</span>}
              </button>
            );
          })}
          <button onClick={() => onGoToProfile?.('coTravelers')}
            style={{display:"flex", alignItems:"center", gap:6, background:"none", border:`1px dashed ${C.border}`, borderRadius:10, padding:"5px 8px", cursor:"pointer", marginTop:4, width:"fit-content"}}>
            <span style={{color:C.accentHi, fontSize:15, fontWeight:700, lineHeight:1}}>+</span>
            <span style={{color:C.accentHi, fontSize:11, fontWeight:600}}>Ajouter un voyageur</span>
          </button>
        </div>
      </div>

      {/* ── Timer ── */}
      <div style={{margin:"8px 16px",...card({background:C.goldDim,borderColor:C.gold,display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 16px"})}}>
        <span style={{color:C.gold,fontSize:12}}>⏱ Price held for</span>
        <span style={{color:C.gold,fontSize:15,fontWeight:800,fontFamily:"monospace"}}>{timer}</span>
      </div>
      {/* summary strip */}
      <div style={{margin:"0 16px 12px",...card({padding:0,overflow:"hidden"})}}>
        <div style={{display:"flex"}}>
          <div style={{flex:1,padding:"10px 12px",borderRight:`1px solid ${C.border}`}}>
            <div style={{color:C.muted,fontSize:9,fontWeight:700,textTransform:"uppercase"}}>FLIGHT</div>
            <div style={{color:step===0?C.accentHi:C.green,fontSize:11,fontWeight:700,marginTop:2}}>{selFlight.airline.name}</div>
            <div style={{color:C.sub,fontSize:10}}>€{selFlight.totalPrice}</div>
          </div>
          {mode==="both"&&<div style={{flex:1,padding:"10px 12px",borderRight:`1px solid ${C.border}`}}>
            <div style={{color:C.muted,fontSize:9,fontWeight:700,textTransform:"uppercase"}}>HOTEL</div>
            <div style={{color:step<=1?C.sub:C.green,fontSize:11,fontWeight:700,marginTop:2}}>{selHotel.name.split(" ")[0]}</div>
            <div style={{color:C.sub,fontSize:10}}>€{hotelCost}</div>
          </div>}
          <div style={{flex:1,padding:"10px 12px"}}>
            <div style={{color:C.muted,fontSize:9,fontWeight:700,textTransform:"uppercase"}}>TOTAL</div>
            <div style={{color:C.text,fontSize:11,fontWeight:700,marginTop:2}}>€{grandTotal}</div>
          </div>
        </div>
      </div>

      {step===0&&(
        <div style={{padding:"0 16px"}}>
          {/* flight summary */}
          <div style={{...card({marginBottom:10})}}>
            <div style={{color:C.muted,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:10}}>Confirming flight</div>
            <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:8}}>
              <AirlineBadge airline={selFlight.airline} size="sm"/>
              <div><div style={{color:C.text,fontSize:13,fontWeight:700}}>{selFlight.airline.name} {selFlight.flightNum}</div>
              <div style={{color:C.sub,fontSize:12}}>ORY → {AIRPORT_CODES[t.dest.city]||"???"} · {selFlight.isDirect?"Direct":`1 stop via ${selFlight.stopCity}`}</div></div>
            </div>
            <div style={{display:"flex",gap:12,fontSize:12,color:C.sub}}>
              <span>🛫 {selFlight.depart}</span><span>🛬 {selFlight.arrive}</span><span>⏱ {fmtMins(selFlight.durationMins)}</span>
            </div>
            <div style={{display:"flex",gap:8,marginTop:6}}>
              {selFlight.luggageIncl&&<Chip label="🧳 Bag incl" color={C.green} bg={C.greenDim} size={9}/>}
              {selFlight.flexTicket&&<Chip label="🔄 Flexible" color={C.accentHi} bg={C.accentDim} size={9}/>}
              <Chip label={`🌿 ${selFlight.co2}t CO₂`} color={C.green} bg={C.greenDim} size={9}/>
            </div>
          </div>
          {/* passengers */}
          <div style={{color:C.muted,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,margin:"0 0 8px"}}>Passenger details</div>
          {Array.from({length:t.pax},(_, i)=>{
            const isMain = i===0;
            const name = isMain
              ? [travelPrefs?.firstName, travelPrefs?.lastName].filter(Boolean).join(' ') || 'Passenger 1'
              : [travelPrefs?.coFirstName, travelPrefs?.coLastName].filter(Boolean).join(' ') || `Passenger ${i+1}`;
            const dob = isMain ? travelPrefs?.birthDate : travelPrefs?.coBirthDate;
            const passport = isMain ? travelPrefs?.passportNumber : travelPrefs?.coPassport;
            const masked = passport ? '•'.repeat(Math.max(0,passport.length-3)) + passport.slice(-3) : '—';
            return (
              <div key={i} style={{...card({marginBottom:8})}}>
                <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:C.text,fontSize:13,fontWeight:700}}>{name}</span><span style={{color:C.accentHi,fontSize:12}}>✎</span></div>
                {dob && <div style={{color:C.sub,fontSize:12,marginTop:4}}>📅 {dob}</div>}
                <div style={{color:C.sub,fontSize:12,marginTop:2}}>🛂 {masked}</div>
                <div style={{display:"flex",gap:10,marginTop:4,fontSize:11,color:C.muted}}>
                  <span>🧳 Cabin bag</span><span>🛡 No insurance</span>
                </div>
              </div>
            );
          })}
          <div style={{color:C.muted,fontSize:11,textAlign:"center",marginBottom:14}}>By continuing you accept our <span style={{color:C.accentHi}}>Terms & Privacy</span></div>
        </div>
      )}

      {step===1&&mode==="both"&&(
        <div style={{padding:"0 16px"}}>
          <div style={{...card({marginBottom:10,padding:0,overflow:"hidden"})}}>
            <div style={{height:100,background:`hsl(220,20%,15%)`,overflow:"hidden"}}>
              <img src={selHotel.photoUrl} alt="" onError={e=>e.target.style.display="none"} style={{width:"100%",height:"100%",objectFit:"cover",opacity:0.8}}/>
            </div>
            <div style={{padding:"12px 14px"}}>
              <div style={{color:C.text,fontSize:14,fontWeight:700}}>{selHotel.name}</div>
              <div style={{marginTop:2}}><StarRow n={selHotel.stars}/> <span style={{color:C.sub,fontSize:11}}>· {selHotel.rating}/10</span></div>
              <div style={{color:C.sub,fontSize:12,marginTop:6}}>📍 {selHotel.distCenter}km from centre</div>
              <div style={{color:C.sub,fontSize:12}}>🛏 {selHotel.room}</div>
              <div style={{display:"flex",justifyContent:"space-between",marginTop:8,paddingTop:8,borderTop:`1px solid ${C.border}`}}>
                <span style={{color:selHotel.cancel.color,fontSize:11}}>{selHotel.cancel.icon} {selHotel.cancel.label}</span>
                <span style={{color:C.text,fontSize:14,fontWeight:800}}>€{selHotel.totalPrice}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {((mode==="both"&&step===2)||(mode!=="both"&&step===1))&&(
        <div style={{padding:"0 16px"}}>
          <div style={{background:"linear-gradient(135deg,#1a1464,#2d2680)",borderRadius:20,padding:"22px 20px",marginBottom:14}}>
            <div style={{color:"rgba(255,255,255,0.45)",fontSize:10,marginBottom:8,letterSpacing:1}}>CARD NUMBER</div>
            <div style={{color:C.white,fontSize:20,letterSpacing:4,fontFamily:"monospace",marginBottom:18}}>4465 7164 6732 7890</div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end"}}>
              <div><div style={{color:"rgba(255,255,255,0.45)",fontSize:9,letterSpacing:1}}>HOLDER</div><div style={{color:C.white,fontSize:13,marginTop:3}}>Christopher Colombus</div></div>
              <div><div style={{color:"rgba(255,255,255,0.45)",fontSize:9,letterSpacing:1}}>EXPIRES</div><div style={{color:C.white,fontSize:13,marginTop:3}}>02 / 2026</div></div>
              <div style={{color:"rgba(255,255,255,0.75)",fontSize:22,fontWeight:900,fontStyle:"italic"}}>VISA</div>
            </div>
          </div>
          <div style={{...card({marginBottom:14})}}>
            <div style={{color:C.muted,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:10}}>Verification code</div>
            <input style={{background:"transparent",border:"none",color:C.text,fontSize:26,letterSpacing:14,width:"100%",outline:"none",fontFamily:"monospace"}} placeholder="•••" maxLength={3} type="password"/>
          </div>
        </div>
      )}

      <div style={{padding:"0 16px", display:'flex', flexDirection:'column', gap:8}}>
        <button onClick={() => setShowPgmModal(true)} style={{
          width:'100%', padding:'12px', borderRadius:14,
          background:`linear-gradient(135deg,#006F87,${C.accent})`,
          border:'none', color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer',
        }}>📋 Générer le programme</button>
        <button style={btnPrimary} onClick={()=>setStep(step+1)}>
          {step<totalSteps-1?"Confirm & Continue →":`Pay €${grandTotal} & Book`}
        </button>
      </div>
      {showPgmModal && <SubscriptionModal onClose={() => setShowPgmModal(false)}/>}
    </Scroll>
  );
}

// ─── MY TRIPS / PROFILE ───────────────────────────────────────────────────────

function MyTrips() {
  const past=[
    {city:"Athens",emoji:"🏺",date:"4 Feb",nights:2,price:535,dur:"3h40",co2:1.5,done:true,airline:"Aegean",hotel:"Hotel Grande Bretagne"},
    {city:"Avignon",emoji:"🌿",date:"1 May",nights:1,price:165,dur:"1h40",co2:0.4,done:true,airline:"easyJet",hotel:"Maison Roquille"},
    {city:"Barcelona",emoji:"🌊",date:"14 Jul",nights:2,price:237,dur:"1h40",co2:0.9,done:true,airline:"Vueling",hotel:"W Barcelona"},
  ];
  const upcoming=[
    {city:"Prague",emoji:"🏰",date:"1 Nov",nights:1,price:395,dur:"1h40",co2:0.9,done:false,airline:"Air France",hotel:"Hotel Pod Věží"},
    {city:"Venezia",emoji:"🎭",date:"15 Feb",nights:1,price:395,dur:"1h40",co2:0.8,done:false,airline:"Ryanair",hotel:"Ca' Sagredo"},
  ];
  return (
    <Scroll>
      <div style={{padding:"calc(env(safe-area-inset-top, 0px) + 16px) 20px 12px"}}><h2 style={titleStyle}>Trips</h2></div>
      <div style={{display:"flex",gap:8,padding:"0 16px 14px"}}>
        {[["5","Total trips"],["€1 569","Total spent"],["4.7t","CO₂ offset"]].map(([v,l])=>(
          <div key={l} style={{flex:1,...card({textAlign:"center",padding:"10px 6px"})}}>
            <div style={{color:C.text,fontSize:16,fontWeight:800}}>{v}</div>
            <div style={{color:C.muted,fontSize:10}}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{padding:"0 16px"}}>
        <div style={{color:C.muted,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Upcoming</div>
        {upcoming.map((t,i)=>(
          <div key={i} style={{...card({display:"flex",gap:10,alignItems:"center",marginBottom:10})}}>
            <div style={{width:44,height:44,borderRadius:12,overflow:"hidden",flexShrink:0}}>
              <img src={DEST_PHOTOS[t.city]} alt={t.city} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            </div>
            <div style={{flex:1}}>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span style={{color:C.text,fontSize:13,fontWeight:700}}>{t.city}</span>
                <Chip label="BOOKED" color={C.accentHi} bg={C.accentDim}/>
              </div>
              <div style={{color:C.sub,fontSize:11,marginTop:2}}>{t.date} · {t.nights}n · {t.airline}</div>
              <div style={{color:C.muted,fontSize:11,marginTop:1}}>🏨 {t.hotel}</div>
            </div>
            <div style={{color:C.text,fontSize:16,fontWeight:800}}>€{t.price}</div>
          </div>
        ))}
        <div style={{height:1,background:C.border,margin:"6px 0 12px"}}/>
        <div style={{color:C.muted,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Past trips</div>
        {past.map((t,i)=>(
          <div key={i} style={{...card({display:"flex",gap:10,alignItems:"center",marginBottom:10})}}>
            <div style={{width:44,height:44,borderRadius:12,overflow:"hidden",flexShrink:0}}>
              <img src={DEST_PHOTOS[t.city]} alt={t.city} style={{width:"100%",height:"100%",objectFit:"cover",filter:"saturate(0.7) brightness(0.8)"}}/>            </div>
            <div style={{flex:1}}>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span style={{color:C.text,fontSize:13,fontWeight:700}}>{t.city}</span>
                <Chip label="DONE" color={C.green} bg={C.greenDim}/>
              </div>
              <div style={{color:C.sub,fontSize:11,marginTop:2}}>{t.date} · {t.nights}n · {t.airline}</div>
              <div style={{color:C.muted,fontSize:11,marginTop:1}}>🏨 {t.hotel}</div>
            </div>
            <div style={{color:C.text,fontSize:16,fontWeight:800}}>€{t.price}</div>
          </div>
        ))}
      </div>
    </Scroll>
  );
}

const PHONE_PREFIXES = [
  {code:'+33',flag:'🇫🇷',lang:'fr'},{code:'+32',flag:'🇧🇪',lang:'fr-be'},
  {code:'+41',flag:'🇨🇭',lang:'fr-ch'},{code:'+352',flag:'🇱🇺',lang:'lb'},
  {code:'+1',flag:'🇺🇸',lang:'en'},{code:'+44',flag:'🇬🇧',lang:'en-gb'},
  {code:'+49',flag:'🇩🇪',lang:'de'},{code:'+43',flag:'🇦🇹',lang:'de-at'},
  {code:'+34',flag:'🇪🇸',lang:'es'},{code:'+39',flag:'🇮🇹',lang:'it'},
  {code:'+351',flag:'🇵🇹',lang:'pt'},{code:'+31',flag:'🇳🇱',lang:'nl'},
  {code:'+46',flag:'🇸🇪',lang:'sv'},{code:'+47',flag:'🇳🇴',lang:'no'},
  {code:'+45',flag:'🇩🇰',lang:'da'},{code:'+358',flag:'🇫🇮',lang:'fi'},
  {code:'+61',flag:'🇦🇺',lang:'en-au'},{code:'+64',flag:'🇳🇿',lang:'en-nz'},
  {code:'+81',flag:'🇯🇵',lang:'ja'},{code:'+86',flag:'🇨🇳',lang:'zh'},
  {code:'+971',flag:'🇦🇪',lang:'ar'},{code:'+966',flag:'🇸🇦',lang:'ar-sa'},
  {code:'+55',flag:'🇧🇷',lang:'pt-br'},{code:'+52',flag:'🇲🇽',lang:'es-mx'},
  {code:'+54',flag:'🇦🇷',lang:'es-ar'},{code:'+27',flag:'🇿🇦',lang:'af'},
  {code:'+212',flag:'🇲🇦',lang:'ar-ma'},{code:'+213',flag:'🇩🇿',lang:'ar-dz'},
  {code:'+216',flag:'🇹🇳',lang:'ar-tn'},
];
const detectPrefix = () => {
  const l = (navigator.language || 'fr').toLowerCase();
  const match = PHONE_PREFIXES.find(p => l === p.lang) || PHONE_PREFIXES.find(p => l.startsWith(p.lang.split('-')[0]));
  return match ? match.code : '+33';
};

// Countries supported by nager.at for public-holiday calculation
const HOLIDAY_COUNTRIES = [
  { code:'AT', name:'Autriche',           flag:'🇦🇹' },
  { code:'BE', name:'Belgique',           flag:'🇧🇪' },
  { code:'CH', name:'Suisse',             flag:'🇨🇭' },
  { code:'CZ', name:'République tchèque', flag:'🇨🇿' },
  { code:'DE', name:'Allemagne',          flag:'🇩🇪' },
  { code:'DK', name:'Danemark',           flag:'🇩🇰' },
  { code:'ES', name:'Espagne',            flag:'🇪🇸' },
  { code:'FI', name:'Finlande',           flag:'🇫🇮' },
  { code:'FR', name:'France',             flag:'🇫🇷' },
  { code:'GB', name:'Royaume-Uni',        flag:'🇬🇧' },
  { code:'GR', name:'Grèce',              flag:'🇬🇷' },
  { code:'HR', name:'Croatie',            flag:'🇭🇷' },
  { code:'HU', name:'Hongrie',            flag:'🇭🇺' },
  { code:'IE', name:'Irlande',            flag:'🇮🇪' },
  { code:'IS', name:'Islande',            flag:'🇮🇸' },
  { code:'IT', name:'Italie',             flag:'🇮🇹' },
  { code:'LU', name:'Luxembourg',         flag:'🇱🇺' },
  { code:'NL', name:'Pays-Bas',           flag:'🇳🇱' },
  { code:'NO', name:'Norvège',            flag:'🇳🇴' },
  { code:'PL', name:'Pologne',            flag:'🇵🇱' },
  { code:'PT', name:'Portugal',           flag:'🇵🇹' },
  { code:'RO', name:'Roumanie',           flag:'🇷🇴' },
  { code:'SE', name:'Suède',              flag:'🇸🇪' },
  { code:'SK', name:'Slovaquie',          flag:'🇸🇰' },
  { code:'US', name:'États-Unis',         flag:'🇺🇸' },
];

const APP_LANGUAGES = [
  { code:'fr', label:'Français', flag:'🇫🇷', available: true  },
  { code:'en', label:'English',  flag:'🌐',  available: true  },
  { code:'es', label:'Español',  flag:'🇪🇸', available: true  },
  { code:'zh', label:'中文',     flag:'🇨🇳', available: true  },
  { code:'de', label:'Deutsch',  flag:'🇩🇪', available: false },
];

function ReferralSection({ refLink, bonusWeeks, travelPrefs, user, t }) {
  const [copied, setCopied] = useState(false);
  const [inviteCount, setInviteCount] = useState(null);

  useEffect(() => {
    if (!user) return;
    supabase.from('referrals').select('id', { count: 'exact' })
      .eq('referrer_id', user.id).not('referred_id', 'is', null)
      .then(({ count }) => setInviteCount(count || 0));
  }, [user]);

  const copy = () => {
    navigator.clipboard.writeText(refLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const share = () => {
    if (navigator.share) {
      navigator.share({ title: 'Plotted', text: 'Découvre Plotted — ton Top 5 de voyages personnalisé !', url: refLink });
    } else {
      copy();
    }
  };

  return (
    <div style={{ margin: '0 16px 16px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px 0', fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{t('profile.referralSection')}</div>
      <div style={{ padding: '10px 16px 14px' }}>
        <div style={{ fontSize: 12, color: C.sub, marginBottom: 10, lineHeight: 1.5 }}>{t('profile.referralBonus')}</div>
        {inviteCount !== null && (
          <div style={{ fontSize: 12, color: C.accentHi, fontWeight: 600, marginBottom: 10 }}>
            {t('profile.referralStats').replace('{n}', inviteCount).replace('{w}', bonusWeeks)}
          </div>
        )}
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px', fontSize: 12, color: C.sub, marginBottom: 10, wordBreak: 'break-all' }}>
          {refLink}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={copy} style={{ flex: 1, padding: '9px', borderRadius: 10, background: copied ? '#27C97A22' : C.bg, border: `1px solid ${copied ? C.green : C.border}`, color: copied ? C.green : C.text, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            {copied ? t('profile.referralCopied') : t('profile.referralCopy')}
          </button>
          <button onClick={share} style={{ flex: 1, padding: '9px', borderRadius: 10, background: `linear-gradient(135deg,#006F87,${C.accent})`, border: 'none', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            {t('profile.referralShare')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Profile({ onSignOut, user, travelPrefs, setTravelPrefs, planIds = [], planLimit = 5, onUpgrade, onBack, openSection, onReplayOnboarding }) {
  const [openPersonal, setOpenPersonal] = useState(openSection === 'passport');
  const [openTravelers, setOpenTravelers] = useState(openSection === 'coTravelers');
  const [openPrefs, setOpenPrefs] = useState(false);
  const [openParams, setOpenParams] = useState(false);
  const [travelerModal, setTravelerModal] = useState(null); // null | { type:'me' } | { type:'co', id }
  const [phonePrefix, setPhonePrefix] = useState(() => travelPrefs?.phonePrefix || detectPrefix());
  const [openPwd, setOpenPwd]       = useState(false);
  const [showAdmin, setShowAdmin]   = useState(false);
  const [deleteStep, setDeleteStep] = useState(0); // 0=hidden 1=confirm 2=deleting
  const [deleteError, setDeleteError] = useState('');
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdMsg, setPwdMsg] = useState(null); // {text, ok}

  const [openSettings, setOpenSettings] = useState(false);

  // Pre-fill email from Supabase Auth if not already set
  useEffect(() => {
    if (user?.email && !travelPrefs?.email) {
      const next = { ...travelPrefs, email: user.email };
      setTravelPrefs(next);
      saveTravelPrefs(next, user.id);
    }
  }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps
  const [openAccount, setOpenAccount] = useState(false);
  const [showAbout, setShowAbout]     = useState(false);
  const [refCopied, setRefCopied] = useState(false);
  const [refInviteCount, setRefInviteCount] = useState(null);
  const [showReferralHelp, setShowReferralHelp] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const autoCountry = detectCountry(); // browser-derived fallback
  const deviceLang = (navigator.language || 'fr').split('-')[0].toLowerCase();
  const [countrySearch, setCountrySearch] = useState(() => {
    const code = travelPrefs?.holidayCountry;
    if (!code) return '';
    const found = HOLIDAY_COUNTRIES.find(c => c.code === code);
    return found ? found.name : code;
  });
  const [countryOpen, setCountryOpen] = useState(false);
  const countryFiltered = HOLIDAY_COUNTRIES.filter(c =>
    !countrySearch ||
    c.name.toLowerCase().includes(countrySearch.replace(/^\S+\s/, '').toLowerCase()) ||
    c.code.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const t = useT(travelPrefs?.appLang);

  const handleChangePwd = async () => {
    if (!currentPwd) { setPwdMsg({ text: t('profile.pwdEnterCurrent'), ok:false }); return; }
    if (newPwd.length < 8) { setPwdMsg({ text: t('profile.pwdMin8'), ok:false }); return; }
    if (newPwd !== confirmPwd) { setPwdMsg({ text: t('profile.pwdNoMatch'), ok:false }); return; }
    // Re-authenticate with current password first
    const { data: { user } } = await supabase.auth.getUser();
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: user.email, password: currentPwd,
    });
    if (signInError) { setPwdMsg({ text: t('profile.pwdWrongCurrent'), ok:false }); return; }
    const { error } = await supabase.auth.updateUser({ password: newPwd });
    if (error) { setPwdMsg({ text: error.message, ok:false }); return; }
    setPwdMsg({ text: t('profile.pwdUpdated'), ok:true });
    setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    setTimeout(() => { setOpenPwd(false); setPwdMsg(null); }, 1800);
  };
  const [deptSearch, setDeptSearch] = useState('');
  const [deptOpen,   setDeptOpen]   = useState(false);

  // Migration : departureAirports (array) remplace departureAirport (string)
  const deptAirports = travelPrefs?.departureAirports?.length
    ? travelPrefs.departureAirports
    : (travelPrefs?.departureAirport ? [travelPrefs.departureAirport] : []);

  const addAirport = iata => {
    if (deptAirports.includes(iata)) return;
    const nl = [...deptAirports, iata];
    const next = { ...travelPrefs, departureAirports: nl, departureAirport: nl[0] };
    setTravelPrefs(next); saveTravelPrefs(next, user?.id);
  };
  const removeAirport = iata => {
    const nl = deptAirports.filter(a => a !== iata);
    const next = { ...travelPrefs, departureAirports: nl, departureAirport: nl[0] || '' };
    setTravelPrefs(next); saveTravelPrefs(next, user?.id);
  };

  // Co-voyageurs — migration champs legacy → tableau
  const RELATION_CODES = ['conjoint', 'enfant', 'ami', 'autre'];
  const relLabel = code => ({ conjoint: t('profile.relConjoint'), enfant: t('profile.relEnfant'), ami: t('profile.relAmi'), autre: t('profile.relAutre') })[code] || code;

  const coTravelers = travelPrefs?.coTravelers ?? [];

  const updateCoTraveler = (id, key, val) => {
    const next = { ...travelPrefs, coTravelers: coTravelers.map(ct => ct.id === id ? { ...ct, [key]: val } : ct) };
    setTravelPrefs(next); saveTravelPrefs(next, user?.id);
  };
  const addCoTraveler = () => {
    const ct = { id: Date.now().toString(), relation:'enfant', firstName:'', lastName:'',
                 birthDate:'', passport:'', passportExpiry:'', passportCountry:'' };
    const next = { ...travelPrefs, coTravelers: [...coTravelers, ct] };
    setTravelPrefs(next); saveTravelPrefs(next, user?.id);
    setTravelerModal({ type:'co', id: ct.id });
  };
  const removeCoTraveler = (id) => {
    const next = { ...travelPrefs, coTravelers: coTravelers.filter(ct => ct.id !== id) };
    setTravelPrefs(next); saveTravelPrefs(next, user?.id);
    if (travelerModal?.type === 'co' && travelerModal.id === id) setTravelerModal(null);
  };

  const update = (key, val) => {
    const next = { ...travelPrefs, [key]: val };
    setTravelPrefs(next);
    saveTravelPrefs(next, user?.id);
    // Sync langue → localStorage pour que tous les useT() sans prop la voient immédiatement
    if (key === 'appLang') try { localStorage.setItem(LANG_STORAGE_KEY, val); } catch (_) {}
  };

  const toggle = (key, val, multi) => {
    const current = travelPrefs?.[key] || (multi ? [] : '');
    const updated = multi
      ? (current.includes(val) ? current.filter(x => x !== val) : [...current, val])
      : (current === val ? '' : val);
    update(key, updated);
  };

  useEffect(() => {
    if (travelPrefs?.departureAirports?.length || travelPrefs?.departureAirport || !travelPrefs?.address) return;
    const addr = travelPrefs.address.toLowerCase();
    const cityMatch = DEPARTURE_AIRPORTS.find(a =>
      addr.includes(a.city.toLowerCase()) ||
      addr.includes(a.name.toLowerCase()) ||
      addr.includes(a.iata.toLowerCase())
    );
    if (cityMatch) {
      const nl = DEPARTURE_AIRPORTS.filter(a => a.city === cityMatch.city).map(a => a.iata);
      const next = { ...travelPrefs, departureAirports: nl, departureAirport: nl[0] };
      setTravelPrefs(next); saveTravelPrefs(next, user?.id);
    }
  }, [travelPrefs?.address]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    supabase.from('referrals').select('id', { count: 'exact' })
      .eq('referrer_id', user.id).not('referred_id', 'is', null)
      .then(({ count }) => setRefInviteCount(count || 0));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const deptFiltered = deptSearch.length >= 1
    ? DEPARTURE_AIRPORTS.filter(a =>
        !deptAirports.includes(a.iata) && (
          a.city.toLowerCase().includes(deptSearch.toLowerCase()) ||
          a.name.toLowerCase().includes(deptSearch.toLowerCase()) ||
          a.iata.toLowerCase().includes(deptSearch.toLowerCase())
        )
      ).slice(0, 6)
    : [];

  // ── Complétion ────────────────────────────────────────────────────────────
  const COMPLETION_CHECKS = [
    () => !!travelPrefs?.firstName,
    () => !!travelPrefs?.lastName,
    () => !!travelPrefs?.birthDate,
    () => !!travelPrefs?.passportNumber,
    () => !!travelPrefs?.passportExpiry,
    () => !!travelPrefs?.passportCountry,
    () => !!travelPrefs?.address,
    () => deptAirports.length > 0,
    () => !!travelPrefs?.email,
    () => !!travelPrefs?.phone,
    () => (travelPrefs?.style||[]).length > 0,
    () => !!travelPrefs?.with,
    () => !!travelPrefs?.dailyBudget,
    () => !!travelPrefs?.hotelBudget,
    () => (travelPrefs?.flightPref||[]).length > 0,
  ];
  const filledCount = COMPLETION_CHECKS.filter(fn => fn()).length;
  const completionPct = Math.round(filledCount / COMPLETION_CHECKS.length * 100);
  const completionColor = completionPct >= 80 ? C.green : completionPct >= 50 ? C.gold : C.accent;

  // ── Helpers UI ────────────────────────────────────────────────────────────
  const field = (label, key, { note, placeholder, type='text' } = {}) => (
    <div style={{ marginBottom:16 }}>
      <div style={{ color:C.sub, fontSize:12, fontWeight:600, marginBottom:6 }}>{label}</div>
      <input
        type="text"
        value={travelPrefs?.[key] || ''}
        onChange={e => update(key, e.target.value)}
        placeholder={placeholder || ''}
        onFocus={e => e.target.style.borderColor = C.accent}
        onBlur={e => e.target.style.borderColor = `${C.accent}44`}
        style={{ width:'100%', minWidth:0, boxSizing:'border-box', background:C.bg,
          border:`1.5px solid ${C.accent}44`, borderRadius:12,
          padding:'13px 14px', color:C.text, fontSize:14, outline:'none' }}
      />
      {note && <div style={{ color:C.muted, fontSize:11, marginTop:5 }}>{note}</div>}
    </div>
  );

  const dateField = (label, key) => {
    const handleChange = (e) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
      let v = digits;
      if (digits.length > 4) v = digits.slice(0,2) + '/' + digits.slice(2,4) + '/' + digits.slice(4);
      else if (digits.length > 2) v = digits.slice(0,2) + '/' + digits.slice(2);
      update(key, v);
    };
    return (
      <div style={{ marginBottom:16 }}>
        <div style={{ color:C.sub, fontSize:12, fontWeight:600, marginBottom:6 }}>{label}</div>
        <input
          type="text" inputMode="numeric"
          value={travelPrefs?.[key] || ''}
          onChange={handleChange}
          placeholder={t('profile.phDate')}
          onFocus={e => e.target.style.borderColor = C.accent}
          onBlur={e => e.target.style.borderColor = `${C.accent}44`}
          style={{ width:'100%', minWidth:0, boxSizing:'border-box', background:C.bg,
            border:`1.5px solid ${C.accent}44`, borderRadius:12,
            padding:'13px 14px', color:C.text, fontSize:14, outline:'none',
            letterSpacing:'1px' }}
        />
      </div>
    );
  };

  const chips = (label, key, options, multi=false, note=null, indent=0) => (
    <div style={{ marginBottom:16 }}>
      {label && <div style={{ color:C.sub, fontSize:12, fontWeight:600, marginBottom:8 }}>{label}</div>}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', paddingLeft: indent }}>
        {options.map(o => {
          const active = multi ? (travelPrefs?.[key]||[]).includes(o) : travelPrefs?.[key] === o;
          return (
            <button key={o} onClick={() => toggle(key, o, multi)} style={{
              padding:'7px 12px', borderRadius:16, fontSize:12, fontWeight:600,
              background: active ? C.accent : C.cardB,
              color: active ? C.white : C.sub,
              border:`1.5px solid ${active ? C.accent : C.border}`, cursor:'pointer',
              boxShadow: active ? `0 2px 10px ${C.accentGlow}` : 'none',
            }}>{o}</button>
          );
        })}
      </div>
      {note && <div style={{ color:C.muted, fontSize:11, marginTop:5 }}>{note}</div>}
    </div>
  );

  const phoneField = (label, key, prefixKey, prefixState, setPrefixState) => (
    <div style={{ marginBottom:16 }}>
      <div style={{ color:C.sub, fontSize:12, fontWeight:600, marginBottom:6 }}>{label}</div>
      <div style={{ display:'flex', gap:6 }}>
        <select value={prefixState} onChange={e => { setPrefixState(e.target.value); update(prefixKey, e.target.value); }}
          style={{ background:C.bg, color:C.text, border:`1.5px solid ${C.accent}44`, borderRadius:12,
            padding:'13px 8px', fontSize:13, outline:'none', flexShrink:0, cursor:'pointer' }}>
          {PHONE_PREFIXES.map(p => <option key={p.code} value={p.code}>{p.flag} {p.code}</option>)}
        </select>
        <input type="tel" value={travelPrefs?.[key] || ''} onChange={e => update(key, e.target.value)}
          placeholder='6 12 34 56 78'
          onFocus={e => e.target.style.borderColor = C.accent}
          onBlur={e => e.target.style.borderColor = `${C.accent}44`}
          style={{ flex:1, background:C.bg, border:`1.5px solid ${C.accent}44`, borderRadius:12,
            padding:'13px 10px', color:C.text, fontSize:14, outline:'none' }}
        />
      </div>
    </div>
  );

  // Hub row : titre + résumé + flèche rotative
  const hubRow = (title, summary, isOpen, setOpen, noBorder=false) => (
    <div onClick={() => setOpen(o => !o)} style={{
      display:'flex', alignItems:'center', gap:14,
      padding:'16px 20px', minHeight:60, cursor:'pointer',
      borderBottom: noBorder ? 'none' : `1px solid ${C.border}`,
    }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ color:C.text, fontSize:14, fontWeight:600 }}>{title}</div>
        {summary && <div style={{ color:C.muted, fontSize:12, marginTop:2,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{summary}</div>}
      </div>
      <span style={{ color:C.accentHi, fontSize:20, lineHeight:1,
        display:'inline-block', transition:'transform .2s',
        transform: isOpen ? 'rotate(90deg)' : 'none' }}>›</span>
    </div>
  );

  const closeBtn = (fn) => (
    <button onClick={fn} style={{ ...btnPrimary, marginTop:8 }}>{t('profile.save')}</button>
  );

  const initials = [travelPrefs?.firstName, travelPrefs?.lastName]
    .filter(Boolean).map(n => n[0].toUpperCase()).join('') || '?';
  const fullName = [travelPrefs?.firstName, travelPrefs?.lastName].filter(Boolean).join(' ') || t('profile.yourProfile');
  // Résumés pour chaque section
  const summaryPersonal = [
    fullName !== t('profile.yourProfile') ? fullName : null,
    travelPrefs?.email || null,
    [travelPrefs?.addressCity, travelPrefs?.addressCountry].filter(Boolean).join(', ') || null,
  ].filter(Boolean).join(' · ') || t('profile.notFilled');

  const paxTotal = 1 + coTravelers.length;
  const summaryTravelers = [
    deptAirports.join(', ') || null,
    paxTotal > 1 ? t('profile.paxTotal').replace('{n}', paxTotal) : null,
  ].filter(Boolean).join(' · ') || t('profile.notFilled');

  const summaryPrefs = [
    (travelPrefs?.style||[]).join(', ') || null,
    travelPrefs?.with || null,
    travelPrefs?.dailyBudget || null,
  ].filter(Boolean).join(' · ') || t('profile.notFilled');

  const summaryParams = [
    APP_LANGUAGES.find(l => l.code === (travelPrefs?.appLang || deviceLang))?.label || null,
    travelPrefs?.flightOnly !== false ? t('profile.flightOnly') : t('profile.flightAndHotel'),
  ].filter(Boolean).join(' · ');

  const refCode = travelPrefs?.refCode;
  const summaryAccount = (() => {
    const plan = travelPrefs?.plan || 'free';
    return plan === 'gold' ? t('sub.planGold') : plan === 'silver' ? t('sub.planSilver') : t('sub.planFree');
  })();

  const avatarFileRef = useRef();
  const handleAvatarPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target.result;
      update('avatarUrl', url);
    };
    reader.readAsDataURL(file);
  };

  const ringC = 2 * Math.PI * 32;
  const ringDash = (completionPct / 100) * ringC;

  return (
    <Scroll>
      {/* ── Retour réservation ── */}
      {onBack && (
        <button onClick={onBack} style={{
          display:'flex', alignItems:'center', gap:8, width:'calc(100% - 32px)',
          margin:'calc(env(safe-area-inset-top, 0px) + 14px) 16px 0', padding:'10px 14px', cursor:'pointer',
          background:'rgba(0,158,194,0.08)', border:`1px solid ${C.accent}`,
          borderRadius:12, textAlign:'left',
        }}>
          <span style={{color:C.accentHi, fontSize:18, lineHeight:1}}>←</span>
          <span style={{color:C.accentHi, fontSize:13, fontWeight:700}}>Retour à la réservation</span>
        </button>
      )}
      {/* ── Header ── */}
      <div style={{ padding:'calc(env(safe-area-inset-top, 0px) + 24px) 20px 16px', display:'flex', alignItems:'center', gap:16 }}>
        {/* Avatar with completion ring */}
        <div style={{ position:'relative', flexShrink:0 }}>
          <svg style={{ position:'absolute', top:-5, left:-5, width:68, height:68, pointerEvents:'none' }} viewBox="0 0 68 68">
            <circle cx="34" cy="34" r="32" fill="none" stroke={C.border} strokeWidth="2.5"/>
            <circle cx="34" cy="34" r="32" fill="none"
              stroke={completionColor} strokeWidth="2.5"
              strokeDasharray={`${ringDash} ${ringC - ringDash}`}
              strokeLinecap="round"
              transform="rotate(-90 34 34)"
              style={{ transition:'stroke-dasharray 0.5s ease' }}
            />
          </svg>
          <div onClick={() => avatarFileRef.current?.click()} style={{
            width:58, height:58, borderRadius:29, cursor:'pointer', overflow:'hidden',
            background:`linear-gradient(135deg,#006F87,${C.accent})`,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:22, fontWeight:800, color:C.white }}>
            {travelPrefs?.avatarUrl
              ? <img src={travelPrefs.avatarUrl} alt="avatar" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
              : initials}
          </div>
          <div onClick={() => avatarFileRef.current?.click()} style={{
            position:'absolute', bottom:0, right:0,
            width:20, height:20, borderRadius:10,
            background:C.accent, border:`2px solid ${C.bg}`,
            display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
              <circle cx="12" cy="13" r="4"/>
            </svg>
          </div>
          <input ref={avatarFileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleAvatarPick}/>
        </div>

        {/* Name + meta */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ color:C.text, fontSize:18, fontWeight:800, letterSpacing:'-0.4px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:4 }}>
            {travelPrefs?.firstName || t('profile.yourProfile')}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap' }}>
            <div style={{
              fontSize:10, fontWeight:800, letterSpacing:'0.06em', textTransform:'uppercase',
              padding:'2px 8px', borderRadius:20,
              background: travelPrefs?.plan === 'gold' ? C.goldDim : travelPrefs?.plan === 'silver' ? 'rgba(176,190,197,0.15)' : C.accentDim,
              color: travelPrefs?.plan === 'gold' ? C.gold : travelPrefs?.plan === 'silver' ? '#b0bec5' : C.accentHi,
              border: `1px solid ${travelPrefs?.plan === 'gold' ? C.gold+'55' : travelPrefs?.plan === 'silver' ? '#b0bec555' : C.accent+'55'}`,
            }}>
              {travelPrefs?.plan === 'gold' ? t('sub.planGold') : travelPrefs?.plan === 'silver' ? t('sub.planSilver') : t('sub.planFree')}
            </div>
            <div style={{ color:C.muted, fontSize:11 }}>{planIds.length}/{planLimit} dest.</div>
            <div style={{ color:completionColor, fontSize:11, fontWeight:700 }}>{completionPct}%</div>
          </div>
          <div style={{ height:3, background:C.border, borderRadius:2, overflow:'hidden', marginTop:8 }}>
            <div style={{ height:'100%', width:`${completionPct}%`,
              background:`linear-gradient(90deg,${C.accent},${completionColor})`,
              borderRadius:2, transition:'width .4s ease' }}/>
          </div>
        </div>
      </div>

      {/* ── Hub sections ── */}
      <div style={{ margin:'0 16px 16px', ...card({ padding:0, overflow:'hidden' }) }}>

        {/* ① Informations personnelles */}
        {hubRow(t('profile.personal'), summaryPersonal, openPersonal, setOpenPersonal)}
        {openPersonal && (
          <div style={{ padding:'20px', borderBottom:`1px solid ${C.border}` }}>
            <div style={{ display:'flex', gap:10 }}>
              <div style={{ flex:1 }}>{field(t('profile.firstName'), 'firstName', { placeholder: t('profile.phFirstName') })}</div>
              <div style={{ flex:1 }}>{field(t('profile.lastName'), 'lastName', { placeholder: t('profile.phLastName') })}</div>
            </div>
            {field(t('profile.email'), 'email', { type:'email', placeholder: user?.email || t('profile.phEmail') })}
            {phoneField(t('profile.phone'), 'phone', 'phonePrefix', phonePrefix, setPhonePrefix)}
            {field(t('profile.street'), 'address', { placeholder: t('profile.phStreet') })}
            <div style={{ display:'flex', gap:10 }}>
              <div style={{ flex:'0 0 38%' }}>{field(t('profile.postalCode'), 'postalCode', { placeholder: t('profile.phPostal') })}</div>
              <div style={{ flex:1 }}>{field(t('profile.city'), 'addressCity', { placeholder: t('profile.phCity') })}</div>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <div style={{ flex:1 }}>{field(t('profile.country'), 'addressCountry', { placeholder: t('profile.phCountry') })}</div>
              <div style={{ flex:1 }}>{field(t('profile.region'), 'addressRegion', { placeholder: t('profile.phRegion') })}</div>
            </div>
            {closeBtn(() => setOpenPersonal(false))}
          </div>
        )}

        {/* ② Voyageurs & départ */}
        {hubRow(t('profile.travelers'), summaryTravelers, openTravelers, setOpenTravelers)}
        {openTravelers && (
          <div style={{ padding:'20px', borderBottom:`1px solid ${C.border}` }}>
            {/* Aéroports de départ */}
            <div style={{ marginBottom:20 }}>
              <div style={{ color:C.sub, fontSize:12, fontWeight:600, marginBottom:8 }}>{t('profile.departureAirports')}</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, alignItems:'center' }}>
                {deptAirports.map(iata => (
                  <div key={iata} style={{ display:'flex', alignItems:'center', gap:3,
                    background:C.accentDim, borderRadius:8, padding:'6px 8px 6px 10px' }}>
                    <span style={{ color:C.accentHi, fontSize:13, fontWeight:700 }}>{iata}</span>
                    <button onClick={() => removeAirport(iata)}
                      style={{ background:'none', border:'none', color:C.muted, fontSize:16,
                        cursor:'pointer', padding:'0 2px', lineHeight:1 }}>×</button>
                  </div>
                ))}
                <button onClick={() => { setDeptOpen(o => !o); setDeptSearch(''); }}
                  style={{ background:C.card, border:`1.5px solid ${C.accent}44`, borderRadius:8,
                    padding:'6px 12px', color:C.accent, fontSize:14, fontWeight:700, cursor:'pointer' }}>+</button>
              </div>
              {deptOpen && (
                <div style={{ position:'relative', marginTop:8 }}>
                  <input value={deptSearch} autoFocus
                    onChange={e => setDeptSearch(e.target.value)}
                    onBlur={() => setTimeout(() => { setDeptOpen(false); setDeptSearch(''); }, 150)}
                    placeholder={t('profile.phAirportSearch')}
                    style={{ width:'100%', boxSizing:'border-box', background:C.bg,
                      border:`1.5px solid ${C.accent}`, borderRadius:12,
                      padding:'11px 14px', color:C.text, fontSize:14, outline:'none' }}
                  />
                  {deptFiltered.length > 0 && (
                    <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:99,
                      background:C.card, border:`1px solid ${C.border}`, borderRadius:12,
                      overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
                      {deptFiltered.map(a => (
                        <div key={a.iata} onMouseDown={() => { addAirport(a.iata); setDeptSearch(''); setDeptOpen(false); }}
                          style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                            padding:'13px 16px', cursor:'pointer', borderBottom:`1px solid ${C.border}` }}
                          onMouseEnter={e => e.currentTarget.style.background = C.bg}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <div>
                            <span style={{ color:C.text, fontSize:14 }}>{a.city}</span>
                            <span style={{ color:C.muted, fontSize:12, marginLeft:8 }}>{a.name}</span>
                          </div>
                          <span style={{ color:C.accent, fontSize:13, fontWeight:700 }}>{a.iata}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Liste voyageurs */}
            <div style={{ borderRadius:12, overflow:'hidden', border:`1px solid ${C.border}`, marginBottom:16 }}>
              {/* Moi */}
              <div style={{ display:'flex', alignItems:'center', gap:12, padding:'13px 16px',
                borderBottom: coTravelers.length > 0 ? `1px solid ${C.border}` : 'none' }}>
                <div style={{ width:34, height:34, borderRadius:17, flexShrink:0,
                  background:`linear-gradient(135deg,#006F87,${C.accent})`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:12, fontWeight:800, color:C.white }}>
                  {initials}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ color:C.text, fontSize:13, fontWeight:600,
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {fullName !== t('profile.yourProfile') ? fullName : t('profile.me')}
                  </div>
                  <div style={{ color:C.muted, fontSize:11, marginTop:1 }}>
                    {travelPrefs?.birthDate || t('profile.notFilled')}
                  </div>
                </div>
                <button onClick={() => setTravelerModal({ type:'me' })}
                  style={{ background:'none', border:'none', color:C.accentHi, fontSize:12,
                    fontWeight:600, cursor:'pointer', padding:'4px 8px', flexShrink:0 }}>
                  {t('profile.edit')}
                </button>
              </div>
              {/* Co-voyageurs */}
              {coTravelers.map((ct, i) => {
                const ctName = [ct.firstName, ct.lastName].filter(Boolean).join(' ') || '—';
                const ctInitials = [ct.firstName, ct.lastName].filter(Boolean).map(n => n[0].toUpperCase()).join('') || '?';
                return (
                  <div key={ct.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'13px 16px',
                    borderBottom: i < coTravelers.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                    <div style={{ width:34, height:34, borderRadius:17, flexShrink:0,
                      background:C.cardB, border:`1px solid ${C.border}`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:12, fontWeight:700, color:C.sub }}>
                      {ctInitials}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ color:C.text, fontSize:13, fontWeight:600,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ctName}</div>
                      <div style={{ color:C.muted, fontSize:11, marginTop:1 }}>{relLabel(ct.relation)}</div>
                    </div>
                    <button onClick={() => setTravelerModal({ type:'co', id: ct.id })}
                      style={{ background:'none', border:'none', color:C.accentHi, fontSize:12,
                        fontWeight:600, cursor:'pointer', padding:'4px 8px', flexShrink:0 }}>
                      {t('profile.edit')}
                    </button>
                    <button onClick={() => removeCoTraveler(ct.id)}
                      style={{ background:'none', border:'none', color:C.muted, fontSize:18,
                        cursor:'pointer', padding:'4px 6px', lineHeight:1, flexShrink:0 }}>×</button>
                  </div>
                );
              })}
            </div>
            <button onClick={addCoTraveler}
              style={{ width:'100%', padding:'11px', borderRadius:12, fontSize:13,
                fontWeight:600, cursor:'pointer', background:C.cardB,
                color:C.accent, border:`1.5px solid ${C.accent}44` }}>
              {t('profile.addTraveler')}
            </button>
            <div style={{ height:16 }}/>
            {closeBtn(() => setOpenTravelers(false))}
          </div>
        )}

        {/* ③ Préférences voyage */}
        {hubRow(t('profile.prefs'), summaryPrefs, openPrefs, setOpenPrefs)}
        {openPrefs && (
          <div style={{ padding:'20px', borderBottom:`1px solid ${C.border}` }}>
            {chips(t('profile.styleLabel'), 'style', ['Aventure','Culture','Détente','Gastronomie'], true, null, 16)}
            {chips(t('profile.groupLabel'), 'with', ['Solo','Couple','Famille','Amis'], false, null, 16)}
            {chips(t('profile.dailyBudgetLabel'), 'dailyBudget', ['< 100 €','100–200 €','200 €+'], false, null, 16)}
            {chips(t('profile.flightServicesLabel'), 'flightServices', [
              'Bagage enregistré','Cabine uniquement','Embarquement prioritaire',
              'Siège choisi','Accès lounge','Fast track sécurité',
              'Animal en cabine','Assistance PMR','Billet flexible',
            ], true, null, 16)}
            <div style={{ height:1, background:C.border, margin:'4px 0 16px' }}/>
            {chips(t('profile.hotelBudgetLabel'), 'hotelBudget', ['< 60 €','60–160 €','160–300 €','300 €+'], false, null, 16)}
            {chips(t('profile.flightClassLabel'), 'flightPref',
              ['Economy','Economy+','Business'],
              true, t('profile.flightClassNote'), 16)}
            {chips(t('profile.departureTimePrefLabel'), 'departureTimePref',
              ['Soir', 'Matin'],
              false, t('profile.departureTimePrefNote'), 16)}
            {closeBtn(() => setOpenPrefs(false))}
          </div>
        )}

        {/* ④ Paramètres Plotted */}
        {hubRow(t('profile.settings'), summaryParams, openParams, setOpenParams)}
        {openParams && (
          <div style={{ padding:'20px', borderBottom:`1px solid ${C.border}` }}>
            {/* Langue */}
            <div style={{ marginBottom:20 }}>
              <div style={{ color:C.sub, fontSize:12, fontWeight:600, marginBottom:8 }}>{t('profile.appLang')}</div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {APP_LANGUAGES.map(l => {
                  const isActive = (travelPrefs?.appLang || deviceLang) === l.code;
                  return (
                    <button key={l.code} onClick={() => l.available && update('appLang', l.code)}
                      title={l.label + (!l.available ? ` ${t('profile.soon')}` : '')}
                      style={{ padding:'8px 10px', borderRadius:18, fontSize:20, lineHeight:1,
                        background: isActive ? C.accent : C.cardB,
                        border:`1.5px solid ${isActive ? C.accent : C.border}`,
                        cursor: l.available ? 'pointer' : 'default',
                        opacity: l.available ? 1 : 0.4 }}>
                      {l.flag}
                    </button>
                  );
                })}
              </div>
              {!travelPrefs?.appLang && (
                <div style={{ color:C.muted, fontSize:11, marginTop:6 }}>
                  {t('profile.detectedLang')} {APP_LANGUAGES.find(l => l.code === deviceLang)?.flag || ''} {APP_LANGUAGES.find(l => l.code === deviceLang)?.label || deviceLang}
                </div>
              )}
            </div>
            {/* Mode vol / vol+hôtel */}
            <div style={{ marginBottom:20 }}>
              <div style={{ color:C.sub, fontSize:12, fontWeight:600, marginBottom:8 }}>{t('profile.searchMode')}</div>
              <div style={{ display:'flex', gap:8 }}>
                {[[true, t('profile.flightOnly')], [false, t('profile.flightAndHotel')]].map(([val, label]) => {
                  const active = (travelPrefs?.flightOnly !== false) === val;
                  return (
                    <button key={String(val)} onClick={() => update('flightOnly', val)}
                      style={{ flex:1, padding:'10px 8px', borderRadius:14, fontSize:12, fontWeight:700,
                        background: active ? C.accent : C.cardB,
                        border:`1.5px solid ${active ? C.accent : C.border}`,
                        color: active ? C.white : C.sub, cursor:'pointer' }}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Pays jours fériés */}
            <div>
              <div style={{ color:C.sub, fontSize:12, fontWeight:600, marginBottom:6 }}>{t('profile.holidayCountry')}</div>
              <div style={{ position:'relative' }}>
                <input
                  value={countrySearch}
                  onChange={e => { setCountrySearch(e.target.value); setCountryOpen(true); }}
                  onFocus={e => { setCountryOpen(true); e.target.style.borderColor = C.accent; }}
                  onBlur={e => { setTimeout(() => setCountryOpen(false), 150); e.target.style.borderColor = `${C.accent}44`; }}
                  placeholder={(() => { const c = HOLIDAY_COUNTRIES.find(x => x.code === autoCountry); return c ? `${t('profile.autoPrefix')} ${c.flag} ${c.name}` : `${t('profile.autoPrefix')} ${autoCountry}`; })()}
                  style={{ width:'100%', boxSizing:'border-box', background:C.bg,
                    border:`1.5px solid ${C.accent}44`, borderRadius:12,
                    padding:'13px 40px 13px 14px', color:C.text, fontSize:14, outline:'none' }}
                />
                {countrySearch && (
                  <button onMouseDown={() => { setCountrySearch(''); update('holidayCountry', null); }}
                    style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
                      background:'none', border:'none', color:C.muted, fontSize:18, cursor:'pointer', padding:4, lineHeight:1 }}>×</button>
                )}
                {countryOpen && countryFiltered.length > 0 && (
                  <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:300,
                    background:C.card, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden',
                    boxShadow:'0 8px 24px rgba(0,0,0,0.4)', maxHeight:200, overflowY:'auto' }}>
                    {countryFiltered.map(c => (
                      <button key={c.code} onMouseDown={() => {
                        setCountrySearch(c.name);
                        update('holidayCountry', c.code);
                        setCountryOpen(false);
                      }} style={{ width:'100%', padding:'11px 16px', textAlign:'left', background:'none',
                        border:'none', borderBottom:`1px solid ${C.border}`, color:C.text, fontSize:13,
                        cursor:'pointer', display:'flex', alignItems:'center', gap:10 }}>
                        <span style={{ fontSize:18 }}>{c.flag}</span>
                        <span style={{ flex:1 }}>{c.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ color:C.muted, fontSize:11, marginTop:5 }}>
                {travelPrefs?.holidayCountry ? t('profile.holidayCountryNote') : t('profile.holidayCountryAuto')}
              </div>
            </div>
            {/* Tutoriel */}
            {onReplayOnboarding && (
              <div style={{ marginTop:20, paddingTop:16, borderTop:`1px solid ${C.border}` }}>
                <button onClick={onReplayOnboarding}
                  style={{ background:'none', border:'none', padding:0, color:C.accent, fontSize:13, cursor:'pointer', textDecoration:'underline' }}>
                  {t('profile.replayOnboarding')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ⑤ Gestion de compte */}
        {hubRow(t('profile.accountSection'), summaryAccount, openAccount, setOpenAccount, true)}
        {openAccount && (
          <div>
            {/* 1. Mot de passe */}
            <div style={{ padding:'12px 20px', borderBottom:`1px solid ${C.border}` }}>
              <div onClick={() => { setOpenPwd(o => !o); setCurrentPwd(''); setNewPwd(''); setConfirmPwd(''); setPwdMsg(null); }}
                style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', padding:'4px 0' }}>
                <span style={{ color:C.sub, fontSize:13 }}>{t('profile.changePassword')}</span>
                <span style={{ color:C.accentHi, fontSize:18, display:'inline-block', transition:'transform 0.2s',
                  transform: openPwd ? 'rotate(90deg)' : 'none' }}>›</span>
              </div>
              {openPwd && (
                <div style={{ display:'flex', flexDirection:'column', gap:10, paddingTop:12, paddingBottom:4 }}>
                  <input type="password" placeholder={t('profile.phCurrentPwd')} value={currentPwd}
                    onChange={e => setCurrentPwd(e.target.value)}
                    style={{ width:'100%', padding:'11px 14px', borderRadius:10, border:`1.5px solid ${C.border}`,
                      background:C.bg, color:C.text, fontSize:13, boxSizing:'border-box' }}
                  />
                  <input type="password" placeholder={t('profile.phNewPwd')} value={newPwd}
                    onChange={e => setNewPwd(e.target.value)}
                    style={{ width:'100%', padding:'11px 14px', borderRadius:10, border:`1.5px solid ${C.border}`,
                      background:C.bg, color:C.text, fontSize:13, boxSizing:'border-box' }}
                  />
                  <input type="password" placeholder={t('profile.phConfirmPwd')} value={confirmPwd}
                    onChange={e => setConfirmPwd(e.target.value)}
                    style={{ width:'100%', padding:'11px 14px', borderRadius:10, border:`1.5px solid ${C.border}`,
                      background:C.bg, color:C.text, fontSize:13, boxSizing:'border-box' }}
                  />
                  {pwdMsg && <div style={{ fontSize:12, color: pwdMsg.ok ? C.green : C.rose, textAlign:'center' }}>{pwdMsg.text}</div>}
                  <button onClick={handleChangePwd} style={{
                    padding:'11px', borderRadius:10, fontSize:13, fontWeight:700, cursor:'pointer',
                    background:`linear-gradient(135deg,#006F87,${C.accent})`, color:C.white, border:'none',
                  }}>{t('profile.confirm')}</button>
                </div>
              )}
            </div>
            {/* 2. Abonnement */}
            {(() => {
              const plan = travelPrefs?.plan || 'free';
              const since = travelPrefs?.planSince ? new Date(travelPrefs.planSince) : null;
              const billing = travelPrefs?.planBilling || 'monthly';
              const renewal = since ? (() => {
                const d = new Date(since);
                billing === 'annual' ? d.setFullYear(d.getFullYear() + 1) : d.setMonth(d.getMonth() + 1);
                return d;
              })() : null;
              const fmtDate = d => d.toLocaleDateString(travelPrefs?.appLang === 'en' ? 'en-GB' : travelPrefs?.appLang === 'zh' ? 'zh-CN' : 'fr-FR', { day:'2-digit', month:'short', year:'numeric' });
              const planColor = plan === 'gold' ? C.gold : plan === 'silver' ? '#b0bec5' : C.accentHi;
              const planName = plan === 'gold' ? t('sub.planGold') : plan === 'silver' ? t('sub.planSilver') : t('sub.planFree');
              const Row = ({ label, value }) => (
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:`1px solid ${C.border}` }}>
                  <span style={{ color:C.sub, fontSize:13 }}>{label}</span>
                  <span style={{ color:C.text, fontSize:13, fontWeight:600 }}>{value}</span>
                </div>
              );
              return (
                <div style={{ padding:'16px 20px', borderBottom: refCode ? `1px solid ${C.border}` : 'none' }}>
                  <Row label={t('profile.planLabel')} value={<span style={{ color: planColor, fontWeight:800 }}>{planName}</span>} />
                  {plan !== 'free' && <Row label={t('profile.subscribedOn')} value={since ? fmtDate(since) : '—'} />}
                  {plan !== 'free' && <Row label={t('profile.renewalOn')} value={
                    renewal
                      ? <span>{fmtDate(renewal)} <span style={{ color:C.muted, fontSize:11 }}>· {billing === 'annual' ? t('sub.annual').toLowerCase() : t('sub.monthly').toLowerCase()}</span></span>
                      : '—'
                  } />}
                  <button onClick={() => setShowSubModal(true)} style={{
                    width:'100%', marginTop:14, padding:'11px', borderRadius:12,
                    background:`linear-gradient(135deg,#006F87,${C.accent})`,
                    border:'none', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer',
                  }}>{t('profile.managePlan')}</button>
                </div>
              );
            })()}
            {/* 3. Parrainage */}
            {refCode && (() => {
              const refLink = `${window.location.origin}?ref=${refCode}`;
              const bonusWeeks = travelPrefs?.silverBonusWeeks || 0;
              const copyRef = () => {
                navigator.clipboard.writeText(refLink).then(() => {
                  setRefCopied(true);
                  setTimeout(() => setRefCopied(false), 2000);
                });
              };
              const shareRef = () => {
                if (navigator.share) {
                  navigator.share({ title: 'Plotted', text: 'Découvre Plotted — ton Top 5 de voyages personnalisé !', url: refLink });
                } else {
                  copyRef();
                }
              };
              return (
                <div style={{ padding:'16px 20px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em' }}>{t('profile.referralSection')}</div>
                    <button onClick={() => setShowReferralHelp(true)} style={{
                      width:18, height:18, borderRadius:9, background:C.accentDim, border:'none',
                      color:C.accentHi, fontSize:11, fontWeight:800, cursor:'pointer',
                      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, padding:0,
                    }}>?</button>
                  </div>
                  <div style={{ fontSize:12, color:C.sub, marginBottom:10, lineHeight:1.5 }}>{t('profile.referralBonus')}</div>
                  {refInviteCount !== null && (
                    <div style={{ fontSize:12, color:C.accentHi, fontWeight:600, marginBottom:10 }}>
                      {t('profile.referralStats').replace('{n}', refInviteCount).replace('{w}', bonusWeeks)}
                    </div>
                  )}
                  <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, padding:'10px 12px', fontSize:12, color:C.sub, marginBottom:10, wordBreak:'break-all' }}>
                    {refLink}
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={copyRef} style={{ flex:1, padding:'9px', borderRadius:10, background: refCopied ? '#27C97A22' : C.bg, border:`1px solid ${refCopied ? C.green : C.border}`, color: refCopied ? C.green : C.text, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                      {refCopied ? t('profile.referralCopied') : t('profile.referralCopy')}
                    </button>
                    <button onClick={shareRef} style={{ flex:1, padding:'9px', borderRadius:10, background:`linear-gradient(135deg,#006F87,${C.accent})`, border:'none', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                      {t('profile.referralShare')}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ⑥ À propos */}
        {hubRow(t('about.title'), null, showAbout, setShowAbout, true)}
        {showAbout && (
          <div style={{ padding:'0 20px 24px', display:'flex', flexDirection:'column', gap:20 }}>
            {/* Intro */}
            <div>
              <div style={{ fontSize:13, fontStyle:'italic', color:C.accent, marginBottom:12 }}>{t('about.tagline')}</div>
              <div style={{ fontSize:13, color:C.sub, lineHeight:1.65, marginBottom:10 }}>{t('about.p1')}</div>
              <div style={{ fontSize:13, color:C.sub, lineHeight:1.65, marginBottom:10 }}>{t('about.p2')}</div>
              <div style={{ fontSize:13, color:C.sub, lineHeight:1.65, marginBottom:10 }}>{t('about.p3')}</div>
              <div style={{ fontSize:13, color:C.sub, lineHeight:1.65, marginBottom:10 }}>{t('about.p4')}</div>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, lineHeight:1.65 }}>{t('about.p5')}</div>
            </div>
            {/* Équipe */}
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:C.text, marginBottom:4 }}>{t('about.teamTitle')}</div>
              <div style={{ fontSize:13, color:C.sub, lineHeight:1.65, marginBottom:16 }}>{t('about.teamIntro')}</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {[
                  { nick:'TheRoamer',      seed:'TheRoamer',        roleKey:'m1role', bioKey:'m1bio' },
                  { nick:'Sketchaway',     seed:'Sketchaway-f',     roleKey:'m2role', bioKey:'m2bio' },
                  { nick:'FareHunter',     seed:'FareHunter',       roleKey:'m3role', bioKey:'m3bio' },
                  { nick:'AlwaysBoarding', seed:'AlwaysBoarding-f', roleKey:'m4role', bioKey:'m4bio' },
                  { nick:'ThePlaceCurator',seed:'ThePlaceCurator',  roleKey:'m5role', bioKey:'m5bio' },
                ].map(({ nick, seed, roleKey, bioKey }, i, arr) => (
                  <div key={nick} style={{
                    display:'flex', flexDirection:'column', gap:8,
                    background:C.cardB, borderRadius:12, padding:'10px',
                    border:`1px solid ${C.border}`,
                  }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <img
                        src={`https://api.dicebear.com/8.x/adventurer/svg?seed=${seed}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf`}
                        alt={nick}
                        style={{ width:40, height:40, borderRadius:'50%', flexShrink:0, background:C.bg }}
                      />
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:2 }}>{nick}</div>
                        <div style={{ fontSize:10, color:C.accent, fontWeight:600, lineHeight:1.4 }}>{t(`about.${roleKey}`)}</div>
                      </div>
                    </div>
                    <div style={{ fontSize:11, color:C.sub, lineHeight:1.5, marginBottom:-2 }}>{t(`about.${bioKey}`)}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Contact */}
            <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:16 }}>
              <div style={{ fontSize:14, fontWeight:800, color:C.text, marginBottom:6 }}>{t('about.contactTitle')}</div>
              <div style={{ fontSize:13, color:C.sub, lineHeight:1.6, marginBottom:8 }}>{t('about.contactText')}</div>
              <a href="mailto:info@plotted.travel"
                style={{ fontSize:13, color:C.accent, textDecoration:'none', fontWeight:600 }}>
                info@plotted.travel
              </a>
            </div>
          </div>
        )}

      </div>

      {showReferralHelp && createPortal(
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
          onClick={() => setShowReferralHelp(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background:C.card, borderRadius:'20px 20px 0 0', padding:'24px 24px 40px',
            width:'100%', maxWidth:600, borderTop:`1px solid ${C.border}`,
          }}>
            <div style={{ width:36, height:4, borderRadius:2, background:C.border, margin:'0 auto 20px' }}/>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
              <div style={{ width:32, height:32, borderRadius:16, background:C.accentDim,
                display:'flex', alignItems:'center', justifyContent:'center',
                color:C.accentHi, fontSize:16, fontWeight:800 }}>?</div>
              <span style={{ color:C.text, fontSize:16, fontWeight:800 }}>{t('profile.referralSection')}</span>
            </div>
            <p style={{ color:C.sub, fontSize:14, lineHeight:1.7, margin:0 }}>{t('profile.referralHelp')}</p>
            <button onClick={() => setShowReferralHelp(false)} style={{
              marginTop:20, width:'100%', padding:'13px', borderRadius:14,
              background:C.accent, border:'none', color:C.white, fontSize:14, fontWeight:700, cursor:'pointer',
            }}>{t('common.understood')}</button>
          </div>
        </div>,
        document.body
      )}

      {/* ── Déconnexion ── */}
      <div style={{ margin:'0 16px 32px' }}>
        <button onClick={onSignOut} style={{
          width:'100%', padding:'16px', borderRadius:16, fontSize:15, fontWeight:700, cursor:'pointer',
          background:'transparent', color:C.rose,
          border:`1.5px solid ${C.rose}`,
        }}>{t('profile.signOut')}</button>
      </div>

      {/* ── Suppression compte ── */}
      <div style={{ margin: '0 16px 8px', textAlign: 'center' }}>
        {deleteStep === 0 && (
          <button onClick={() => setDeleteStep(1)} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 12, cursor: 'pointer', textDecoration: 'underline', padding: '4px 8px' }}>
            {t('profile.deleteAccount')}
          </button>
        )}
        {deleteStep === 1 && (
          <div style={{ background: C.cardBg || '#fff8f8', border: `1px solid ${C.rose}`, borderRadius: 12, padding: '12px 14px' }}>
            <div style={{ fontSize: 12, color: C.text, marginBottom: 10 }}>{t('profile.deleteAccountConfirm')}</div>
            {deleteError && (
              <div style={{ fontSize: 12, color: C.rose, marginBottom: 10 }}>{deleteError}</div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setDeleteStep(0)} style={{ flex: 1, padding: '8px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'none', fontSize: 13, color: C.sub, cursor: 'pointer' }}>
                {t('profile.deleteAccountCancel')}
              </button>
              <button onClick={async () => {
                setDeleteError('');
                try {
                  const { error } = await supabase.functions.invoke('delete-account', {});
                  if (error) throw error;
                } catch (e) {
                  setDeleteError(t('profile.deleteAccountError'));
                  return;
                }
                setDeleteStep(2);
                onSignOut?.();
              }} style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: C.rose, fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer' }}>
                {t('profile.deleteAccountBtn')}
              </button>
            </div>
          </div>
        )}
        {deleteStep === 2 && (
          <div style={{ fontSize: 12, color: C.muted, padding: '8px 0' }}>{t('profile.deleteAccountDone')}</div>
        )}
      </div>

      {/* ── Footer éditeur + admin ── */}
      <div style={{ display:'flex', alignItems:'center', padding:'0 20px 32px' }}>
        <div style={{ flex:1, textAlign:'center', color:C.muted, fontSize:11, lineHeight:1.8 }}>
          <div style={{ fontWeight:700, color:C.sub, letterSpacing:'0.08em' }}>PLOTTED</div>
          <div style={{ color:C.sub }}>{t('brand.tagline')}</div>
          <div>Éditeur de l'application</div>
        </div>
        <button onClick={() => setShowAdmin(true)} style={{
          background:'none', border:'none', color:C.muted, fontSize:16, cursor:'pointer', padding:'4px 8px',
        }}>⚙</button>
      </div>

      {showAdmin && <AdminScreen onClose={() => setShowAdmin(false)} adminUserId={user?.id} />}
      {showSubModal && <SubscriptionModal appLang={travelPrefs?.appLang} currentPlan={travelPrefs?.plan||'free'} onUpgrade={(planId, billing) => { onUpgrade?.(planId, billing); setShowSubModal(false); }} prefillEmail={travelPrefs?.email||''} onClose={() => setShowSubModal(false)}/>}

      {/* ── Traveler modal ── */}
      {travelerModal && (() => {
        const isMe = travelerModal.type === 'me';
        const ct = isMe ? null : coTravelers.find(c => c.id === travelerModal.id);
        if (!isMe && !ct) return null;
        const modalTitle = isMe
          ? (fullName !== t('profile.yourProfile') ? fullName : t('profile.me'))
          : ([ct.firstName, ct.lastName].filter(Boolean).join(' ') || '—');

        const ctDateField = (label, key) => {
          const handleChange = e => {
            const digits = e.target.value.replace(/\D/g,'').slice(0,8);
            let v = digits;
            if (digits.length > 4) v = digits.slice(0,2)+'/'+digits.slice(2,4)+'/'+digits.slice(4);
            else if (digits.length > 2) v = digits.slice(0,2)+'/'+digits.slice(2);
            updateCoTraveler(ct.id, key, v);
          };
          return (
            <div style={{ marginBottom:16 }}>
              <div style={{ color:C.sub, fontSize:12, fontWeight:600, marginBottom:6 }}>{label}</div>
              <input type="text" inputMode="numeric" value={ct[key] || ''} onChange={handleChange}
                placeholder={t('profile.phDate')}
                onFocus={e => e.target.style.borderColor = C.accent}
                onBlur={e => e.target.style.borderColor = `${C.accent}44`}
                style={{ width:'100%', minWidth:0, boxSizing:'border-box', background:C.bg,
                  border:`1.5px solid ${C.accent}44`, borderRadius:12,
                  padding:'13px 14px', color:C.text, fontSize:14, outline:'none', letterSpacing:'1px' }}
              />
            </div>
          );
        };
        const ctTextField = (label, key, placeholder) => (
          <div style={{ marginBottom:16 }}>
            <div style={{ color:C.sub, fontSize:12, fontWeight:600, marginBottom:6 }}>{label}</div>
            <input value={ct[key] || ''} onChange={e => updateCoTraveler(ct.id, key, e.target.value)}
              placeholder={placeholder || ''}
              onFocus={e => e.target.style.borderColor = C.accent}
              onBlur={e => e.target.style.borderColor = `${C.accent}44`}
              style={{ width:'100%', minWidth:0, boxSizing:'border-box', background:C.bg,
                border:`1.5px solid ${C.accent}44`, borderRadius:12,
                padding:'13px 14px', color:C.text, fontSize:14, outline:'none' }}
            />
          </div>
        );

        return (
          <div style={{ position:'fixed', inset:0, zIndex:500, background:'rgba(0,0,0,0.65)',
            display:'flex', flexDirection:'column', justifyContent:'flex-end' }}
            onClick={e => { if (e.target === e.currentTarget) setTravelerModal(null); }}>
            <div style={{ background:C.card, borderRadius:'20px 20px 0 0',
              maxHeight:'88vh', overflowY:'auto' }}>
              {/* Header */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'20px 20px 16px', borderBottom:`1px solid ${C.border}`,
                position:'sticky', top:0, background:C.card, zIndex:1 }}>
                <div style={{ fontSize:16, fontWeight:700, color:C.text }}>{modalTitle}</div>
                <button onClick={() => setTravelerModal(null)}
                  style={{ background:'none', border:'none', color:C.muted,
                    fontSize:26, cursor:'pointer', lineHeight:1, padding:'0 4px' }}>×</button>
              </div>
              {/* Fields */}
              <div style={{ padding:'20px 20px 8px' }}>
                {isMe ? (
                  <>
                    {dateField(t('profile.birthDate'), 'birthDate')}
                    {field(t('profile.passportNumber'), 'passportNumber', { placeholder: t('profile.phPassport') })}
                    {dateField(t('profile.passportExpiry'), 'passportExpiry')}
                    {field(t('profile.passportCountry'), 'passportCountry', { placeholder: t('profile.phCountry') })}
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom:16 }}>
                      <div style={{ color:C.sub, fontSize:12, fontWeight:600, marginBottom:8 }}>{t('profile.relation')}</div>
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                        {RELATION_CODES.map(code => {
                          const active = ct.relation === code;
                          return (
                            <button key={code} onClick={() => updateCoTraveler(ct.id, 'relation', code)}
                              style={{ padding:'7px 14px', borderRadius:14, fontSize:12, fontWeight:600,
                                background: active ? C.accent : C.cardB,
                                color: active ? C.white : C.sub,
                                border:`1.5px solid ${active ? C.accent : C.border}`, cursor:'pointer' }}>
                              {relLabel(code)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:10 }}>
                      <div style={{ flex:1 }}>{ctTextField(t('profile.firstName'), 'firstName', t('profile.phFirstName'))}</div>
                      <div style={{ flex:1 }}>{ctTextField(t('profile.lastName'), 'lastName', t('profile.phLastName'))}</div>
                    </div>
                    {ctDateField(t('profile.birthDate'), 'birthDate')}
                    {ctTextField(t('profile.passportNumber'), 'passport', t('profile.phPassport'))}
                    <div style={{ display:'flex', gap:10 }}>
                      <div style={{ flex:1 }}>{ctDateField(t('profile.passportExpiry'), 'passportExpiry')}</div>
                      <div style={{ flex:1 }}>{ctTextField(t('profile.passportCountry'), 'passportCountry', t('profile.phCountry'))}</div>
                    </div>
                  </>
                )}
              </div>
              {/* Footer */}
              <div style={{ padding:'8px 20px 40px' }}>
                <button onClick={() => setTravelerModal(null)} style={{ ...btnPrimary }}>
                  {t('profile.save')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </Scroll>
  );
}

// ─── STATS ────────────────────────────────────────────────────────────────────

async function logEvent(userId, sessionId, type, payload = {}) {
  try { await supabase.from('plotted_events').insert({ user_id: userId, session_id: sessionId, event_type: type, payload }); }
  catch (_) {}
}

function mkBuckets(period) {
  const now = Date.now(), DAY = 86400000;
  if (period === 'day') return Array.from({length:14}, (_, i) => {
    const d = new Date(now - (13-i)*DAY); d.setHours(0,0,0,0);
    return { label:`${d.getDate()}/${d.getMonth()+1}`, from:d.getTime(), to:d.getTime()+86399999, count:0 };
  });
  if (period === 'week') return Array.from({length:8}, (_, i) => {
    const t = now - (7-i)*7*DAY; const d = new Date(t);
    const mon = new Date(t - ((d.getDay()||7)-1)*DAY); mon.setHours(0,0,0,0);
    const sun = new Date(mon.getTime()+6*DAY); sun.setHours(23,59,59,999);
    return { label:`${mon.getDate()}/${mon.getMonth()+1}`, from:mon.getTime(), to:sun.getTime(), count:0 };
  });
  return Array.from({length:6}, (_, i) => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth()-(5-i));
    const to = new Date(d.getFullYear(), d.getMonth()+1, 0, 23, 59, 59, 999).getTime();
    return { label:d.toLocaleString('fr',{month:'short'}), from:d.getTime(), to, count:0 };
  });
}

function fillBuckets(buckets, events, getTime) {
  const result = buckets.map(b => ({...b}));
  events.forEach(e => { const s = result.find(b => { const t=getTime(e); return t>=b.from && t<=b.to; }); if (s) s.count++; });
  return result;
}

function BarChart({ data, color = '#009EC2' }) {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:3, height:80 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:3, minWidth:0 }}>
          <span style={{ fontSize:8, color:'#666', lineHeight:1 }}>{d.count > 0 ? d.count : ''}</span>
          <div style={{ width:'100%', borderRadius:'3px 3px 0 0', background: d.count > 0 ? color : '#1A1A1A',
            height: Math.max(2, Math.round(d.count / max * 54)) }} />
          <span style={{ fontSize:7, color:'#555', lineHeight:1, width:'100%', textAlign:'center',
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}

function SCard({ label, value, sub }) {
  return (
    <div style={{ background:'#111', border:'1px solid #1E1E1E', borderRadius:14, padding:'14px 16px', flex:1, minWidth:0 }}>
      <div style={{ fontSize:10, color:'#666', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:800, color:'#E6FBFF', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:10, color:'#555', marginTop:4 }}>{sub}</div>}
    </div>
  );
}

const TAB_LABELS = { home:'Accueil', dest:'Destinations', map:'Carte', cal:'Calendrier', user:'Profil', trips:'Voyages' };

function ApifyPanel() {
  const APIFY_KEY = 'apify_token';
  const [token,       setToken]       = useState(() => localStorage.getItem(APIFY_KEY) || '');
  const [url,         setUrl]         = useState('');
  const [running,     setRunning]     = useState(false);
  const [reviews,     setReviews]     = useState(null);
  const [error,       setError]       = useState(null);
  const [maxRev,      setMaxRev]      = useState(50);
  const [synthesizing,setSynthesizing]= useState(false);
  const [synthesis,   setSynthesis]   = useState(null);
  const [synthError,  setSynthError]  = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [history,     setHistory]     = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const [activeSection, setActiveSection] = useState('new'); // 'new' | 'history'

  const saveToken = (v) => { setToken(v); localStorage.setItem(APIFY_KEY, v); };

  const loadHistory = async () => {
    setLoadingHist(true);
    const { data, error } = await supabase.from('place_analyses')
      .select('id, place_name, google_score, reviews_analyzed, synthesis, analyzed_at')
      .order('analyzed_at', { ascending: false })
      .limit(50);
    console.log('[loadHistory] data:', data, 'error:', error);
    setHistory(data || []);
    setLoadingHist(false);
  };

  const saveAnalysis = async () => {
    if (!synthesis || !reviews) return;
    setSaving(true);
    const payload = {
      place_name: reviews.title || reviews.name || 'Inconnu',
      place_url: url,
      place_address: reviews.address || null,
      google_score: reviews.totalScore || null,
      google_review_count: reviews.reviewsCount || null,
      reviews_analyzed: reviews.reviews?.filter(r => r.text).length || 0,
      synthesis,
    };
    console.log('[saveAnalysis] payload:', payload);
    const { data: inserted, error: insertError } = await supabase.from('place_analyses').insert(payload).select();
    console.log('[saveAnalysis] inserted:', inserted, 'error:', insertError);
    setSaving(false);
    if (insertError) {
      setSynthError('Erreur sauvegarde : ' + insertError.message);
    } else {
      setSaved(true);
      await loadHistory();
      setActiveSection('history');
    }
  };

  const run = async () => {
    if (!token || !url) return;
    setRunning(true); setError(null); setReviews(null); setSynthesis(null);
    try {
      const res = await fetch(
        `https://api.apify.com/v2/acts/compass~google-maps-reviews-scraper/run-sync-get-dataset-items?token=${token}&timeout=120`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ startUrls: [{ url }], maxReviews: maxRev, reviewsSort: 'mostRelevant', language: 'fr' }),
        }
      );
      if (!res.ok) throw new Error(`Apify error ${res.status}`);
      const data = await res.json();
      let place;
      if (Array.isArray(data) && data.length > 0) {
        const first = data[0];
        if (first.reviews) {
          // Format A : un objet place avec reviews[]
          place = first;
        } else {
          // Format B : chaque item est un avis (format le plus courant)
          place = {
            title: first.title || first.placeName || first.name,
            totalScore: first.totalScore,
            reviewsCount: first.reviewsCount || data.length,
            address: first.address,
            reviews: data.map(r => ({
              name: r.reviewer?.displayName || r.reviewerName || r.name || 'Anonyme',
              stars: r.stars,
              text: r.text,
              publishedAtDate: r.publishedAtDate || r.reviewDate,
            })),
          };
        }
      } else {
        place = Array.isArray(data) ? data[0] : data;
      }
      setReviews(place);
    } catch (e) { setError(e.message); }
    setRunning(false);
  };

  const analyze = async () => {
    if (!reviews?.reviews?.length) return;
    setSynthesizing(true); setSynthError(null); setSynthesis(null);
    try {
      const reviewsText = (reviews.reviews || [])
        .filter(r => r.text)
        .map((r, i) => `[${i+1}] ${r.stars}★ — ${r.text}`)
        .join('\n');

      const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/claude-proxy`;
      const fnRes = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_KEY}`,
          'apikey': process.env.REACT_APP_SUPABASE_KEY,
        },
        body: JSON.stringify({
          model: 'claude-haiku-4-5-20251001',
          max_tokens: 1200,
          system: 'Tu es un expert en analyse de réputation. Réponds toujours en français avec le format JSON demandé, sans markdown autour du JSON.',
          messages: [{
            role: 'user',
            content: `Voici ${reviews.reviews.filter(r=>r.text).length} avis clients pour "${reviews.title || reviews.name}" (note globale : ${reviews.totalScore?.toFixed(1)}/5).\n\n${reviewsText}\n\nAnalyse ces avis et réponds UNIQUEMENT avec ce JSON (sans balises markdown) :\n{\n  "verdict": "Excellent | Bien | Mitigé | Déconseillé",\n  "score": <note sur 10>,\n  "resume": "<2-3 phrases de synthèse>",\n  "points_forts": ["...", "...", "..."],\n  "points_faibles": ["...", "...", "..."],\n  "profil_clientele": "<qui fréquente cet endroit>",\n  "a_savoir": "<1 info clé que les avis révèlent>"\n}`,
          }],
        }),
      });
      const rawText = await fnRes.text();
      if (!fnRes.ok) throw new Error(`Edge Function ${fnRes.status}: ${rawText}`);
      const resData = JSON.parse(rawText);
      if (resData.error) throw new Error(`Anthropic: ${resData.error.message || JSON.stringify(resData.error)}`);
      const text = resData.content?.[0]?.text || '';
      if (!text) throw new Error(`Réponse vide: ${rawText}`);
      const json = JSON.parse(text.replace(/```json\n?/g,'').replace(/```\n?/g,'').trim());
      setSynthesis(json);
    } catch (e) { setSynthError(e.message); }
    setSynthesizing(false);
  };

  const inp = (extra = {}) => ({
    background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 10,
    padding: '10px 14px', color: '#E6FBFF', fontSize: 13, outline: 'none',
    width: '100%', boxSizing: 'border-box', ...extra,
  });

  const stars = n => '★'.repeat(Math.round(n || 0)) + '☆'.repeat(5 - Math.round(n || 0));

  const verdictColor = v => ({ 'Excellent':'#4AC47F','Bien':'#4DD4F0','Mitigé':'#F4A928','Déconseillé':'#ef4444' }[v] || '#888');

  return (
    <div style={{ padding: '16px 16px 80px', display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Toggle Nouvelle analyse / Historique */}
      <div style={{ display:'flex', background:'#0D0D0D', borderRadius:12, padding:3, border:'1px solid #1A1A1A' }}>
        {[['new','Nouvelle analyse'],['history','Mes analyses']].map(([k,l]) => (
          <button key={k} onClick={() => { setActiveSection(k); if (k==='history') loadHistory(); }} style={{
            flex:1, padding:'8px', borderRadius:10, border:'none',
            background: activeSection===k ? '#1A1A1A' : 'transparent',
            color: activeSection===k ? '#4DD4F0' : '#555',
            fontSize:12, fontWeight:700, cursor:'pointer',
          }}>{l}</button>
        ))}
      </div>

      {/* ── Historique ── */}
      {activeSection === 'history' && (
        loadingHist
          ? <div style={{ color:'#555', fontSize:13, textAlign:'center', padding:40 }}>Chargement…</div>
          : history.length === 0
            ? <div style={{ color:'#444', fontSize:13, textAlign:'center', padding:40 }}>Aucune analyse sauvegardée.</div>
            : history.map(h => (
              <div key={h.id} style={{ background:'#0D0D0D', border:`1px solid ${verdictColor(h.synthesis?.verdict)}33`, borderRadius:14, padding:14 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
                  <div style={{ fontSize:14, fontWeight:800, color:'#E6FBFF' }}>{h.place_name}</div>
                  <div style={{ fontSize:20, fontWeight:900, color:verdictColor(h.synthesis?.verdict) }}>{h.synthesis?.score}<span style={{ fontSize:11, color:'#555' }}>/10</span></div>
                </div>
                <div style={{ display:'flex', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                  <span style={{ fontSize:11, color:verdictColor(h.synthesis?.verdict), fontWeight:700 }}>{h.synthesis?.verdict}</span>
                  {h.google_score && <span style={{ fontSize:11, color:'#F4A928' }}>Google {h.google_score.toFixed(1)}/5</span>}
                  <span style={{ fontSize:11, color:'#444' }}>{h.reviews_analyzed} avis analysés</span>
                </div>
                <div style={{ fontSize:12, color:'#888', lineHeight:1.6, marginBottom:6 }}>{h.synthesis?.resume}</div>
                <div style={{ fontSize:10, color:'#333' }}>{new Date(h.analyzed_at).toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' })}</div>
              </div>
            ))
      )}

      {activeSection === 'new' && <>
      {/* Token Apify */}
      <div style={{ background: '#0D0D0D', border: '1px solid #1A1A1A', borderRadius: 14, padding: 14 }}>
        <div style={{ fontSize: 10, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Token Apify</div>
        <input type="password" value={token} onChange={e => saveToken(e.target.value)}
          placeholder="apify_api_xxxxxxxxxxxxxxxx" style={inp()} />
        <div style={{ fontSize: 11, color: '#444', marginTop: 6 }}>apify.com → Account → Integrations → API token</div>
      </div>

      {/* URL + nb avis */}
      <div style={{ background: '#0D0D0D', border: '1px solid #1A1A1A', borderRadius: 14, padding: 14 }}>
        <div style={{ fontSize: 10, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>URL Google Maps</div>
        <input value={url} onChange={e => setUrl(e.target.value)}
          placeholder="https://maps.google.com/?cid=..." style={inp({ marginBottom: 10 })} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 12, color: '#666' }}>Nb avis :</div>
          {[20, 50, 100, 200].map(n => (
            <button key={n} onClick={() => setMaxRev(n)} style={{
              background: maxRev === n ? 'rgba(0,158,194,0.2)' : 'none',
              border: `1px solid ${maxRev === n ? '#009EC2' : '#2A2A2A'}`,
              borderRadius: 20, padding: '4px 10px',
              color: maxRev === n ? '#4DD4F0' : '#555',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}>{n}</button>
          ))}
        </div>
      </div>

      <button onClick={run} disabled={running || !token || !url} style={{
        background: running ? '#1A1A1A' : 'linear-gradient(135deg,#009EC2,#4DD4F0)',
        border: 'none', borderRadius: 12, padding: '13px',
        color: running ? '#555' : '#000', fontSize: 14, fontWeight: 800,
        cursor: running || !token || !url ? 'default' : 'pointer',
      }}>
        {running ? 'Récupération en cours… (30-90s)' : 'Récupérer les avis'}
      </button>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, padding: 14, color: '#f87171', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Résultats Apify */}
      {reviews && (
        <>
          <div style={{ background: '#0D0D0D', border: '1px solid #1A1A1A', borderRadius: 14, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#E6FBFF' }}>{reviews.title || reviews.name || '—'}</div>
              {reviews.totalScore && (
                <div style={{ fontSize: 13, color: '#F4A928', fontWeight: 700 }}>
                  {reviews.totalScore.toFixed(1)}/5 · {reviews.reviews?.length} avis récupérés
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#444' }}>{reviews.address || ''}</div>
          </div>

          {/* Bouton analyse Claude */}
          <button onClick={analyze} disabled={synthesizing || !reviews.reviews?.length} style={{
            background: synthesizing ? '#1A1A1A' : 'linear-gradient(135deg,#5B21B6,#7C3AED)',
            border: 'none', borderRadius: 12, padding: '13px',
            color: synthesizing ? '#555' : '#fff', fontSize: 14, fontWeight: 800,
            cursor: synthesizing || !reviews.reviews?.length ? 'default' : 'pointer',
          }}>
            {synthesizing ? 'Analyse en cours…' : `Analyser avec Claude (${reviews.reviews?.filter(r=>r.text).length} avis)`}
          </button>

          {synthError && (
            <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, padding: 14, color: '#f87171', fontSize: 13 }}>
              {synthError}
            </div>
          )}

          {/* Synthèse Claude */}
          {synthesis && (
            <div style={{ background: '#0D0D0D', border: `1px solid ${verdictColor(synthesis.verdict)}44`, borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: verdictColor(synthesis.verdict) }}>{synthesis.verdict}</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: verdictColor(synthesis.verdict) }}>{synthesis.score}<span style={{ fontSize: 14, color: '#555' }}>/10</span></div>
              </div>
              <div style={{ fontSize: 13, color: '#ccc', lineHeight: 1.7 }}>{synthesis.resume}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#4AC47F', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>Points forts</div>
                  {synthesis.points_forts?.map((p, i) => <div key={i} style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>+ {p}</div>)}
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>Points faibles</div>
                  {synthesis.points_faibles?.map((p, i) => <div key={i} style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>− {p}</div>)}
                </div>
              </div>
              {synthesis.profil_clientele && (
                <div style={{ borderTop: '1px solid #1A1A1A', paddingTop: 10 }}>
                  <div style={{ fontSize: 10, color: '#555', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase' }}>Clientèle</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{synthesis.profil_clientele}</div>
                </div>
              )}
              {synthesis.a_savoir && (
                <div style={{ background: 'rgba(244,169,40,0.08)', border: '1px solid rgba(244,169,40,0.2)', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, color: '#F4A928', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase' }}>À savoir</div>
                  <div style={{ fontSize: 12, color: '#ccc' }}>{synthesis.a_savoir}</div>
                </div>
              )}
              <button onClick={saveAnalysis} disabled={saving || saved} style={{
                background: saved ? '#1A3A1A' : saving ? '#1A1A1A' : 'linear-gradient(135deg,#166534,#4AC47F)',
                border: `1px solid ${saved ? '#4AC47F' : '#2A2A2A'}`,
                borderRadius: 10, padding: '11px',
                color: saved ? '#4AC47F' : saving ? '#555' : '#fff',
                fontSize: 13, fontWeight: 800, cursor: saving || saved ? 'default' : 'pointer',
                width: '100%',
              }}>
                {saved ? 'Sauvegardé' : saving ? 'Sauvegarde…' : 'Sauvegarder dans Mes analyses'}
              </button>
            </div>
          )}

          {/* Liste brute des avis */}
          <div style={{ background: '#0D0D0D', border: '1px solid #1A1A1A', borderRadius: 14, padding: 14 }}>
            <div style={{ fontSize: 10, color: '#555', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Avis bruts</div>
            {(reviews.reviews || []).map((r, i) => (
              <div key={i} style={{ borderTop: i > 0 ? '1px solid #1A1A1A' : 'none', paddingTop: i > 0 ? 12 : 0, marginTop: i > 0 ? 12 : 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#888' }}>{r.name || 'Anonyme'}</div>
                  <div style={{ fontSize: 12, color: '#F4A928' }}>{stars(r.stars)}</div>
                </div>
                {r.text && <div style={{ fontSize: 12, color: '#666', lineHeight: 1.6 }}>{r.text}</div>}
                {r.publishedAtDate && <div style={{ fontSize: 10, color: '#333', marginTop: 3 }}>{new Date(r.publishedAtDate).toLocaleDateString('fr-FR')}</div>}
              </div>
            ))}
            {(!reviews.reviews || reviews.reviews.length === 0) && (
              <div style={{ color: '#444', fontSize: 13 }}>Aucun avis retourné.</div>
            )}
          </div>
        </>
      )}

      {activeSection === 'new' && <div style={{ height: 1 }} />}
      </>}
    </div>
  );
}

function StatsScreen({ onClose, adminUserId, defaultAuthed = false, noPortal = false }) {
  // defaultAuthed means the parent (AdminScreen) already checked the role.
  const [authed, setAuthed]   = useState(defaultAuthed ? true : null);
  const [loading, setLoading] = useState(false);
  const [data, setData]       = useState(null);
  const [period, setPeriod]   = useState('week');
  const [openAcc, setOpenAcc] = useState(new Set(['acquisition']));
  const [selectedUser, setSelectedUser] = useState(null);

  const toggleAcc = key => setOpenAcc(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const fetchAllEvents = async (from) => {
    const all = []; const BATCH = 1000; let offset = 0;
    while (true) {
      const { data } = await supabase.from('plotted_events')
        .select('user_id, event_type, created_at, payload')
        .gte('created_at', from).order('created_at').range(offset, offset + BATCH - 1);
      if (!data || data.length === 0) break;
      all.push(...data);
      if (data.length < BATCH) break;
      offset += BATCH;
    }
    return all;
  };

  const load = async () => {
    setLoading(true);
    const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 3600 * 1000).toISOString();
    const [evts, { data: profiles }] = await Promise.all([
      fetchAllEvents(ninetyDaysAgo),
      supabase.from('profiles').select('user_id, travel_prefs, selected_destinations'),
    ]);
    if (evts.length > 0) {
      const TEST_EMAILS = new Set(['boutissou@yahoo.fr']);
      const testUserIds = new Set((profiles||[]).filter(p => TEST_EMAILS.has(p.travel_prefs?.email)).map(p => p.user_id));
      const isExcluded = e => e.user_id === adminUserId || testUserIds.has(e.user_id);
      const isProd = e => !['localhost','127.0.0.1'].includes(e.payload?.host);
      const prod = evts.filter(e => isProd(e) && !isExcluded(e));

      const profileMap = Object.fromEntries((profiles||[]).map(p => [p.user_id, p.travel_prefs]));
      const destMap    = Object.fromEntries((profiles||[]).map(p => {
        let ids = [];
        try { ids = JSON.parse(p.selected_destinations || '[]'); } catch (_) {}
        return [p.user_id, ids];
      }));

      // Meilleure date de création connue par user :
      // 1) première session_start (toujours présente), 2) user_created si dispo (plus précis)
      const joinDates = {};
      prod.filter(e => e.event_type === 'session_start').forEach(e => {
        if (!joinDates[e.user_id] || e.created_at < joinDates[e.user_id]) joinDates[e.user_id] = e.created_at;
      });
      prod.filter(e => e.event_type === 'user_created').forEach(e => {
        joinDates[e.user_id] = e.created_at; // user_created plus précis quand disponible
      });

      // Source de vérité : tous les profils (créés via upsert à chaque login, jamais manqués)
      const allUsers = (profiles||[])
        .filter(p => p.user_id !== adminUserId && !testUserIds.has(p.user_id))
        .map(p => ({ user_id: p.user_id, created_at: joinDates[p.user_id] || null }));

      setData({
        allUsers,
        joinDates,
        created:  allUsers, // compat : graphes utilisent data.created
        sessions: prod.filter(e => e.event_type === 'session_start'),
        ends:     prod.filter(e => e.event_type === 'session_end'),
        profileMap,
        destMap,
      });
    }
    setLoading(false);
  };

  useEffect(() => { if (authed === true) load(); }, [authed]); // eslint-disable-line

  useEffect(() => {
    if (defaultAuthed) return;
    let stale = false;
    supabase.rpc('is_admin').then(({ data, error }) => {
      if (!stale) setAuthed(!error && data === true);
    });
    return () => { stale = true; };
  }, [defaultAuthed]);

  const byDate = e => new Date(e.created_at).getTime();
  const toSec  = e => Number(e.payload?.duration_s) || 0;
  const fmtDur = s => s < 60 ? `${s}s` : `${Math.floor(s/60)}m${s%60 ? s%60+'s':''}`;
  const avgDur = arr => arr.length ? Math.round(arr.reduce((a,e)=>a+toSec(e),0)/arr.length) : 0;

  if (authed !== true) return createPortal(
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', backdropFilter:'blur(12px)', zIndex:9000,
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16, padding:24 }}>
      <span style={{ fontSize:40 }}>📊</span>
      <div style={{ fontSize:20, fontWeight:800, color:'#E6FBFF' }}>Admin · Stats</div>
      <div style={{ color: authed === null ? '#7ab8c8' : '#ef4444', fontSize:13 }}>
        {authed === null ? 'Vérification…' : "Ce compte n'a pas le rôle admin."}
      </div>
      <button onClick={onClose} style={{ background:'none', border:'none', color:'#555', cursor:'pointer', fontSize:13, marginTop:8 }}>Fermer</button>
    </div>,
    document.body
  );

  const PBtn = ({p, label}) => (
    <button onClick={() => setPeriod(p)} style={{
      background: period===p ? 'rgba(0,158,194,0.2)' : 'none',
      border:`1px solid ${period===p?'#009EC2':'#2A2A2A'}`, borderRadius:20, padding:'5px 14px',
      color: period===p ? '#4DD4F0' : '#666', fontSize:11, fontWeight:700, cursor:'pointer',
    }}>{label}</button>
  );
  const Box = ({title, children}) => (
    <div style={{ background:'#0F0F0F', border:'1px solid #1A1A1A', borderRadius:14, padding:'14px 12px', marginBottom:10 }}>
      {title && <div style={{ fontSize:10, color:'#555', marginBottom:10, textTransform:'uppercase', letterSpacing:'0.08em' }}>{title}</div>}
      {children}
    </div>
  );
  const Accordion = ({id, title, children}) => {
    const isOpen = openAcc.has(id);
    return (
      <div style={{ background:'#0D0D0D', border:'1px solid #1A1A1A', borderRadius:14, overflow:'hidden', marginBottom:10 }}>
        <div onClick={() => toggleAcc(id)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'14px 16px', cursor:'pointer' }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#888', textTransform:'uppercase', letterSpacing:'0.1em' }}>{title}</div>
          <span style={{ color:'#444', fontSize:18, display:'inline-block', transition:'transform .2s',
            transform: isOpen ? 'rotate(90deg)' : 'none' }}>›</span>
        </div>
        {isOpen && <div style={{ padding:'0 12px 14px', borderTop:'1px solid #1A1A1A' }}>{children}</div>}
      </div>
    );
  };
  const pctBar = (n, total, color='#009EC2') => (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, height:4, background:'#1A1A1A', borderRadius:2, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${total>0?Math.round(n/total*100):0}%`, background:color, borderRadius:2 }}/>
      </div>
      <div style={{ color:'#4DD4F0', fontSize:12, fontWeight:700, width:36, textAlign:'right' }}>
        {total>0?Math.round(n/total*100):0}%
      </div>
      <div style={{ color:'#444', fontSize:11, width:20 }}>{n}</div>
    </div>
  );

  const computeStats = () => {
    if (!data) return null;
    const now = Date.now();
    const total = data.allUsers.length; // profiles = source de vérité (upsert à chaque login)
    const active7d  = new Set(data.sessions.filter(e => now-byDate(e) < 7*86400000).map(e=>e.user_id)).size;
    const active30d = new Set(data.sessions.filter(e => now-byDate(e) < 30*86400000).map(e=>e.user_id)).size;
    const avgSess = total > 0 ? (data.sessions.length / total).toFixed(1) : '—';
    const avgDurAll = fmtDur(avgDur(data.ends));

    const usersWithAirport = data.created.filter(e => {
      const p = data.profileMap[e.user_id];
      return p?.departureAirports?.length > 0 || !!p?.departureAirport;
    }).length;
    const usersWithPrefs = data.created.filter(e => {
      const p = data.profileMap[e.user_id];
      return (p?.style?.length>0) || !!p?.with || !!p?.dailyBudget;
    }).length;
    const usersWithDest = data.created.filter(e => {
      const p = data.profileMap[e.user_id];
      return (p?.planIds?.length > 0);
    }).length;
    const usersReturned = data.created.filter(e => {
      const uid = e.user_id;
      return data.sessions.filter(s => s.user_id === uid).length > 1;
    }).length;

    const buckets = mkBuckets(period);
    const from = buckets[0].from, to = buckets[buckets.length-1].to;
    const sessInPeriod = data.sessions.filter(e => { const t=byDate(e); return t>=from&&t<=to; });
    const endsInPeriod = data.ends.filter(e => { const t=byDate(e); return t>=from&&t<=to; });
    const usersWithDate = data.allUsers.filter(u => u.created_at);
    const newInPeriod  = fillBuckets(mkBuckets(period), usersWithDate, u => new Date(u.created_at).getTime());
    const sessChart    = fillBuckets(mkBuckets(period), data.sessions, byDate);
    const durChart     = (() => {
      const slots = mkBuckets(period);
      data.ends.forEach(e => { const t=byDate(e); const s=slots.find(b=>t>=b.from&&t<=b.to);
        if(s){s.sum=(s.sum||0)+toSec(e);s.cnt=(s.cnt||0)+1;} });
      return slots.map(s=>({...s,count:s.cnt?Math.round((s.sum||0)/s.cnt):0}));
    })();

    const counts={}, durSums={}, durCnts={};
    sessInPeriod.forEach(e=>{counts[e.user_id]=(counts[e.user_id]||0)+1;});
    endsInPeriod.forEach(e=>{const s=toSec(e);if(s>0){durSums[e.user_id]=(durSums[e.user_id]||0)+s;durCnts[e.user_id]=(durCnts[e.user_id]||0)+1;}});
    const topUsers = Object.entries(counts).sort((a,b)=>b[1]-a[1]).slice(0,10);

    const exitMap={};
    data.ends.forEach(e=>{const tab=e.payload?.last_tab;if(tab)exitMap[tab]=(exitMap[tab]||0)+1;});
    const topExits = Object.entries(exitMap).sort((a,b)=>b[1]-a[1]).slice(0,5);

    const flightOnly = data.created.filter(e=>data.profileMap[e.user_id]?.flightOnly!==false).length;

    const noDateCount = data.allUsers.filter(u => !u.created_at).length; // users sans event dans les 90j

    return { total, active7d, active30d, avgSess, avgDurAll,
      usersWithAirport, usersWithPrefs, usersWithDest, usersReturned,
      newInPeriod, sessChart, durChart, topUsers, topExits, flightOnly,
      counts, durSums, durCnts, noDateCount };
  };
  const s = computeStats();

  const statsBody = (
    <div style={{ ...(noPortal ? { flex:1, overflowY:'auto' } : { position:'fixed', inset:0, zIndex:9000, overflowY:'auto' }), background:'#080808', fontFamily:"'DM Sans',sans-serif" }}>
      {/* Header */}
      <div style={{ position:'sticky', top:0, background:'rgba(8,8,8,0.96)', backdropFilter:'blur(14px)',
        borderBottom:'1px solid #1A1A1A', padding:'max(16px,env(safe-area-inset-top)) 16px 12px',
        display:'flex', alignItems:'center', justifyContent:'space-between', zIndex:1 }}>
        <div style={{ fontSize:17, fontWeight:800, color:'#E6FBFF' }}>Stats</div>
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>
          <PBtn p="day" label="Jour" /><PBtn p="week" label="Sem." /><PBtn p="month" label="Mois" />
          <button onClick={load} disabled={loading} style={{ background:'none', border:'1px solid #2A2A2A', borderRadius:20, padding:'5px 10px', color: loading ? '#444' : '#666', fontSize:13, fontWeight:700, cursor: loading ? 'default' : 'pointer' }}>↻</button>
          <button onClick={() => { window.location.href = window.location.origin + '?ob'; }}
            style={{ background:'none', border:'1px solid #2A2A2A', borderRadius:20, padding:'5px 12px',
              color:'#666', fontSize:11, fontWeight:700, cursor:'pointer' }}>OB</button>
          <button onClick={onClose} style={{ background:'#1A1A1A', border:'none', borderRadius:20,
            padding:'6px 14px', color:'#888', fontSize:12, cursor:'pointer', marginLeft:6 }}>✕</button>
        </div>
      </div>

      {loading && <div style={{ textAlign:'center', padding:80, color:'#555', fontSize:14 }}>Chargement…</div>}

      {/* ── User drill-down panel ── */}
      {selectedUser && (() => {
        const uid = selectedUser;
        const prefs = data?.profileMap?.[uid];
        const name = [prefs?.firstName, prefs?.lastName].filter(Boolean).join(' ') || `···${uid.slice(-6)}`;
        const firstEvt = { created_at: data.joinDates?.[uid] || null };
        const allSessions = data.sessions.filter(e => e.user_id === uid);
        const allEnds = data.ends.filter(e => e.user_id === uid);
        const avgDurUser = allEnds.length ? Math.round(allEnds.reduce((a,e)=>a+toSec(e),0)/allEnds.length) : 0;
        const lastSess = allSessions.length ? new Date(Math.max(...allSessions.map(e=>byDate(e)))) : null;
        const airport = prefs?.departureAirports?.[0] || prefs?.departureAirport || '—';
        const nbDest = prefs?.planIds?.length ?? 0;
        const fmtDate2 = d => d ? new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit',year:'2-digit'}) : '—';
        const Row = ({label, value}) => (
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 0', borderBottom:'1px solid #1A1A1A' }}>
            <span style={{ color:'#666', fontSize:12 }}>{label}</span>
            <span style={{ color:'#ccc', fontSize:12, fontWeight:600 }}>{value}</span>
          </div>
        );
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:9100, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}
            onClick={() => setSelectedUser(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background:'#0D0D0D', borderTop:'1px solid #1A1A1A', borderRadius:'20px 20px 0 0', padding:'20px 20px 40px', maxHeight:'75vh', overflowY:'auto' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:800, color:'#E6FBFF' }}>{name}</div>
                  <div style={{ fontSize:10, color:'#444', marginTop:2 }}>{uid.slice(-12)}</div>
                </div>
                <button onClick={() => setSelectedUser(null)} style={{ background:'#1A1A1A', border:'none', borderRadius:20, padding:'6px 14px', color:'#888', fontSize:12, cursor:'pointer' }}>✕</button>
              </div>
              <Row label="1ère connexion" value={fmtDate2(firstEvt?.created_at)} />
              <Row label="Sessions (all time)" value={allSessions.length} />
              <Row label="Durée moy. session" value={avgDurUser ? fmtDur(avgDurUser) : '—'} />
              <Row label="Dernière session" value={fmtDate2(lastSess)} />
              <Row label="Aéroport de départ" value={airport} />
              <Row label="Destinations dans le plan" value={nbDest} />
            </div>
          </div>
        );
      })()}

      {s && !loading && (
        <div style={{ padding:'16px 16px 80px', display:'flex', flexDirection:'column', gap:10 }}>

          {/* ── Synthèse ── */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:6 }}>
            <SCard label="Users total" value={s.total} sub={s.noDateCount > 0 ? `dont ${s.noDateCount} sans date connue` : 'depuis le début'} />
            <SCard label="Actifs 7j" value={s.active7d} sub={`${s.active30d} sur 30j`} />
            <SCard label="Sessions / user" value={s.avgSess} sub="all time" />
            <SCard label="Durée moy." value={s.avgDurAll} sub="par session" />
          </div>
          <Box title={`Nouveaux users — ${{day:'14 derniers jours',week:'8 dernières semaines',month:'6 derniers mois'}[period]}`}>
            <BarChart data={s.newInPeriod} color="#4AC47F" />
          </Box>

          {/* ── Acquisition ── */}
          <Accordion id="acquisition" title="Acquisition">
            <div style={{ marginTop:12 }}>
              <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                <SCard label={{day:"Aujourd'hui",week:'Cette semaine',month:'Ce mois'}[period]}
                  value={s.newInPeriod[s.newInPeriod.length-1]?.count??0} sub="nouveaux" />
                <SCard label="Total prod" value={s.total} sub="users uniques" />
              </div>
              {s.topUsers.length > 0 && (
                <Box title={`Top users actifs — ${{day:"aujourd'hui",week:'cette semaine',month:'ce mois'}[period]}`}>
                  {s.topUsers.map(([uid, n], i) => {
                    const prefs = data.profileMap?.[uid];
                    const name = [prefs?.firstName, prefs?.lastName].filter(Boolean).join(' ') || `···${uid.slice(-6)}`;
                    const max = s.topUsers[0][1];
                    return (
                      <div key={uid} onClick={() => setSelectedUser(uid)} style={{ display:'flex', alignItems:'center', gap:8, marginBottom: i<s.topUsers.length-1?8:0, cursor:'pointer' }}>
                        <div style={{ color:'#333', fontSize:10, fontWeight:700, width:14, textAlign:'right', flexShrink:0 }}>{i+1}</div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ color:'#ccc', fontSize:12, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{name}</div>
                          <div style={{ height:2, background:'#1A1A1A', borderRadius:1, marginTop:3, overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${Math.round(n/max*100)}%`, background:'linear-gradient(90deg,#006F87,#009EC2)', borderRadius:1 }}/>
                          </div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <div style={{ color:'#4DD4F0', fontSize:11, fontWeight:700 }}>{n} sess.</div>
                          {s.durCnts[uid] ? <div style={{ color:'#444', fontSize:10 }}>{fmtDur(Math.round(s.durSums[uid]/s.durCnts[uid]))} moy.</div> : null}
                        </div>
                      </div>
                    );
                  })}
                </Box>
              )}
            </div>
          </Accordion>

          {/* ── Activation ── */}
          <Accordion id="activation" title="Activation">
            <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:10 }}>
              <Box title="Aéroport de départ renseigné">
                {pctBar(s.usersWithAirport, s.total, '#4DD4F0')}
              </Box>
              <Box title="Au moins 1 destination dans le plan">
                {pctBar(s.usersWithDest, s.total, '#4AC47F')}
              </Box>
              <Box title="Préférences voyage renseignées (style / avec / budget)">
                {pctBar(s.usersWithPrefs, s.total, '#F4A928')}
              </Box>
            </div>
          </Accordion>

          {/* ── Rétention ── */}
          <Accordion id="retention" title="Rétention">
            <div style={{ marginTop:12 }}>
              <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                <SCard label="Actifs 7j" value={s.active7d} sub={`/ ${s.total} users`} />
                <SCard label="Revenu > 1 fois" value={s.usersReturned} sub={`${s.total>0?Math.round(s.usersReturned/s.total*100):0}% des users`} />
              </div>
              <Box title="Sessions par période"><BarChart data={s.sessChart} color="#009EC2" /></Box>
              <Box title="Durée moy. session (s)"><BarChart data={s.durChart} color="#F4A928" /></Box>
            </div>
          </Accordion>

          {/* ── Engagement ── */}
          <Accordion id="engagement" title="Engagement">
            <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:10 }}>
              <Box title="Mode de recherche">
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                  <span style={{ color:'#888', fontSize:12 }}>Vol uniquement</span>
                  <span style={{ color:'#4DD4F0', fontSize:12, fontWeight:700 }}>{s.flightOnly} / {s.total}</span>
                </div>
                {pctBar(s.flightOnly, s.total, '#4DD4F0')}
                <div style={{ display:'flex', justifyContent:'space-between', marginTop:10, marginBottom:6 }}>
                  <span style={{ color:'#888', fontSize:12 }}>Vol + hôtel</span>
                  <span style={{ color:'#4AC47F', fontSize:12, fontWeight:700 }}>{s.total-s.flightOnly} / {s.total}</span>
                </div>
                {pctBar(s.total - s.flightOnly, s.total, '#4AC47F')}
              </Box>
              <Box title="Pages de sortie">
                {s.topExits.length === 0
                  ? <div style={{ color:'#444', fontSize:12 }}>Aucune donnée</div>
                  : s.topExits.map(([tab, cnt], i) => (
                    <div key={tab} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                      paddingBottom: i<s.topExits.length-1?8:0, marginBottom: i<s.topExits.length-1?8:0,
                      borderBottom: i<s.topExits.length-1?'1px solid #1A1A1A':'none' }}>
                      <span style={{ color:'#888', fontSize:12 }}>{TAB_LABELS[tab]||tab}</span>
                      <span style={{ color:'#4DD4F0', fontSize:12, fontWeight:700 }}>{cnt}</span>
                    </div>
                  ))
                }
              </Box>
            </div>
          </Accordion>

        </div>
      )}
    </div>
  );
  return noPortal ? statsBody : createPortal(statsBody, document.body);
}

// ─── DEST ADMIN ───────────────────────────────────────────────────────────────

const EMPTY_DEST_FORM = {
  id:'', name:'', name_en:'', name_es:'', name_zh:'',
  country:'', country_en:'',
  desc:'', photo_url:'',
  type:'ville', airport_iata:'',
  lat:'', lng:'',
};

async function fetchWikidataPhotoUrl(nameEn, nameFr) {
  const BAD = /flag|coat|logo|blason|emblem|icon|seal|arms|wappen|shield|banner|locator|map|carte|plan\b/i;
  const tryWikidata = async (siteLang, title) => {
    try {
      const r = await fetch(
        `https://www.wikidata.org/w/api.php?action=wbgetentities&sites=${siteLang}wiki` +
        `&titles=${encodeURIComponent(title)}&props=claims&format=json&origin=*`
      );
      const data = await r.json();
      const entity = Object.values(data.entities || {}).find(e => e.id && e.id !== '-1');
      if (!entity) return null;
      const p18 = entity.claims?.P18?.[0]?.mainsnak?.datavalue?.value;
      if (!p18 || BAD.test(p18)) return null;
      return `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(p18.replace(/ /g, '_'))}?width=600`;
    } catch { return null; }
  };
  const tryMediaList = async (lang, title) => {
    try {
      const r = await fetch(`https://${lang}.wikipedia.org/api/rest_v1/page/media-list/${encodeURIComponent(title)}`);
      if (!r.ok) return null;
      const data = await r.json();
      const item = (data.items || []).find(it => it.type === 'image' && it.showInGallery && !BAD.test(it.title || ''));
      if (!item) return null;
      const srcset = item.srcset || [];
      const best = srcset[srcset.length - 1]?.src || srcset[0]?.src;
      return best ? 'https:' + best : null;
    } catch { return null; }
  };
  return (await tryWikidata('en', nameEn)) ||
    (await tryWikidata('fr', nameFr)) ||
    (await tryMediaList('en', nameEn)) ||
    (await tryMediaList('fr', nameFr));
}

function DestAdminTab() {
  const [dests, setDests]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [editing, setEditing]       = useState(null);   // id en cours d'édition
  const [form, setForm]             = useState({});
  const [creating, setCreating]     = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_DEST_FORM);
  const [saving, setSaving]         = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [msg, setMsg]               = useState(null);
  const [fetchingPhoto, setFetchingPhoto] = useState(false); // wikidata in-progress

  useEffect(() => {
    supabase.from('destinations').select('*').order('name')
      .then(({ data }) => { setDests(data || []); setLoading(false); });
  }, []);

  const filtered = dests.filter(d => {
    if (typeFilter && d.type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!d.name?.toLowerCase().includes(q) && !d.name_en?.toLowerCase().includes(q) && !d.id?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const startEdit = d => {
    setEditing(d.id); setCreating(false);
    setForm({
      name: d.name || '', name_en: d.name_en || '', name_es: d.name_es || '', name_zh: d.name_zh || '',
      country: d.country || '', country_en: d.country_en || '',
      desc: d.desc || '', photo_url: d.photo_url || '',
      type: d.type || 'ville', airport_iata: d.airport_iata || '',
      lat: d.lat ?? '', lng: d.lng ?? '',
    });
  };

  const handleWikidata = async (f, setF) => {
    setFetchingPhoto(true);
    const url = await fetchWikidataPhotoUrl(f.name_en || f.name, f.name);
    setFetchingPhoto(false);
    if (url) setF(prev => ({ ...prev, photo_url: url }));
    else setMsg({ ok: false, text: '✗ Aucune photo trouvée sur Wikidata' });
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      name: form.name, name_en: form.name_en || null, name_es: form.name_es || null, name_zh: form.name_zh || null,
      country: form.country || null, country_en: form.country_en || null,
      desc: form.desc || null, photo_url: form.photo_url || null,
      type: form.type, airport_iata: form.airport_iata || null,
      lat: form.lat !== '' ? parseFloat(form.lat) : null,
      lng: form.lng !== '' ? parseFloat(form.lng) : null,
    };
    const { error } = await supabase.from('destinations').update(payload).eq('id', editing);
    if (!error) {
      setDests(prev => prev.map(d => d.id === editing ? { ...d, ...payload } : d));
      setEditing(null);
      setMsg({ ok: true, text: '✓ Sauvegardé' });
    } else {
      setMsg({ ok: false, text: '✗ ' + (error.message.includes('policy') ? 'RLS bloque la modif' : error.message) });
    }
    setSaving(false);
    setTimeout(() => setMsg(null), 4000);
  };

  const doCreate = async () => {
    if (!createForm.id || !createForm.name) { setMsg({ ok: false, text: '✗ ID et Nom FR obligatoires' }); return; }
    setSaving(true);
    const payload = {
      id: createForm.id.toLowerCase().trim(),
      name: createForm.name, name_en: createForm.name_en || null, name_es: createForm.name_es || null, name_zh: createForm.name_zh || null,
      country: createForm.country || null, country_en: createForm.country_en || null,
      desc: createForm.desc || null, photo_url: createForm.photo_url || null,
      type: createForm.type, airport_iata: createForm.airport_iata || null,
      lat: createForm.lat !== '' ? parseFloat(createForm.lat) : null,
      lng: createForm.lng !== '' ? parseFloat(createForm.lng) : null,
    };
    const { data, error } = await supabase.from('destinations').insert(payload).select().single();
    if (!error && data) {
      setDests(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setCreating(false);
      setCreateForm(EMPTY_DEST_FORM);
      setMsg({ ok: true, text: `✓ "${data.name}" ajoutée` });
    } else {
      setMsg({ ok: false, text: '✗ ' + (error?.message || 'Erreur inconnue') });
    }
    setSaving(false);
    setTimeout(() => setMsg(null), 4000);
  };

  const doDelete = async id => {
    const { error } = await supabase.from('destinations').delete().eq('id', id);
    if (!error) {
      setDests(prev => prev.filter(d => d.id !== id));
      setConfirmDel(null);
      setMsg({ ok: true, text: '✓ Supprimé' });
    } else {
      setMsg({ ok: false, text: '✗ ' + error.message });
    }
    setTimeout(() => setMsg(null), 4000);
  };

  const inp = (extra = {}) => ({ padding:'6px 10px', borderRadius:8, border:'1px solid #1e3347', background:'#0a1520', color:'#E6FBFF', fontSize:12, width:'100%', boxSizing:'border-box', ...extra });
  const lbl = txt => <div style={{ color:'#7ab8c8', fontSize:10, marginBottom:3, opacity:0.7 }}>{txt}</div>;

  const PhotoSection = ({ f, setF }) => (
    <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
      <div style={{ width:80, height:60, borderRadius:8, overflow:'hidden', flexShrink:0, background:'#0a1520', border:'1px solid #1e3347' }}>
        {f.photo_url
          ? <img src={f.photo_url} alt="" onError={e => { e.target.src = TYPE_PHOTOS[f.type] || TYPE_PHOTOS.ville; }} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
          : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'#333', fontSize:18 }}>🏙</div>
        }
      </div>
      <div style={{ flex:1, display:'flex', flexDirection:'column', gap:5 }}>
        {lbl('URL photo (vide = Wikidata auto)')}
        <input value={f.photo_url} onChange={e => setF(p => ({ ...p, photo_url: e.target.value }))} style={inp()} placeholder="https://…"/>
        <div style={{ display:'flex', gap:6 }}>
          <button onClick={() => handleWikidata(f, setF)} disabled={fetchingPhoto} style={{
            flex:1, padding:'5px 8px', borderRadius:7, border:'1px solid #1e3347', background:'#0d1a27',
            color: fetchingPhoto ? '#555' : '#7ab8c8', fontSize:11, cursor:'pointer',
          }}>{fetchingPhoto ? 'Recherche…' : 'Wikidata'}</button>
          {f.photo_url && <button onClick={() => setF(p => ({ ...p, photo_url: '' }))} style={{
            padding:'5px 10px', borderRadius:7, border:'1px solid rgba(239,68,68,0.3)', background:'transparent',
            color:'#ef4444', fontSize:11, cursor:'pointer',
          }} title="Vider — repasse en auto">✕</button>}
        </div>
      </div>
    </div>
  );

  const DestForm = ({ f, setF, onSave, onCancel, isNew = false }) => (
    <div style={{ padding:14, background:'#0d1a27', borderTop:'1px solid #1e3347', display:'flex', flexDirection:'column', gap:10 }}>
      <PhotoSection f={f} setF={setF}/>
      {isNew && (
        <div>{lbl('ID (slug unique, ex: porto)')}
          <input value={f.id} onChange={e => setF(p => ({ ...p, id: e.target.value.toLowerCase().replace(/\s+/g,'-') }))}
            style={inp({ fontFamily:'monospace' })} placeholder="porto"/>
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
        <div>{lbl('Nom FR *')}<input value={f.name} onChange={e => setF(p => ({ ...p, name: e.target.value }))} style={inp()}/></div>
        <div>{lbl('Nom EN')}<input value={f.name_en} onChange={e => setF(p => ({ ...p, name_en: e.target.value }))} style={inp()}/></div>
        <div>{lbl('Nom ES')}<input value={f.name_es} onChange={e => setF(p => ({ ...p, name_es: e.target.value }))} style={inp()}/></div>
        <div>{lbl('Nom ZH')}<input value={f.name_zh} onChange={e => setF(p => ({ ...p, name_zh: e.target.value }))} style={inp()}/></div>
        <div>{lbl('Pays FR')}<input value={f.country} onChange={e => setF(p => ({ ...p, country: e.target.value }))} style={inp()}/></div>
        <div>{lbl('Pays EN')}<input value={f.country_en} onChange={e => setF(p => ({ ...p, country_en: e.target.value }))} style={inp()}/></div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:8 }}>
        <div>{lbl('Type')}
          <select value={f.type} onChange={e => setF(p => ({ ...p, type: e.target.value }))} style={inp()}>
            <option value="ville">ville</option>
            <option value="nature">nature</option>
            <option value="event">event</option>
          </select>
        </div>
        <div>{lbl('IATA')}<input value={f.airport_iata} onChange={e => setF(p => ({ ...p, airport_iata: e.target.value.toUpperCase() }))} maxLength={3} style={inp({ textTransform:'uppercase' })}/></div>
        <div>{lbl('Lat')}<input value={f.lat} onChange={e => setF(p => ({ ...p, lat: e.target.value }))} style={inp()} placeholder="48.856"/></div>
        <div>{lbl('Lng')}<input value={f.lng} onChange={e => setF(p => ({ ...p, lng: e.target.value }))} style={inp()} placeholder="2.352"/></div>
      </div>
      <div>{lbl('Description')}
        <textarea value={f.desc} onChange={e => setF(p => ({ ...p, desc: e.target.value }))} rows={2} style={{ ...inp(), resize:'vertical' }}/>
      </div>
      <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
        <button onClick={onCancel} style={{ padding:'7px 16px', borderRadius:10, border:'1px solid #1e3347', background:'transparent', color:'#7ab8c8', fontSize:12, cursor:'pointer' }}>Annuler</button>
        <button onClick={onSave} disabled={saving} style={{ padding:'7px 16px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#006F87,#009EC2)', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
          {saving ? '…' : isNew ? '+ Créer' : '✓ Sauvegarder'}
        </button>
      </div>
    </div>
  );

  if (loading) return <div style={{ padding:24, color:'#7ab8c8', textAlign:'center' }}>Chargement…</div>;

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      {/* Toolbar */}
      <div style={{ flexShrink:0, padding:'10px 14px', display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', borderBottom:'1px solid #1e3347' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Nom, EN, ID…"
          style={{ flex:1, minWidth:120, padding:'7px 12px', borderRadius:10, border:'1px solid #1e3347', background:'#0d1a27', color:'#E6FBFF', fontSize:12 }}/>
        {['','ville','nature','event'].map(tp => (
          <button key={tp} onClick={() => setTypeFilter(tp)} style={{
            padding:'5px 10px', borderRadius:20, border:'1px solid #1e3347', cursor:'pointer', fontSize:11,
            background: typeFilter === tp ? '#006F87' : 'transparent', color: typeFilter === tp ? '#fff' : '#7ab8c8',
          }}>{tp || 'Tout'}</button>
        ))}
        <span style={{ color:'#555', fontSize:11 }}>{filtered.length}/{dests.length}</span>
        <button onClick={() => { setCreating(c => !c); setEditing(null); setCreateForm(EMPTY_DEST_FORM); }} style={{
          padding:'5px 12px', borderRadius:20, border:'1px solid #1e3347', cursor:'pointer', fontSize:11,
          background: creating ? '#006F87' : 'transparent', color: creating ? '#fff' : '#7ab8c8',
        }}>+ Nouvelle</button>
      </div>
      {msg && (
        <div style={{ flexShrink:0, padding:'8px 16px', fontSize:12, color: msg.ok ? '#27C97A' : '#ef4444', background: msg.ok ? 'rgba(39,201,122,0.07)' : 'rgba(239,68,68,0.07)' }}>
          {msg.text}
        </div>
      )}
      {/* Formulaire nouvelle destination */}
      {creating && (
        <div style={{ flexShrink:0, borderBottom:'2px solid #006F87' }}>
          <div style={{ padding:'8px 14px 0', color:'#7ab8c8', fontSize:11, fontWeight:700 }}>NOUVELLE DESTINATION</div>
          <DestForm f={createForm} setF={setCreateForm} onSave={doCreate} onCancel={() => setCreating(false)} isNew/>
        </div>
      )}
      {/* List */}
      <div style={{ flex:1, overflowY:'auto', padding:'8px 10px' }}>
        {filtered.map(d => (
          <div key={d.id} style={{ borderRadius:12, border:'1px solid #1e3347', marginBottom:8, overflow:'hidden' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 12px', background:'#0a1520' }}>
              <div style={{ width:36, height:36, borderRadius:8, overflow:'hidden', flexShrink:0 }}>
                <img src={d.photo_url || DEST_PHOTOS[d.name] || TYPE_PHOTOS[d.type] || TYPE_PHOTOS.ville} alt=""
                  onError={e => { e.target.src = TYPE_PHOTOS[d.type] || TYPE_PHOTOS.ville; }}
                  style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ color:'#E6FBFF', fontSize:13, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.name}</div>
                <div style={{ color:'#555', fontSize:10 }}>{d.id} · {d.type} · {d.airport_iata || '—'} · {d.lat ? `${d.lat},${d.lng}` : 'no coords'}</div>
              </div>
              <button onClick={() => editing === d.id ? setEditing(null) : startEdit(d)} style={{
                padding:'4px 10px', borderRadius:8, border:'1px solid #1e3347', cursor:'pointer', fontSize:11,
                background: editing === d.id ? '#006F87' : 'transparent', color: editing === d.id ? '#fff' : '#7ab8c8',
              }}>{editing === d.id ? '▲' : '✎'}</button>
              <button onClick={() => setConfirmDel(d.id)} style={{ padding:'4px 8px', borderRadius:8, border:'1px solid rgba(239,68,68,0.3)', background:'transparent', color:'#ef4444', fontSize:11, cursor:'pointer' }}>🗑</button>
            </div>
            {editing === d.id && (
              <DestForm f={form} setF={setForm} onSave={save} onCancel={() => setEditing(null)}/>
            )}
            {confirmDel === d.id && (
              <div style={{ padding:'10px 12px', background:'rgba(239,68,68,0.05)', borderTop:'1px solid rgba(239,68,68,0.2)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span style={{ color:'#ef4444', fontSize:12 }}>Supprimer "{d.name}" définitivement ?</span>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => setConfirmDel(null)} style={{ padding:'5px 12px', borderRadius:8, border:'1px solid #1e3347', background:'transparent', color:'#7ab8c8', fontSize:11, cursor:'pointer' }}>Non</button>
                  <button onClick={() => doDelete(d.id)} style={{ padding:'5px 12px', borderRadius:8, border:'none', background:'#ef4444', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}>Oui</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ExpPanel() {
  const [open, setOpen] = useState('apify');
  const experiments = [
    { key: 'apify', label: 'Scrapping GoogleMap & analyse des avis', component: <ApifyPanel /> },
    // futurs experiments ici
  ];
  return (
    <div style={{ flex:1, overflowY:'auto', background:'#080808', padding:'12px 0 80px' }}>
      {experiments.map(exp => (
        <div key={exp.key} style={{ margin:'0 12px 8px', background:'#0D0D0D', border:'1px solid #1A1A1A', borderRadius:14, overflow:'hidden' }}>
          <button onClick={() => setOpen(o => o === exp.key ? null : exp.key)} style={{
            width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'14px 16px', background:'none', border:'none', cursor:'pointer',
          }}>
            <span style={{ fontSize:13, fontWeight:700, color:'#E6FBFF' }}>{exp.label}</span>
            <span style={{ color:'#444', fontSize:18, display:'inline-block', transition:'transform .2s',
              transform: open === exp.key ? 'rotate(90deg)' : 'none' }}>›</span>
          </button>
          {open === exp.key && (
            <div style={{ borderTop:'1px solid #1A1A1A' }}>{exp.component}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function UsersAdminTab({ adminUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      // Load profiles + events for activity data
      const [{ data: profiles }, { data: events }] = await Promise.all([
        supabase.from('profiles').select('user_id, email, pax, selected_destinations, travel_prefs'),
        supabase.from('plotted_events').select('user_id, event_type, created_at').order('created_at'),
      ]);
      // Build user map
      const userMap = {};
      for (const p of (profiles || [])) {
        userMap[p.user_id] = {
          id: p.user_id,
          email: p.email || null,
          pax: p.pax,
          destinations: (() => { try { return JSON.parse(p.selected_destinations || '[]').length; } catch { return 0; } })(),
          prefs: p.travel_prefs || {},
          firstSeen: null,
          lastSeen: null,
          eventCount: 0,
          sessions: new Set(),
        };
      }
      for (const e of (events || [])) {
        if (!userMap[e.user_id]) {
          userMap[e.user_id] = { id: e.user_id, email: null, pax: null, destinations: 0, prefs: {}, firstSeen: null, lastSeen: null, eventCount: 0, sessions: new Set() };
        }
        const u = userMap[e.user_id];
        const ts = e.created_at;
        if (!u.firstSeen || ts < u.firstSeen) u.firstSeen = ts;
        if (!u.lastSeen || ts > u.lastSeen) u.lastSeen = ts;
        u.eventCount++;
      }
      // Sort by firstSeen desc
      const list = Object.values(userMap)
        .filter(u => u.id !== adminUserId)
        .sort((a, b) => (b.firstSeen || '').localeCompare(a.firstSeen || ''));
      setUsers(list);
      setLoading(false);
    })();
  }, []);

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'2-digit' }) : '—';
  const ago = (iso) => {
    if (!iso) return '—';
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (d === 0) return 'today';
    if (d === 1) return 'yesterday';
    return `${d}d ago`;
  };
  const status = (u) => {
    if (!u.firstSeen) return { label: 'ghost', color: '#6b7280' };
    const daysSince = Math.floor((Date.now() - new Date(u.lastSeen).getTime()) / 86400000);
    if (daysSince <= 3) return { label: 'active', color: '#10b981' };
    if (daysSince <= 14) return { label: 'recent', color: '#f59e0b' };
    return { label: 'inactive', color: '#ef4444' };
  };

  if (loading) return <div style={{ color:'#7ab8c8', padding:24, textAlign:'center' }}>Loading...</div>;

  return (
    <div style={{ flex:1, overflowY:'auto', padding:'12px 16px' }}>
      <div style={{ color:'#7ab8c8', fontSize:12, marginBottom:10 }}>{users.length} users (excl. admin)</div>
      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
        {users.map(u => {
          const s = status(u);
          const city = u.prefs?.departure_city || '';
          return (
            <div key={u.id} style={{ background:'#0d1a27', border:'1px solid #1e3347', borderRadius:10, padding:'8px 12px', display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:2 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:s.color, background:s.color+'22', padding:'1px 6px', borderRadius:6 }}>{s.label}</span>
                  <span style={{ fontSize:11, color:'#E6FBFF', fontWeight:500 }}>{u.email || u.id.slice(0,8)}</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:1 }}>
                  {city && <span style={{ fontSize:10, color:'#7ab8c8' }}>{city}</span>}
                  {u.pax && <span style={{ fontSize:10, color:'#557' }}>{u.pax} pax</span>}
                </div>
                <div style={{ fontSize:10, color:'#557', display:'flex', gap:8 }}>
                  <span>joined {fmt(u.firstSeen)}</span>
                  <span>last {ago(u.lastSeen)}</span>
                  <span>{u.eventCount} events</span>
                  <span>{u.destinations} dest</span>
                </div>
              </div>
              {!u.email && <div style={{ fontSize:10, color:'#334', fontFamily:'monospace', flexShrink:0 }}>{u.id.slice(0,8)}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RlsAuditPanel() {
  const [rlsTables, setRlsTables] = useState(null);
  const [rlsLoading, setRlsLoading] = useState(false);
  function loadRls() {
    setRlsLoading(true);
    supabase.rpc('rls_audit').then(({ data }) => { setRlsTables(data || []); setRlsLoading(false); }).catch(() => setRlsLoading(false));
  }
  useEffect(() => { loadRls(); }, []);
  const unprotected = rlsTables ? rlsTables.filter(t => !t.rowsecurity) : [];
  const total = rlsTables ? rlsTables.length : 0;
  const allOk = rlsTables && unprotected.length === 0;
  const badge = (bg, color) => ({ display: 'inline-block', background: bg, color, borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700 });
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ color: '#E6FBFF', fontSize: 15, fontWeight: 800 }}>RLS Audit</span>
        {rlsTables && (
          <span style={badge(allOk ? '#134e4a' : '#7f1d1d', allOk ? '#6ee7b7' : '#fca5a5')}>
            {allOk ? `${total} tables OK` : `${unprotected.length} unprotected`}
          </span>
        )}
      </div>
      {rlsLoading && <div style={{ fontSize: 13, color: '#7ab8c8' }}>Loading...</div>}
      {rlsTables && (
        <>
          {unprotected.length > 0 && (
            <div style={{ background: '#451a03', border: '1px solid #92400e', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: '#fbbf24', lineHeight: 1.5 }}>
                {unprotected.length} table{unprotected.length > 1 ? 's' : ''} without RLS — anyone with the project URL can read/write data.
              </div>
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1e3347' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: '#7ab8c8', fontWeight: 600 }}>Table</th>
                <th style={{ textAlign: 'center', padding: '6px 8px', color: '#7ab8c8', fontWeight: 600, width: 80 }}>RLS</th>
              </tr>
            </thead>
            <tbody>
              {rlsTables.map(row => (
                <tr key={row.tablename} style={{ borderBottom: '1px solid #1e3347' }}>
                  <td style={{ padding: '5px 8px', color: '#E6FBFF', fontFamily: 'monospace', fontSize: 11 }}>{row.tablename}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                    <span style={badge(row.rowsecurity ? '#134e4a' : '#7f1d1d', row.rowsecurity ? '#6ee7b7' : '#fca5a5')}>
                      {row.rowsecurity ? 'ON' : 'OFF'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Prevention safeguards */}
          <div style={{ marginTop: 16, background: '#052e16', border: '1px solid #166534', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6ee7b7', marginBottom: 8 }}>3 layers of prevention</div>
            <div style={{ fontSize: 11, color: '#86efac', lineHeight: 1.6 }}>
              <div style={{ marginBottom: 4 }}><strong>1. Pre-commit hook</strong> — Claude Code blocks any commit containing CREATE TABLE without ENABLE ROW LEVEL SECURITY in the same .sql file.</div>
              <div style={{ marginBottom: 4 }}><strong>2. Smoke tests</strong> — <code style={{ fontSize: 10, background: '#14532d', padding: '1px 4px', borderRadius: 3 }}>npm test</code> on HP verifies RLS is enabled on all tables + auth routing returns correct routes for every role.</div>
              <div><strong>3. Daily remote audit</strong> — A scheduled agent checks all 4 Supabase projects (HP, Remora, Reworkr, Plotted) every morning at 9:17. Auto-fixes any unprotected table.</div>
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={loadRls} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #1e3347', background: 'none', fontSize: 11, color: '#7ab8c8', cursor: 'pointer' }}>
              Refresh
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function AdminScreen({ onClose, adminUserId }) {
  // Admin is a database role now (profiles.roles @> {admin}), not a shared
  // password. RLS enforces it server-side; this only decides what to render.
  const [authed, setAuthed] = useState(null); // null = still checking
  const [tab, setTab]     = useState('stats');
  const [tabLang, setTabLang] = useState('fr');

  useEffect(() => {
    let stale = false;
    supabase.rpc('is_admin').then(({ data, error }) => {
      if (!stale) setAuthed(!error && data === true);
    });
    return () => { stale = true; };
  }, []);

  if (authed !== true) return createPortal(
    <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.82)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#0d1a27', borderRadius:20, padding:28, width:280, border:'1px solid #1e3347' }}>
        <div style={{ color:'#E6FBFF', fontSize:16, fontWeight:800, marginBottom:16, textAlign:'center' }}>⚙ Admin Plotted</div>
        <div style={{ color: authed === null ? '#7ab8c8' : '#ef4444', fontSize:13, textAlign:'center', marginBottom:14 }}>
          {authed === null ? 'Vérification…' : "Ce compte n'a pas le rôle admin."}
        </div>
        <button onClick={onClose} style={{ width:'100%', padding:8, borderRadius:12, border:'1px solid #1e3347', background:'transparent', color:'#7ab8c8', fontSize:13, cursor:'pointer' }}>Fermer</button>
      </div>
    </div>,
    document.body
  );

  const tabs = [['stats','Stats'], ['users','Users'], ['dest','Destinations'], ['exp','Expérimentation'], ['sec','Security']];

  return createPortal(
    <div style={{ position:'fixed', inset:0, zIndex:9998, background:'#07101a', display:'flex', flexDirection:'column', alignItems:'center' }}>
      {/* Header */}
      <div style={{ flexShrink:0, width:'100%', maxWidth:600, display:'flex', alignItems:'center', gap:10, padding:'max(14px,env(safe-area-inset-top)) 16px 0', background:'#07101a', borderBottom:'1px solid #1e3347' }}>
        <span style={{ color:'#E6FBFF', fontSize:15, fontWeight:800, marginRight:8 }}>⚙ Admin</span>
        <div style={{ display:'flex', gap:2, flex:1, overflowX:'auto' }}>
          {tabs.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{
              padding:'8px 12px', borderRadius:'10px 10px 0 0', border:'1px solid #1e3347',
              borderBottom: tab === k ? '1px solid #07101a' : '1px solid #1e3347',
              background: tab === k ? '#0d1a27' : 'transparent',
              color: tab === k ? '#009EC2' : '#aaa', fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap',
            }}>{l}</button>
          ))}
        </div>
        <button onClick={onClose} style={{ width:30, height:30, borderRadius:15, border:'1px solid #1e3347', background:'transparent', color:'#7ab8c8', fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>×</button>
      </div>
      {/* Content */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', width:'100%', maxWidth:600 }}>
        {/* Tab-level EN/FR toggle */}
        <div style={{ flexShrink:0, display:'flex', justifyContent:'flex-end', padding:'6px 12px', background:'#0d1a27', borderBottom:'1px solid #1e3347' }}>
          <div style={{ display:'flex', border:'1px solid #1e3347', borderRadius:6, overflow:'hidden' }}>
            {['fr','en'].map(l => (
              <button key={l} onClick={() => setTabLang(l)} style={{ padding:'4px 10px', border:'none', background: tabLang===l ? '#009EC2' : 'transparent', color: tabLang===l ? '#fff' : '#7ab8c8', fontSize:10, fontWeight:700, cursor:'pointer', textTransform:'uppercase' }}>{l}</button>
            ))}
          </div>
        </div>
        {tab === 'stats'   && <StatsScreen defaultAuthed noPortal onClose={onClose} adminUserId={adminUserId}/>}
        {tab === 'users'   && <UsersAdminTab adminUserId={adminUserId} />}
        {tab === 'dest'    && <DestAdminTab/>}
        {tab === 'exp'     && <ExpPanel />}
        {tab === 'sec'     && <RlsAuditPanel />}
      </div>
    </div>,
    document.body
  );
}

// ─── ONBOARDING ───────────────────────────────────────────────────────────────

function Onboarding({ onDone }) {
  const t = useT(null);
  const [pg, setPg] = useState(0);

  // Injection police manuscrite Caveat (Google Fonts)
  useEffect(() => {
    if (document.getElementById('caveat-font')) return;
    const link = document.createElement('link');
    link.id = 'caveat-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap';
    document.head.appendChild(link);
  }, []);

  const pages = [
    { title: t('ob.page0title'), tagline: t('ob.page0sub') },
    { title: t('ob.page1title'), tagline: t('ob.page1sub') },
    { title: t('ob.page2title'), tagline: t('ob.page2sub') },
    { title: t('ob.page3title'), tagline: t('ob.page3sub') },
  ];
  const p = pages[pg];
  const isLast = pg === pages.length - 1;

  // ── Page 0: Polaroids empilés verticalement ──────────────────────────────────
  // Pulse = ville ancienne | Rewild = crique | Unleash = concert/fête
  const STACK = [
    { src: 'https://images.unsplash.com/photo-1529260830199-42c24126f198?auto=format&fit=crop&w=600&q=80',
      name: t('ob.labelCity'),   rotate: -7, top: 0,   translateX: '-58%', z: 3 }, // Rome toits + Vatican
    { src: '/rewild.jpg',
      name: t('ob.labelNature'), rotate:  5, top: 137, translateX: '-42%', z: 2 }, // Plage chaise longue Thaïlande
    { src: 'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?auto=format&fit=crop&w=600&q=80',
      name: t('ob.labelEvent'),  rotate: -4, top: 274, translateX: '-56%', z: 1 }, // Festival foule colorée
  ];
  const TilesVisual = (
    <div style={{ position:'relative', width:'100%', height:445 }}>
      {STACK.map(({ src, name, rotate, top, translateX, z }) => (
        <div key={name} style={{
          position: 'absolute',
          top,
          left: '50%',
          transform: `translateX(${translateX}) rotate(${rotate}deg)`,
          zIndex: z,
          background: 'white',
          padding: '7px 7px 0',
          borderRadius: 3,
          boxShadow: '4px 6px 20px rgba(0,0,0,0.7)',
          width: '61%',
          maxWidth: 220,
          flexShrink: 0,
        }}>
          {/* Photo 3:2 landscape */}
          <div style={{ width:'100%', aspectRatio:'3/2', overflow:'hidden' }}>
            <img src={src} alt={name}
              style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
          </div>
          {/* Bande manuscrite — padding égal haut/bas */}
          <div style={{
            textAlign: 'center',
            padding: '7px 4px 8px',
            fontFamily: "'Caveat', 'Comic Sans MS', cursive",
            fontSize: 16, fontWeight: 700, color: '#1a1a1a',
            lineHeight: 1,
          }}>
            {name}
          </div>
        </div>
      ))}
    </div>
  );

  // ── Page 1: Carte Europe SVG (react-simple-maps) + mini-polaroids ──────────
  // Les coordonnées sont des vraies lat/lon — projection geoAzimuthalEqualArea
  const MAP_PINS = [
    { city:'Bergen',   lon:5.32,  lat:60.39, rotate:-5,
      photo:'https://images.unsplash.com/photo-1531206715517-5c0ba140b2b8?auto=format&fit=crop&w=100&q=70' },
    { city:'Paris',    lon:2.35,  lat:48.85, rotate: 4,
      photo:'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=100&q=70' },
    { city:'Rome',     lon:12.50, lat:41.90, rotate:-3,
      photo:'https://images.unsplash.com/photo-1529260830199-42c24126f198?auto=format&fit=crop&w=100&q=70' },
    { city:'Budapest', lon:19.05, lat:47.50, rotate: 6,
      photo:'https://images.unsplash.com/photo-1541343672885-9be56236302a?auto=format&fit=crop&w=100&q=70' },
    { city:'Porto',    lon:-8.61, lat:41.15, rotate: 3,
      photo:'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?auto=format&fit=crop&w=100&q=70' },
    { city:'Rhodes',   lon:28.22, lat:36.43, rotate:-4,
      photo:'https://images.unsplash.com/photo-1555993539-1732b0258235?auto=format&fit=crop&w=100&q=70' },
    { city:'Riga',     lon:24.11, lat:56.95, rotate: 3,
      photo:'/riga.jpg' },
  ];
  const MapVisual = (
    <div style={{ display:'flex', justifyContent:'center', width:'100%', padding:'12px 0' }}>
      <div style={{
        width: '82%',
        transform: 'rotate(-3deg)',
        boxShadow: '6px 10px 28px rgba(0,0,0,0.72)',
        border: '7px solid white',
        borderBottom: '18px solid white',
        borderRadius: 2,
        overflow: 'hidden',
        lineHeight: 0,
        backgroundColor: '#b8d4e8',
      }}>
        <ComposableMap
          projection="geoAzimuthalEqualArea"
          projectionConfig={{ rotate: [-15, -52, 0], scale: 520 }}
          width={400}
          height={380}
          style={{ width:'100%', height:'auto', display:'block', backgroundColor:'#b8d4e8' }}
        >
          <defs>
            <filter id="polaShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="1.5" dy="2.5" stdDeviation="3" floodColor="rgba(0,0,0,0.55)"/>
            </filter>
          </defs>
          <Geographies geography={`${process.env.PUBLIC_URL}/world-50m.json`}>
            {({ geographies }) => <>
              {geographies.map(geo => (
                <Geography key={geo.rsmKey} geography={geo} style={{
                  default: { fill:'#c0cec0', stroke:'white', strokeWidth:0.4, outline:'none' },
                  hover:   { fill:'#c0cec0', outline:'none' },
                  pressed: { fill:'#c0cec0', outline:'none' },
                }}/>
              ))}
              {geographies.length > 0 && MAP_PINS.map(({ city, lon, lat, rotate, photo }) => (
                <Marker key={city} coordinates={[lon, lat]}>
                  {/* Pure SVG polaroid — no foreignObject (broken in Safari) */}
                  <g transform={`rotate(${rotate})`} filter="url(#polaShadow)">
                    {/* White frame */}
                    <rect x={-24} y={2} width={48} height={60} fill="white" rx={1}/>
                    {/* Photo */}
                    <image href={photo} x={-21} y={5} width={42} height={34} preserveAspectRatio="xMidYMid slice"/>
                    {/* City label */}
                    <text x={0} y={55}
                      textAnchor="middle"
                      fontFamily="Caveat, 'Comic Sans MS', cursive"
                      fontSize={9} fontWeight={700} fill="#1a1a1a">{city}</text>
                  </g>
                  <circle r={3.5} fill="#DC2626" stroke="white" strokeWidth={0.8}/>
                </Marker>
              ))}
            </>}
          </Geographies>
        </ComposableMap>
      </div>
    </div>
  );

  // ── Page 2: Two overlapping calendar months ───────────────────────────────────
  const CalendarVisual = (() => {
    const renderMonth = (label, totalDays, startOffset, hlRanges) => {
      const cells = Array.from({ length: 35 }, (_, i) => {
        const day = i - startOffset + 1;
        const valid = day >= 1 && day <= totalDays;
        const isHL  = valid && hlRanges.some(([s, e]) => day === s || day === e);
        const isMid = valid && hlRanges.some(([s, e]) => day > s && day < e);
        return (
          <div key={i} style={{
            height: 26, borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: isHL ? 800 : isMid ? 600 : 400,
            background: isHL ? '#009EC2' : isMid ? 'rgba(0,158,194,0.22)' : valid ? 'rgba(255,255,255,0.04)' : 'transparent',
            color: isHL ? 'white' : isMid ? '#4DD4F0' : valid ? '#6aa8ba' : 'transparent',
          }}>{valid ? day : ''}</div>
        );
      });
      return (
        <div style={{
          background: '#0b1929', border: '1px solid #1e3347', borderRadius: 18,
          padding: '13px 15px', width: 223,
          boxShadow: '6px 12px 32px rgba(0,0,0,0.75)',
        }}>
          <div style={{ color:'#4DD4F0', fontSize:13, fontWeight:800, textAlign:'center', marginBottom:8, letterSpacing:'0.08em' }}>{label}</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, marginBottom:3 }}>
            {t('ob.calDays').map((d, i) => (
              <div key={i} style={{ textAlign:'center', color:'#2d5060', fontSize:9, fontWeight:700 }}>{d}</div>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>{cells}</div>
        </div>
      );
    };
    // Mois dynamiques : connexion+3 (devant) et connexion+4 (derrière)
    const MONTH_NAMES = t('ob.calMonths');
    const getMonthMeta = (offset) => {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() + offset);
      const y = d.getFullYear(), m = d.getMonth();
      return {
        label:       `${MONTH_NAMES[m]} ${y}`,
        totalDays:   new Date(y, m + 1, 0).getDate(),
        startOffset: (new Date(y, m, 1).getDay() + 6) % 7, // lundi=0
      };
    };
    const m3 = getMonthMeta(3);
    const m4 = getMonthMeta(4);
    return (
      <div style={{ position:'relative', width:'100%', height:310, display:'flex', alignItems:'center', justifyContent:'center' }}>
        {/* Mois+4 — derrière, incliné à droite */}
        <div style={{ position:'absolute', transform:'rotate(5deg) translate(68px, 14px)', zIndex:1 }}>
          {renderMonth(m4.label, m4.totalDays, m4.startOffset, [[11,14],[25,28]])}
        </div>
        {/* Mois+3 — devant, incliné à gauche */}
        <div style={{ position:'absolute', transform:'rotate(-3deg) translate(-33px, -8px)', zIndex:2 }}>
          {renderMonth(m3.label, m3.totalDays, m3.startOffset, [[6,9],[20,25]])}
        </div>
      </div>
    );
  })();

  // ── Page 3: Mini map + mini calendar → Top N (visual de fusion) ───────────────
  const TopNVisual = (() => {
    // Mini calendrier (mois+3, version réduite)
    const MONTH_NAMES = t('ob.calMonths');
    const now2 = new Date(); now2.setDate(1); now2.setMonth(now2.getMonth() + 3);
    const y2 = now2.getFullYear(), mo = now2.getMonth();
    const calLabel   = `${MONTH_NAMES[mo]} ${y2}`;
    const calDays    = new Date(y2, mo + 1, 0).getDate();
    const calOffset  = (new Date(y2, mo, 1).getDay() + 6) % 7;
    const calHL      = [[6,9],[20,25]];
    const miniCal = (
      <div style={{ background:'#0b1929', border:'1px solid #1e3347', borderRadius:11,
        padding:'8px 9px', boxShadow:'4px 8px 22px rgba(0,0,0,0.65)' }}>
        <div style={{ color:'#4DD4F0', fontSize:8, fontWeight:800, textAlign:'center', marginBottom:5, letterSpacing:'0.07em' }}>{calLabel}</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:1, marginBottom:2 }}>
          {t('ob.calDays').map((d,i) => (
            <div key={i} style={{ textAlign:'center', color:'#2d5060', fontSize:6, fontWeight:700 }}>{d}</div>
          ))}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:1 }}>
          {Array.from({length:35}, (_,i) => {
            const day = i - calOffset + 1;
            const valid = day >= 1 && day <= calDays;
            const isHL  = valid && calHL.some(([s,e]) => day===s||day===e);
            const isMid = valid && calHL.some(([s,e]) => day>s&&day<e);
            return <div key={i} style={{ height:15, borderRadius:3, display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:7, fontWeight: isHL ? 800 : 400,
              background: isHL ? '#009EC2' : isMid ? 'rgba(0,158,194,0.2)' : valid ? 'rgba(255,255,255,0.04)' : 'transparent',
              color: isHL ? 'white' : isMid ? '#4DD4F0' : valid ? '#6aa8ba' : 'transparent',
            }}>{valid ? day : ''}</div>;
          })}
        </div>
      </div>
    );
    // Mini carte avec mini-polaroids
    const miniMap = (
      <div style={{ borderRadius:11, overflow:'hidden', boxShadow:'4px 8px 22px rgba(0,0,0,0.65)',
        border:'4px solid white', borderBottom:'10px solid white', backgroundColor:'#b8d4e8' }}>
        <ComposableMap projection="geoAzimuthalEqualArea"
          projectionConfig={{ rotate:[-15,-52,0], scale:520 }}
          width={400} height={380}
          style={{ width:'100%', height:'auto', display:'block' }}>
          <Geographies geography={`${process.env.PUBLIC_URL}/world-50m.json`}>
            {({ geographies }) => <>
              {geographies.map(geo => (
                <Geography key={geo.rsmKey} geography={geo} style={{
                  default:{ fill:'#c0cec0', stroke:'white', strokeWidth:0.4, outline:'none' },
                  hover:  { fill:'#c0cec0', outline:'none' },
                  pressed:{ fill:'#c0cec0', outline:'none' },
                }}/>
              ))}
              {geographies.length > 0 && MAP_PINS.map(({ city, lon, lat, rotate, photo }) => (
                <Marker key={city} coordinates={[lon, lat]}>
                  <circle r={2.5} fill="#DC2626" stroke="white" strokeWidth={0.6}/>
                  <foreignObject x={-13} y={2} width={26} height={33} style={{overflow:'visible'}}>
                    <div style={{
                      transform:`rotate(${rotate}deg)`, transformOrigin:'top center',
                      background:'white', padding:'2px 2px 0',
                      boxShadow:'1px 2px 5px rgba(0,0,0,0.55)', width:26,
                      fontFamily:"'Caveat','Comic Sans MS',cursive",
                    }}>
                      <div style={{ width:'100%', height:18, overflow:'hidden' }}>
                        <img src={photo} alt={city} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
                      </div>
                      <div style={{ textAlign:'center', padding:'2px 1px 2px', fontSize:6, fontWeight:700, color:'#1a1a1a', lineHeight:1 }}>{city}</div>
                    </div>
                  </foreignObject>
                </Marker>
              ))}
            </>}
          </Geographies>
        </ComposableMap>
      </div>
    );
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:'100%', gap:4 }}>
        {/* Ligne du haut : mini carte (48%) + mini calendrier centré verticalement */}
        <div style={{ position:'relative', width:'92%' }}>
          <div style={{ width:'48%', transform:'rotate(-2deg)', transformOrigin:'bottom right', zIndex:1 }}>{miniMap}</div>
          <div style={{ position:'absolute', top:'50%', left:'38%', width:'44%',
            transform:'translateY(-50%) rotate(3deg)', zIndex:2 }}>{miniCal}</div>
        </div>
        {/* Symbole de fusion : deux sources → algorithme → résultat */}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:0, marginTop:2 }}>
          <svg width="100" height="20" viewBox="0 0 100 20" fill="none">
            <path d="M18 2 C18 16 50 17 50 17" stroke="#8ab4a0" strokeWidth="1.3" opacity="0.6"/>
            <path d="M82 2 C82 16 50 17 50 17" stroke="#4DD4F0" strokeWidth="1.3" opacity="0.6"/>
            <circle cx="50" cy="17" r="3.5" fill="#009EC2" opacity="0.9"/>
          </svg>
          <svg width="2" height="18" viewBox="0 0 2 18"><line x1="1" y1="0" x2="1" y2="18" stroke="#009EC2" strokeWidth="1.5" opacity="0.5"/></svg>
        </div>
        {/* Carte Top N — 62% de largeur */}
        <div style={{ background:'#0d1a27', border:'1px solid #1e3347', borderRadius:18,
          padding:'13px 16px', width:'62%', boxShadow:'6px 10px 32px rgba(0,0,0,0.7)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:11 }}>
            <span style={{ color:'#E6FBFF', fontSize:14, fontWeight:800 }}>Top</span>
            <span style={{ color:'#4DD4F0', fontSize:14, fontWeight:800 }}>5</span>
            <span style={{ color:'#3d6b7a', fontSize:9, marginLeft:'auto' }}>2 travellers · 8 slots</span>
          </div>
          {[
            { rank:1, city:'Barcelona', nights:4, price:284, save:18, real:true  },
            { rank:2, city:'Prague',    nights:3, price:197, save:24, real:true  },
            { rank:3, city:'Rome',      nights:5, price:312, save:11, real:false },
          ].map((d, i) => (
            <div key={d.city} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0',
              borderBottom: i < 2 ? '1px solid #1e3347' : 'none' }}>
              <span style={{ fontSize:10, fontWeight:800, width:16, color: d.rank===1 ? '#F4A928' : '#3d6b7a' }}>#{d.rank}</span>
              <div style={{ flex:1 }}>
                <div style={{ color:'#E6FBFF', fontSize:12, fontWeight:700 }}>{d.city}</div>
                <div style={{ color:'#3d6b7a', fontSize:9, marginTop:1 }}>{d.nights}n · ✈ direct</div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ color:'#E6FBFF', fontSize:12, fontWeight:800 }}>{d.real ? '' : '~'}€{d.price}</div>
                <div style={{ color:'#27C97A', fontSize:9, fontWeight:700 }}>↓{d.save}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  })();

  const visuals = [TilesVisual, MapVisual, CalendarVisual, TopNVisual];

  return (
    <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column',
      background:'linear-gradient(170deg,#07101a 0%,#0d1a27 65%)', padding:'0 24px',
      boxSizing:'border-box', overflow:'hidden' }}>
      <div style={{ maxWidth:600, margin:'0 auto', width:'100%', display:'flex', flexDirection:'column', height:'100%' }}>

        {/* Step dots */}
        <div style={{ paddingTop:52, display:'flex', justifyContent:'center', gap:6, flexShrink:0 }}>
          {pages.map((_,i) => (
            <div key={i} style={{ height:5, borderRadius:3,
              width: i===pg ? 22 : 6,
              background: i===pg ? '#009EC2' : i<pg ? 'rgba(0,158,194,0.35)' : '#1e3347',
              transition:'all .3s' }}/>
          ))}
        </div>

        {/* Title — line break forcé sur ". " */}
        <div style={{ padding:'20px 0 12px', textAlign:'center', flexShrink:0 }}>
          <h1 style={{ color:'#E6FBFF', fontSize:22, fontWeight:800, lineHeight:1.25,
            margin:0, letterSpacing:'-0.5px' }}>
            {p.title.includes('. ') ? p.title.split('. ').map((part, i, arr) => (
              <span key={i}>{part}{i < arr.length-1 && <>.<br/></>}</span>
            )) : p.title}
          </h1>
        </div>

        {/* Visual */}
        <div style={{ flex:1, minHeight:0, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', padding:'4px 0' }}>
          {visuals[pg]}
        </div>

        {/* Tagline — même style que le titre */}
        <div style={{ padding:'12px 0 0', textAlign:'center', flexShrink:0 }}>
          <p style={{ color:'#E6FBFF', fontSize:22, fontWeight:800, lineHeight:1.25,
            margin:0, letterSpacing:'-0.5px' }}>{p.tagline}</p>
        </div>

        {/* Buttons */}
        <div style={{ paddingBottom:'max(34px,env(safe-area-inset-bottom))', paddingTop: pg === 0 ? 28 : 14,
          display:'flex', flexDirection:'column', gap:10, flexShrink:0 }}>
          <button style={{ ...btnPrimary, width:'100%' }} onClick={() => isLast ? onDone('dest') : setPg(pg+1)}>
            {isLast ? t('ob.letsGo') : t('ob.continue')}
          </button>
          <button style={{ ...btnSec, width:'100%' }} onClick={() => onDone('dest')}>
            {t('ob.skip')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Saving is fire-and-forget from many call sites, so failures are reported
// centrally by userPrefs and surfaced here once, instead of silently.
function SaveErrorBanner({ label }) {
  const [failing, setFailing] = useState(false);
  useEffect(() => onSaveStatus(setFailing), []);
  if (!failing) return null;
  return (
    <div style={{
      flexShrink: 0, background: '#7a2323', color: '#ffe9e9',
      fontSize: 12, lineHeight: 1.35, textAlign: 'center',
      padding: 'max(8px, env(safe-area-inset-top)) 14px 8px',
    }}>{label}</div>
  );
}

const TAB_ICONS = {
  home: (c) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="10" width="6" height="11" rx="1"/>
      <rect x="9" y="5" width="6" height="16" rx="1"/>
      <rect x="16" y="13" width="6" height="8" rx="1"/>
      <line x1="1" y1="21" x2="23" y2="21"/>
    </svg>
  ),
  dest: (c) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
      <circle cx="12" cy="9" r="2.5"/>
    </svg>
  ),
  map: (c) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="1,6 1,22 8,18 16,22 23,18 23,2 16,6 8,2"/>
      <line x1="8" y1="2" x2="8" y2="18"/>
      <line x1="16" y1="6" x2="16" y2="22"/>
    </svg>
  ),
  cal: (c) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <circle cx="8" cy="15" r="1" fill={c}/>
      <circle cx="12" cy="15" r="1" fill={c}/>
      <circle cx="16" cy="15" r="1" fill={c}/>
    </svg>
  ),
  trips: (c) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="13" rx="2"/>
      <path d="M16 8V6a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
      <line x1="3" y1="14" x2="21" y2="14"/>
    </svg>
  ),
  user: (c) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
};

const TAB_IDS = ['home','dest','map','cal','trips','user'];

function TabBar({ tab, setTab, appLang }) {
  const t = useT(appLang);
  return (
    <div style={{background:C.tabBg, backdropFilter:"blur(20px)", borderTop:`1px solid ${C.border}`,
      display:"flex", padding:`6px 2px max(18px, env(safe-area-inset-bottom, 18px)) 2px`,
      zIndex:9999, width:"100%", position:"relative"}}>
      {TAB_IDS.map(id => {
        const active = tab === id;
        const color = active ? C.accentHi : C.muted;
        return (
          <button key={id} onClick={()=>setTab(id)}
            style={{flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:3,
              background:"transparent", border:"none", cursor:"pointer", padding:"0 2px"}}>
            <div style={{
              width:46, height:28, borderRadius:14,
              background: active ? C.accentDim : 'transparent',
              display:'flex', alignItems:'center', justifyContent:'center',
              transition:"background .2s",
            }}>
              {TAB_ICONS[id](color)}
            </div>
            <div style={{fontSize:9, fontWeight: active ? 700 : 500, color, letterSpacing:0.4,
              textTransform:"uppercase", transition:"color .2s"}}>
              {t(`nav.${id}`)}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function NoSlotWarningModal({ appLang, dest, longestSlot, onAdjust, onCreateSlot, onClose }) {
  const t = useT(appLang);
  const destName = dest?.name || '';
  const bodyFn = t('dest.noSlotBody');
  const body = typeof bodyFn === 'function' ? bodyFn(destName, longestSlot?.nights ?? '?') : bodyFn;
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:3000,
      background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'flex-end', justifyContent:'center',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.card, borderRadius:'20px 20px 0 0', padding:'28px 20px 36px',
        width:'100%', maxWidth:480, boxSizing:'border-box',
      }}>
        <div style={{ width:36, height:4, borderRadius:2, background:C.border, margin:'0 auto 20px' }} />
        <div style={{ fontSize:20, marginBottom:10 }}>⚠️</div>
        <div style={{ fontWeight:800, fontSize:16, color:C.text, marginBottom:8 }}>{t('dest.noSlotTitle')}</div>
        <div style={{ fontSize:13, color:C.sub, lineHeight:1.5, marginBottom:24 }}>{body}</div>
        <button onClick={onAdjust} style={{
          display:'block', width:'100%', padding:'13px 0', borderRadius:12,
          background: C.accentDim, color: C.accentHi, border:`1px solid ${C.accentHi}`,
          fontWeight:700, fontSize:14, cursor:'pointer', marginBottom:10,
        }}>{t('dest.noSlotAdjust')}</button>
        <button onClick={onCreateSlot} style={{
          display:'block', width:'100%', padding:'13px 0', borderRadius:12,
          background: C.accent, color:'#fff', border:'none',
          fontWeight:700, fontSize:14, cursor:'pointer',
        }}>{t('dest.noSlotCreate')}</button>
      </div>
    </div>
  );
}

function DowngradeDestsScreen({ appLang, planIds, destLimit, onConfirm, onAutoTrim }) {
  const t = useT(appLang);
  const [localIds, setLocalIds] = useState(planIds);
  const [destMap, setDestMap] = useState({});
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (!planIds.length) return;
    supabase.from('destinations')
      .select('id, name, country, name_en, name_es, name_zh, country_en, country_es, country_zh')
      .in('id', planIds)
      .then(({ data }) => {
        if (!data) return;
        const m = {};
        data.forEach(d => { m[d.id] = d; });
        setDestMap(m);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getName  = d => !d ? '—' : (appLang === 'en' ? d.name_en : appLang === 'es' ? d.name_es : appLang === 'zh' ? d.name_zh : null) || d.name;
  const getCtry  = d => !d ? '' : (appLang === 'en' ? d.country_en : appLang === 'es' ? d.country_es : appLang === 'zh' ? d.country_zh : null) || d.country;
  const excess    = Math.max(0, localIds.length - destLimit);
  const canConfirm = excess === 0;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:1100,
      display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div style={{ background:C.card, borderRadius:'20px 20px 0 0', maxHeight:'82vh',
        display:'flex', flexDirection:'column', borderTop:`1px solid ${C.border}` }}>
        {/* Handle */}
        <div style={{ width:36, height:4, borderRadius:2, background:C.border, margin:'14px auto 0', flexShrink:0 }}/>
        {/* Header */}
        <div style={{ padding:'16px 20px 0', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            <div style={{ color:C.text, fontSize:16, fontWeight:800, flex:1 }}>{t('downgrade.destsTitle')}</div>
            <button onClick={() => setShowHelp(true)} style={{
              width:20, height:20, borderRadius:10, background:C.accentDim, border:'none',
              color:C.accentHi, fontSize:12, fontWeight:800, cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, padding:0 }}>?</button>
            <div style={{ color: canConfirm ? C.green : C.rose, fontSize:13, fontWeight:700 }}>
              {localIds.length} / {destLimit}
            </div>
          </div>
          {!canConfirm && (
            <div style={{ color:C.rose, fontSize:12, marginBottom:8 }}>
              {t('downgrade.removeN').replace('{n}', excess)}
            </div>
          )}
          <div style={{ height:4, background:C.border, borderRadius:2, overflow:'hidden', marginBottom:12 }}>
            <div style={{ height:'100%', width:`${Math.min(100, localIds.length / destLimit * 100)}%`,
              background: canConfirm ? C.green : C.rose, borderRadius:2, transition:'width .3s' }}/>
          </div>
        </div>
        {showHelp && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1200,
            display:'flex', alignItems:'flex-end', justifyContent:'center' }}
            onClick={() => setShowHelp(false)}>
            <div onClick={e => e.stopPropagation()} style={{
              background:C.card, borderRadius:'20px 20px 0 0', padding:'24px 24px 40px',
              width:'100%', maxWidth:600, borderTop:`1px solid ${C.border}` }}>
              <div style={{ width:36, height:4, borderRadius:2, background:C.border, margin:'0 auto 20px' }}/>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                <div style={{ width:32, height:32, borderRadius:16, background:C.accentDim,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  color:C.accentHi, fontSize:16, fontWeight:800 }}>?</div>
                <span style={{ color:C.text, fontSize:16, fontWeight:800 }}>{t('downgrade.destsTitle')}</span>
              </div>
              <p style={{ color:C.sub, fontSize:14, lineHeight:1.7, margin:0 }}>{t('downgrade.helpText')}</p>
              <button onClick={() => setShowHelp(false)} style={{
                marginTop:20, width:'100%', padding:'13px', borderRadius:14,
                background:C.accent, border:'none', color:C.white, fontSize:14, fontWeight:700, cursor:'pointer',
              }}>{t('common.understood')}</button>
            </div>
          </div>
        )}
        {/* List */}
        <div style={{ flex:1, overflowY:'auto', borderTop:`1px solid ${C.border}` }}>
          {localIds.map(id => {
            const d = destMap[id];
            return (
              <div key={id} style={{ display:'flex', alignItems:'center', gap:12, padding:'13px 20px',
                borderBottom:`1px solid ${C.border}` }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ color:C.text, fontSize:14, fontWeight:600,
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {getName(d) || id}
                  </div>
                  <div style={{ color:C.muted, fontSize:12, marginTop:2 }}>{getCtry(d)}</div>
                </div>
                <button onClick={() => setLocalIds(prev => prev.filter(x => x !== id))}
                  style={{ background:'none', border:`1px solid ${C.border}`, color:C.muted,
                    borderRadius:8, padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
                  {t('downgrade.remove')}
                </button>
              </div>
            );
          })}
        </div>
        {/* Footer */}
        <div style={{ padding:'14px 20px 40px', borderTop:`1px solid ${C.border}`,
          display:'flex', flexDirection:'column', gap:10, flexShrink:0 }}>
          <button onClick={() => canConfirm && onConfirm(localIds)} disabled={!canConfirm}
            style={{ width:'100%', padding:'14px', borderRadius:14, fontSize:14, fontWeight:700,
              cursor: canConfirm ? 'pointer' : 'default',
              background: canConfirm ? `linear-gradient(135deg,#006F87,${C.accent})` : C.border,
              border:'none', color: canConfirm ? C.white : C.muted, transition:'all .2s' }}>
            {t('downgrade.confirm')}
          </button>
          <button onClick={onAutoTrim}
            style={{ width:'100%', padding:'12px', borderRadius:14, fontSize:13, fontWeight:600,
              background:'transparent', border:`1.5px solid ${C.border}`, color:C.sub, cursor:'pointer' }}>
            {t('downgrade.autoTrim')}
          </button>
        </div>
      </div>
    </div>
  );
}

function DowngradeSlotsScreen({ appLang, planSlotIds, slots, slotLimit, onConfirm, onAutoTrim }) {
  const t = useT(appLang);
  const [localIds, setLocalIds] = useState(planSlotIds);
  const [showHelp, setShowHelp] = useState(false);
  const locale   = appLang === 'en' ? 'en-GB' : appLang === 'zh' ? 'zh-CN' : appLang === 'es' ? 'es-ES' : 'fr-FR';
  const excess    = Math.max(0, localIds.length - slotLimit);
  const canConfirm = excess === 0;
  const slotById  = Object.fromEntries((slots || []).map(s => [s.id, s]));

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:1100,
      display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div style={{ background:C.card, borderRadius:'20px 20px 0 0', maxHeight:'82vh',
        display:'flex', flexDirection:'column', borderTop:`1px solid ${C.border}` }}>
        {/* Handle */}
        <div style={{ width:36, height:4, borderRadius:2, background:C.border, margin:'14px auto 0', flexShrink:0 }}/>
        {/* Header */}
        <div style={{ padding:'16px 20px 0', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            <div style={{ color:C.text, fontSize:16, fontWeight:800, flex:1 }}>{t('downgrade.slotsTitle')}</div>
            <button onClick={() => setShowHelp(true)} style={{
              width:20, height:20, borderRadius:10, background:C.accentDim, border:'none',
              color:C.accentHi, fontSize:12, fontWeight:800, cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, padding:0 }}>?</button>
            <div style={{ color: canConfirm ? C.green : C.rose, fontSize:13, fontWeight:700 }}>
              {localIds.length} / {slotLimit}
            </div>
          </div>
          {!canConfirm && (
            <div style={{ color:C.rose, fontSize:12, marginBottom:8 }}>
              {t('downgrade.removeN').replace('{n}', excess)}
            </div>
          )}
          <div style={{ height:4, background:C.border, borderRadius:2, overflow:'hidden', marginBottom:12 }}>
            <div style={{ height:'100%', width:`${Math.min(100, localIds.length / slotLimit * 100)}%`,
              background: canConfirm ? C.green : C.rose, borderRadius:2, transition:'width .3s' }}/>
          </div>
        </div>
        {showHelp && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1200,
            display:'flex', alignItems:'flex-end', justifyContent:'center' }}
            onClick={() => setShowHelp(false)}>
            <div onClick={e => e.stopPropagation()} style={{
              background:C.card, borderRadius:'20px 20px 0 0', padding:'24px 24px 40px',
              width:'100%', maxWidth:600, borderTop:`1px solid ${C.border}` }}>
              <div style={{ width:36, height:4, borderRadius:2, background:C.border, margin:'0 auto 20px' }}/>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                <div style={{ width:32, height:32, borderRadius:16, background:C.accentDim,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  color:C.accentHi, fontSize:16, fontWeight:800 }}>?</div>
                <span style={{ color:C.text, fontSize:16, fontWeight:800 }}>{t('downgrade.slotsTitle')}</span>
              </div>
              <p style={{ color:C.sub, fontSize:14, lineHeight:1.7, margin:0 }}>{t('downgrade.helpText')}</p>
              <button onClick={() => setShowHelp(false)} style={{
                marginTop:20, width:'100%', padding:'13px', borderRadius:14,
                background:C.accent, border:'none', color:C.white, fontSize:14, fontWeight:700, cursor:'pointer',
              }}>{t('common.understood')}</button>
            </div>
          </div>
        )}
        {/* List */}
        <div style={{ flex:1, overflowY:'auto', borderTop:`1px solid ${C.border}` }}>
          {localIds.map(id => {
            const s = slotById[id];
            return (
              <div key={id} style={{ display:'flex', alignItems:'center', gap:12, padding:'13px 20px',
                borderBottom:`1px solid ${C.border}` }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ color:C.text, fontSize:13, fontWeight:600,
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {s ? fmtSlotDisplay(s, locale) : id}
                  </div>
                  {s?.nights && <div style={{ color:C.muted, fontSize:12, marginTop:2 }}>{s.nights} nuits</div>}
                </div>
                <button onClick={() => setLocalIds(prev => prev.filter(x => x !== id))}
                  style={{ background:'none', border:`1px solid ${C.border}`, color:C.muted,
                    borderRadius:8, padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
                  {t('downgrade.remove')}
                </button>
              </div>
            );
          })}
        </div>
        {/* Footer */}
        <div style={{ padding:'14px 20px 40px', borderTop:`1px solid ${C.border}`,
          display:'flex', flexDirection:'column', gap:10, flexShrink:0 }}>
          <button onClick={() => canConfirm && onConfirm(localIds)} disabled={!canConfirm}
            style={{ width:'100%', padding:'14px', borderRadius:14, fontSize:14, fontWeight:700,
              cursor: canConfirm ? 'pointer' : 'default',
              background: canConfirm ? `linear-gradient(135deg,#006F87,${C.accent})` : C.border,
              border:'none', color: canConfirm ? C.white : C.muted, transition:'all .2s' }}>
            {t('downgrade.confirm')}
          </button>
          <button onClick={onAutoTrim}
            style={{ width:'100%', padding:'12px', borderRadius:14, fontSize:13, fontWeight:600,
              background:'transparent', border:`1.5px solid ${C.border}`, color:C.sub, cursor:'pointer' }}>
            {t('downgrade.autoTrim')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App({ user, onSignOut }) {
  const [screen, setScreen] = useState(
    new URLSearchParams(window.location.search).has('ob') ? "onboarding" : "loading"
  );
  const [obHelpMode, setObHelpMode] = useState(false);
  const [tab, setTab] = useState("home");
const [selIds, setSelIds] = useState([]);
const [pax, setPax] = useState(2);
const [travelPrefs, setTravelPrefs] = useState(() => loadTravelPrefs() || {});
const travelPrefsLoadedRef = useRef(false); // true once loadPrefs() from Supabase has completed
const travelPrefsRef = useRef(loadTravelPrefs() || {});
const [slots, setSlots] = useState([]);
const [planIds,     setPlanIds]     = useState(() => loadTravelPrefs()?.planIds     || []);
const [planSlotIds, setPlanSlotIds] = useState(() => loadTravelPrefs()?.planSlotIds || []);

const PLAN_LIMITS = { free: 5, silver: 10, gold: 15 };
const SLOT_LIMITS = { free: 5, silver: 10, gold: 15 };
const [showPaywall, setShowPaywall] = useState(false);
const [mapInitFilter, setMapInitFilter] = useState(null);
const [downgradeExcess, setDowngradeExcess] = useState(null); // null | { destLimit, slotLimit }
const [noSlotWarning, setNoSlotWarning] = useState(null); // null | { dest, longestSlot }

// ── Détection multi-onglets ───────────────────────────────────────────────────
const tabIdRef = useRef(Date.now().toString(36) + Math.random().toString(36).slice(2));
const [duplicateSession, setDuplicateSession] = useState(false);

useEffect(() => {
  if (!user) return;
  const key = `plotted_tab_${user.id}`;
  const myId = tabIdRef.current;
  // Claim this tab (last-write-wins)
  localStorage.setItem(key, myId);
  setDuplicateSession(false);
  const onStorage = e => {
    if (e.key === key && e.newValue && e.newValue !== myId) setDuplicateSession(true);
  };
  window.addEventListener('storage', onStorage);
  const onUnload = () => { if (localStorage.getItem(key) === myId) localStorage.removeItem(key); };
  window.addEventListener('beforeunload', onUnload);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('beforeunload', onUnload);
    if (localStorage.getItem(key) === myId) localStorage.removeItem(key);
  };
}, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
const planLimit = PLAN_LIMITS[travelPrefs?.plan || 'free'] || 5;
const slotLimit = SLOT_LIMITS[travelPrefs?.plan || 'free'] || 5;

const [shareModal, setShareModal] = useState(null); // null | { url } | 'creating'

const buildPhotos = (city, type) => {
  const base = (DEST_PHOTOS[city] || TYPE_PHOTOS[type] || TYPE_PHOTOS.ville).split('?')[0];
  return [
    `${base}?w=900&h=560&fit=crop&crop=entropy&q=85`,
    `${base}?w=500&h=620&fit=crop&crop=focalpoint&q=80`,
    `${base}?w=500&h=440&fit=crop&crop=top&q=80`,
  ];
};

const handleShareTopN = async (rankedDests) => {
  if (!rankedDests?.length) return;
  setShareModal('creating');

  const token = Math.random().toString(36).slice(2,8).toUpperCase() +
                Math.random().toString(36).slice(2,8).toUpperCase();

  // Plan dests — directly from rankedDests (reliable, no query needed)
  const planIdSet = new Set(rankedDests.map(t => String(t.dest.id)));
  const planDestsPayload = rankedDests.map((t, i) => {
    const city = t.dest.city || t.dest.name || '';
    return {
      rank: i + 1,
      name: city,
      country: t.dest.country || '',
      type: t.dest.type || 'ville',
      photoUrl: DEST_PHOTOS[city] || TYPE_PHOTOS[t.dest.type] || TYPE_PHOTOS.ville,
      minPrice: t.total,
      slotFrom: t.slot?.from ?? null,
      slotTo:   t.slot?.to   ?? null,
      inPlan: true,
      id: t.dest.id ?? null,
      lat: t.dest.lat ?? null,
      lng: t.dest.lng ?? null,
    };
  });

  // Bucket list dests — fetch only non-plan IDs from Supabase
  const nonPlanIds = selIds.filter(id => !planIdSet.has(String(id)));
  let bucketDestsPayload = [];
  if (nonPlanIds.length > 0) {
    const { data: nonPlanData } = await supabase
      .from('destinations')
      .select('id, name, country, type, lat, lng')
      .in('id', nonPlanIds);
    bucketDestsPayload = (nonPlanData || []).map(d => ({
      rank: null,
      name: d.name,
      country: d.country || '',
      type: d.type || 'ville',
      photoUrl: DEST_PHOTOS[d.name] || TYPE_PHOTOS[d.type] || TYPE_PHOTOS.ville,
      minPrice: null,
      slotFrom: null,
      slotTo:   null,
      inPlan: false,
      id: d.id ?? null,
      lat: d.lat ?? null,
      lng: d.lng ?? null,
    })).sort((a, b) => a.name.localeCompare(b.name));
  }

  const dests = [...planDestsPayload, ...bucketDestsPayload];

  const payload = {
    mode: 'topn',
    ownerName: travelPrefs?.firstName || '',
    planCount: rankedDests.length,
    lang: travelPrefs?.appLang || 'fr',
    dests,
  };

  const { error } = await supabase.from('shared_topn').insert({ token, owner_id: user.id, payload });
  if (error) { setShareModal(null); return; }
  setShareModal({ url: `${window.location.origin}/share/${token}`, payload });
};

const handleShare = async ({ mode, dest, destTrips: sharedTrips } = {}) => {
  if (!trips.length && !sharedTrips?.length) return;
  setShareModal('creating');

  const activeDest = dest || trips[0]?.dest;
  const activeTrips = sharedTrips?.length ? sharedTrips : trips.slice(0, planLimit);
  // dest objects from computeRealRanking use `city` (not `name`) and omit `country`
  const destName = activeDest?.city || activeDest?.name || '';

  const [token, guide, destRow] = await Promise.all([
    Promise.resolve(
      Math.random().toString(36).slice(2,8).toUpperCase() +
      Math.random().toString(36).slice(2,8).toUpperCase()
    ),
    fetchDestGuide(destName, '', travelPrefs?.appLang || 'fr', travelPrefs?.passportCountry || ''),
    activeDest?.id
      ? supabase.from('destinations').select('country, name_en').eq('id', activeDest.id).maybeSingle().then(r => r.data)
      : Promise.resolve(null),
  ]);

  const destCountry = destRow?.country || activeDest?.country || '';

  // Photo: DEST_PHOTOS first, then Wikidata (same logic as DestPhotoBanner), then type fallback
  const BAD_IMG = /flag|coat|logo|blason|emblem|icon|seal|arms|wappen|shield|banner|locator|map|carte|plan\b/i;
  let photoUrl = DEST_PHOTOS[destName] || DEST_PHOTOS[destRow?.name_en] || null;
  if (!photoUrl) {
    const tryWiki = async (lang, title) => {
      try {
        const r = await fetch(`https://www.wikidata.org/w/api.php?action=wbgetentities&sites=${lang}wiki&titles=${encodeURIComponent(title)}&props=claims&format=json&origin=*`);
        const d = await r.json();
        const entity = Object.values(d.entities || {}).find(e => e.id && e.id !== '-1');
        const p18 = entity?.claims?.P18?.[0]?.mainsnak?.datavalue?.value;
        if (p18 && !BAD_IMG.test(p18)) return `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(p18.replace(/ /g,'_'))}?width=800`;
      } catch { /* ignore */ }
      return null;
    };
    const nameEn = destRow?.name_en || destName;
    photoUrl = await tryWiki('en', nameEn) || await tryWiki('fr', destName)
      || (TYPE_PHOTOS[activeDest?.type] || TYPE_PHOTOS.ville);
  }
  const photos = [photoUrl, photoUrl, photoUrl];

  const payload = {
    mode: 'list',
    ownerName: travelPrefs?.firstName || '',
    lang: travelPrefs?.appLang || 'fr',
    dest: {
      name: destName,
      country: destCountry,
      city: destName,
      type: activeDest?.type || 'ville',
    },
    photos,
    slots: activeTrips.map(t => ({
      from: t.slot?.from,
      to: t.slot?.to,
      nights: t.nights,
      type: t.slot?.type,
      price: t.total,
      pricePerNight: t.nights ? Math.round(t.total / t.nights) : null,
      airline: t.bestFlight?.airline?.name || null,
      isReal: t.bestFlight?.isReal !== false,
    })),
  };

  const { error } = await supabase.from('shared_topn').insert({ token, owner_id: user.id, payload });
  if (error) { setShareModal(null); return; }
  setShareModal({ url: `${window.location.origin}/share/${token}`, payload });
};

const handleShareTrip = async (tripToShare) => {
  if (!tripToShare) return;
  setShareModal('creating');

  const dest = tripToShare.dest;
  const destName = dest?.city || dest?.name || '';

  const withTimeout = (p, ms) => Promise.race([p, new Promise(r => setTimeout(() => r(null), ms))]);

  const [token, guide, destRow, itinerary] = await Promise.all([
    Promise.resolve(
      Math.random().toString(36).slice(2,8).toUpperCase() +
      Math.random().toString(36).slice(2,8).toUpperCase()
    ),
    fetchDestGuide(destName, '', travelPrefs?.appLang || 'fr', travelPrefs?.passportCountry || '', tripToShare.slot?.from || '', tripToShare.slot?.to || ''),
    dest?.id
      ? supabase.from('destinations').select('country, name_en').eq('id', dest.id).maybeSingle().then(r => r.data)
      : Promise.resolve(null),
    withTimeout(fetchDestItinerary(destName, '', tripToShare.nights || 3, travelPrefs?.appLang || 'fr'), 20000),
  ]);

  const destCountry = destRow?.country || dest?.country || '';

  // Photo: DEST_PHOTOS → Wikidata → type fallback (same as DestPhotoBanner)
  const BAD_IMG = /flag|coat|logo|blason|emblem|icon|seal|arms|wappen|shield|banner|locator|map|carte|plan\b/i;
  let photoUrl = DEST_PHOTOS[destName] || DEST_PHOTOS[destRow?.name_en] || null;
  if (!photoUrl) {
    const tryWiki = async (lang, title) => {
      try {
        const r = await fetch(`https://www.wikidata.org/w/api.php?action=wbgetentities&sites=${lang}wiki&titles=${encodeURIComponent(title)}&props=claims&format=json&origin=*`);
        const d = await r.json();
        const entity = Object.values(d.entities || {}).find(e => e.id && e.id !== '-1');
        const p18 = entity?.claims?.P18?.[0]?.mainsnak?.datavalue?.value;
        if (p18 && !BAD_IMG.test(p18)) return `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(p18.replace(/ /g,'_'))}?width=800`;
      } catch { /* ignore */ }
      return null;
    };
    const nameEn = destRow?.name_en || destName;
    photoUrl = await tryWiki('en', nameEn) || await tryWiki('fr', destName)
      || TYPE_PHOTOS[dest?.type] || TYPE_PHOTOS.ville;
  }
  const photos = [photoUrl, photoUrl, photoUrl];

  const fl = tripToShare.bestFlight;
  const ht = tripToShare.bestHotel;

  const travelerNames = [
    travelPrefs?.firstName,
    ...(travelPrefs?.coTravelers?.length
      ? travelPrefs.coTravelers.map(c => c.firstName)
      : travelPrefs?.coFirstName ? [travelPrefs.coFirstName] : []),
  ].filter(Boolean);

  const payload = {
    mode: 'trip',
    ownerName: travelPrefs?.firstName || '',
    lang: travelPrefs?.appLang || 'fr',
    travelers: travelerNames,
    itinerary: itinerary || null,
    dest: {
      name: destName,
      country: destCountry,
      city: destName,
      type: dest?.type || 'ville',
    },
    photos,
    trip: {
      from: tripToShare.slot?.from,
      to: tripToShare.slot?.to,
      nights: tripToShare.nights,
      type: tripToShare.slot?.type,
      total: tripToShare.total,
      flight: fl ? {
        airline: fl.airline?.name || '',
        price: fl.totalPrice,
        durationMins: fl.durationMins,
        departureTime: fl.departureTime,
        arrivalTime: fl.arrivalTime,
        stops: fl.stops || 0,
        isReal: fl.isReal !== false,
      } : null,
      hotel: ht ? {
        name: ht.name,
        rating: ht.rating,
        stars: ht.stars,
        price: ht.totalPrice,
        pricePerNight: ht.pricePerNight,
        cancelFree: ht.cancelFree,
      } : null,
    },
    guide: guide || null,
  };

  const { error } = await supabase.from('shared_topn').insert({ token, owner_id: user.id, payload });
  if (error) { setShareModal(null); return; }
  setShareModal({ url: `${window.location.origin}/share/${token}`, payload });
};

const handleUpgrade = (planId, billing = 'monthly') => {
  const newPrefs = planId === 'free'
    ? { ...travelPrefs, plan: 'free', planBilling: null, planSince: null }
    : { ...travelPrefs, plan: planId, planBilling: billing, planSince: new Date().toISOString() };
  setTravelPrefs(newPrefs);
  saveTravelPrefs(newPrefs, user?.id);
  const newDestLimit = PLAN_LIMITS[planId] || 5;
  const newSlotLimit = SLOT_LIMITS[planId] || 5;
  const destOver = planIds.length > newDestLimit;
  const slotOver = planSlotIds.length > newSlotLimit;
  if (destOver || slotOver) {
    setDowngradeExcess({ destLimit: newDestLimit, slotLimit: newSlotLimit, step: destOver ? 'dests' : 'slots' });
  }
};

// FIFO : garde les N plus récemment ajoutés (slice(-limit))
const handleAutoTrim = () => {
  const { destLimit, slotLimit } = downgradeExcess;
  const newPlanIds     = planIds.length > destLimit     ? planIds.slice(-destLimit)     : planIds;
  const newPlanSlotIds = planSlotIds.length > slotLimit ? planSlotIds.slice(-slotLimit) : planSlotIds;
  setPlanIds(newPlanIds);
  setPlanSlotIds(newPlanSlotIds);
  const newPrefs = { ...travelPrefs, planIds: newPlanIds, planSlotIds: newPlanSlotIds };
  setTravelPrefs(newPrefs);
  saveTravelPrefs(newPrefs, user?.id);
  setDowngradeExcess(null);
};

const handleDestsConfirm = (newPlanIds) => {
  setPlanIds(newPlanIds);
  const newPrefs = { ...travelPrefs, planIds: newPlanIds };
  setTravelPrefs(newPrefs);
  saveTravelPrefs(newPrefs, user?.id);
  if (downgradeExcess && planSlotIds.length > downgradeExcess.slotLimit) {
    setDowngradeExcess(prev => ({ ...prev, step: 'slots' }));
  } else {
    setDowngradeExcess(null);
  }
};

const handleSlotsConfirm = (newPlanSlotIds) => {
  setPlanSlotIds(newPlanSlotIds);
  const newPrefs = { ...travelPrefs, planSlotIds: newPlanSlotIds };
  setTravelPrefs(newPrefs);
  saveTravelPrefs(newPrefs, user?.id);
  setDowngradeExcess(null);
};

const togglePlan = (destOrId) => {
  const id = typeof destOrId === 'object' ? destOrId.id : destOrId;
  const isAdding = !planIds.includes(id);

  if (isAdding && planIds.length >= planLimit) {
    setShowPaywall(true);
    return;
  }

  // When adding, check if any plan slot is compatible
  if (isAdding && typeof destOrId === 'object') {
    const dest = destOrId;
    const iata = dest.airport_iata || AIRPORT_CODES[dest.name];
    const compatTypes = compatibleSlotTypes(iata);
    const userSlots = slots.filter(s => planSlotIds.includes(s.id));
    const hasCompatible = userSlots.some(s => compatTypes.includes(s.type));
    if (!hasCompatible && userSlots.length > 0) {
      const longestSlot = userSlots.reduce((best, s) => s.nights > best.nights ? s : best, userSlots[0]);
      setNoSlotWarning({ dest, longestSlot });
      return;
    }
  }

  setPlanIds(prev => {
    const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
    const newPrefs = { ...travelPrefs, planIds: next };
    setTravelPrefs(newPrefs);
    saveTravelPrefs(newPrefs, user?.id);
    return next;
  });
};

const togglePlanSlot = (id) => {
  if (!planSlotIds.includes(id) && planSlotIds.length >= slotLimit) {
    setShowPaywall(true);
    return;
  }
  setPlanSlotIds(prev => {
    const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
    const newPrefs = { ...travelPrefs, planSlotIds: next };
    setTravelPrefs(newPrefs);
    saveTravelPrefs(newPrefs, user?.id);
    return next;
  });
};

useEffect(() => {
  if (!user) return;
  if (new URLSearchParams(window.location.search).has('ob')) return; // ?ob → onboarding forcé
  loadPrefs(user.id).then(prefs => {
    // Auth/network error — keep local state intact, don't redirect to onboarding
    if (prefs?._error) { travelPrefsLoadedRef.current = true; setScreen("main"); return; }
    const ids = prefs?.selectedIds || [];
    let tp    = prefs?.travelPrefs || {};
    const isNewAccount = !ids.length && !Object.keys(tp).length;

    // Apply referral Silver bonus if set by App.js after signup
    const referralPlan = sessionStorage.getItem('plotted_referral_plan');
    if (referralPlan && !tp.plan) {
      tp = { ...tp, plan: referralPlan, planSince: new Date().toISOString(), planBilling: 'bonus' };
      saveTravelPrefs(tp, user.id);
      sessionStorage.removeItem('plotted_referral_plan');
    }

    if (prefs && !isNewAccount) {
      // Fallback: si selected_destinations est vide en Supabase mais planIds a des données,
      // restaurer depuis planIds (protection contre perte de données réseau/timing)
      const effectiveIds = ids.length ? ids : (tp.planIds?.length ? tp.planIds : []);
      if (!ids.length && effectiveIds.length) {
        savePrefs(user.id, prefs.pax || 2, effectiveIds); // restaurer en Supabase
      }
      setSelIds(effectiveIds);
      setPax(prefs.pax);
      setTravelPrefs(tp);
      const saved = tp.planIds;
      if (saved != null) {
        // planIds existe (même vide []) → respecter le choix de l'user
        setPlanIds(saved);
      } else if (ids.length) {
        // Ancien compte sans planIds sauvegardé → auto-populate une seule fois
        const auto = ids.slice(0, PLAN_LIMITS[tp.plan || 'free'] || 5);
        setPlanIds(auto);
        const newPrefs = { ...tp, planIds: auto };
        setTravelPrefs(newPrefs);
        saveTravelPrefs(newPrefs, user.id);
      }
      // Restore plan slot IDs
      if (tp.planSlotIds?.length) setPlanSlotIds(tp.planSlotIds);
      // Restore manual slots (not auto-generated, only exist in Supabase)
      if (tp.manualSlots?.length) {
        const todayIso = new Date().toISOString().split('T')[0];
        const future = tp.manualSlots.filter(s => (s.returnIso || s.departIso) >= todayIso);
        if (future.length) setSlots(prev => {
          const existingIds = new Set((prev || []).filter(s => s.source === 'manual').map(s => s.id));
          const toAdd = future.filter(s => !existingIds.has(s.id));
          return toAdd.length ? [...(prev || []), ...toAdd] : (prev || []);
        });
      }
      // Generate refCode if missing
      if (!tp.refCode) {
        const code = Math.random().toString(36).slice(2,6).toUpperCase() + Math.random().toString(36).slice(2,6).toUpperCase();
        const newTp = { ...tp, refCode: code };
        setTravelPrefs(newTp);
        saveTravelPrefs(newTp, user.id);
        supabase.from('profiles').update({ ref_code: code }).eq('user_id', user.id);
      }
      // Restore cached ranking for instant display on new device / cold start
      if (prefs.lastRanking?.length) {
        setTrips(prefs.lastRanking);
        hasCachedRankingRef.current = true;
      }
      // Géoloc browser si aucun aéroport de départ n'est défini
      if (!tp?.departureAirports?.length && !tp?.departureAirport && !tp?.geoOrigin && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(pos => {
          const geo = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          const next = { ...tp, geoOrigin: geo };
          setTravelPrefs(next);
          saveTravelPrefs(next, user.id);
        }, () => {
          // Refus ou erreur — afficher le bandeau si pas d'aéroport configuré
          if (!localStorage.getItem('plotted_geo_dismissed')) setGeoBlocked(true);
        });
      }
      travelPrefsLoadedRef.current = true;
      setScreen("main");
    } else {
      // Nouveau compte ou compte réinitialisé — vider tout l'état local stale
      localStorage.removeItem('plotted_home_help_seen');
      localStorage.removeItem('plotted_dest_help_seen');
      localStorage.removeItem('plotted_cal_help_seen');
      localStorage.removeItem('travel_prefs');
      localStorage.removeItem('plotted_ranking_cache');
      setSelIds([]);
      setPlanIds([]);
      setPlanSlotIds([]);
      setSlots([]);
      setTravelPrefs({});
      if (!prefs) logEvent(user.id, null, 'user_created', { host: window.location.hostname });
      travelPrefsLoadedRef.current = true;
      setScreen("onboarding");
    }
  });
}, [user]);

// ── Session tracking ─────────────────────────────────────────────────────────
useEffect(() => {
  if (!user) return;
  const sid = Date.now().toString(36) + Math.random().toString(36).slice(2);
  sessionIdRef.current    = sid;
  sessionStartRef.current = Date.now();
  const host = window.location.hostname;
  logEvent(user.id, sid, 'session_start', { host });
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      const dur = Math.round((Date.now() - sessionStartRef.current) / 1000);
      logEvent(user.id, sid, 'session_end', { duration_s: dur, last_tab: lastTabRef.current, host });
    } else {
      // Retour en foreground → refresh préventif du JWT
      supabase.auth.getSession();
    }
  };
  document.addEventListener('visibilitychange', onVisibility);
  return () => document.removeEventListener('visibilitychange', onVisibility);
}, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => {
  if (!user || !sessionIdRef.current) return;
  lastTabRef.current = tab;
  logEvent(user.id, sessionIdRef.current, 'page_view', { tab, host: window.location.hostname });
}, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => {
  if (!user) return;
  const todayIso = new Date().toISOString().split('T')[0];
  const country = travelPrefs?.holidayCountry || (travelPrefs?.appLang === 'en' ? detectCountry() : 'FR');
  generateBridgeSlots(country).then(bridgeSlots => {
    setSlots(prev => {
      // 1. Purger les slots passés (toutes sources)
      const future = (prev || []).filter(s => (s.returnIso || s.departIso) >= todayIso);
      // 2. Garder les slots manuels et events existants
      const manual  = future.filter(s => s.source === 'manual');
      const evSlots = future.filter(s => s.source === 'event');
      // 3. Merge incrémental des auto slots — exclure les dismissés par l'user
      const dismissed = new Set(travelPrefsRef.current?.dismissedAutoSlotIds || []);
      const existingAutoIds = new Set(future.filter(s => s.source === 'auto').map(s => s.id));
      const newAuto = bridgeSlots.filter(s => !existingAutoIds.has(s.id) && !dismissed.has(s.id));
      const allAuto = [...future.filter(s => s.source === 'auto'), ...newAuto];
      return [...allAuto, ...manual, ...evSlots];
    });
    // Auto-populate planSlotIds si pas encore défini (nouvel utilisateur)
    // GUARD : si travelPrefsRef est vide (nouveau device, localStorage absent), on ne sauvegarde
    // rien en DB — loadPrefs() va restaurer les vrais planSlotIds depuis Supabase.
    setPlanSlotIds(prev => {
      if (prev.length) return prev;
      const currentPrefs = travelPrefsRef.current;
      if (Object.keys(currentPrefs).length === 0) return prev; // prefs pas encore chargées
      const limit = SLOT_LIMITS[currentPrefs?.plan || 'free'] || 5;
      const auto = bridgeSlots.slice(0, limit).map(s => s.id);
      const newPrefs = { ...currentPrefs, planSlotIds: auto };
      setTravelPrefs(newPrefs);
      saveTravelPrefs(newPrefs, user?.id);
      return auto;
    });
  });
}, [user]); // eslint-disable-line react-hooks/exhaustive-deps

// Auto-create / sync event slots whenever selIds changes
useEffect(() => {
  if (!user || !selIds.length) return;
  supabase.from('destinations').select('id, name, type, event_date, recurrent')
    .in('id', selIds)
    .then(({ data }) => {
      const events = (data || []).filter(d => d.type === 'event' && d.event_date);
      setSlots(prev => {
        // Remove event slots whose event is no longer selected
        const cleaned = (prev || []).filter(s => s.source !== 'event' || selIds.includes(s.eventId));
        // Add canonical slot for each selected event not yet present
        const existingIds = new Set(cleaned.filter(s => s.source === 'event').map(s => s.eventId));
        const newSlots = events.filter(ev => !existingIds.has(ev.id)).map(ev => canonicalEventSlot(ev)).filter(Boolean);
        return newSlots.length ? [...cleaned, ...newSlots] : cleaned;
      });
    });
}, [selIds, user]); // eslint-disable-line react-hooks/exhaustive-deps

// Garder planIds ⊆ selIds (nettoyage des IDs fantômes + persist en Supabase)
useEffect(() => {
  if (!selIds.length || !planIds.length) return;
  // Guard: ne pas sauvegarder si travelPrefs n'est pas encore chargé depuis Supabase
  // (race condition : setSelIds peut déclencher cet effet avant que setTravelPrefs soit appelé)
  if (!travelPrefsLoadedRef.current) return;
  const selSet = new Set(selIds.map(String));
  const filtered = planIds.filter(id => selSet.has(String(id)));
  if (filtered.length !== planIds.length) {
    setPlanIds(filtered);
    const newPrefs = { ...travelPrefs, planIds: filtered };
    setTravelPrefs(newPrefs);
    if (user?.id) saveTravelPrefs(newPrefs, user.id);
  }
}, [selIds]); // eslint-disable-line react-hooks/exhaustive-deps

// Keep planSlotIds ⊆ slots
useEffect(() => {
  if (!slots.length) return;
  setPlanSlotIds(prev => {
    const slotSet = new Set(slots.map(s => s.id));
    const filtered = prev.filter(id => slotSet.has(id));
    return filtered.length === prev.length ? prev : filtered;
  });
}, [slots]); // eslint-disable-line react-hooks/exhaustive-deps

// Keep travelPrefs.planSlotIds in sync with planSlotIds state.
// The "keep ⊆ slots" effect above updates planSlotIds state without touching travelPrefs,
// causing any subsequent saveTravelPrefs({ ...travelPrefs }) to write stale planSlotIds.
useEffect(() => {
  if (!travelPrefsLoadedRef.current) return;
  setTravelPrefs(prev => {
    if (JSON.stringify(prev?.planSlotIds) === JSON.stringify(planSlotIds)) return prev;
    return { ...prev, planSlotIds };
  });
}, [planSlotIds]); // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => {
  if (!user || !selIds.length) return;
  const timer = setTimeout(() => {
    savePrefs(user.id, pax, selIds);
  }, 1000);
  return () => clearTimeout(timer);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [pax, selIds]);

// Keep travelPrefsRef in sync so the slots-save effect always has a fresh reference
useEffect(() => { travelPrefsRef.current = travelPrefs; }, [travelPrefs]);

// Sync pax = 1 + coTravelers.length whenever co-travelers change
useEffect(() => {
  if (!user || !selIds.length) return;
  const computed = 1 + (travelPrefs?.coTravelers?.length || 0);
  if (computed === pax) return;
  setPax(computed);
  savePrefs(user.id, computed, selIds);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [travelPrefs?.coTravelers]);

// Persist manual slots in travel_prefs.manualSlots (Supabase + localStorage)
useEffect(() => {
  if (!user) return;
  const timer = setTimeout(() => {
    const manualSlots = (slots || []).filter(s => s.source === 'manual');
    const prev = travelPrefsRef.current?.manualSlots || [];
    if (JSON.stringify(manualSlots) === JSON.stringify(prev)) return;
    const next = { ...travelPrefsRef.current, manualSlots };
    setTravelPrefs(next);
    saveTravelPrefs(next, user.id);
  }, 600);
  return () => clearTimeout(timer);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [slots]);
  const [activeTrip, setActiveTrip] = useState(null);
  const [bookFlight, setBookFlight] = useState(null);
  const [bookHotel, setBookHotel] = useState(null);
  const [bookMode, setBookMode] = useState("both");
  const [profileBackTo, setProfileBackTo] = useState(null); // 'booking' | null
  const [profileSection, setProfileSection] = useState(null); // 'coTravelers' | 'passport' | null
  const [nightPickerCtx, setNightPickerCtx] = useState(null);
  const [itineraryCtx,   setItineraryCtx]   = useState(null);

  const handleEditNights = (dest, photo, openItineraryAfter) =>
    setNightPickerCtx({ dest, photo, openItineraryAfter });

  const handleNightsConfirm = ({ min, max }) => {
    const { dest, photo, openItineraryAfter } = nightPickerCtx;
    const next = { ...travelPrefs, destNights: { ...(travelPrefs?.destNights || {}), [dest.id]: { min, max } } };
    setTravelPrefs(next);
    saveTravelPrefs(next, user?.id);
    setNightPickerCtx(null);
    if (openItineraryAfter) setItineraryCtx({ dest, photo, nights: min });
  };

  const handleOpenItinerary = (dest, photo, nights) =>
    setItineraryCtx({ dest, photo, nights });

  const [trips,        setTrips]        = useState([]);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [geoBlocked, setGeoBlocked] = useState(false);
  const [rankingKey,   setRankingKey]   = useState(0);
  const [planDestNames, setPlanDestNames] = useState([]);
  const RANKING_TTL = 24 * 60 * 60 * 1000; // 24h
  const [lastRankingTime, setLastRankingTime] = useState(() => {
    try { return JSON.parse(localStorage.getItem('plotted_ranking_cache') || 'null')?.time || null; } catch { return null; }
  });
  const sessionIdRef    = useRef(null);
  const sessionStartRef = useRef(null);
  const lastTabRef      = useRef('home');
  const hasCachedRankingRef = useRef(false); // true until first fresh compute after cache restore

  // Slots visibles dans le Top N — uniquement ceux du plan, comme pour les destinations
  const activePlanSlots = planSlotIds.length ? slots.filter(s => planSlotIds.includes(s.id)) : [];

  useEffect(() => {
    if (tab !== 'home') return; // wait until user is on the home tab
    if (!planIds?.length || !activePlanSlots.length) { setTrips([]); setRankingLoading(false); return; }

    // ── Cache check: skip compute if fresh + same inputs + no forced refresh ──
    const origin0 = travelPrefs?.departureAirports?.[0] || travelPrefs?.departureAirport || '';
    const fingerprint = JSON.stringify([[...(planIds||[])].sort(), [...(planSlotIds||[])].sort(), pax, origin0]);
    try {
      const cached = JSON.parse(localStorage.getItem('plotted_ranking_cache') || 'null');
      if (cached?.fingerprint === fingerprint && cached?.rankingKey === rankingKey &&
          cached?.trips?.length && Date.now() - cached.time < RANKING_TTL) {
        setTrips(cached.trips);
        setLastRankingTime(cached.time);
        hasCachedRankingRef.current = true;
        setRankingLoading(false);
        return;
      }
    } catch {}

    let cancelled = false;
    let debounceTimer = null;
    let delayTimer = null;
    const hasCache = hasCachedRankingRef.current;
    if (!hasCache) setRankingLoading(true);
    // Debounce 400ms: if deps change rapidly (bridge slots + event slots + login batch),
    // only the final stable state triggers Duffel calls
    // Spinner duration scales with √(nDest × nSlots) — more combos = longer animation
    const nCombos = planIds.length * activePlanSlots.length;
    const targetMs = Math.min(14000, 2500 + 1400 * Math.sqrt(nCombos));
    debounceTimer = setTimeout(() => {
      if (cancelled) return;
      const t0 = Date.now();
      const origin = travelPrefs?.departureAirports?.[0] || travelPrefs?.departureAirport || '';
      const origins = travelPrefs?.departureAirports?.filter(Boolean) || (travelPrefs?.departureAirport ? [travelPrefs.departureAirport] : []);
      computeRealRanking(planIds, pax, activePlanSlots, origin, travelPrefs, travelPrefs?.flightOnly !== false).then(({ trips: t }) => {
        if (cancelled) return;
        hasCachedRankingRef.current = false;
        // Save to localStorage cache
        const now = Date.now();
        try {
          localStorage.setItem('plotted_ranking_cache', JSON.stringify({ time: now, fingerprint, rankingKey, trips: t }));
        } catch {}
        setLastRankingTime(now);
        // Save fresh ranking to Supabase — skip write if dest order is identical (saves Disk IO)
        const newKey = t.map(x => x.dest?.id).join(',');
        const oldKey = trips.map(x => x.dest?.id).join(',');
        if (user?.id && newKey !== oldKey) {
          supabase.from('profiles').update({ last_ranking: t }).eq('user_id', user.id);
        }
        // If cache was shown: swap results immediately (no spinner delay)
        // If no cache: respect the spinner animation timing
        const extra = hasCache ? 0 : Math.max(200, targetMs - (Date.now() - t0));
        delayTimer = setTimeout(() => {
          if (cancelled) return;
          setTrips(t);
          if (!hasCache) setRankingLoading(false);
          // Pre-warm Duffel cache for top 3 destinations — all plan slots
          // so TripDetail hits cache instead of re-fetching everything
          t.slice(0, 3).forEach(trip => {
            if (trip?.dest) {
              computeDestSlots(trip.dest, activePlanSlots, pax, origins, travelPrefs, travelPrefs?.flightOnly !== false);
            }
          });
        }, extra);
      }).catch(err => {
        if (cancelled) return;
        console.error('[ranking] computeRealRanking failed:', err);
        setTrips([]);
        setRankingLoading(false);
      });
    }, 400);
    return () => { cancelled = true; clearTimeout(debounceTimer); clearTimeout(delayTimer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, planIds, planSlotIds, pax, slots, travelPrefs?.departureAirports?.[0], travelPrefs?.departureAirport, rankingKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve plan destination names for the spinner (before ranking completes)
  useEffect(() => {
    setPlanDestNames([]); // clear stale names immediately when plan changes
    if (!planIds?.length) return;
    const cacheKey = [...planIds].sort().join(',');
    const cached = _destsCache.get(cacheKey);
    if (cached) { setPlanDestNames(cached.map(d => ({ city: d.city || d.name, nameEn: d.name_en, type: d.type || 'ville' }))); return; }
    supabase.from('destinations').select('id,name,name_en,type').in('id', planIds).then(({ data }) => {
      if (data?.length) setPlanDestNames(data.map(d => ({ city: d.name, nameEn: d.name_en, type: d.type || 'ville' })));
    });
  }, [planIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAutoGenSlots = () => {
    const country = travelPrefs?.holidayCountry || (travelPrefs?.appLang === 'en' ? detectCountry() : 'FR');
    generateBridgeSlots(country).then(bridgeSlots => {
      if (!bridgeSlots.length) return;
      setSlots(prev => {
        const manual  = (prev || []).filter(s => s.source === 'manual');
        const evSlots = (prev || []).filter(s => s.source === 'event');
        const existingAutoIds = new Set((prev || []).filter(s => s.source === 'auto').map(s => s.id));
        const newSlots  = bridgeSlots.filter(s => !existingAutoIds.has(s.id));
        const existingAuto = (prev || []).filter(s => s.source === 'auto');
        const allAuto = [...existingAuto, ...newSlots];
        // Re-add ALL auto slots to plan (including ones the user had removed)
        setPlanSlotIds(p => {
          const pSet = new Set(p);
          const toAdd = allAuto.map(s => s.id).filter(id => !pSet.has(id));
          return toAdd.length ? [...p, ...toAdd] : p;
        });
        return [...allAuto, ...manual, ...evSlots];
      });
    });
  };

  const goTrip  = t => { setActiveTrip(t); setScreen("detail"); };
  const goBook  = (t, fl, ht) => { setActiveTrip(t); setBookFlight(fl); setBookHotel(ht); setBookMode("both"); setScreen("booking"); };
  const goDone  = () => { setActiveTrip(null); setScreen("main"); setTab("home"); };

  const trl = useT(travelPrefs?.appLang);

  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:C.tabBg,display:"flex",flexDirection:"column",fontFamily:"-apple-system,'SF Pro Display','Helvetica Neue',sans-serif"}}>

      <SaveErrorBanner label={trl('save.failed')}/>

      {/* Multi-onglet : cet onglet a été supplanté */}
      {duplicateSession && createPortal(
        <div style={{position:'fixed',inset:0,background:'rgba(7,16,26,0.96)',zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center',padding:'24px'}}>
          <div style={{background:C.card,borderRadius:20,padding:'32px 24px',maxWidth:340,width:'100%',textAlign:'center',border:`1px solid ${C.border}`}}>
            <div style={{fontSize:32,marginBottom:16}}>🔒</div>
            <div style={{color:C.text,fontSize:17,fontWeight:800,marginBottom:10}}>{trl('session.takenOver')}</div>
            <div style={{color:C.sub,fontSize:13,lineHeight:1.6,marginBottom:24}}>{trl('session.takenOverSub')}</div>
            <button onClick={() => {
              const key = `plotted_tab_${user?.id}`;
              const myId = tabIdRef.current;
              localStorage.setItem(key, myId);
              setDuplicateSession(false);
            }} style={{...btnPrimary,fontSize:14}}>{trl('session.resumeHere')}</button>
          </div>
        </div>,
        document.body
      )}

        {/* Content */}
        <div style={{flex:1,position:"relative",overflow:"hidden",minHeight:0}}>
          {screen==="loading" && (
            <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:C.bg}}>
              <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${C.accentDim}`,borderTopColor:C.accent,animation:"spin 0.8s linear infinite"}}/>
            </div>
          )}
          {screen==="onboarding" && <Onboarding onDone={(targetTab) => {
            setScreen("main");
            if (obHelpMode) { setObHelpMode(false); return; }
            if (targetTab) setTab(targetTab);
            // Pre-populate 3 iconic destinations based on browser language
            const lang = (navigator.language || 'fr').toLowerCase();
            const starters =
              lang.startsWith('ar')                          ? ['Istanbul', 'Marrakech', 'Athens'] :
              lang.startsWith('zh') || lang.startsWith('ja') || lang.startsWith('ko') ? ['Istanbul', 'Rome', 'Barcelona'] :
              lang.startsWith('en') && !lang.includes('gb') ? ['Reykjavik', 'Edinburgh', 'Lisbon'] :
                                                              ['Rome', 'Prague', 'Barcelone'];
            supabase.from('destinations')
              .select('id').in('name', starters)
              .then(({ data }) => {
                if (!data?.length) return;
                const ids = data.map(d => d.id);
                setSelIds(ids);
                setPlanIds(ids);
                const newPrefs = { ...loadTravelPrefs(), planIds: ids };
                setTravelPrefs(newPrefs);
                saveTravelPrefs(newPrefs, user?.id);
                savePrefs(user?.id, pax, ids);
              });
          }}/>}
          {screen==="main" && <>
            {tab==="home"  && <Home allTrips={trips} planIds={planIds} planLimit={planLimit} slots={slots} planSlotIds={planSlotIds} pax={pax} onTrip={goTrip} loading={rankingLoading} travelPrefs={travelPrefs} saveTravelPrefs={saveTravelPrefs} user={user} onRefresh={() => { hasCachedRankingRef.current = false; setRankingKey(k => k+1); }} onShare={handleShareTopN} onShareDest={handleShare} autoHelp={!localStorage.getItem('plotted_home_help_seen')} onHelpSeen={() => localStorage.setItem('plotted_home_help_seen','1')} onGoToDest={() => setTab('dest')} onGoToCalendar={() => setTab('calendar')} onGoToMap={() => { setMapInitFilter('all'); setTab('map'); }} selCount={selIds?.length ?? 0} geoBlocked={geoBlocked && !travelPrefs?.departureAirports?.length && !travelPrefs?.departureAirport} onGeoDismiss={() => { localStorage.setItem('plotted_geo_dismissed','1'); setGeoBlocked(false); }} onGoToProfile={() => setTab('profile')} planDestNames={planDestNames} onAutoGenSlots={handleAutoGenSlots} lastRankingTime={lastRankingTime}/>}
            {tab==="dest"  && <Destinations sel={selIds} setSel={setSelIds} onExplore={()=>setTab("map")} onGoToMap={()=>{ setMapInitFilter('sel'); setTab('map'); }} travelPrefs={travelPrefs} onEditNights={handleEditNights} onOpenItinerary={handleOpenItinerary} planIds={planIds} planLimit={planLimit} onTogglePlan={togglePlan} onUpgrade={handleUpgrade}
              autoHelp={!localStorage.getItem('plotted_dest_help_seen')} onHelpSeen={()=>localStorage.setItem('plotted_dest_help_seen','1')}/>}
            {tab==="map"   && <MapScreen selIds={selIds} setSelIds={setSelIds} onBack={()=>setTab("dest")} onGoToDest={()=>setTab("dest")} appLang={travelPrefs?.appLang} destPhotos={DEST_PHOTOS} typePhotos={TYPE_PHOTOS} initialFilter={mapInitFilter} key={mapInitFilter}/>}
            {tab==="cal"   && <Calendar pax={pax} setPax={setPax} slots={slots} setSlots={setSlots}
              slotLimit={slotLimit} planSlotIds={planSlotIds} onTogglePlanSlot={togglePlanSlot}
              onSlotAdded={id => { if (planSlotIds.length < slotLimit) setPlanSlotIds(prev => prev.includes(id) ? prev : [...prev, id]); else setShowPaywall(true); }}
              plan={travelPrefs?.plan} onUpgrade={() => setShowPaywall(true)}
              appLang={travelPrefs?.appLang}
              autoHelp={!localStorage.getItem('plotted_cal_help_seen')} onHelpSeen={()=>localStorage.setItem('plotted_cal_help_seen','1')}
              onAutoSlotDeleted={id => {
                const next = { ...travelPrefsRef.current, dismissedAutoSlotIds: [...(travelPrefsRef.current?.dismissedAutoSlotIds || []).filter(x => x !== id), id] };
                setTravelPrefs(next);
                saveTravelPrefs(next, user?.id);
              }}
              onRegenAuto={() => {
              // Vider les dismissed et regénérer tous les slots auto
              const clearedPrefs = { ...travelPrefsRef.current, dismissedAutoSlotIds: [] };
              setTravelPrefs(clearedPrefs);
              saveTravelPrefs(clearedPrefs, user?.id);
              const country = travelPrefs?.holidayCountry || (travelPrefs?.appLang === 'en' ? detectCountry() : 'FR');
              generateBridgeSlots(country).then(bridgeSlots => {
                if (bridgeSlots.length > 0) setSlots(prev => {
                  const manual  = (prev || []).filter(s => s.source === 'manual');
                  const evSlots = (prev || []).filter(s => s.source === 'event');
                  // Remplacer tous les auto (pas juste les nouveaux)
                  setPlanSlotIds(p => [...p, ...bridgeSlots.map(s => s.id).filter(id => !p.includes(id))]);
                  return [...bridgeSlots, ...manual, ...evSlots];
                });
              });
            }}/>}
            {tab==="trips" && <MyTrips/>}
            {tab==="user"  && <Profile onSignOut={onSignOut} user={user} travelPrefs={travelPrefs} setTravelPrefs={setTravelPrefs} planIds={planIds} planLimit={planLimit} onUpgrade={handleUpgrade}
              onBack={profileBackTo === 'booking' ? () => { setProfileBackTo(null); setProfileSection(null); setScreen('booking'); } : null}
              openSection={profileSection}
              onReplayOnboarding={() => { setObHelpMode(true); setScreen("onboarding"); }}/>}
          </>}
          {screen==="detail" && activeTrip &&
            <TripDetail t={activeTrip} onBook={goBook} onBack={()=>setScreen("main")} travelPrefs={travelPrefs} onShare={handleShareTrip}/>}
          {screen==="booking" && activeTrip && bookFlight && bookHotel &&
            <Booking t={activeTrip} selFlight={bookFlight} selHotel={bookHotel} mode={bookMode} onBack={()=>setScreen("detail")} onDone={goDone} travelPrefs={travelPrefs}
              onGoToProfile={(section) => { setProfileBackTo('booking'); setProfileSection(section || null); setScreen('main'); setTab('user'); }}/>}
        </div>
        {/* Modaux — position:absolute dans le root App (inset:0) */}
        {nightPickerCtx && (
          <NightPicker
            dest={nightPickerCtx.dest} photo={nightPickerCtx.photo}
            defNights={getRecommendedNights(nightPickerCtx.dest, getOriginCoords(travelPrefs))}
            current={travelPrefs?.destNights?.[nightPickerCtx.dest.id] ?? null}
            onConfirm={handleNightsConfirm}
            onCancel={() => setNightPickerCtx(null)}
            appLang={travelPrefs?.appLang}
          />
        )}
        {itineraryCtx && (
          <ItineraryModal
            dest={itineraryCtx.dest} photo={itineraryCtx.photo}
            nights={itineraryCtx.nights} prefs={travelPrefs}
            onClose={() => setItineraryCtx(null)}
          />
        )}
        {/* TabBar — rendu après les modaux = toujours au-dessus */}
        {(screen==="main" || screen==="detail") &&
          <TabBar tab={tab} setTab={t=>{
            setTab(t);
            if (t==='home' && activeTrip) setScreen("detail");
            else setScreen("main");
          }} appLang={travelPrefs?.appLang}/>}
        {shareModal && <ShareTopNModal
          modal={shareModal}
          onClose={() => setShareModal(null)}
          travelPrefs={travelPrefs}
        />}
        {showPaywall && (
          <SubscriptionModal
            appLang={travelPrefs?.appLang}
            currentPlan={travelPrefs?.plan || 'free'}
            onUpgrade={handleUpgrade}
            onClose={() => setShowPaywall(false)}
          />
        )}
        {noSlotWarning && createPortal(
          <NoSlotWarningModal
            appLang={travelPrefs?.appLang}
            dest={noSlotWarning.dest}
            longestSlot={noSlotWarning.longestSlot}
            onAdjust={() => {
              const { dest, longestSlot } = noSlotWarning;
              const newDestNights = { ...(travelPrefs.destNights || {}), [dest.id]: { min: longestSlot.nights, max: longestSlot.nights + 2 } };
              const next = [...planIds, dest.id];
              const newPrefs = { ...travelPrefs, destNights: newDestNights, planIds: next };
              setTravelPrefs(newPrefs);
              setPlanIds(next);
              saveTravelPrefs(newPrefs, user?.id);
              setNoSlotWarning(null);
            }}
            onCreateSlot={() => {
              const { dest } = noSlotWarning;
              const next = [...planIds, dest.id];
              const newPrefs = { ...travelPrefs, planIds: next };
              setTravelPrefs(newPrefs);
              setPlanIds(next);
              saveTravelPrefs(newPrefs, user?.id);
              setNoSlotWarning(null);
              setTab('cal');
            }}
            onClose={() => setNoSlotWarning(null)}
          />,
          document.body
        )}
        {downgradeExcess?.step === 'dests' && createPortal(
          <DowngradeDestsScreen
            appLang={travelPrefs?.appLang}
            planIds={planIds}
            destLimit={downgradeExcess.destLimit}
            onConfirm={handleDestsConfirm}
            onAutoTrim={handleAutoTrim}
          />,
          document.body
        )}
        {downgradeExcess?.step === 'slots' && createPortal(
          <DowngradeSlotsScreen
            appLang={travelPrefs?.appLang}
            planSlotIds={planSlotIds}
            slots={slots}
            slotLimit={downgradeExcess.slotLimit}
            onConfirm={handleSlotsConfirm}
            onAutoTrim={handleAutoTrim}
          />,
          document.body
        )}
    </div>
  );
}
